import {
  useMutation,
  useQuery,
  useSuspenseQuery,
  useInfiniteQuery,
  useSuspenseInfiniteQuery,
  UseMutationOptions,
  UseQueryOptions,
  UseSuspenseQueryOptions,
  UseInfiniteQueryOptions,
  InfiniteData,
  UseSuspenseInfiniteQueryOptions,
} from "@tanstack/react-query"

import * as Types from "./admin-types"
import { adminFetcher } from "./fetcher"

export const CostSummary_FragmentDoc = `
    fragment CostSummary_ on CostSummary_ {
  totalCost
  totalEnergyUsage
  tariff {
    id
    code
    utility {
      shortName
    }
  }
}
    `
export const OrgRefFragmentDoc = `
    fragment OrgRef on OrgRef {
  pid
  name
}
    `
export const CostsFragmentFragmentDoc = `
    fragment CostsFragment on ETBCosts {
  totalCost
  totalIncentiveSavings
  netCost
  energyConsumptionKwhYear1
  netEnergyConsumptionKwhYear1
  utilityCostPerKwh
  averageLoadFactor
  energyCostBefore
  energyCostAfter
  energyCostSavings
  demandCostBefore
  demandCostAfter
  demandCostSavings
  totalCostBefore
  totalCostAfter
  totalCostSavings
  pvTotalCost
  pvNetCost
  pvPowerRatingKwDc
  pvPowerRatingKwAc
  pvDegradationRate
  pvGenerationKwhYear1
  pvKwhPerKwDc
  pvSavingsPerKwh
  pvGenerationPercentExported
  storageTotalCost
  storageNetCost
  storageCapacityKwh
  storageMaxDischargePowerKw
  storageTwoHourPowerRatingKw
  storageDegradationRate
  storageDischargeYear1
  storageConsumptionKwhYear1
  storageSavingsPerKw
  storageSavingsPerKwh
}
    `
export const OverrideFragmentDoc = `
    fragment Override on FinmodelInputOverride {
  id
  dateCreated
  dateTrashed
  inputName
  inputValue
  source
  createdBy
}
    `
export const SiteRefFragmentDoc = `
    fragment SiteRef on SiteRef {
  pid
  name
  streetAddress
}
    `
export const ScenarioRefFragmentDoc = `
    fragment ScenarioRef on ScenarioRef {
  pid
  name
}
    `
export const FinmodelSourceFragmentFragmentDoc = `
    fragment FinmodelSourceFragment on FinmodelSource {
  __typename
  ... on PVoptRef {
    id
  }
  ... on LuxRef {
    id
  }
  ... on ReoptRef {
    pid
  }
  ... on ETBReportRef {
    id
    projectName
  }
}
    `
export const FinmodelFailureDetailFragmentDoc = `
    fragment FinmodelFailureDetail on FinmodelFailure {
  id
  createdAt
  fmv
  name
  message
  isPvopt
  isLux
  site {
    ...SiteRef
  }
  scenario {
    ...ScenarioRef
  }
  source {
    ...FinmodelSourceFragment
  }
}
    `
export const FinmodelFailureRefFragmentDoc = `
    fragment FinmodelFailureRef on FinmodelFailureRef {
  id
}
    `
export const SitesTableFragmentDoc = `
    fragment SitesTable on Site {
  pid
  propertyName
  fullAddress
  state
  buildingType
  sqft
  roofSqft
  occupancyType
  leaseType
  metering
  mortgageLender
  roofReplacedYr
  hiddenFromCustomers
  portfolio {
    pid
    title
    org {
      slug
    }
  }
  roofImage {
    id
  }
  roofMaxPvKw
  carportMaxPvKw
  primaryScenario {
    pid
    solarLocation
    includeStorage
  }
  currentStatus {
    pid
    status
  }
  createdAt
}
    `
export const UserRefFragmentDoc = `
    fragment UserRef on UserRef {
  pid
  email
  name
  isAdmin
  avatarUrl
}
    `
export const MeteringFragmentDoc = `
    fragment Metering on Site {
  metering
  meteringSetDate
  meteringSetBy {
    ...UserRef
  }
}
    `
export const LeaseTypeFragmentDoc = `
    fragment LeaseType on Site {
  leaseType
}
    `
export const BuildingOccupancyTypeFragmentDoc = `
    fragment BuildingOccupancyType on Site {
  occupancyType
}
    `
export const EtbReportRefFragmentDoc = `
    fragment ETBReportRef on ETBReportRef {
  id
  projectName
}
    `
export const EtbScenarioRefFragmentDoc = `
    fragment ETBScenarioRef on ETBScenarioRef {
  pid
  name
  isPrimary
  solarLocation
  includeStorage
  loadCategory
}
    `
export const AttachmentFragmentFragmentDoc = `
    fragment AttachmentFragment on Attachment {
  id
  size
  name
  contentType
  downloadHref
  createdAt
  trashedAt
  isTrashable
  isRoofImageCandidate
  isEtb
  createdBy {
    ...UserRef
  }
}
    `
export const ScenarioHelioscopeFragmentFragmentDoc = `
    fragment ScenarioHelioscopeFragment on Helioscope {
  id
  pid
  createdAt
  isTrashed
  roofPvKw
  carportPvKw
  pdf {
    ...AttachmentFragment
  }
  roofImage {
    ...AttachmentFragment
  }
}
    `
export const ScenarioEtbFragmentFragmentDoc = `
    fragment ScenarioEtbFragment on ETBReport {
  id
  dateCreated
  projectName
  roofPvKw
  carportPvKw
  groundPvKw
  batteryKw
  batteryKwh
}
    `
export const TariffDetailFragmentFragmentDoc = `
    fragment TariffDetailFragment on TariffDetails {
  tariff {
    id
    code
    name
    customerClass
  }
  version {
    tariffId
    effectiveDate
    endDate
  }
  properties {
    keyName
    dataType
    propertyValue
    formulaDetail
  }
  rawJson
  rates {
    tariffRateId
    name
    classes
    chargeClass
    chargeType
    chargePeriod
    transactionType
    applicabilityKey
    quantityKey
    isDemandCharge
    isFixedPrice
    isQuantity
    isTou
    variableLimitValue {
      keyName
      formulaDetail
    }
    bands {
      tariffRateBandId
      rateUnit
      price
      isCredit
      hasConsumptionLimit
      propertyUpperLimit
      consumptionUpperLimit
      demandUpperLimit
      calculationFactor
      applicabilityValue
    }
    season {
      seasonFromMonth
      seasonToMonth
      seasonFromDay
      seasonToDay
    }
    tou {
      touName
      season {
        seasonFromMonth
        seasonToMonth
        seasonFromDay
        seasonToDay
      }
      touPeriods {
        fromDayOfWeek
        toDayOfWeek
        fromHour
        toHour
        fromMinute
        toMinute
      }
    }
  }
}
    `
export const FinRelFragmentDoc = `
    fragment FinRel on AdminFinRel {
  disabledAt
  finproduct {
    id
    type
    name
    financier {
      name
      pid
    }
  }
  recvr {
    pid
  }
}
    `
export const AdminOrgFragmentDoc = `
    fragment AdminOrg on AdminOrg {
  pid
  name
  logoUrl
}
    `
export const UserListUserFragmentDoc = `
    fragment UserListUser on AdminUser {
  id
  pid
  email
  name
  affiliation
  isAdmin
  isEnabled
  avatarUrl
  meetingSchedulingUrl
  portfoliosWithRole {
    pid
    title
  }
  unclaimedInvitations {
    isClaimed
    isExpired
    expiresAt
    portfolio {
      pid
      title
    }
  }
  licenseAgreements {
    forOrg {
      pid
      name
    }
    acceptedAt
  }
  org {
    pid
    name
    slug
  }
  lastSeenAt
}
    `
export const RuleValueFragmentDoc = `
    fragment RuleValue on RuleValue {
  __typename
  ... on YesNoValue {
    yes
  }
  ... on NumberValue {
    number
  }
  ... on CurrencyValue {
    usd
  }
  ... on IntegerValue {
    integer
  }
  ... on TextValue {
    text
  }
}
    `
export const PolicyConditionFragmentDoc = `
    fragment PolicyCondition on PolicyCondition {
  __typename
  ... on BuildingSuperTypeCondition {
    buildingSuperType
  }
  ... on VnemEligibilityCondition {
    vnemEligibilityRequired
  }
}
    `
export const RuleFragmentDoc = `
    fragment Rule on Rule {
  id
  description
  units
  value {
    ...RuleValue
  }
  application {
    status
    detail
  }
  conditions {
    ...PolicyCondition
  }
}
    `
export const PolicyListItemFragmentDoc = `
    fragment PolicyListItem on Policy {
  pid
  name
  jurisdiction {
    scope
    name
  }
  status
  startDate
  endDate
  userVisible
  application {
    status
  }
  rules {
    ...Rule
  }
}
    `
export const BauTariffFragmentDoc = `
    fragment BauTariff on Tariff {
  id
  name
  code
  masterTariffId
}
    `
export const BauMeterFragmentDoc = `
    fragment BauMeter on BauMeter {
  id
  createdAt
  createdBy {
    email
  }
  label
  loadOrigin
  loadCategory
  meterSource
  utility {
    id
    name
  }
  tariff {
    ...BauTariff
  }
  tariffSetBy {
    pid
    email
  }
  underHumanControl
  tariffsUnderHumanControl
  postSolarTariff {
    ...BauTariff
  }
  postSolarTariffSetBy {
    pid
    email
  }
  postBatteryTariff {
    ...BauTariff
  }
  postBatteryTariffSetBy {
    pid
    email
  }
  archivedAt
  archivedBy {
    pid
    email
  }
  archivedReason
  rollup {
    season
    days
    numbers
  }
}
    `
export const BauMeterIntervalsFragmentDoc = `
    fragment BauMeterIntervals on BauMeter {
  id
  tariffUsdPerKwh
  rollup {
    season
    days
    numbers
  }
  intervals {
    units
    year
    timeZone
    maxKw
    totalKwh
    kwh8760
  }
}
    `
export const PvConfigFragmentDoc = `
    fragment PVConfig on PVConfig {
  pvType
  origin
  sizeKw
  productionKwh
}
    `
export const BatteryConfigFragmentDoc = `
    fragment BatteryConfig on BatteryConfig {
  vendor
  sizeKwh
  sizeKw
  chargeStrategy
  dischargeStrategy
}
    `
export const SlimLuxFragmentDoc = `
    fragment SlimLux on Lux {
  id
  etbReport {
    id
  }
  createdAt
  fmvStr
  goal
  maxOffsetPct
  pvKw
  roofPvKw
  carportPvThreeHighKw
  carportPvSixHighKw
  battKw
  battKwh
  meterSet {
    id
  }
  meters {
    bauMeter {
      id
    }
    postSolarMeter {
      pv {
        ...PVConfig
      }
    }
    postBatteryMeter {
      batt {
        ...BatteryConfig
      }
    }
  }
}
    `
export const CostSummaryFragmentDoc = `
    fragment CostSummary on CostSummary {
  totalCost
  totalEnergyUsage
  energyCost
  demandCost
  fixedCost
  nbcCost
  exportBenefit
}
    `
export const LuxMeterFragmentDoc = `
    fragment LuxMeter on LuxMeter {
  id
  gridMeter {
    preSolar {
      costSummary {
        ...CostSummary
      }
    }
    postSolar {
      costSummary {
        ...CostSummary
      }
    }
    postBattery {
      costSummary {
        ...CostSummary
      }
    }
  }
  dayIntervals {
    pvToBldg
    battDischarge
    gridToBldg
  }
  bauMeter {
    id
    label
    importsKwh
    tariff {
      name
      code
    }
  }
  postSolarMeter {
    pv {
      ...PVConfig
    }
    tariff {
      parent {
        name
        code
      }
    }
  }
  postBatteryMeter {
    batt {
      ...BatteryConfig
    }
    tariff {
      parent {
        name
        code
      }
    }
  }
}
    `
export const LuxFragmentDoc = `
    fragment Lux on Lux {
  ...SlimLux
  meters {
    ...LuxMeter
  }
  iterations {
    meters {
      bauMeterId
      totalPvKw
      pv {
        pvType
        sizeKw
      }
    }
    metric
    postTaxItcDeprIrr
  }
}
    `
export const CleanBillSuggestionFragmentDoc = `
    fragment CleanBillSuggestion on Bill_ {
  utilityName
  customerName
  serviceIdentifier
  billingAccountNumber
  billingAddress
  billingPeriodStart
  billingPeriodEnd
  meters {
    serviceAccount
    meterId
    serviceAddress
    tariffLabel
    totalKwh
    peakKw
    totalCostUsd
    lineItems {
      label
      unit
      volume
      rate
      costUsd
    }
  }
}
    `
export const ChangelogFragmentDoc = `
    fragment Changelog on Changelog {
  field
  updatedAt
  updatedBy {
    ...UserRef
  }
}
    `
export const ReonomyContactFragmentDoc = `
    fragment ReonomyContact on ReonomyContact {
  firstName
  lastName
  company
  jobTitle
  emailAddresses
  phoneNumbers
  fullAddresses
}
    `
export const UtilityFragmentFragmentDoc = `
    fragment UtilityFragment on Utility {
  id
  pid
  name
  shortName
  numTariffs
  dateTariffsRefreshed
  utilityApiName
  isPreferred
  notes
}
    `
export const EtbMissingDataFragmentDoc = `
    fragment ETBMissingData on ETBReport {
  id
  meters {
    id
    name
    state
    zipCode
    utilityName
    tariffShortDescriptionBefore
    tariffShortDescriptionAfter
    utility {
      id
      name
      shortName
    }
  }
  site {
    pid
  }
}
    `
export const SmallScenarioFragmentFragmentDoc = `
    fragment SmallScenarioFragment on AdminScenario {
  id
  pid
  name
  dateCreated
  includeStorage
  isPrimary
  isMultifamily
  loadCategory
  solarLocation
  createdBy {
    ...UserRef
  }
  hiddenFromCustomers
  allowMulticonnect
}
    `
export const ScenarioFragmentFragmentDoc = `
    fragment ScenarioFragment on AdminScenario {
  ...SmallScenarioFragment
  activeEtbReport {
    id
    projectName
  }
  helioscope {
    id
    pid
  }
}
    `
export const AiMessageFragmentDoc = `
    fragment AIMessage on Message {
  role
  content {
    __typename
    ... on TextContent {
      text
    }
    ... on ImageContent {
      imageUrl {
        url
      }
    }
  }
  toolCalls {
    id
    type
    function {
      name
      arguments
    }
  }
}
    `
export const BasicOrgDetailsFragmentDoc = `
    fragment BasicOrgDetails on AdminOrg {
  id
  pid
  name
  slug
  rawSlug
  description
  logoUrl
  logomarkUrl
}
    `
export const DeltaFragmentDoc = `
    fragment Delta on Delta {
  a
  b
  diff
  pct
}
    `
export const NameThatTariffResultFragmentDoc = `
    fragment NameThatTariffResult on NameThatTariffResult {
  notTheCorrectRateVersion
  rate {
    startDate
    endDate
    tariffId
  }
  expected {
    start
    end
    totalCostUsd
    totalKwh
    peakKw
    lineItems {
      chargeType
      costUsd
    }
  }
  actual {
    start
    end
    totalCostUsd
    totalKwh
    peakKw
    lineItems {
      chargeType
      costUsd
    }
  }
  delta {
    usd {
      ...Delta
    }
    kwh {
      ...Delta
    }
    kw {
      ...Delta
    }
  }
  document {
    pid
    contentType
    viewHref
  }
}
    `
export const KwhFlowFragmentDoc = `
    fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}
    `
export const RevampSystemFragmentDoc = `
    fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    assumptions {
      battChemName
    }
  }
}
    `
export const FinmodelCostFragmentDoc = `
    fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
    `
export const RevampFinmodelFragmentDoc = `
    fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
    `
export const AdminFinmodelDetailFragmentDoc = `
    fragment AdminFinmodelDetail on RevampFinmodel {
  __typename
  ...RevampFinmodel
  financing {
    id
    name
    type
    financierName
    financierPid
    isThirdParty
    status
  }
  scenario {
    pid
    name
    loadCategory
    includeStorage
    solarLocation
  }
  investorDetails {
    investorXlsxHref
    irrs {
      preTax
      postTaxItcDepr
    }
  }
  moreDetails {
    cashPurchaseNpv
    totalInstallCost
    pvCostPerWatt
    finmodelInputs
    finmodelOutputs
    luxId
    failureId
  }
  importReductionPct
}
    `
export const PossibleTariffFragmentDoc = `
    fragment PossibleTariff on Tariff {
  id
  name
  code
  customerClass
  customerCount
  customerLikelihood
  isPreferred
  minMonthlyKw
  maxMonthlyKw
  minMonthlyKwh
  maxMonthlyKwh
  masterTariffId
  hasNetMetering
  requiresPv
  requiresStorage
  tariffType
}
    `
export const ResultRefFragmentDoc = `
    fragment ResultRef on Result {
  id
  pid
  createdAt
  scenario {
    pid
    name
    solarLocation
    loadCategory
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
    status
  }
}
    `
export const RevampCsFragmentDoc = `
    fragment RevampCS on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    lifetimeUsd
    annualUsd
    usdPerSqft
    npv
    discountRate
    roofRentPerYear
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  leaseProjections {
    systemSizeKw
    annualRentLowerRangeUsd
    annualRentUpperRangeUsd
    methodology
  }
}
    `
export const HelioscopeFragmentDoc = `
    fragment Helioscope on Helioscope {
  pid
  roofPvKw
  carportPvKw
  groundPvKw
  systemProductivityKwhPerKw
  roofImage {
    id
    name
    downloadHref
  }
  pdf {
    downloadHref
    contentType
    name
    id
  }
  roofImageHref
  pdfHref
  dcNameplateKw
  acNameplateKw
  loadRatio
  moduleType
  inverterType
  rackingTiltDegrees
  rackingManufacturer
  rackingConnectionType
  notes
}
    `
export const ResultFragmentDoc = `
    fragment Result on Result {
  id
  pid
  createdAt
  salesPdfReportHref
  scenario {
    pid
    name
    solarLocation
    loadCategory
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
    status
  }
  result {
    __typename
    ... on PhysicalCheck {
      createdAt
      roofIsGood
      carportIsGood
      existingSolar
      badRoof
      groundmountIsGood
    }
    ... on PolicyCheck {
      createdAt
      productType
      isGood
      disqualReason
    }
    ... on RevampFinmodelFailure {
      id
      createdAt
      fmvStr
      details {
        label
        description
      }
    }
    ... on RevampFinmodel {
      ...RevampFinmodel
      iterations {
        cashPurchaseNpv
        pvKw
        marginalCo2Averted
        storagePvKw
        storagePvKwh
      }
      meters {
        loadCategory
        meterSource
        bau {
          totalKwhFlow {
            ...KwhFlow
          }
        }
        der {
          totalKwhFlow {
            ...KwhFlow
          }
          averageDayKwhFlow {
            ...KwhFlow
          }
        }
      }
      emissions {
        bauCo2Tons
        derCo2Tons
        bauAverageDayCo2Tons
        derAverageDayCo2Tons
      }
    }
    ... on RevampCS {
      ...RevampCS
    }
    ... on Viability {
      isAllowed
      viabilityType: type
      reason
      level
    }
  }
  helioscope {
    ...Helioscope
  }
}
    `
export const ResultWithEnergyDataFragmentDoc = `
    fragment ResultWithEnergyData on Result {
  id
  result {
    ... on RevampFinmodel {
      meters {
        loadCategory
        meterSource
        bau {
          totalKwhFlow {
            ...KwhFlow
          }
        }
        der {
          totalKwhFlow {
            ...KwhFlow
          }
          averageDayKwhFlow {
            ...KwhFlow
          }
        }
      }
      emissions {
        bauCo2Tons
        derCo2Tons
        bauAverageDayCo2Tons
        derAverageDayCo2Tons
      }
    }
  }
}
    `
export const DecodedRateFragmentDoc = `
    fragment DecodedRate on DecodedRate {
  id
  name
  chargeClass
}
    `
export const CostOrTaxItemFragmentDoc = `
    fragment CostOrTaxItem on CostItemTaxItem {
  __typename
  ... on CostItem {
    rateObj {
      ...DecodedRate
    }
    name
    rateId
    chargeType
    chargeCategory
    volume
    rate
    cost
  }
  ... on TaxItem {
    taxId
    subtotal
    cost
    percentage
  }
}
    `
export const BillSectionFragmentDoc = `
    fragment BillSection on BillSection {
  name
  rates {
    ...DecodedRate
  }
  costs {
    ...CostOrTaxItem
  }
}
    `
export const FormattedBillFragmentDoc = `
    fragment FormattedBill on FormattedBills {
  rate {
    tariffId
    startDate
    endDate
  }
  bills {
    month
    charges {
      ...BillSection
    }
    exports {
      ...BillSection
    }
    kwhSupplyCharge
    kwhCharge
  }
}
    `
export const BidRevampSiteFragmentDoc = `
    fragment BidRevampSite on RevampSite {
  pid
  name
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  assetManager
  buildingType
  meterSet {
    totalCost
    loadKwh
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  siteDetails {
    salePlans
    roofCondition
    roofReplacedYr
  }
  roofMaxPvKw
  buildingClass
  targetUtility {
    name
    pid
    id
  }
  ppaType
}
    `
export const SiteConfigFragmentDoc = `
    fragment SiteConfig on SiteConfig {
  createdAt
  createdBy {
    ...UserRef
  }
  scenario {
    pid
    name
    loadCategory
    solarLocation
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
}
    `
export const FootprintFragmentDoc = `
    fragment Footprint on Footprint {
  latitude
  longitude
  bounds
  obstructionShape
  roofShape
  roofShapeGeojson
  roofShapeUpdatedAt
  roofShapeUpdatedBy {
    ...UserRef
  }
  roofSetbackShape
  roofSetbackShapeGeojson
  roofSetbackShapeUpdatedAt
  roofSetbackShapeUpdatedBy {
    ...UserRef
  }
  carportShape
  carportShapeGeojson
  carportShapeUpdatedAt
  carportShapeUpdatedBy {
    ...UserRef
  }
  groundmountShape
  groundmountShapeGeojson
  groundmountShapeUpdatedAt
  groundmountShapeUpdatedBy {
    ...UserRef
  }
  keepoutsShape
  keepoutsShapeGeojson
  keepoutsShapeUpdatedAt
  keepoutsShapeUpdatedBy {
    ...UserRef
  }
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadShapeUpdatedAt
  batteryPadShapeUpdatedBy {
    ...UserRef
  }
}
    `
export const RevampSiteBasicsFragmentDoc = `
    fragment RevampSiteBasics on RevampSite {
  pid
  name
  isFavorite
  isBehindTheMeter
  isEnergyCommunity
  isLowIncomeCommunity
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  metering
  assetManager
  vnemAllowed
  buildingType
  usingBillingMeters
  numParkingSpaces
  selectedConfig {
    ...SiteConfig
  }
  documents {
    pid
    downloadHref
    fileName
    tag
  }
  energyThings {
    canRooftopSolar
    canCarportSolar
    canGroundmountSolar
    canStorage
  }
  financingThings {
    canSelfInvest
    canLoan
    canPpa
    canCommunitySolar
  }
  physicalCheck {
    existingSolar
    unobstructedFlatRoof
    badRoof
  }
  siteDetails {
    leaseApprovers
    externalLeaseApprovers
    needsTenantApproval
    roofRightsHolder
    propertyLenders
    propertyInsurer
    constructionEmail
    propertySiteVisitContacts
    leasePropertyLegalDescription
    leaseLegalOwnerName
    signatureBlocks
    isFannieMaeLender
    isFmGlobalInsurer
    roofReplacedYr
    roofCondition
    salePlans
    buildingLegalName
    propertyFundName
    leaseSignatory
    counselApproval
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  usableRoofSqft
  usableCarportFt
  impliedCarportLinearFt
  impliedUsableRoofSqft
  impliedUsableRoofSqftExplanation
  groundAvailableSqft
  hideBuildingFootprint
  keepoutsFootprintSqft
  batteryPadFootprintSqft
  roofMaxPvKw
  carportMaxPvKw
  groundmountMaxPvKw
  maxPvKwPolicyLimited
  isMultifamily
  occupancyType
  leaseType
  tenantLeaseTerm
  tenantLeaseEnds
  tenantInterest
  mortgageLender
  notes {
    text
  }
  buildingClass
  buildingData {
    numStories
    numUnits
    yearBuilt
    sqft
    roofSqft
    numParkingSpaces
    occupancyRate
    totalBuildings
    yearRenovated
    parentCompany
    lastSalePrice
    leedCertified
    energyStar
    buildingOpex
    capRate
  }
  footprint {
    ...Footprint
  }
  thirdPartySupplier {
    pid
    name
  }
  meterSet {
    loadOrigin
  }
  targetUtility {
    name
    pid
    id
    eiaGridEsc {
      cagrBlended
      fromYear
      toYear
    }
    hostingCapacityMaps {
      url
      requiresLogin
      types
    }
  }
  hasSubmittedEnergyData
}
    `
export const SiteVisualsFragmentDoc = `
    fragment SiteVisuals on SiteVisuals {
  solarPanels {
    latitude
    longitude
    angle
  }
}
    `
export const BasicEvCalcFragmentDoc = `
    fragment BasicEVCalc on EVCalc {
  id
  createdAt
  projectInputs {
    projectStartDate
    installType
    termYears
    chargerKw
    yr0NumTenantEvs
    discountRate
  }
  outputs {
    irr
    npv
    yr0NumTenantEvs
    breakevenYear
    numChargersDemandedSeries
    cashflowSeries
    revenueSeries
    incentiveUsd
  }
  numChargers
  optimalNumChargers
  npv
  markupUsdPerKwh
  tariffRate {
    parent {
      name
      code
    }
  }
  utility {
    shortName
  }
  timeZone
  dayRates
  seasonalDayRates {
    name
    startMonth
    endMonth
    dayRates
  }
  downloadHourlyRatesHref
  xlsx
  downloadXlsxHref
  optIters {
    numChargers
    npv
  }
  yr3Revenue
  yr3Cashflow
  yr3NumEvs
  parkingRatio
  year3Expenses {
    label
    value
  }
  installCosts {
    label
    value
  }
}
    `
export const DocumentFragmentDoc = `
    fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}
    `
export const CreatePortfolioDocument = `
    mutation CreatePortfolio($title: String!, $logo: Upload!, $orgPid: String, $createEponymousOrg: Boolean!, $createOpenInvite: Boolean!) {
  portfolio {
    new(
      title: $title
      upload: $logo
      orgPid: $orgPid
      createEponymousOrg: $createEponymousOrg
      createOpenInvite: $createOpenInvite
    ) {
      pid
      title
    }
  }
}
    `

export const useCreatePortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreatePortfolioMutation,
    TError,
    Types.CreatePortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreatePortfolioMutation,
    TError,
    Types.CreatePortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["CreatePortfolio"],
    mutationFn: (variables?: Types.CreatePortfolioMutationVariables) =>
      adminFetcher<
        Types.CreatePortfolioMutation,
        Types.CreatePortfolioMutationVariables
      >(CreatePortfolioDocument, variables)(),
    ...options,
  })
}

useCreatePortfolioMutation.fetcher = (
  variables: Types.CreatePortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.CreatePortfolioMutation, Types.CreatePortfolioMutationVariables>(
    CreatePortfolioDocument,
    variables,
    options,
  )

export const SetSitePortfolioDocument = `
    mutation SetSitePortfolio($sitePid: String!, $portfolioPid: String) {
  site {
    setPortfolio(sitePid: $sitePid, portfolioPid: $portfolioPid) {
      pid
      portfolio {
        pid
        title
      }
    }
  }
}
    `

export const useSetSitePortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetSitePortfolioMutation,
    TError,
    Types.SetSitePortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSitePortfolioMutation,
    TError,
    Types.SetSitePortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["SetSitePortfolio"],
    mutationFn: (variables?: Types.SetSitePortfolioMutationVariables) =>
      adminFetcher<
        Types.SetSitePortfolioMutation,
        Types.SetSitePortfolioMutationVariables
      >(SetSitePortfolioDocument, variables)(),
    ...options,
  })
}

useSetSitePortfolioMutation.fetcher = (
  variables: Types.SetSitePortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetSitePortfolioMutation, Types.SetSitePortfolioMutationVariables>(
    SetSitePortfolioDocument,
    variables,
    options,
  )

export const SetBuildingTypeDocument = `
    mutation SetBuildingType($sitePid: String!, $value: BuildingType) {
  site {
    setBuildingType(sitePid: $sitePid, value: $value) {
      pid
      buildingType
    }
  }
}
    `

export const useSetBuildingTypeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBuildingTypeMutation,
    TError,
    Types.SetBuildingTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBuildingTypeMutation,
    TError,
    Types.SetBuildingTypeMutationVariables,
    TContext
  >({
    mutationKey: ["SetBuildingType"],
    mutationFn: (variables?: Types.SetBuildingTypeMutationVariables) =>
      adminFetcher<
        Types.SetBuildingTypeMutation,
        Types.SetBuildingTypeMutationVariables
      >(SetBuildingTypeDocument, variables)(),
    ...options,
  })
}

useSetBuildingTypeMutation.fetcher = (
  variables: Types.SetBuildingTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetBuildingTypeMutation, Types.SetBuildingTypeMutationVariables>(
    SetBuildingTypeDocument,
    variables,
    options,
  )

export const SetCostarPropertyTypeDocument = `
    mutation SetCostarPropertyType($sitePid: String!, $value: CoStarPropertyType!) {
  site {
    setCostarPropertyType(sitePid: $sitePid, value: $value) {
      pid
      costarPropertyType
    }
  }
}
    `

export const useSetCostarPropertyTypeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetCostarPropertyTypeMutation,
    TError,
    Types.SetCostarPropertyTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCostarPropertyTypeMutation,
    TError,
    Types.SetCostarPropertyTypeMutationVariables,
    TContext
  >({
    mutationKey: ["SetCostarPropertyType"],
    mutationFn: (variables?: Types.SetCostarPropertyTypeMutationVariables) =>
      adminFetcher<
        Types.SetCostarPropertyTypeMutation,
        Types.SetCostarPropertyTypeMutationVariables
      >(SetCostarPropertyTypeDocument, variables)(),
    ...options,
  })
}

useSetCostarPropertyTypeMutation.fetcher = (
  variables: Types.SetCostarPropertyTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetCostarPropertyTypeMutation,
    Types.SetCostarPropertyTypeMutationVariables
  >(SetCostarPropertyTypeDocument, variables, options)

export const SetCostarSecondaryTypeDocument = `
    mutation SetCostarSecondaryType($sitePid: String!, $value: CoStarSecondaryType) {
  site {
    setCostarSecondaryType(sitePid: $sitePid, value: $value) {
      pid
      costarSecondaryType
    }
  }
}
    `

export const useSetCostarSecondaryTypeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetCostarSecondaryTypeMutation,
    TError,
    Types.SetCostarSecondaryTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCostarSecondaryTypeMutation,
    TError,
    Types.SetCostarSecondaryTypeMutationVariables,
    TContext
  >({
    mutationKey: ["SetCostarSecondaryType"],
    mutationFn: (variables?: Types.SetCostarSecondaryTypeMutationVariables) =>
      adminFetcher<
        Types.SetCostarSecondaryTypeMutation,
        Types.SetCostarSecondaryTypeMutationVariables
      >(SetCostarSecondaryTypeDocument, variables)(),
    ...options,
  })
}

useSetCostarSecondaryTypeMutation.fetcher = (
  variables: Types.SetCostarSecondaryTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetCostarSecondaryTypeMutation,
    Types.SetCostarSecondaryTypeMutationVariables
  >(SetCostarSecondaryTypeDocument, variables, options)

export const SetCapRateDocument = `
    mutation SetCapRate($sitePid: String!, $value: Float) {
  site {
    setCapRate(sitePid: $sitePid, value: $value) {
      pid
      capRate
    }
  }
}
    `

export const useSetCapRateMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetCapRateMutation,
    TError,
    Types.SetCapRateMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCapRateMutation,
    TError,
    Types.SetCapRateMutationVariables,
    TContext
  >({
    mutationKey: ["SetCapRate"],
    mutationFn: (variables?: Types.SetCapRateMutationVariables) =>
      adminFetcher<Types.SetCapRateMutation, Types.SetCapRateMutationVariables>(
        SetCapRateDocument,
        variables,
      )(),
    ...options,
  })
}

useSetCapRateMutation.fetcher = (
  variables: Types.SetCapRateMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetCapRateMutation, Types.SetCapRateMutationVariables>(
    SetCapRateDocument,
    variables,
    options,
  )

export const SetZipCodeDocument = `
    mutation SetZipCode($sitePid: String!, $value: String!) {
  site {
    setZipCode(sitePid: $sitePid, value: $value) {
      pid
      zipCode
    }
  }
}
    `

export const useSetZipCodeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetZipCodeMutation,
    TError,
    Types.SetZipCodeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetZipCodeMutation,
    TError,
    Types.SetZipCodeMutationVariables,
    TContext
  >({
    mutationKey: ["SetZipCode"],
    mutationFn: (variables?: Types.SetZipCodeMutationVariables) =>
      adminFetcher<Types.SetZipCodeMutation, Types.SetZipCodeMutationVariables>(
        SetZipCodeDocument,
        variables,
      )(),
    ...options,
  })
}

useSetZipCodeMutation.fetcher = (
  variables: Types.SetZipCodeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetZipCodeMutation, Types.SetZipCodeMutationVariables>(
    SetZipCodeDocument,
    variables,
    options,
  )

export const SetBuildingClassDocument = `
    mutation SetBuildingClass($sitePid: String!, $buildingClass: String) {
  site {
    setBuildingClass(sitePid: $sitePid, buildingClass: $buildingClass)
  }
}
    `

export const useSetBuildingClassMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBuildingClassMutation,
    TError,
    Types.SetBuildingClassMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBuildingClassMutation,
    TError,
    Types.SetBuildingClassMutationVariables,
    TContext
  >({
    mutationKey: ["SetBuildingClass"],
    mutationFn: (variables?: Types.SetBuildingClassMutationVariables) =>
      adminFetcher<
        Types.SetBuildingClassMutation,
        Types.SetBuildingClassMutationVariables
      >(SetBuildingClassDocument, variables)(),
    ...options,
  })
}

useSetBuildingClassMutation.fetcher = (
  variables: Types.SetBuildingClassMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetBuildingClassMutation, Types.SetBuildingClassMutationVariables>(
    SetBuildingClassDocument,
    variables,
    options,
  )

export const SetSqftDocument = `
    mutation SetSqft($sitePid: String!, $value: Float!) {
  site {
    setSqft(sitePid: $sitePid, value: $value) {
      pid
      sqft
    }
  }
}
    `

export const useSetSqftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetSqftMutation,
    TError,
    Types.SetSqftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSqftMutation,
    TError,
    Types.SetSqftMutationVariables,
    TContext
  >({
    mutationKey: ["SetSqft"],
    mutationFn: (variables?: Types.SetSqftMutationVariables) =>
      adminFetcher<Types.SetSqftMutation, Types.SetSqftMutationVariables>(
        SetSqftDocument,
        variables,
      )(),
    ...options,
  })
}

useSetSqftMutation.fetcher = (
  variables: Types.SetSqftMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetSqftMutation, Types.SetSqftMutationVariables>(
    SetSqftDocument,
    variables,
    options,
  )

export const SetRoofSqftDocument = `
    mutation SetRoofSqft($sitePid: String!, $value: Float!, $sourceType: ChangeSourceType, $sourceId: String) {
  site {
    setRoofSqft(
      sitePid: $sitePid
      value: $value
      sourceType: $sourceType
      sourceId: $sourceId
    ) {
      pid
      roofSqft
    }
  }
}
    `

export const useSetRoofSqftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetRoofSqftMutation,
    TError,
    Types.SetRoofSqftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetRoofSqftMutation,
    TError,
    Types.SetRoofSqftMutationVariables,
    TContext
  >({
    mutationKey: ["SetRoofSqft"],
    mutationFn: (variables?: Types.SetRoofSqftMutationVariables) =>
      adminFetcher<Types.SetRoofSqftMutation, Types.SetRoofSqftMutationVariables>(
        SetRoofSqftDocument,
        variables,
      )(),
    ...options,
  })
}

useSetRoofSqftMutation.fetcher = (
  variables: Types.SetRoofSqftMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetRoofSqftMutation, Types.SetRoofSqftMutationVariables>(
    SetRoofSqftDocument,
    variables,
    options,
  )

export const SetUsableRoofSqftDocument = `
    mutation SetUsableRoofSqft($sitePid: String!, $value: Float!) {
  site {
    setUsableRoofSqft(sitePid: $sitePid, value: $value) {
      pid
      usableRoofSqft
    }
  }
}
    `

export const useSetUsableRoofSqftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetUsableRoofSqftMutation,
    TError,
    Types.SetUsableRoofSqftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetUsableRoofSqftMutation,
    TError,
    Types.SetUsableRoofSqftMutationVariables,
    TContext
  >({
    mutationKey: ["SetUsableRoofSqft"],
    mutationFn: (variables?: Types.SetUsableRoofSqftMutationVariables) =>
      adminFetcher<
        Types.SetUsableRoofSqftMutation,
        Types.SetUsableRoofSqftMutationVariables
      >(SetUsableRoofSqftDocument, variables)(),
    ...options,
  })
}

useSetUsableRoofSqftMutation.fetcher = (
  variables: Types.SetUsableRoofSqftMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetUsableRoofSqftMutation,
    Types.SetUsableRoofSqftMutationVariables
  >(SetUsableRoofSqftDocument, variables, options)

export const SetUsableRoofPctDocument = `
    mutation SetUsableRoofPct($sitePid: String!, $value: Float!) {
  site {
    setUsableRoofPct(sitePid: $sitePid, value: $value) {
      pid
      usableRoofPct
    }
  }
}
    `

export const useSetUsableRoofPctMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetUsableRoofPctMutation,
    TError,
    Types.SetUsableRoofPctMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetUsableRoofPctMutation,
    TError,
    Types.SetUsableRoofPctMutationVariables,
    TContext
  >({
    mutationKey: ["SetUsableRoofPct"],
    mutationFn: (variables?: Types.SetUsableRoofPctMutationVariables) =>
      adminFetcher<
        Types.SetUsableRoofPctMutation,
        Types.SetUsableRoofPctMutationVariables
      >(SetUsableRoofPctDocument, variables)(),
    ...options,
  })
}

useSetUsableRoofPctMutation.fetcher = (
  variables: Types.SetUsableRoofPctMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetUsableRoofPctMutation, Types.SetUsableRoofPctMutationVariables>(
    SetUsableRoofPctDocument,
    variables,
    options,
  )

export const SetGroundAvailableSqftDocument = `
    mutation SetGroundAvailableSqft($sitePid: String!, $value: Float!) {
  site {
    setGroundAvailableSqft(sitePid: $sitePid, value: $value) {
      pid
      groundAvailableSqft
    }
  }
}
    `

export const useSetGroundAvailableSqftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetGroundAvailableSqftMutation,
    TError,
    Types.SetGroundAvailableSqftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetGroundAvailableSqftMutation,
    TError,
    Types.SetGroundAvailableSqftMutationVariables,
    TContext
  >({
    mutationKey: ["SetGroundAvailableSqft"],
    mutationFn: (variables?: Types.SetGroundAvailableSqftMutationVariables) =>
      adminFetcher<
        Types.SetGroundAvailableSqftMutation,
        Types.SetGroundAvailableSqftMutationVariables
      >(SetGroundAvailableSqftDocument, variables)(),
    ...options,
  })
}

useSetGroundAvailableSqftMutation.fetcher = (
  variables: Types.SetGroundAvailableSqftMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetGroundAvailableSqftMutation,
    Types.SetGroundAvailableSqftMutationVariables
  >(SetGroundAvailableSqftDocument, variables, options)

export const SetUsableCarportFtDocument = `
    mutation SetUsableCarportFt($sitePid: String!, $value: Float!) {
  site {
    setUsableCarportFt(sitePid: $sitePid, value: $value) {
      pid
      usableCarportFt
    }
  }
}
    `

export const useSetUsableCarportFtMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetUsableCarportFtMutation,
    TError,
    Types.SetUsableCarportFtMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetUsableCarportFtMutation,
    TError,
    Types.SetUsableCarportFtMutationVariables,
    TContext
  >({
    mutationKey: ["SetUsableCarportFt"],
    mutationFn: (variables?: Types.SetUsableCarportFtMutationVariables) =>
      adminFetcher<
        Types.SetUsableCarportFtMutation,
        Types.SetUsableCarportFtMutationVariables
      >(SetUsableCarportFtDocument, variables)(),
    ...options,
  })
}

useSetUsableCarportFtMutation.fetcher = (
  variables: Types.SetUsableCarportFtMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetUsableCarportFtMutation,
    Types.SetUsableCarportFtMutationVariables
  >(SetUsableCarportFtDocument, variables, options)

export const SetSlopedRoofDocument = `
    mutation SetSlopedRoof($sitePid: String!, $value: Boolean) {
  site {
    setSlopedRoof(sitePid: $sitePid, value: $value) {
      pid
      slopedRoof
    }
  }
}
    `

export const useSetSlopedRoofMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetSlopedRoofMutation,
    TError,
    Types.SetSlopedRoofMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSlopedRoofMutation,
    TError,
    Types.SetSlopedRoofMutationVariables,
    TContext
  >({
    mutationKey: ["SetSlopedRoof"],
    mutationFn: (variables?: Types.SetSlopedRoofMutationVariables) =>
      adminFetcher<Types.SetSlopedRoofMutation, Types.SetSlopedRoofMutationVariables>(
        SetSlopedRoofDocument,
        variables,
      )(),
    ...options,
  })
}

useSetSlopedRoofMutation.fetcher = (
  variables: Types.SetSlopedRoofMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetSlopedRoofMutation, Types.SetSlopedRoofMutationVariables>(
    SetSlopedRoofDocument,
    variables,
    options,
  )

export const SetYearBuiltDocument = `
    mutation SetYearBuilt($sitePid: String!, $value: Int) {
  site {
    setYearBuilt(sitePid: $sitePid, yearBuilt: $value) {
      pid
    }
  }
}
    `

export const useSetYearBuiltMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetYearBuiltMutation,
    TError,
    Types.SetYearBuiltMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetYearBuiltMutation,
    TError,
    Types.SetYearBuiltMutationVariables,
    TContext
  >({
    mutationKey: ["SetYearBuilt"],
    mutationFn: (variables?: Types.SetYearBuiltMutationVariables) =>
      adminFetcher<Types.SetYearBuiltMutation, Types.SetYearBuiltMutationVariables>(
        SetYearBuiltDocument,
        variables,
      )(),
    ...options,
  })
}

useSetYearBuiltMutation.fetcher = (
  variables: Types.SetYearBuiltMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetYearBuiltMutation, Types.SetYearBuiltMutationVariables>(
    SetYearBuiltDocument,
    variables,
    options,
  )

export const SetNumStoriesDocument = `
    mutation SetNumStories($sitePid: String!, $value: Int) {
  site {
    setNumStories(sitePid: $sitePid, numStories: $value) {
      pid
    }
  }
}
    `

export const useSetNumStoriesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetNumStoriesMutation,
    TError,
    Types.SetNumStoriesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetNumStoriesMutation,
    TError,
    Types.SetNumStoriesMutationVariables,
    TContext
  >({
    mutationKey: ["SetNumStories"],
    mutationFn: (variables?: Types.SetNumStoriesMutationVariables) =>
      adminFetcher<Types.SetNumStoriesMutation, Types.SetNumStoriesMutationVariables>(
        SetNumStoriesDocument,
        variables,
      )(),
    ...options,
  })
}

useSetNumStoriesMutation.fetcher = (
  variables: Types.SetNumStoriesMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetNumStoriesMutation, Types.SetNumStoriesMutationVariables>(
    SetNumStoriesDocument,
    variables,
    options,
  )

export const SetNumParkingSpacesDocument = `
    mutation SetNumParkingSpaces($sitePid: String!, $value: Int) {
  site {
    setNumParkingSpaces(sitePid: $sitePid, numParkingSpaces: $value) {
      pid
    }
  }
}
    `

export const useSetNumParkingSpacesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetNumParkingSpacesMutation,
    TError,
    Types.SetNumParkingSpacesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetNumParkingSpacesMutation,
    TError,
    Types.SetNumParkingSpacesMutationVariables,
    TContext
  >({
    mutationKey: ["SetNumParkingSpaces"],
    mutationFn: (variables?: Types.SetNumParkingSpacesMutationVariables) =>
      adminFetcher<
        Types.SetNumParkingSpacesMutation,
        Types.SetNumParkingSpacesMutationVariables
      >(SetNumParkingSpacesDocument, variables)(),
    ...options,
  })
}

useSetNumParkingSpacesMutation.fetcher = (
  variables: Types.SetNumParkingSpacesMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetNumParkingSpacesMutation,
    Types.SetNumParkingSpacesMutationVariables
  >(SetNumParkingSpacesDocument, variables, options)

export const SetExistingSolarDocument = `
    mutation SetExistingSolar($sitePid: String!, $value: Boolean, $source: PhysicalCheckSource!) {
  site {
    setExistingSolar(sitePid: $sitePid, value: $value, source: $source) {
      pid
      physicalCheck {
        existingSolar
      }
    }
  }
}
    `

export const useSetExistingSolarMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetExistingSolarMutation,
    TError,
    Types.SetExistingSolarMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetExistingSolarMutation,
    TError,
    Types.SetExistingSolarMutationVariables,
    TContext
  >({
    mutationKey: ["SetExistingSolar"],
    mutationFn: (variables?: Types.SetExistingSolarMutationVariables) =>
      adminFetcher<
        Types.SetExistingSolarMutation,
        Types.SetExistingSolarMutationVariables
      >(SetExistingSolarDocument, variables)(),
    ...options,
  })
}

useSetExistingSolarMutation.fetcher = (
  variables: Types.SetExistingSolarMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetExistingSolarMutation, Types.SetExistingSolarMutationVariables>(
    SetExistingSolarDocument,
    variables,
    options,
  )

export const SetUnobstructedFlatRoofDocument = `
    mutation SetUnobstructedFlatRoof($sitePid: String!, $value: Boolean, $source: PhysicalCheckSource!) {
  site {
    setUnobstructedFlatRoof(sitePid: $sitePid, value: $value, source: $source) {
      pid
      physicalCheck {
        unobstructedFlatRoof
      }
    }
  }
}
    `

export const useSetUnobstructedFlatRoofMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetUnobstructedFlatRoofMutation,
    TError,
    Types.SetUnobstructedFlatRoofMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetUnobstructedFlatRoofMutation,
    TError,
    Types.SetUnobstructedFlatRoofMutationVariables,
    TContext
  >({
    mutationKey: ["SetUnobstructedFlatRoof"],
    mutationFn: (variables?: Types.SetUnobstructedFlatRoofMutationVariables) =>
      adminFetcher<
        Types.SetUnobstructedFlatRoofMutation,
        Types.SetUnobstructedFlatRoofMutationVariables
      >(SetUnobstructedFlatRoofDocument, variables)(),
    ...options,
  })
}

useSetUnobstructedFlatRoofMutation.fetcher = (
  variables: Types.SetUnobstructedFlatRoofMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetUnobstructedFlatRoofMutation,
    Types.SetUnobstructedFlatRoofMutationVariables
  >(SetUnobstructedFlatRoofDocument, variables, options)

export const SetBadRoofDocument = `
    mutation SetBadRoof($sitePid: String!, $value: Boolean, $source: PhysicalCheckSource!) {
  site {
    setBadRoof(sitePid: $sitePid, value: $value, source: $source) {
      pid
      physicalCheck {
        badRoof
      }
    }
  }
}
    `

export const useSetBadRoofMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBadRoofMutation,
    TError,
    Types.SetBadRoofMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBadRoofMutation,
    TError,
    Types.SetBadRoofMutationVariables,
    TContext
  >({
    mutationKey: ["SetBadRoof"],
    mutationFn: (variables?: Types.SetBadRoofMutationVariables) =>
      adminFetcher<Types.SetBadRoofMutation, Types.SetBadRoofMutationVariables>(
        SetBadRoofDocument,
        variables,
      )(),
    ...options,
  })
}

useSetBadRoofMutation.fetcher = (
  variables: Types.SetBadRoofMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetBadRoofMutation, Types.SetBadRoofMutationVariables>(
    SetBadRoofDocument,
    variables,
    options,
  )

export const SetCustomerIdDocument = `
    mutation SetCustomerId($sitePid: String!, $customerId: String) {
  site {
    setCustomerId(sitePid: $sitePid, customerId: $customerId)
  }
}
    `

export const useSetCustomerIdMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetCustomerIdMutation,
    TError,
    Types.SetCustomerIdMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCustomerIdMutation,
    TError,
    Types.SetCustomerIdMutationVariables,
    TContext
  >({
    mutationKey: ["SetCustomerId"],
    mutationFn: (variables?: Types.SetCustomerIdMutationVariables) =>
      adminFetcher<Types.SetCustomerIdMutation, Types.SetCustomerIdMutationVariables>(
        SetCustomerIdDocument,
        variables,
      )(),
    ...options,
  })
}

useSetCustomerIdMutation.fetcher = (
  variables: Types.SetCustomerIdMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetCustomerIdMutation, Types.SetCustomerIdMutationVariables>(
    SetCustomerIdDocument,
    variables,
    options,
  )

export const SetAssetManagerDocument = `
    mutation SetAssetManager($sitePid: String!, $assetManager: String) {
  site {
    setAssetManager(sitePid: $sitePid, assetManager: $assetManager)
  }
}
    `

export const useSetAssetManagerMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetAssetManagerMutation,
    TError,
    Types.SetAssetManagerMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetAssetManagerMutation,
    TError,
    Types.SetAssetManagerMutationVariables,
    TContext
  >({
    mutationKey: ["SetAssetManager"],
    mutationFn: (variables?: Types.SetAssetManagerMutationVariables) =>
      adminFetcher<
        Types.SetAssetManagerMutation,
        Types.SetAssetManagerMutationVariables
      >(SetAssetManagerDocument, variables)(),
    ...options,
  })
}

useSetAssetManagerMutation.fetcher = (
  variables: Types.SetAssetManagerMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetAssetManagerMutation, Types.SetAssetManagerMutationVariables>(
    SetAssetManagerDocument,
    variables,
    options,
  )

export const TrashAttachmentDocument = `
    mutation TrashAttachment($pid: String!, $attachmentId: Int!) {
  site {
    trashAttachment(pid: $pid, attachmentId: $attachmentId) {
      pid
      roofImage {
        id
        name
      }
      attachments {
        id
        trashedAt
        isTrashable
        isRoofImageCandidate
      }
    }
  }
}
    `

export const useTrashAttachmentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TrashAttachmentMutation,
    TError,
    Types.TrashAttachmentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TrashAttachmentMutation,
    TError,
    Types.TrashAttachmentMutationVariables,
    TContext
  >({
    mutationKey: ["TrashAttachment"],
    mutationFn: (variables?: Types.TrashAttachmentMutationVariables) =>
      adminFetcher<
        Types.TrashAttachmentMutation,
        Types.TrashAttachmentMutationVariables
      >(TrashAttachmentDocument, variables)(),
    ...options,
  })
}

useTrashAttachmentMutation.fetcher = (
  variables: Types.TrashAttachmentMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.TrashAttachmentMutation, Types.TrashAttachmentMutationVariables>(
    TrashAttachmentDocument,
    variables,
    options,
  )

export const SetSiteHiddenFromCustomersDocument = `
    mutation SetSiteHiddenFromCustomers($sitePid: String!, $hiddenFromCustomers: Boolean!) {
  site {
    setSiteHiddenFromCustomers(
      sitePid: $sitePid
      hiddenFromCustomers: $hiddenFromCustomers
    ) {
      pid
      hiddenFromCustomers
    }
  }
}
    `

export const useSetSiteHiddenFromCustomersMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetSiteHiddenFromCustomersMutation,
    TError,
    Types.SetSiteHiddenFromCustomersMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSiteHiddenFromCustomersMutation,
    TError,
    Types.SetSiteHiddenFromCustomersMutationVariables,
    TContext
  >({
    mutationKey: ["SetSiteHiddenFromCustomers"],
    mutationFn: (variables?: Types.SetSiteHiddenFromCustomersMutationVariables) =>
      adminFetcher<
        Types.SetSiteHiddenFromCustomersMutation,
        Types.SetSiteHiddenFromCustomersMutationVariables
      >(SetSiteHiddenFromCustomersDocument, variables)(),
    ...options,
  })
}

useSetSiteHiddenFromCustomersMutation.fetcher = (
  variables: Types.SetSiteHiddenFromCustomersMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetSiteHiddenFromCustomersMutation,
    Types.SetSiteHiddenFromCustomersMutationVariables
  >(SetSiteHiddenFromCustomersDocument, variables, options)

export const AttachFileDocument = `
    mutation AttachFile($pid: String!, $file: Upload!, $tag: String) {
  site {
    attachFile(pid: $pid, upload: $file, tag: $tag) {
      __typename
      ... on Site {
        pid
        attachments {
          ...AttachmentFragment
        }
      }
      ... on DuplicatesAttachment {
        attachment {
          name
        }
      }
      ... on Error {
        message
      }
    }
  }
}
    ${AttachmentFragmentFragmentDoc}
${UserRefFragmentDoc}`

export const useAttachFileMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AttachFileMutation,
    TError,
    Types.AttachFileMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AttachFileMutation,
    TError,
    Types.AttachFileMutationVariables,
    TContext
  >({
    mutationKey: ["AttachFile"],
    mutationFn: (variables?: Types.AttachFileMutationVariables) =>
      adminFetcher<Types.AttachFileMutation, Types.AttachFileMutationVariables>(
        AttachFileDocument,
        variables,
      )(),
    ...options,
  })
}

useAttachFileMutation.fetcher = (
  variables: Types.AttachFileMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AttachFileMutation, Types.AttachFileMutationVariables>(
    AttachFileDocument,
    variables,
    options,
  )

export const CreateUserDocument = `
    mutation CreateUser($email: String!, $name: String, $orgPid: String!, $portfolioPids: [String!]!, $sendInvite: Boolean!) {
  user {
    new(
      email: $email
      name: $name
      orgPid: $orgPid
      portfolioPids: $portfolioPids
      sendInvite: $sendInvite
    ) {
      __typename
      ... on AdminUser {
        id
        email
        name
      }
      ... on Error {
        message
      }
    }
  }
}
    `

export const useCreateUserMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateUserMutation,
    TError,
    Types.CreateUserMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateUserMutation,
    TError,
    Types.CreateUserMutationVariables,
    TContext
  >({
    mutationKey: ["CreateUser"],
    mutationFn: (variables?: Types.CreateUserMutationVariables) =>
      adminFetcher<Types.CreateUserMutation, Types.CreateUserMutationVariables>(
        CreateUserDocument,
        variables,
      )(),
    ...options,
  })
}

useCreateUserMutation.fetcher = (
  variables: Types.CreateUserMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.CreateUserMutation, Types.CreateUserMutationVariables>(
    CreateUserDocument,
    variables,
    options,
  )

export const ChangeUserNameDocument = `
    mutation ChangeUserName($id: Int!, $name: String!) {
  user {
    setName(id: $id, name: $name) {
      id
      name
    }
  }
}
    `

export const useChangeUserNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ChangeUserNameMutation,
    TError,
    Types.ChangeUserNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ChangeUserNameMutation,
    TError,
    Types.ChangeUserNameMutationVariables,
    TContext
  >({
    mutationKey: ["ChangeUserName"],
    mutationFn: (variables?: Types.ChangeUserNameMutationVariables) =>
      adminFetcher<Types.ChangeUserNameMutation, Types.ChangeUserNameMutationVariables>(
        ChangeUserNameDocument,
        variables,
      )(),
    ...options,
  })
}

useChangeUserNameMutation.fetcher = (
  variables: Types.ChangeUserNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.ChangeUserNameMutation, Types.ChangeUserNameMutationVariables>(
    ChangeUserNameDocument,
    variables,
    options,
  )

export const SetUserMeetingUrlDocument = `
    mutation SetUserMeetingUrl($id: Int!, $url: String!) {
  user {
    setMeetingSchedulingUrl(userId: $id, url: $url) {
      __typename
      ... on Error {
        message
      }
      ... on AdminUser {
        id
        name
        email
        meetingSchedulingUrl
      }
    }
  }
}
    `

export const useSetUserMeetingUrlMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetUserMeetingUrlMutation,
    TError,
    Types.SetUserMeetingUrlMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetUserMeetingUrlMutation,
    TError,
    Types.SetUserMeetingUrlMutationVariables,
    TContext
  >({
    mutationKey: ["SetUserMeetingUrl"],
    mutationFn: (variables?: Types.SetUserMeetingUrlMutationVariables) =>
      adminFetcher<
        Types.SetUserMeetingUrlMutation,
        Types.SetUserMeetingUrlMutationVariables
      >(SetUserMeetingUrlDocument, variables)(),
    ...options,
  })
}

useSetUserMeetingUrlMutation.fetcher = (
  variables: Types.SetUserMeetingUrlMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetUserMeetingUrlMutation,
    Types.SetUserMeetingUrlMutationVariables
  >(SetUserMeetingUrlDocument, variables, options)

export const InviteToPortfolioDocument = `
    mutation InviteToPortfolio($email: String!, $orgPid: String!, $portfolioPid: String!) {
  user {
    inviteToPortfolio(email: $email, orgPid: $orgPid, portfolioPid: $portfolioPid)
  }
}
    `

export const useInviteToPortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.InviteToPortfolioMutation,
    TError,
    Types.InviteToPortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.InviteToPortfolioMutation,
    TError,
    Types.InviteToPortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["InviteToPortfolio"],
    mutationFn: (variables?: Types.InviteToPortfolioMutationVariables) =>
      adminFetcher<
        Types.InviteToPortfolioMutation,
        Types.InviteToPortfolioMutationVariables
      >(InviteToPortfolioDocument, variables)(),
    ...options,
  })
}

useInviteToPortfolioMutation.fetcher = (
  variables: Types.InviteToPortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.InviteToPortfolioMutation,
    Types.InviteToPortfolioMutationVariables
  >(InviteToPortfolioDocument, variables, options)

export const GiveMeAMagicLinkDocument = `
    mutation GiveMeAMagicLink($email: String!) {
  user {
    giveMeAMagicLink(email: $email) {
      href
      expiresAt
    }
  }
}
    `

export const useGiveMeAMagicLinkMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.GiveMeAMagicLinkMutation,
    TError,
    Types.GiveMeAMagicLinkMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.GiveMeAMagicLinkMutation,
    TError,
    Types.GiveMeAMagicLinkMutationVariables,
    TContext
  >({
    mutationKey: ["GiveMeAMagicLink"],
    mutationFn: (variables?: Types.GiveMeAMagicLinkMutationVariables) =>
      adminFetcher<
        Types.GiveMeAMagicLinkMutation,
        Types.GiveMeAMagicLinkMutationVariables
      >(GiveMeAMagicLinkDocument, variables)(),
    ...options,
  })
}

useGiveMeAMagicLinkMutation.fetcher = (
  variables: Types.GiveMeAMagicLinkMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.GiveMeAMagicLinkMutation, Types.GiveMeAMagicLinkMutationVariables>(
    GiveMeAMagicLinkDocument,
    variables,
    options,
  )

export const AddUserToPortfolioDocument = `
    mutation AddUserToPortfolio($userId: Int!, $portfolioPid: String!, $sendEmail: Boolean!) {
  user {
    addToPortfolio(
      userId: $userId
      portfolioPid: $portfolioPid
      sendEmail: $sendEmail
    ) {
      id
      pid
    }
  }
}
    `

export const useAddUserToPortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddUserToPortfolioMutation,
    TError,
    Types.AddUserToPortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddUserToPortfolioMutation,
    TError,
    Types.AddUserToPortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["AddUserToPortfolio"],
    mutationFn: (variables?: Types.AddUserToPortfolioMutationVariables) =>
      adminFetcher<
        Types.AddUserToPortfolioMutation,
        Types.AddUserToPortfolioMutationVariables
      >(AddUserToPortfolioDocument, variables)(),
    ...options,
  })
}

useAddUserToPortfolioMutation.fetcher = (
  variables: Types.AddUserToPortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.AddUserToPortfolioMutation,
    Types.AddUserToPortfolioMutationVariables
  >(AddUserToPortfolioDocument, variables, options)

export const RemoveFromPortfolioDocument = `
    mutation RemoveFromPortfolio($userId: Int!, $portfolioPid: String!) {
  user {
    removeFromPortfolio(userId: $userId, portfolioPid: $portfolioPid) {
      id
      pid
    }
  }
}
    `

export const useRemoveFromPortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RemoveFromPortfolioMutation,
    TError,
    Types.RemoveFromPortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveFromPortfolioMutation,
    TError,
    Types.RemoveFromPortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveFromPortfolio"],
    mutationFn: (variables?: Types.RemoveFromPortfolioMutationVariables) =>
      adminFetcher<
        Types.RemoveFromPortfolioMutation,
        Types.RemoveFromPortfolioMutationVariables
      >(RemoveFromPortfolioDocument, variables)(),
    ...options,
  })
}

useRemoveFromPortfolioMutation.fetcher = (
  variables: Types.RemoveFromPortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.RemoveFromPortfolioMutation,
    Types.RemoveFromPortfolioMutationVariables
  >(RemoveFromPortfolioDocument, variables, options)

export const SetRoofMaxPvKwDocument = `
    mutation SetRoofMaxPvKw($sitePid: String!, $value: Float) {
  site {
    setRoofMaxPvKw(sitePid: $sitePid, value: $value) {
      pid
      roofMaxPvKw
    }
  }
}
    `

export const useSetRoofMaxPvKwMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetRoofMaxPvKwMutation,
    TError,
    Types.SetRoofMaxPvKwMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetRoofMaxPvKwMutation,
    TError,
    Types.SetRoofMaxPvKwMutationVariables,
    TContext
  >({
    mutationKey: ["SetRoofMaxPvKw"],
    mutationFn: (variables?: Types.SetRoofMaxPvKwMutationVariables) =>
      adminFetcher<Types.SetRoofMaxPvKwMutation, Types.SetRoofMaxPvKwMutationVariables>(
        SetRoofMaxPvKwDocument,
        variables,
      )(),
    ...options,
  })
}

useSetRoofMaxPvKwMutation.fetcher = (
  variables: Types.SetRoofMaxPvKwMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetRoofMaxPvKwMutation, Types.SetRoofMaxPvKwMutationVariables>(
    SetRoofMaxPvKwDocument,
    variables,
    options,
  )

export const SetCarportMaxPvKwDocument = `
    mutation SetCarportMaxPvKw($sitePid: String!, $value: Float) {
  site {
    setCarportMaxPvKw(sitePid: $sitePid, value: $value) {
      pid
      carportMaxPvKw
    }
  }
}
    `

export const useSetCarportMaxPvKwMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetCarportMaxPvKwMutation,
    TError,
    Types.SetCarportMaxPvKwMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCarportMaxPvKwMutation,
    TError,
    Types.SetCarportMaxPvKwMutationVariables,
    TContext
  >({
    mutationKey: ["SetCarportMaxPvKw"],
    mutationFn: (variables?: Types.SetCarportMaxPvKwMutationVariables) =>
      adminFetcher<
        Types.SetCarportMaxPvKwMutation,
        Types.SetCarportMaxPvKwMutationVariables
      >(SetCarportMaxPvKwDocument, variables)(),
    ...options,
  })
}

useSetCarportMaxPvKwMutation.fetcher = (
  variables: Types.SetCarportMaxPvKwMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetCarportMaxPvKwMutation,
    Types.SetCarportMaxPvKwMutationVariables
  >(SetCarportMaxPvKwDocument, variables, options)

export const SetGroundMaxPvKwDocument = `
    mutation SetGroundMaxPvKw($sitePid: String!, $value: Float) {
  site {
    setGroundMaxPvKw(sitePid: $sitePid, value: $value) {
      pid
      groundMaxPvKw
    }
  }
}
    `

export const useSetGroundMaxPvKwMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetGroundMaxPvKwMutation,
    TError,
    Types.SetGroundMaxPvKwMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetGroundMaxPvKwMutation,
    TError,
    Types.SetGroundMaxPvKwMutationVariables,
    TContext
  >({
    mutationKey: ["SetGroundMaxPvKw"],
    mutationFn: (variables?: Types.SetGroundMaxPvKwMutationVariables) =>
      adminFetcher<
        Types.SetGroundMaxPvKwMutation,
        Types.SetGroundMaxPvKwMutationVariables
      >(SetGroundMaxPvKwDocument, variables)(),
    ...options,
  })
}

useSetGroundMaxPvKwMutation.fetcher = (
  variables: Types.SetGroundMaxPvKwMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetGroundMaxPvKwMutation, Types.SetGroundMaxPvKwMutationVariables>(
    SetGroundMaxPvKwDocument,
    variables,
    options,
  )

export const UpdateTariffDocument = `
    mutation UpdateTariff($id: Int!, $isPreferred: Boolean, $requiresPv: Boolean, $requiresStorage: Boolean) {
  tariff {
    setFields(
      id: $id
      isPreferred: $isPreferred
      requiresPv: $requiresPv
      requiresStorage: $requiresStorage
    ) {
      id
      isPreferred
      requiresPv
      requiresStorage
    }
  }
}
    `

export const useUpdateTariffMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateTariffMutation,
    TError,
    Types.UpdateTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateTariffMutation,
    TError,
    Types.UpdateTariffMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateTariff"],
    mutationFn: (variables?: Types.UpdateTariffMutationVariables) =>
      adminFetcher<Types.UpdateTariffMutation, Types.UpdateTariffMutationVariables>(
        UpdateTariffDocument,
        variables,
      )(),
    ...options,
  })
}

useUpdateTariffMutation.fetcher = (
  variables: Types.UpdateTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.UpdateTariffMutation, Types.UpdateTariffMutationVariables>(
    UpdateTariffDocument,
    variables,
    options,
  )

export const AddOverrideDocument = `
    mutation AddOverride($sitePid: String, $portfolioPid: String, $orgPid: String, $finProductId: Int, $inputName: String!, $inputValue: String!) {
  finmodel {
    addOverride(
      sitePid: $sitePid
      portfolioPid: $portfolioPid
      orgPid: $orgPid
      finproductId: $finProductId
      inputName: $inputName
      inputValue: $inputValue
    ) {
      __typename
      ... on Error {
        message
      }
      ... on FinmodelInputOverride {
        inputName
        inputValue
      }
    }
  }
}
    `

export const useAddOverrideMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddOverrideMutation,
    TError,
    Types.AddOverrideMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddOverrideMutation,
    TError,
    Types.AddOverrideMutationVariables,
    TContext
  >({
    mutationKey: ["AddOverride"],
    mutationFn: (variables?: Types.AddOverrideMutationVariables) =>
      adminFetcher<Types.AddOverrideMutation, Types.AddOverrideMutationVariables>(
        AddOverrideDocument,
        variables,
      )(),
    ...options,
  })
}

useAddOverrideMutation.fetcher = (
  variables: Types.AddOverrideMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AddOverrideMutation, Types.AddOverrideMutationVariables>(
    AddOverrideDocument,
    variables,
    options,
  )

export const DeleteOverrideDocument = `
    mutation DeleteOverride($sitePid: String, $portfolioPid: String, $orgPid: String, $finProductId: Int, $inputName: String!) {
  finmodel {
    deleteOverride(
      sitePid: $sitePid
      portfolioPid: $portfolioPid
      orgPid: $orgPid
      finproductId: $finProductId
      inputName: $inputName
    ) {
      inputName
      inputValue
    }
  }
}
    `

export const useDeleteOverrideMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteOverrideMutation,
    TError,
    Types.DeleteOverrideMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteOverrideMutation,
    TError,
    Types.DeleteOverrideMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteOverride"],
    mutationFn: (variables?: Types.DeleteOverrideMutationVariables) =>
      adminFetcher<Types.DeleteOverrideMutation, Types.DeleteOverrideMutationVariables>(
        DeleteOverrideDocument,
        variables,
      )(),
    ...options,
  })
}

useDeleteOverrideMutation.fetcher = (
  variables: Types.DeleteOverrideMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.DeleteOverrideMutation, Types.DeleteOverrideMutationVariables>(
    DeleteOverrideDocument,
    variables,
    options,
  )

export const SetPortfolioMinKwDocument = `
    mutation SetPortfolioMinKw($portfolioPid: String!, $minKw: Float) {
  portfolio {
    setPortfolioMinKw(portfolioPid: $portfolioPid, minKw: $minKw) {
      pid
      minKw
      minKwSetBy
      minKwSetDate
    }
  }
}
    `

export const useSetPortfolioMinKwMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetPortfolioMinKwMutation,
    TError,
    Types.SetPortfolioMinKwMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetPortfolioMinKwMutation,
    TError,
    Types.SetPortfolioMinKwMutationVariables,
    TContext
  >({
    mutationKey: ["SetPortfolioMinKw"],
    mutationFn: (variables?: Types.SetPortfolioMinKwMutationVariables) =>
      adminFetcher<
        Types.SetPortfolioMinKwMutation,
        Types.SetPortfolioMinKwMutationVariables
      >(SetPortfolioMinKwDocument, variables)(),
    ...options,
  })
}

useSetPortfolioMinKwMutation.fetcher = (
  variables: Types.SetPortfolioMinKwMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetPortfolioMinKwMutation,
    Types.SetPortfolioMinKwMutationVariables
  >(SetPortfolioMinKwDocument, variables, options)

export const SetEtbProjectNameDocument = `
    mutation SetEtbProjectName($etbId: Int!, $name: String!) {
  etb(etbId: $etbId) {
    setProjectName(name: $name) {
      id
      projectName
    }
  }
}
    `

export const useSetEtbProjectNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetEtbProjectNameMutation,
    TError,
    Types.SetEtbProjectNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEtbProjectNameMutation,
    TError,
    Types.SetEtbProjectNameMutationVariables,
    TContext
  >({
    mutationKey: ["SetEtbProjectName"],
    mutationFn: (variables?: Types.SetEtbProjectNameMutationVariables) =>
      adminFetcher<
        Types.SetEtbProjectNameMutation,
        Types.SetEtbProjectNameMutationVariables
      >(SetEtbProjectNameDocument, variables)(),
    ...options,
  })
}

useSetEtbProjectNameMutation.fetcher = (
  variables: Types.SetEtbProjectNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetEtbProjectNameMutation,
    Types.SetEtbProjectNameMutationVariables
  >(SetEtbProjectNameDocument, variables, options)

export const SetEtbMeterTariffBeforeDocument = `
    mutation SetEtbMeterTariffBefore($etbMeterId: Int!, $tariffId: Int) {
  etbMeter {
    setTariffBefore(id: $etbMeterId, tariffBeforeId: $tariffId) {
      id
      genabilityTariffBefore: tariffBefore {
        id
        name
        code
      }
    }
  }
}
    `

export const useSetEtbMeterTariffBeforeMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetEtbMeterTariffBeforeMutation,
    TError,
    Types.SetEtbMeterTariffBeforeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEtbMeterTariffBeforeMutation,
    TError,
    Types.SetEtbMeterTariffBeforeMutationVariables,
    TContext
  >({
    mutationKey: ["SetEtbMeterTariffBefore"],
    mutationFn: (variables?: Types.SetEtbMeterTariffBeforeMutationVariables) =>
      adminFetcher<
        Types.SetEtbMeterTariffBeforeMutation,
        Types.SetEtbMeterTariffBeforeMutationVariables
      >(SetEtbMeterTariffBeforeDocument, variables)(),
    ...options,
  })
}

useSetEtbMeterTariffBeforeMutation.fetcher = (
  variables: Types.SetEtbMeterTariffBeforeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetEtbMeterTariffBeforeMutation,
    Types.SetEtbMeterTariffBeforeMutationVariables
  >(SetEtbMeterTariffBeforeDocument, variables, options)

export const SetEtbMeterTariffAfterDocument = `
    mutation SetEtbMeterTariffAfter($etbMeterId: Int!, $tariffId: Int) {
  etbMeter {
    setTariffAfter(id: $etbMeterId, tariffAfterId: $tariffId) {
      id
      genabilityTariffAfter: tariffAfter {
        id
        name
        code
      }
    }
  }
}
    `

export const useSetEtbMeterTariffAfterMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetEtbMeterTariffAfterMutation,
    TError,
    Types.SetEtbMeterTariffAfterMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEtbMeterTariffAfterMutation,
    TError,
    Types.SetEtbMeterTariffAfterMutationVariables,
    TContext
  >({
    mutationKey: ["SetEtbMeterTariffAfter"],
    mutationFn: (variables?: Types.SetEtbMeterTariffAfterMutationVariables) =>
      adminFetcher<
        Types.SetEtbMeterTariffAfterMutation,
        Types.SetEtbMeterTariffAfterMutationVariables
      >(SetEtbMeterTariffAfterDocument, variables)(),
    ...options,
  })
}

useSetEtbMeterTariffAfterMutation.fetcher = (
  variables: Types.SetEtbMeterTariffAfterMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetEtbMeterTariffAfterMutation,
    Types.SetEtbMeterTariffAfterMutationVariables
  >(SetEtbMeterTariffAfterDocument, variables, options)

export const SetEtbMeterBauMeterDocument = `
    mutation SetEtbMeterBauMeter($etbMeterId: Int!, $bauMeterId: Int!) {
  etbMeter {
    setBauMeter(id: $etbMeterId, bauMeterId: $bauMeterId) {
      id
    }
  }
}
    `

export const useSetEtbMeterBauMeterMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetEtbMeterBauMeterMutation,
    TError,
    Types.SetEtbMeterBauMeterMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEtbMeterBauMeterMutation,
    TError,
    Types.SetEtbMeterBauMeterMutationVariables,
    TContext
  >({
    mutationKey: ["SetEtbMeterBauMeter"],
    mutationFn: (variables?: Types.SetEtbMeterBauMeterMutationVariables) =>
      adminFetcher<
        Types.SetEtbMeterBauMeterMutation,
        Types.SetEtbMeterBauMeterMutationVariables
      >(SetEtbMeterBauMeterDocument, variables)(),
    ...options,
  })
}

useSetEtbMeterBauMeterMutation.fetcher = (
  variables: Types.SetEtbMeterBauMeterMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetEtbMeterBauMeterMutation,
    Types.SetEtbMeterBauMeterMutationVariables
  >(SetEtbMeterBauMeterDocument, variables, options)

export const SetETbMeterPvTypeDocument = `
    mutation SetETbMeterPvType($etbMeterId: Int!, $pvType: PVType) {
  etbMeter {
    setPvType(id: $etbMeterId, pvType: $pvType) {
      id
    }
  }
}
    `

export const useSetETbMeterPvTypeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetETbMeterPvTypeMutation,
    TError,
    Types.SetETbMeterPvTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetETbMeterPvTypeMutation,
    TError,
    Types.SetETbMeterPvTypeMutationVariables,
    TContext
  >({
    mutationKey: ["SetETbMeterPvType"],
    mutationFn: (variables?: Types.SetETbMeterPvTypeMutationVariables) =>
      adminFetcher<
        Types.SetETbMeterPvTypeMutation,
        Types.SetETbMeterPvTypeMutationVariables
      >(SetETbMeterPvTypeDocument, variables)(),
    ...options,
  })
}

useSetETbMeterPvTypeMutation.fetcher = (
  variables: Types.SetETbMeterPvTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetETbMeterPvTypeMutation,
    Types.SetETbMeterPvTypeMutationVariables
  >(SetETbMeterPvTypeDocument, variables, options)

export const SetSiteStatusDocument = `
    mutation SetSiteStatus($sitePid: String!, $status: SiteStatus!, $reason: StatusReason, $customReason: String, $unixtimeSec: Int) {
  site {
    setStatus(
      status: $status
      sitePid: $sitePid
      reason: $reason
      customReason: $customReason
      unixtimeSec: $unixtimeSec
    ) {
      pid
      currentStatus {
        pid
      }
    }
  }
}
    `

export const useSetSiteStatusMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetSiteStatusMutation,
    TError,
    Types.SetSiteStatusMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSiteStatusMutation,
    TError,
    Types.SetSiteStatusMutationVariables,
    TContext
  >({
    mutationKey: ["SetSiteStatus"],
    mutationFn: (variables?: Types.SetSiteStatusMutationVariables) =>
      adminFetcher<Types.SetSiteStatusMutation, Types.SetSiteStatusMutationVariables>(
        SetSiteStatusDocument,
        variables,
      )(),
    ...options,
  })
}

useSetSiteStatusMutation.fetcher = (
  variables: Types.SetSiteStatusMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetSiteStatusMutation, Types.SetSiteStatusMutationVariables>(
    SetSiteStatusDocument,
    variables,
    options,
  )

export const SetPrimaryScenarioDocument = `
    mutation SetPrimaryScenario($scenarioPid: String!) {
  scenario(scenarioPid: $scenarioPid) {
    setPrimary {
      pid
      scenarios {
        pid
        isPrimary
      }
    }
  }
}
    `

export const useSetPrimaryScenarioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetPrimaryScenarioMutation,
    TError,
    Types.SetPrimaryScenarioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetPrimaryScenarioMutation,
    TError,
    Types.SetPrimaryScenarioMutationVariables,
    TContext
  >({
    mutationKey: ["SetPrimaryScenario"],
    mutationFn: (variables?: Types.SetPrimaryScenarioMutationVariables) =>
      adminFetcher<
        Types.SetPrimaryScenarioMutation,
        Types.SetPrimaryScenarioMutationVariables
      >(SetPrimaryScenarioDocument, variables)(),
    ...options,
  })
}

useSetPrimaryScenarioMutation.fetcher = (
  variables: Types.SetPrimaryScenarioMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetPrimaryScenarioMutation,
    Types.SetPrimaryScenarioMutationVariables
  >(SetPrimaryScenarioDocument, variables, options)

export const SetScenarioNameDocument = `
    mutation SetScenarioName($scenarioPid: String!, $name: String!) {
  scenario(scenarioPid: $scenarioPid) {
    setName(name: $name) {
      pid
      scenarios {
        pid
        name
      }
    }
  }
}
    `

export const useSetScenarioNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetScenarioNameMutation,
    TError,
    Types.SetScenarioNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetScenarioNameMutation,
    TError,
    Types.SetScenarioNameMutationVariables,
    TContext
  >({
    mutationKey: ["SetScenarioName"],
    mutationFn: (variables?: Types.SetScenarioNameMutationVariables) =>
      adminFetcher<
        Types.SetScenarioNameMutation,
        Types.SetScenarioNameMutationVariables
      >(SetScenarioNameDocument, variables)(),
    ...options,
  })
}

useSetScenarioNameMutation.fetcher = (
  variables: Types.SetScenarioNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetScenarioNameMutation, Types.SetScenarioNameMutationVariables>(
    SetScenarioNameDocument,
    variables,
    options,
  )

export const SetScenarioEtbDocument = `
    mutation SetScenarioETB($scenarioPid: String!, $etbId: Int) {
  scenario(scenarioPid: $scenarioPid) {
    setEtb(etbId: $etbId) {
      pid
      activeEtbReport {
        id
        projectName
      }
    }
  }
}
    `

export const useSetScenarioEtbMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetScenarioEtbMutation,
    TError,
    Types.SetScenarioEtbMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetScenarioEtbMutation,
    TError,
    Types.SetScenarioEtbMutationVariables,
    TContext
  >({
    mutationKey: ["SetScenarioETB"],
    mutationFn: (variables?: Types.SetScenarioEtbMutationVariables) =>
      adminFetcher<Types.SetScenarioEtbMutation, Types.SetScenarioEtbMutationVariables>(
        SetScenarioEtbDocument,
        variables,
      )(),
    ...options,
  })
}

useSetScenarioEtbMutation.fetcher = (
  variables: Types.SetScenarioEtbMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetScenarioEtbMutation, Types.SetScenarioEtbMutationVariables>(
    SetScenarioEtbDocument,
    variables,
    options,
  )

export const AddScenarioDocument = `
    mutation AddScenario($sitePid: String!, $solarLocation: SolarLocation!, $includeStorage: Boolean!, $loadCategory: LoadCategory!, $name: String) {
  site {
    addScenario(
      sitePid: $sitePid
      solarLocation: $solarLocation
      includeStorage: $includeStorage
      loadCategory: $loadCategory
      name: $name
    ) {
      __typename
      ... on ScenarioRef {
        pid
      }
      ... on Error {
        message
      }
    }
  }
}
    `

export const useAddScenarioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddScenarioMutation,
    TError,
    Types.AddScenarioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddScenarioMutation,
    TError,
    Types.AddScenarioMutationVariables,
    TContext
  >({
    mutationKey: ["AddScenario"],
    mutationFn: (variables?: Types.AddScenarioMutationVariables) =>
      adminFetcher<Types.AddScenarioMutation, Types.AddScenarioMutationVariables>(
        AddScenarioDocument,
        variables,
      )(),
    ...options,
  })
}

useAddScenarioMutation.fetcher = (
  variables: Types.AddScenarioMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AddScenarioMutation, Types.AddScenarioMutationVariables>(
    AddScenarioDocument,
    variables,
    options,
  )

export const ToggleScenarioVisibilityDocument = `
    mutation ToggleScenarioVisibility($scenarioPid: String!, $hidden: Boolean!) {
  scenario(scenarioPid: $scenarioPid) {
    toggleVisibility(hidden: $hidden) {
      pid
      hiddenFromCustomers
    }
  }
}
    `

export const useToggleScenarioVisibilityMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.ToggleScenarioVisibilityMutation,
    TError,
    Types.ToggleScenarioVisibilityMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ToggleScenarioVisibilityMutation,
    TError,
    Types.ToggleScenarioVisibilityMutationVariables,
    TContext
  >({
    mutationKey: ["ToggleScenarioVisibility"],
    mutationFn: (variables?: Types.ToggleScenarioVisibilityMutationVariables) =>
      adminFetcher<
        Types.ToggleScenarioVisibilityMutation,
        Types.ToggleScenarioVisibilityMutationVariables
      >(ToggleScenarioVisibilityDocument, variables)(),
    ...options,
  })
}

useToggleScenarioVisibilityMutation.fetcher = (
  variables: Types.ToggleScenarioVisibilityMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.ToggleScenarioVisibilityMutation,
    Types.ToggleScenarioVisibilityMutationVariables
  >(ToggleScenarioVisibilityDocument, variables, options)

export const ToggleMulticonnectDocument = `
    mutation ToggleMulticonnect($scenarioPid: String!, $allow: Boolean!) {
  scenario(scenarioPid: $scenarioPid) {
    toggleMulticonnect(allow: $allow) {
      pid
      allowMulticonnect
    }
  }
}
    `

export const useToggleMulticonnectMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ToggleMulticonnectMutation,
    TError,
    Types.ToggleMulticonnectMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ToggleMulticonnectMutation,
    TError,
    Types.ToggleMulticonnectMutationVariables,
    TContext
  >({
    mutationKey: ["ToggleMulticonnect"],
    mutationFn: (variables?: Types.ToggleMulticonnectMutationVariables) =>
      adminFetcher<
        Types.ToggleMulticonnectMutation,
        Types.ToggleMulticonnectMutationVariables
      >(ToggleMulticonnectDocument, variables)(),
    ...options,
  })
}

useToggleMulticonnectMutation.fetcher = (
  variables: Types.ToggleMulticonnectMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.ToggleMulticonnectMutation,
    Types.ToggleMulticonnectMutationVariables
  >(ToggleMulticonnectDocument, variables, options)

export const AddEtbDocument = `
    mutation AddETB($sitePid: String!, $scenarioPid: String!, $loadOrigin: LoadOrigin!, $upload: Upload!, $roofPvKw: Float, $carportPvKw: Float, $makeActive: Boolean!) {
  site {
    addEtb(
      sitePid: $sitePid
      scenarioPid: $scenarioPid
      loadOrigin: $loadOrigin
      upload: $upload
      roofPvKw: $roofPvKw
      carportPvKw: $carportPvKw
      makeActive: $makeActive
    ) {
      pid
    }
  }
}
    `

export const useAddEtbMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddEtbMutation,
    TError,
    Types.AddEtbMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddEtbMutation,
    TError,
    Types.AddEtbMutationVariables,
    TContext
  >({
    mutationKey: ["AddETB"],
    mutationFn: (variables?: Types.AddEtbMutationVariables) =>
      adminFetcher<Types.AddEtbMutation, Types.AddEtbMutationVariables>(
        AddEtbDocument,
        variables,
      )(),
    ...options,
  })
}

useAddEtbMutation.fetcher = (
  variables: Types.AddEtbMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AddEtbMutation, Types.AddEtbMutationVariables>(
    AddEtbDocument,
    variables,
    options,
  )

export const AddSitesToPortfolioByOwnerNameDocument = `
    mutation AddSitesToPortfolioByOwnerName($portfolioPid: String!, $names: [String!]!) {
  portfolio {
    addSitesByOwnerName(portfolioPid: $portfolioPid, names: $names)
  }
}
    `

export const useAddSitesToPortfolioByOwnerNameMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.AddSitesToPortfolioByOwnerNameMutation,
    TError,
    Types.AddSitesToPortfolioByOwnerNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddSitesToPortfolioByOwnerNameMutation,
    TError,
    Types.AddSitesToPortfolioByOwnerNameMutationVariables,
    TContext
  >({
    mutationKey: ["AddSitesToPortfolioByOwnerName"],
    mutationFn: (variables?: Types.AddSitesToPortfolioByOwnerNameMutationVariables) =>
      adminFetcher<
        Types.AddSitesToPortfolioByOwnerNameMutation,
        Types.AddSitesToPortfolioByOwnerNameMutationVariables
      >(AddSitesToPortfolioByOwnerNameDocument, variables)(),
    ...options,
  })
}

useAddSitesToPortfolioByOwnerNameMutation.fetcher = (
  variables: Types.AddSitesToPortfolioByOwnerNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.AddSitesToPortfolioByOwnerNameMutation,
    Types.AddSitesToPortfolioByOwnerNameMutationVariables
  >(AddSitesToPortfolioByOwnerNameDocument, variables, options)

export const SetTargetUtilityDocument = `
    mutation SetTargetUtility($sitePid: String!, $utilityId: Int!) {
  site {
    setTargetUtility(sitePid: $sitePid, utilityId: $utilityId) {
      pid
      targetUtility {
        id
        name
      }
      targetUtilitySetBy {
        ...UserRef
      }
      dateTargetUtilitySet
    }
  }
}
    ${UserRefFragmentDoc}`

export const useSetTargetUtilityMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetTargetUtilityMutation,
    TError,
    Types.SetTargetUtilityMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetTargetUtilityMutation,
    TError,
    Types.SetTargetUtilityMutationVariables,
    TContext
  >({
    mutationKey: ["SetTargetUtility"],
    mutationFn: (variables?: Types.SetTargetUtilityMutationVariables) =>
      adminFetcher<
        Types.SetTargetUtilityMutation,
        Types.SetTargetUtilityMutationVariables
      >(SetTargetUtilityDocument, variables)(),
    ...options,
  })
}

useSetTargetUtilityMutation.fetcher = (
  variables: Types.SetTargetUtilityMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetTargetUtilityMutation, Types.SetTargetUtilityMutationVariables>(
    SetTargetUtilityDocument,
    variables,
    options,
  )

export const SetThirdPartySupplierDocument = `
    mutation SetThirdPartySupplier($sitePid: String!, $thirdPartySupplierId: Int) {
  site {
    setThirdPartySupplier(
      sitePid: $sitePid
      thirdPartySupplierId: $thirdPartySupplierId
    ) {
      pid
      thirdPartySupplier {
        id
        name
      }
    }
  }
}
    `

export const useSetThirdPartySupplierMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetThirdPartySupplierMutation,
    TError,
    Types.SetThirdPartySupplierMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetThirdPartySupplierMutation,
    TError,
    Types.SetThirdPartySupplierMutationVariables,
    TContext
  >({
    mutationKey: ["SetThirdPartySupplier"],
    mutationFn: (variables?: Types.SetThirdPartySupplierMutationVariables) =>
      adminFetcher<
        Types.SetThirdPartySupplierMutation,
        Types.SetThirdPartySupplierMutationVariables
      >(SetThirdPartySupplierDocument, variables)(),
    ...options,
  })
}

useSetThirdPartySupplierMutation.fetcher = (
  variables: Types.SetThirdPartySupplierMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetThirdPartySupplierMutation,
    Types.SetThirdPartySupplierMutationVariables
  >(SetThirdPartySupplierDocument, variables, options)

export const SetMeteringDocument = `
    mutation SetMetering($sitePids: [String!]!, $metering: Metering) {
  site {
    setMetering(sitePids: $sitePids, metering: $metering)
  }
}
    `

export const useSetMeteringMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetMeteringMutation,
    TError,
    Types.SetMeteringMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetMeteringMutation,
    TError,
    Types.SetMeteringMutationVariables,
    TContext
  >({
    mutationKey: ["SetMetering"],
    mutationFn: (variables?: Types.SetMeteringMutationVariables) =>
      adminFetcher<Types.SetMeteringMutation, Types.SetMeteringMutationVariables>(
        SetMeteringDocument,
        variables,
      )(),
    ...options,
  })
}

useSetMeteringMutation.fetcher = (
  variables: Types.SetMeteringMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetMeteringMutation, Types.SetMeteringMutationVariables>(
    SetMeteringDocument,
    variables,
    options,
  )

export const SetLeaseTypeDocument = `
    mutation SetLeaseType($sitePids: [String!]!, $leaseType: LeaseType) {
  site {
    setLeaseType(sitePids: $sitePids, leaseType: $leaseType)
  }
}
    `

export const useSetLeaseTypeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetLeaseTypeMutation,
    TError,
    Types.SetLeaseTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetLeaseTypeMutation,
    TError,
    Types.SetLeaseTypeMutationVariables,
    TContext
  >({
    mutationKey: ["SetLeaseType"],
    mutationFn: (variables?: Types.SetLeaseTypeMutationVariables) =>
      adminFetcher<Types.SetLeaseTypeMutation, Types.SetLeaseTypeMutationVariables>(
        SetLeaseTypeDocument,
        variables,
      )(),
    ...options,
  })
}

useSetLeaseTypeMutation.fetcher = (
  variables: Types.SetLeaseTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetLeaseTypeMutation, Types.SetLeaseTypeMutationVariables>(
    SetLeaseTypeDocument,
    variables,
    options,
  )

export const SetOccupancyTypeDocument = `
    mutation SetOccupancyType($sitePids: [String!]!, $occupancyType: BuildingOccupancyType) {
  site {
    setOccupancyType(sitePids: $sitePids, occupancyType: $occupancyType)
  }
}
    `

export const useSetOccupancyTypeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOccupancyTypeMutation,
    TError,
    Types.SetOccupancyTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOccupancyTypeMutation,
    TError,
    Types.SetOccupancyTypeMutationVariables,
    TContext
  >({
    mutationKey: ["SetOccupancyType"],
    mutationFn: (variables?: Types.SetOccupancyTypeMutationVariables) =>
      adminFetcher<
        Types.SetOccupancyTypeMutation,
        Types.SetOccupancyTypeMutationVariables
      >(SetOccupancyTypeDocument, variables)(),
    ...options,
  })
}

useSetOccupancyTypeMutation.fetcher = (
  variables: Types.SetOccupancyTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetOccupancyTypeMutation, Types.SetOccupancyTypeMutationVariables>(
    SetOccupancyTypeDocument,
    variables,
    options,
  )

export const RemoveUserFromPortfolioDocument = `
    mutation RemoveUserFromPortfolio($portfolioPid: String!, $userId: Int!) {
  portfolio {
    removeUserFromPortfolio(portfolioPid: $portfolioPid, userId: $userId) {
      pid
      users {
        id
        email
      }
    }
  }
}
    `

export const useRemoveUserFromPortfolioMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveUserFromPortfolioMutation,
    TError,
    Types.RemoveUserFromPortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveUserFromPortfolioMutation,
    TError,
    Types.RemoveUserFromPortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveUserFromPortfolio"],
    mutationFn: (variables?: Types.RemoveUserFromPortfolioMutationVariables) =>
      adminFetcher<
        Types.RemoveUserFromPortfolioMutation,
        Types.RemoveUserFromPortfolioMutationVariables
      >(RemoveUserFromPortfolioDocument, variables)(),
    ...options,
  })
}

useRemoveUserFromPortfolioMutation.fetcher = (
  variables: Types.RemoveUserFromPortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.RemoveUserFromPortfolioMutation,
    Types.RemoveUserFromPortfolioMutationVariables
  >(RemoveUserFromPortfolioDocument, variables, options)

export const RenamePortfolioDocument = `
    mutation RenamePortfolio($portfolioPid: String!, $newName: String!) {
  portfolio {
    rename(portfolioPid: $portfolioPid, newName: $newName) {
      pid
      title
    }
  }
}
    `

export const useRenamePortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RenamePortfolioMutation,
    TError,
    Types.RenamePortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RenamePortfolioMutation,
    TError,
    Types.RenamePortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["RenamePortfolio"],
    mutationFn: (variables?: Types.RenamePortfolioMutationVariables) =>
      adminFetcher<
        Types.RenamePortfolioMutation,
        Types.RenamePortfolioMutationVariables
      >(RenamePortfolioDocument, variables)(),
    ...options,
  })
}

useRenamePortfolioMutation.fetcher = (
  variables: Types.RenamePortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.RenamePortfolioMutation, Types.RenamePortfolioMutationVariables>(
    RenamePortfolioDocument,
    variables,
    options,
  )

export const UtilityApiNameDocument = `
    mutation UtilityApiName($utilityId: Int!, $utilityApiName: String) {
  utility {
    setUtilityApiName(id: $utilityId, utilityApiName: $utilityApiName) {
      id
      utilityApiName
    }
  }
}
    `

export const useUtilityApiNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UtilityApiNameMutation,
    TError,
    Types.UtilityApiNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UtilityApiNameMutation,
    TError,
    Types.UtilityApiNameMutationVariables,
    TContext
  >({
    mutationKey: ["UtilityApiName"],
    mutationFn: (variables?: Types.UtilityApiNameMutationVariables) =>
      adminFetcher<Types.UtilityApiNameMutation, Types.UtilityApiNameMutationVariables>(
        UtilityApiNameDocument,
        variables,
      )(),
    ...options,
  })
}

useUtilityApiNameMutation.fetcher = (
  variables: Types.UtilityApiNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.UtilityApiNameMutation, Types.UtilityApiNameMutationVariables>(
    UtilityApiNameDocument,
    variables,
    options,
  )

export const UtilityIsPreferredDocument = `
    mutation UtilityIsPreferred($utilityId: Int!, $isPreferred: Boolean) {
  utility {
    setIsPreferred(id: $utilityId, isPreferred: $isPreferred) {
      id
      isPreferred
    }
  }
}
    `

export const useUtilityIsPreferredMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UtilityIsPreferredMutation,
    TError,
    Types.UtilityIsPreferredMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UtilityIsPreferredMutation,
    TError,
    Types.UtilityIsPreferredMutationVariables,
    TContext
  >({
    mutationKey: ["UtilityIsPreferred"],
    mutationFn: (variables?: Types.UtilityIsPreferredMutationVariables) =>
      adminFetcher<
        Types.UtilityIsPreferredMutation,
        Types.UtilityIsPreferredMutationVariables
      >(UtilityIsPreferredDocument, variables)(),
    ...options,
  })
}

useUtilityIsPreferredMutation.fetcher = (
  variables: Types.UtilityIsPreferredMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.UtilityIsPreferredMutation,
    Types.UtilityIsPreferredMutationVariables
  >(UtilityIsPreferredDocument, variables, options)

export const UtilityNotesDocument = `
    mutation UtilityNotes($utilityId: Int!, $notes: String) {
  utility {
    setNotes(id: $utilityId, notes: $notes) {
      id
      notes
    }
  }
}
    `

export const useUtilityNotesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UtilityNotesMutation,
    TError,
    Types.UtilityNotesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UtilityNotesMutation,
    TError,
    Types.UtilityNotesMutationVariables,
    TContext
  >({
    mutationKey: ["UtilityNotes"],
    mutationFn: (variables?: Types.UtilityNotesMutationVariables) =>
      adminFetcher<Types.UtilityNotesMutation, Types.UtilityNotesMutationVariables>(
        UtilityNotesDocument,
        variables,
      )(),
    ...options,
  })
}

useUtilityNotesMutation.fetcher = (
  variables: Types.UtilityNotesMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.UtilityNotesMutation, Types.UtilityNotesMutationVariables>(
    UtilityNotesDocument,
    variables,
    options,
  )

export const CreatePropertyNoteDocument = `
    mutation CreatePropertyNote($propertyPid: String!, $text: String!, $hiddenFromCustomers: Boolean!) {
  note {
    new(
      sitePid: $propertyPid
      text: $text
      hiddenFromCustomers: $hiddenFromCustomers
    ) {
      pid
    }
  }
}
    `

export const useCreatePropertyNoteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreatePropertyNoteMutation,
    TError,
    Types.CreatePropertyNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreatePropertyNoteMutation,
    TError,
    Types.CreatePropertyNoteMutationVariables,
    TContext
  >({
    mutationKey: ["CreatePropertyNote"],
    mutationFn: (variables?: Types.CreatePropertyNoteMutationVariables) =>
      adminFetcher<
        Types.CreatePropertyNoteMutation,
        Types.CreatePropertyNoteMutationVariables
      >(CreatePropertyNoteDocument, variables)(),
    ...options,
  })
}

useCreatePropertyNoteMutation.fetcher = (
  variables: Types.CreatePropertyNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.CreatePropertyNoteMutation,
    Types.CreatePropertyNoteMutationVariables
  >(CreatePropertyNoteDocument, variables, options)

export const EditPropertyNoteDocument = `
    mutation EditPropertyNote($notePid: String!, $text: String!, $hiddenFromCustomers: Boolean!) {
  note {
    edit(notePid: $notePid, text: $text, hiddenFromCustomers: $hiddenFromCustomers) {
      pid
    }
  }
}
    `

export const useEditPropertyNoteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditPropertyNoteMutation,
    TError,
    Types.EditPropertyNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditPropertyNoteMutation,
    TError,
    Types.EditPropertyNoteMutationVariables,
    TContext
  >({
    mutationKey: ["EditPropertyNote"],
    mutationFn: (variables?: Types.EditPropertyNoteMutationVariables) =>
      adminFetcher<
        Types.EditPropertyNoteMutation,
        Types.EditPropertyNoteMutationVariables
      >(EditPropertyNoteDocument, variables)(),
    ...options,
  })
}

useEditPropertyNoteMutation.fetcher = (
  variables: Types.EditPropertyNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.EditPropertyNoteMutation, Types.EditPropertyNoteMutationVariables>(
    EditPropertyNoteDocument,
    variables,
    options,
  )

export const TrashPropertyNoteDocument = `
    mutation TrashPropertyNote($notePid: String!) {
  note {
    trash(notePid: $notePid) {
      pid
    }
  }
}
    `

export const useTrashPropertyNoteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TrashPropertyNoteMutation,
    TError,
    Types.TrashPropertyNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TrashPropertyNoteMutation,
    TError,
    Types.TrashPropertyNoteMutationVariables,
    TContext
  >({
    mutationKey: ["TrashPropertyNote"],
    mutationFn: (variables?: Types.TrashPropertyNoteMutationVariables) =>
      adminFetcher<
        Types.TrashPropertyNoteMutation,
        Types.TrashPropertyNoteMutationVariables
      >(TrashPropertyNoteDocument, variables)(),
    ...options,
  })
}

useTrashPropertyNoteMutation.fetcher = (
  variables: Types.TrashPropertyNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.TrashPropertyNoteMutation,
    Types.TrashPropertyNoteMutationVariables
  >(TrashPropertyNoteDocument, variables, options)

export const AddHelioscopeDocument = `
    mutation AddHelioscope($sitePid: String!, $pdfId: Int!, $roofImageId: Int!, $productionCsvId: Int!, $roofPvKw: Float!, $carportPvThreeHighKw: Float!, $carportPvSixHighKw: Float!, $groundPvKw: Float!, $roofIsMax: Boolean!, $carportIsMax: Boolean!) {
  site {
    addHelioscope(
      sitePid: $sitePid
      pdfId: $pdfId
      roofImageId: $roofImageId
      productionCsvId: $productionCsvId
      roofPvKw: $roofPvKw
      carportPvThreeHighKw: $carportPvThreeHighKw
      carportPvSixHighKw: $carportPvSixHighKw
      groundPvKw: $groundPvKw
      roofIsMax: $roofIsMax
      carportIsMax: $carportIsMax
    ) {
      __typename
      pid
    }
  }
}
    `

export const useAddHelioscopeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddHelioscopeMutation,
    TError,
    Types.AddHelioscopeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddHelioscopeMutation,
    TError,
    Types.AddHelioscopeMutationVariables,
    TContext
  >({
    mutationKey: ["AddHelioscope"],
    mutationFn: (variables?: Types.AddHelioscopeMutationVariables) =>
      adminFetcher<Types.AddHelioscopeMutation, Types.AddHelioscopeMutationVariables>(
        AddHelioscopeDocument,
        variables,
      )(),
    ...options,
  })
}

useAddHelioscopeMutation.fetcher = (
  variables: Types.AddHelioscopeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AddHelioscopeMutation, Types.AddHelioscopeMutationVariables>(
    AddHelioscopeDocument,
    variables,
    options,
  )

export const UploadHelioscopePdfDocument = `
    mutation UploadHelioscopePdf($sitePid: String!, $reportPdf: Upload!) {
  site {
    uploadHelioscopePdf(sitePid: $sitePid, reportPdf: $reportPdf) {
      reportPdf {
        ...AttachmentFragment
      }
      roofImage {
        ...AttachmentFragment
      }
    }
  }
}
    ${AttachmentFragmentFragmentDoc}
${UserRefFragmentDoc}`

export const useUploadHelioscopePdfMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UploadHelioscopePdfMutation,
    TError,
    Types.UploadHelioscopePdfMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UploadHelioscopePdfMutation,
    TError,
    Types.UploadHelioscopePdfMutationVariables,
    TContext
  >({
    mutationKey: ["UploadHelioscopePdf"],
    mutationFn: (variables?: Types.UploadHelioscopePdfMutationVariables) =>
      adminFetcher<
        Types.UploadHelioscopePdfMutation,
        Types.UploadHelioscopePdfMutationVariables
      >(UploadHelioscopePdfDocument, variables)(),
    ...options,
  })
}

useUploadHelioscopePdfMutation.fetcher = (
  variables: Types.UploadHelioscopePdfMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.UploadHelioscopePdfMutation,
    Types.UploadHelioscopePdfMutationVariables
  >(UploadHelioscopePdfDocument, variables, options)

export const SetScenarioHelioscopeDocument = `
    mutation SetScenarioHelioscope($scenarioPid: String!, $helioscopePid: String, $finproductId: Int, $finproductType: FinProductType) {
  scenario(scenarioPid: $scenarioPid) {
    setHelioscope(
      helioscopePid: $helioscopePid
      finproductId: $finproductId
      finproductType: $finproductType
    ) {
      __typename
      pid
    }
  }
}
    `

export const useSetScenarioHelioscopeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetScenarioHelioscopeMutation,
    TError,
    Types.SetScenarioHelioscopeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetScenarioHelioscopeMutation,
    TError,
    Types.SetScenarioHelioscopeMutationVariables,
    TContext
  >({
    mutationKey: ["SetScenarioHelioscope"],
    mutationFn: (variables?: Types.SetScenarioHelioscopeMutationVariables) =>
      adminFetcher<
        Types.SetScenarioHelioscopeMutation,
        Types.SetScenarioHelioscopeMutationVariables
      >(SetScenarioHelioscopeDocument, variables)(),
    ...options,
  })
}

useSetScenarioHelioscopeMutation.fetcher = (
  variables: Types.SetScenarioHelioscopeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetScenarioHelioscopeMutation,
    Types.SetScenarioHelioscopeMutationVariables
  >(SetScenarioHelioscopeDocument, variables, options)

export const TrashHelioscopeDocument = `
    mutation TrashHelioscope($sitePid: String!, $helioscopePid: String!) {
  site {
    trashHelioscope(sitePid: $sitePid, helioscopePid: $helioscopePid) {
      pid
    }
  }
}
    `

export const useTrashHelioscopeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TrashHelioscopeMutation,
    TError,
    Types.TrashHelioscopeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TrashHelioscopeMutation,
    TError,
    Types.TrashHelioscopeMutationVariables,
    TContext
  >({
    mutationKey: ["TrashHelioscope"],
    mutationFn: (variables?: Types.TrashHelioscopeMutationVariables) =>
      adminFetcher<
        Types.TrashHelioscopeMutation,
        Types.TrashHelioscopeMutationVariables
      >(TrashHelioscopeDocument, variables)(),
    ...options,
  })
}

useTrashHelioscopeMutation.fetcher = (
  variables: Types.TrashHelioscopeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.TrashHelioscopeMutation, Types.TrashHelioscopeMutationVariables>(
    TrashHelioscopeDocument,
    variables,
    options,
  )

export const DeleteParcelFootprintDocument = `
    mutation DeleteParcelFootprint($sitePid: String!) {
  site {
    deleteParcelFootprint(sitePid: $sitePid) {
      pid
      footprint {
        roofShape
        parcelShape
      }
    }
  }
}
    `

export const useDeleteParcelFootprintMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteParcelFootprintMutation,
    TError,
    Types.DeleteParcelFootprintMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteParcelFootprintMutation,
    TError,
    Types.DeleteParcelFootprintMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteParcelFootprint"],
    mutationFn: (variables?: Types.DeleteParcelFootprintMutationVariables) =>
      adminFetcher<
        Types.DeleteParcelFootprintMutation,
        Types.DeleteParcelFootprintMutationVariables
      >(DeleteParcelFootprintDocument, variables)(),
    ...options,
  })
}

useDeleteParcelFootprintMutation.fetcher = (
  variables: Types.DeleteParcelFootprintMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.DeleteParcelFootprintMutation,
    Types.DeleteParcelFootprintMutationVariables
  >(DeleteParcelFootprintDocument, variables, options)

export const DeleteBuildingFootprintDocument = `
    mutation DeleteBuildingFootprint($sitePid: String!) {
  site {
    deleteBuildingFootprint(sitePid: $sitePid) {
      pid
      footprint {
        roofShape
        parcelShape
        obstructionShape
      }
    }
  }
}
    `

export const useDeleteBuildingFootprintMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.DeleteBuildingFootprintMutation,
    TError,
    Types.DeleteBuildingFootprintMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteBuildingFootprintMutation,
    TError,
    Types.DeleteBuildingFootprintMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteBuildingFootprint"],
    mutationFn: (variables?: Types.DeleteBuildingFootprintMutationVariables) =>
      adminFetcher<
        Types.DeleteBuildingFootprintMutation,
        Types.DeleteBuildingFootprintMutationVariables
      >(DeleteBuildingFootprintDocument, variables)(),
    ...options,
  })
}

useDeleteBuildingFootprintMutation.fetcher = (
  variables: Types.DeleteBuildingFootprintMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.DeleteBuildingFootprintMutation,
    Types.DeleteBuildingFootprintMutationVariables
  >(DeleteBuildingFootprintDocument, variables, options)

export const SetOrgNameDocument = `
    mutation setOrgName($pid: String!, $value: String!) {
  orgs {
    setName(pid: $pid, name: $value) {
      pid
      name
    }
  }
}
    `

export const useSetOrgNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgNameMutation,
    TError,
    Types.SetOrgNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgNameMutation,
    TError,
    Types.SetOrgNameMutationVariables,
    TContext
  >({
    mutationKey: ["setOrgName"],
    mutationFn: (variables?: Types.SetOrgNameMutationVariables) =>
      adminFetcher<Types.SetOrgNameMutation, Types.SetOrgNameMutationVariables>(
        SetOrgNameDocument,
        variables,
      )(),
    ...options,
  })
}

useSetOrgNameMutation.fetcher = (
  variables: Types.SetOrgNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetOrgNameMutation, Types.SetOrgNameMutationVariables>(
    SetOrgNameDocument,
    variables,
    options,
  )

export const SetOrgSlugDocument = `
    mutation setOrgSlug($pid: String!, $value: String!) {
  orgs {
    setSlug(pid: $pid, slug: $value) {
      pid
      slug
    }
  }
}
    `

export const useSetOrgSlugMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgSlugMutation,
    TError,
    Types.SetOrgSlugMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgSlugMutation,
    TError,
    Types.SetOrgSlugMutationVariables,
    TContext
  >({
    mutationKey: ["setOrgSlug"],
    mutationFn: (variables?: Types.SetOrgSlugMutationVariables) =>
      adminFetcher<Types.SetOrgSlugMutation, Types.SetOrgSlugMutationVariables>(
        SetOrgSlugDocument,
        variables,
      )(),
    ...options,
  })
}

useSetOrgSlugMutation.fetcher = (
  variables: Types.SetOrgSlugMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetOrgSlugMutation, Types.SetOrgSlugMutationVariables>(
    SetOrgSlugDocument,
    variables,
    options,
  )

export const SetOrgLogoDocument = `
    mutation setOrgLogo($pid: String!, $upload: Upload!) {
  orgs {
    setLogo(pid: $pid, upload: $upload) {
      pid
      slug
      logoUrl
    }
  }
}
    `

export const useSetOrgLogoMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgLogoMutation,
    TError,
    Types.SetOrgLogoMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgLogoMutation,
    TError,
    Types.SetOrgLogoMutationVariables,
    TContext
  >({
    mutationKey: ["setOrgLogo"],
    mutationFn: (variables?: Types.SetOrgLogoMutationVariables) =>
      adminFetcher<Types.SetOrgLogoMutation, Types.SetOrgLogoMutationVariables>(
        SetOrgLogoDocument,
        variables,
      )(),
    ...options,
  })
}

useSetOrgLogoMutation.fetcher = (
  variables: Types.SetOrgLogoMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetOrgLogoMutation, Types.SetOrgLogoMutationVariables>(
    SetOrgLogoDocument,
    variables,
    options,
  )

export const SetOrgLogomarkDocument = `
    mutation setOrgLogomark($pid: String!, $upload: Upload!) {
  orgs {
    setLogomark(pid: $pid, upload: $upload) {
      pid
      slug
      logoUrl
    }
  }
}
    `

export const useSetOrgLogomarkMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgLogomarkMutation,
    TError,
    Types.SetOrgLogomarkMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgLogomarkMutation,
    TError,
    Types.SetOrgLogomarkMutationVariables,
    TContext
  >({
    mutationKey: ["setOrgLogomark"],
    mutationFn: (variables?: Types.SetOrgLogomarkMutationVariables) =>
      adminFetcher<Types.SetOrgLogomarkMutation, Types.SetOrgLogomarkMutationVariables>(
        SetOrgLogomarkDocument,
        variables,
      )(),
    ...options,
  })
}

useSetOrgLogomarkMutation.fetcher = (
  variables: Types.SetOrgLogomarkMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetOrgLogomarkMutation, Types.SetOrgLogomarkMutationVariables>(
    SetOrgLogomarkDocument,
    variables,
    options,
  )

export const SetOrgAllowlistDocument = `
    mutation SetOrgAllowlist($orgPid: String!, $domains: [String!]) {
  orgs {
    setAllowedEmailDomains(pid: $orgPid, allowedEmailDomains: $domains) {
      pid
      allowedEmailDomains
    }
  }
}
    `

export const useSetOrgAllowlistMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgAllowlistMutation,
    TError,
    Types.SetOrgAllowlistMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgAllowlistMutation,
    TError,
    Types.SetOrgAllowlistMutationVariables,
    TContext
  >({
    mutationKey: ["SetOrgAllowlist"],
    mutationFn: (variables?: Types.SetOrgAllowlistMutationVariables) =>
      adminFetcher<
        Types.SetOrgAllowlistMutation,
        Types.SetOrgAllowlistMutationVariables
      >(SetOrgAllowlistDocument, variables)(),
    ...options,
  })
}

useSetOrgAllowlistMutation.fetcher = (
  variables: Types.SetOrgAllowlistMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetOrgAllowlistMutation, Types.SetOrgAllowlistMutationVariables>(
    SetOrgAllowlistDocument,
    variables,
    options,
  )

export const SetUserAvatarDocument = `
    mutation setUserAvatar($userId: Int!, $upload: Upload!) {
  user {
    setAvatar(userId: $userId, upload: $upload) {
      avatarUrl
    }
  }
}
    `

export const useSetUserAvatarMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetUserAvatarMutation,
    TError,
    Types.SetUserAvatarMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetUserAvatarMutation,
    TError,
    Types.SetUserAvatarMutationVariables,
    TContext
  >({
    mutationKey: ["setUserAvatar"],
    mutationFn: (variables?: Types.SetUserAvatarMutationVariables) =>
      adminFetcher<Types.SetUserAvatarMutation, Types.SetUserAvatarMutationVariables>(
        SetUserAvatarDocument,
        variables,
      )(),
    ...options,
  })
}

useSetUserAvatarMutation.fetcher = (
  variables: Types.SetUserAvatarMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetUserAvatarMutation, Types.SetUserAvatarMutationVariables>(
    SetUserAvatarDocument,
    variables,
    options,
  )

export const UpdateUserOrgMembershipDocument = `
    mutation updateUserOrgMembership($userId: Int!, $orgPid: String!) {
  user {
    updateOrgMembership(userId: $userId, orgPid: $orgPid) {
      ...UserListUser
    }
  }
}
    ${UserListUserFragmentDoc}`

export const useUpdateUserOrgMembershipMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UpdateUserOrgMembershipMutation,
    TError,
    Types.UpdateUserOrgMembershipMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateUserOrgMembershipMutation,
    TError,
    Types.UpdateUserOrgMembershipMutationVariables,
    TContext
  >({
    mutationKey: ["updateUserOrgMembership"],
    mutationFn: (variables?: Types.UpdateUserOrgMembershipMutationVariables) =>
      adminFetcher<
        Types.UpdateUserOrgMembershipMutation,
        Types.UpdateUserOrgMembershipMutationVariables
      >(UpdateUserOrgMembershipDocument, variables)(),
    ...options,
  })
}

useUpdateUserOrgMembershipMutation.fetcher = (
  variables: Types.UpdateUserOrgMembershipMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.UpdateUserOrgMembershipMutation,
    Types.UpdateUserOrgMembershipMutationVariables
  >(UpdateUserOrgMembershipDocument, variables, options)

export const RemoveUserOrgMembershipDocument = `
    mutation removeUserOrgMembership($userId: Int!) {
  user {
    removeOrgMembership(userId: $userId) {
      ...UserListUser
    }
  }
}
    ${UserListUserFragmentDoc}`

export const useRemoveUserOrgMembershipMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveUserOrgMembershipMutation,
    TError,
    Types.RemoveUserOrgMembershipMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveUserOrgMembershipMutation,
    TError,
    Types.RemoveUserOrgMembershipMutationVariables,
    TContext
  >({
    mutationKey: ["removeUserOrgMembership"],
    mutationFn: (variables?: Types.RemoveUserOrgMembershipMutationVariables) =>
      adminFetcher<
        Types.RemoveUserOrgMembershipMutation,
        Types.RemoveUserOrgMembershipMutationVariables
      >(RemoveUserOrgMembershipDocument, variables)(),
    ...options,
  })
}

useRemoveUserOrgMembershipMutation.fetcher = (
  variables: Types.RemoveUserOrgMembershipMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.RemoveUserOrgMembershipMutation,
    Types.RemoveUserOrgMembershipMutationVariables
  >(RemoveUserOrgMembershipDocument, variables, options)

export const CreateCustomTariffDocument = `
    mutation CreateCustomTariff($sitePid: String!, $utilityId: Int!, $cfg: String!) {
  tariff {
    createCustomTariff(sitePid: $sitePid, utilityId: $utilityId, cfg: $cfg) {
      id
      name
      code
      isCustom
      utility {
        id
        name
      }
    }
  }
}
    `

export const useCreateCustomTariffMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateCustomTariffMutation,
    TError,
    Types.CreateCustomTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateCustomTariffMutation,
    TError,
    Types.CreateCustomTariffMutationVariables,
    TContext
  >({
    mutationKey: ["CreateCustomTariff"],
    mutationFn: (variables?: Types.CreateCustomTariffMutationVariables) =>
      adminFetcher<
        Types.CreateCustomTariffMutation,
        Types.CreateCustomTariffMutationVariables
      >(CreateCustomTariffDocument, variables)(),
    ...options,
  })
}

useCreateCustomTariffMutation.fetcher = (
  variables: Types.CreateCustomTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.CreateCustomTariffMutation,
    Types.CreateCustomTariffMutationVariables
  >(CreateCustomTariffDocument, variables, options)

export const ArchiveCustomTariffDocument = `
    mutation ArchiveCustomTariff($customTariffId: Int!) {
  tariff {
    archiveCustomTariff(customTariffId: $customTariffId)
  }
}
    `

export const useArchiveCustomTariffMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ArchiveCustomTariffMutation,
    TError,
    Types.ArchiveCustomTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ArchiveCustomTariffMutation,
    TError,
    Types.ArchiveCustomTariffMutationVariables,
    TContext
  >({
    mutationKey: ["ArchiveCustomTariff"],
    mutationFn: (variables?: Types.ArchiveCustomTariffMutationVariables) =>
      adminFetcher<
        Types.ArchiveCustomTariffMutation,
        Types.ArchiveCustomTariffMutationVariables
      >(ArchiveCustomTariffDocument, variables)(),
    ...options,
  })
}

useArchiveCustomTariffMutation.fetcher = (
  variables: Types.ArchiveCustomTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.ArchiveCustomTariffMutation,
    Types.ArchiveCustomTariffMutationVariables
  >(ArchiveCustomTariffDocument, variables, options)

export const SetPVoptGoalDocument = `
    mutation SetPVoptGoal($scenarioPid: String!, $pvoptGoal: PVoptGoal!, $roofKw: Float, $carportKw: Float, $groundKw: Float) {
  scenario(scenarioPid: $scenarioPid) {
    setPvoptGoal(
      pvoptGoal: $pvoptGoal
      roofKw: $roofKw
      carportKw: $carportKw
      groundKw: $groundKw
    ) {
      pid
    }
  }
}
    `

export const useSetPVoptGoalMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetPVoptGoalMutation,
    TError,
    Types.SetPVoptGoalMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetPVoptGoalMutation,
    TError,
    Types.SetPVoptGoalMutationVariables,
    TContext
  >({
    mutationKey: ["SetPVoptGoal"],
    mutationFn: (variables?: Types.SetPVoptGoalMutationVariables) =>
      adminFetcher<Types.SetPVoptGoalMutation, Types.SetPVoptGoalMutationVariables>(
        SetPVoptGoalDocument,
        variables,
      )(),
    ...options,
  })
}

useSetPVoptGoalMutation.fetcher = (
  variables: Types.SetPVoptGoalMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetPVoptGoalMutation, Types.SetPVoptGoalMutationVariables>(
    SetPVoptGoalDocument,
    variables,
    options,
  )

export const SetScenarioConfigDocument = `
    mutation SetScenarioConfig($scenarioPid: String!, $finproductId: Int, $finproductType: FinProductType, $helioscopePid: String, $goal: PVoptGoal, $roofKw: Float, $carportKw: Float, $groundKw: Float, $batteryProduct: BatteryProductInput) {
  scenario(scenarioPid: $scenarioPid) {
    setConfig(
      finproductId: $finproductId
      finproductType: $finproductType
      goal: $goal
      roofKw: $roofKw
      carportKw: $carportKw
      groundKw: $groundKw
      helioscopePid: $helioscopePid
      batteryProduct: $batteryProduct
    ) {
      pid
    }
  }
}
    `

export const useSetScenarioConfigMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetScenarioConfigMutation,
    TError,
    Types.SetScenarioConfigMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetScenarioConfigMutation,
    TError,
    Types.SetScenarioConfigMutationVariables,
    TContext
  >({
    mutationKey: ["SetScenarioConfig"],
    mutationFn: (variables?: Types.SetScenarioConfigMutationVariables) =>
      adminFetcher<
        Types.SetScenarioConfigMutation,
        Types.SetScenarioConfigMutationVariables
      >(SetScenarioConfigDocument, variables)(),
    ...options,
  })
}

useSetScenarioConfigMutation.fetcher = (
  variables: Types.SetScenarioConfigMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetScenarioConfigMutation,
    Types.SetScenarioConfigMutationVariables
  >(SetScenarioConfigDocument, variables, options)

export const RemoveScenarioConfigDocument = `
    mutation RemoveScenarioConfig($scenarioPid: String!, $scenarioConfigId: Int!) {
  scenario(scenarioPid: $scenarioPid) {
    removeConfig(scenarioConfigId: $scenarioConfigId)
  }
}
    `

export const useRemoveScenarioConfigMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RemoveScenarioConfigMutation,
    TError,
    Types.RemoveScenarioConfigMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveScenarioConfigMutation,
    TError,
    Types.RemoveScenarioConfigMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveScenarioConfig"],
    mutationFn: (variables?: Types.RemoveScenarioConfigMutationVariables) =>
      adminFetcher<
        Types.RemoveScenarioConfigMutation,
        Types.RemoveScenarioConfigMutationVariables
      >(RemoveScenarioConfigDocument, variables)(),
    ...options,
  })
}

useRemoveScenarioConfigMutation.fetcher = (
  variables: Types.RemoveScenarioConfigMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.RemoveScenarioConfigMutation,
    Types.RemoveScenarioConfigMutationVariables
  >(RemoveScenarioConfigDocument, variables, options)

export const CreateOpenInviteDocument = `
    mutation CreateOpenInvite($portfolioPid: String!) {
  openInvite {
    new(portfolioPid: $portfolioPid) {
      pid
    }
  }
}
    `

export const useCreateOpenInviteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateOpenInviteMutation,
    TError,
    Types.CreateOpenInviteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateOpenInviteMutation,
    TError,
    Types.CreateOpenInviteMutationVariables,
    TContext
  >({
    mutationKey: ["CreateOpenInvite"],
    mutationFn: (variables?: Types.CreateOpenInviteMutationVariables) =>
      adminFetcher<
        Types.CreateOpenInviteMutation,
        Types.CreateOpenInviteMutationVariables
      >(CreateOpenInviteDocument, variables)(),
    ...options,
  })
}

useCreateOpenInviteMutation.fetcher = (
  variables: Types.CreateOpenInviteMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.CreateOpenInviteMutation, Types.CreateOpenInviteMutationVariables>(
    CreateOpenInviteDocument,
    variables,
    options,
  )

export const SetOpenInviteExpiresAtDocument = `
    mutation SetOpenInviteExpiresAt($id: Int!, $expiresAt: DateTime!) {
  openInvite {
    setExpiresAt(id: $id, expiresAt: $expiresAt) {
      id
      expiresAt
    }
  }
}
    `

export const useSetOpenInviteExpiresAtMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOpenInviteExpiresAtMutation,
    TError,
    Types.SetOpenInviteExpiresAtMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOpenInviteExpiresAtMutation,
    TError,
    Types.SetOpenInviteExpiresAtMutationVariables,
    TContext
  >({
    mutationKey: ["SetOpenInviteExpiresAt"],
    mutationFn: (variables?: Types.SetOpenInviteExpiresAtMutationVariables) =>
      adminFetcher<
        Types.SetOpenInviteExpiresAtMutation,
        Types.SetOpenInviteExpiresAtMutationVariables
      >(SetOpenInviteExpiresAtDocument, variables)(),
    ...options,
  })
}

useSetOpenInviteExpiresAtMutation.fetcher = (
  variables: Types.SetOpenInviteExpiresAtMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetOpenInviteExpiresAtMutation,
    Types.SetOpenInviteExpiresAtMutationVariables
  >(SetOpenInviteExpiresAtDocument, variables, options)

export const SetBauMeterTariffDocument = `
    mutation SetBauMeterTariff($input: SetBauMeterTariffInput!) {
  site {
    setBauMeterTariff(input: $input) {
      pid
    }
  }
}
    `

export const useSetBauMeterTariffMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBauMeterTariffMutation,
    TError,
    Types.SetBauMeterTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBauMeterTariffMutation,
    TError,
    Types.SetBauMeterTariffMutationVariables,
    TContext
  >({
    mutationKey: ["SetBauMeterTariff"],
    mutationFn: (variables?: Types.SetBauMeterTariffMutationVariables) =>
      adminFetcher<
        Types.SetBauMeterTariffMutation,
        Types.SetBauMeterTariffMutationVariables
      >(SetBauMeterTariffDocument, variables)(),
    ...options,
  })
}

useSetBauMeterTariffMutation.fetcher = (
  variables: Types.SetBauMeterTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetBauMeterTariffMutation,
    Types.SetBauMeterTariffMutationVariables
  >(SetBauMeterTariffDocument, variables, options)

export const SetBauMeterLabelDocument = `
    mutation SetBauMeterLabel($input: SetBauMeterLabelInput!) {
  site {
    setBauMeterLabel(input: $input) {
      pid
    }
  }
}
    `

export const useSetBauMeterLabelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBauMeterLabelMutation,
    TError,
    Types.SetBauMeterLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBauMeterLabelMutation,
    TError,
    Types.SetBauMeterLabelMutationVariables,
    TContext
  >({
    mutationKey: ["SetBauMeterLabel"],
    mutationFn: (variables?: Types.SetBauMeterLabelMutationVariables) =>
      adminFetcher<
        Types.SetBauMeterLabelMutation,
        Types.SetBauMeterLabelMutationVariables
      >(SetBauMeterLabelDocument, variables)(),
    ...options,
  })
}

useSetBauMeterLabelMutation.fetcher = (
  variables: Types.SetBauMeterLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetBauMeterLabelMutation, Types.SetBauMeterLabelMutationVariables>(
    SetBauMeterLabelDocument,
    variables,
    options,
  )

export const OverrideFailureDocument = `
    mutation OverrideFailure($finmodelFailureId: Int!, $scope: GuardrailScope!, $names: [String!]!, $note: String) {
  overrideFinmodelFailure(
    finmodelFailureId: $finmodelFailureId
    scope: $scope
    names: $names
    note: $note
  ) {
    __typename
    ... on Error {
      message
    }
    ... on GuardrailOverrideResult {
      promotedFailure
    }
  }
}
    `

export const useOverrideFailureMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.OverrideFailureMutation,
    TError,
    Types.OverrideFailureMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.OverrideFailureMutation,
    TError,
    Types.OverrideFailureMutationVariables,
    TContext
  >({
    mutationKey: ["OverrideFailure"],
    mutationFn: (variables?: Types.OverrideFailureMutationVariables) =>
      adminFetcher<
        Types.OverrideFailureMutation,
        Types.OverrideFailureMutationVariables
      >(OverrideFailureDocument, variables)(),
    ...options,
  })
}

useOverrideFailureMutation.fetcher = (
  variables: Types.OverrideFailureMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.OverrideFailureMutation, Types.OverrideFailureMutationVariables>(
    OverrideFailureDocument,
    variables,
    options,
  )

export const OrgFinRelToggleDocument = `
    mutation OrgFinRelToggle($orgPid: String!, $finproductId: Int!, $enable: Boolean!) {
  orgs {
    finrelToggle(orgPid: $orgPid, finproductId: $finproductId, enable: $enable) {
      ...FinRel
    }
  }
}
    ${FinRelFragmentDoc}`

export const useOrgFinRelToggleMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.OrgFinRelToggleMutation,
    TError,
    Types.OrgFinRelToggleMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.OrgFinRelToggleMutation,
    TError,
    Types.OrgFinRelToggleMutationVariables,
    TContext
  >({
    mutationKey: ["OrgFinRelToggle"],
    mutationFn: (variables?: Types.OrgFinRelToggleMutationVariables) =>
      adminFetcher<
        Types.OrgFinRelToggleMutation,
        Types.OrgFinRelToggleMutationVariables
      >(OrgFinRelToggleDocument, variables)(),
    ...options,
  })
}

useOrgFinRelToggleMutation.fetcher = (
  variables: Types.OrgFinRelToggleMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.OrgFinRelToggleMutation, Types.OrgFinRelToggleMutationVariables>(
    OrgFinRelToggleDocument,
    variables,
    options,
  )

export const CreateAndApplySiteLabelDocument = `
    mutation CreateAndApplySiteLabel($input: CreateAndApplySiteLabelInput!) {
  site {
    labels {
      createAndApplySiteLabel(input: $input) {
        __typename
        ... on Error {
          message
        }
        ... on CreateAndApplySiteLabelOutput {
          label {
            pid
          }
        }
      }
    }
  }
}
    `

export const useCreateAndApplySiteLabelMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateAndApplySiteLabelMutation,
    TError,
    Types.CreateAndApplySiteLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateAndApplySiteLabelMutation,
    TError,
    Types.CreateAndApplySiteLabelMutationVariables,
    TContext
  >({
    mutationKey: ["CreateAndApplySiteLabel"],
    mutationFn: (variables?: Types.CreateAndApplySiteLabelMutationVariables) =>
      adminFetcher<
        Types.CreateAndApplySiteLabelMutation,
        Types.CreateAndApplySiteLabelMutationVariables
      >(CreateAndApplySiteLabelDocument, variables)(),
    ...options,
  })
}

useCreateAndApplySiteLabelMutation.fetcher = (
  variables: Types.CreateAndApplySiteLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.CreateAndApplySiteLabelMutation,
    Types.CreateAndApplySiteLabelMutationVariables
  >(CreateAndApplySiteLabelDocument, variables, options)

export const ApplySiteLabelDocument = `
    mutation ApplySiteLabel($input: ApplyLabelToSiteInput!) {
  site {
    labels {
      applyLabelToSite(input: $input) {
        __typename
        ... on Error {
          message
        }
      }
    }
  }
}
    `

export const useApplySiteLabelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ApplySiteLabelMutation,
    TError,
    Types.ApplySiteLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ApplySiteLabelMutation,
    TError,
    Types.ApplySiteLabelMutationVariables,
    TContext
  >({
    mutationKey: ["ApplySiteLabel"],
    mutationFn: (variables?: Types.ApplySiteLabelMutationVariables) =>
      adminFetcher<Types.ApplySiteLabelMutation, Types.ApplySiteLabelMutationVariables>(
        ApplySiteLabelDocument,
        variables,
      )(),
    ...options,
  })
}

useApplySiteLabelMutation.fetcher = (
  variables: Types.ApplySiteLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.ApplySiteLabelMutation, Types.ApplySiteLabelMutationVariables>(
    ApplySiteLabelDocument,
    variables,
    options,
  )

export const RenameSiteLabelDocument = `
    mutation RenameSiteLabel($input: RenameLabelInput!) {
  site {
    labels {
      renameLabel(input: $input) {
        __typename
        ... on RenameSiteLabelOutput {
          label {
            pid
            name
          }
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `

export const useRenameSiteLabelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RenameSiteLabelMutation,
    TError,
    Types.RenameSiteLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RenameSiteLabelMutation,
    TError,
    Types.RenameSiteLabelMutationVariables,
    TContext
  >({
    mutationKey: ["RenameSiteLabel"],
    mutationFn: (variables?: Types.RenameSiteLabelMutationVariables) =>
      adminFetcher<
        Types.RenameSiteLabelMutation,
        Types.RenameSiteLabelMutationVariables
      >(RenameSiteLabelDocument, variables)(),
    ...options,
  })
}

useRenameSiteLabelMutation.fetcher = (
  variables: Types.RenameSiteLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.RenameSiteLabelMutation, Types.RenameSiteLabelMutationVariables>(
    RenameSiteLabelDocument,
    variables,
    options,
  )

export const ChangeSiteLabelColorDocument = `
    mutation ChangeSiteLabelColor($input: ChangeSiteLabelColorInput!) {
  site {
    labels {
      changeSiteLabelColor(input: $input) {
        __typename
        ... on ChangeSiteLabelColorOutput {
          label {
            pid
            themeColor
          }
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `

export const useChangeSiteLabelColorMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ChangeSiteLabelColorMutation,
    TError,
    Types.ChangeSiteLabelColorMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ChangeSiteLabelColorMutation,
    TError,
    Types.ChangeSiteLabelColorMutationVariables,
    TContext
  >({
    mutationKey: ["ChangeSiteLabelColor"],
    mutationFn: (variables?: Types.ChangeSiteLabelColorMutationVariables) =>
      adminFetcher<
        Types.ChangeSiteLabelColorMutation,
        Types.ChangeSiteLabelColorMutationVariables
      >(ChangeSiteLabelColorDocument, variables)(),
    ...options,
  })
}

useChangeSiteLabelColorMutation.fetcher = (
  variables: Types.ChangeSiteLabelColorMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.ChangeSiteLabelColorMutation,
    Types.ChangeSiteLabelColorMutationVariables
  >(ChangeSiteLabelColorDocument, variables, options)

export const RemoveSiteLabelDocument = `
    mutation RemoveSiteLabel($input: RemoveLabelInput!) {
  site {
    labels {
      removeLabel(input: $input)
    }
  }
}
    `

export const useRemoveSiteLabelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RemoveSiteLabelMutation,
    TError,
    Types.RemoveSiteLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveSiteLabelMutation,
    TError,
    Types.RemoveSiteLabelMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveSiteLabel"],
    mutationFn: (variables?: Types.RemoveSiteLabelMutationVariables) =>
      adminFetcher<
        Types.RemoveSiteLabelMutation,
        Types.RemoveSiteLabelMutationVariables
      >(RemoveSiteLabelDocument, variables)(),
    ...options,
  })
}

useRemoveSiteLabelMutation.fetcher = (
  variables: Types.RemoveSiteLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.RemoveSiteLabelMutation, Types.RemoveSiteLabelMutationVariables>(
    RemoveSiteLabelDocument,
    variables,
    options,
  )

export const DeleteSiteLabelDocument = `
    mutation DeleteSiteLabel($input: DeleteLabelInput!) {
  site {
    labels {
      deleteLabel(input: $input) {
        __typename
        ... on Error {
          message
        }
      }
    }
  }
}
    `

export const useDeleteSiteLabelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteSiteLabelMutation,
    TError,
    Types.DeleteSiteLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteSiteLabelMutation,
    TError,
    Types.DeleteSiteLabelMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteSiteLabel"],
    mutationFn: (variables?: Types.DeleteSiteLabelMutationVariables) =>
      adminFetcher<
        Types.DeleteSiteLabelMutation,
        Types.DeleteSiteLabelMutationVariables
      >(DeleteSiteLabelDocument, variables)(),
    ...options,
  })
}

useDeleteSiteLabelMutation.fetcher = (
  variables: Types.DeleteSiteLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.DeleteSiteLabelMutation, Types.DeleteSiteLabelMutationVariables>(
    DeleteSiteLabelDocument,
    variables,
    options,
  )

export const SetLatitudeDocument = `
    mutation SetLatitude($sitePid: String!, $value: Float!) {
  site {
    setLatitude(sitePid: $sitePid, value: $value)
  }
}
    `

export const useSetLatitudeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetLatitudeMutation,
    TError,
    Types.SetLatitudeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetLatitudeMutation,
    TError,
    Types.SetLatitudeMutationVariables,
    TContext
  >({
    mutationKey: ["SetLatitude"],
    mutationFn: (variables?: Types.SetLatitudeMutationVariables) =>
      adminFetcher<Types.SetLatitudeMutation, Types.SetLatitudeMutationVariables>(
        SetLatitudeDocument,
        variables,
      )(),
    ...options,
  })
}

useSetLatitudeMutation.fetcher = (
  variables: Types.SetLatitudeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetLatitudeMutation, Types.SetLatitudeMutationVariables>(
    SetLatitudeDocument,
    variables,
    options,
  )

export const SetLongitudeDocument = `
    mutation SetLongitude($sitePid: String!, $value: Float!) {
  site {
    setLongitude(sitePid: $sitePid, value: $value)
  }
}
    `

export const useSetLongitudeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetLongitudeMutation,
    TError,
    Types.SetLongitudeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetLongitudeMutation,
    TError,
    Types.SetLongitudeMutationVariables,
    TContext
  >({
    mutationKey: ["SetLongitude"],
    mutationFn: (variables?: Types.SetLongitudeMutationVariables) =>
      adminFetcher<Types.SetLongitudeMutation, Types.SetLongitudeMutationVariables>(
        SetLongitudeDocument,
        variables,
      )(),
    ...options,
  })
}

useSetLongitudeMutation.fetcher = (
  variables: Types.SetLongitudeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetLongitudeMutation, Types.SetLongitudeMutationVariables>(
    SetLongitudeDocument,
    variables,
    options,
  )

export const BoostDocument = `
    mutation Boost($orgPid: String!) {
  queue {
    boost(orgPid: $orgPid)
  }
}
    `

export const useBoostMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.BoostMutation,
    TError,
    Types.BoostMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.BoostMutation,
    TError,
    Types.BoostMutationVariables,
    TContext
  >({
    mutationKey: ["Boost"],
    mutationFn: (variables?: Types.BoostMutationVariables) =>
      adminFetcher<Types.BoostMutation, Types.BoostMutationVariables>(
        BoostDocument,
        variables,
      )(),
    ...options,
  })
}

useBoostMutation.fetcher = (
  variables: Types.BoostMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.BoostMutation, Types.BoostMutationVariables>(
    BoostDocument,
    variables,
    options,
  )

export const LinkConserviceAddressesDocument = `
    mutation LinkConserviceAddresses($orgPid: String!, $sitePid: String!, $conserviceAddresses: [String!]!) {
  site {
    linkConserviceAddresses(
      orgPid: $orgPid
      sitePid: $sitePid
      conserviceAddresses: $conserviceAddresses
    ) {
      ... on Error {
        __typename
        message
      }
    }
  }
}
    `

export const useLinkConserviceAddressesMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.LinkConserviceAddressesMutation,
    TError,
    Types.LinkConserviceAddressesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.LinkConserviceAddressesMutation,
    TError,
    Types.LinkConserviceAddressesMutationVariables,
    TContext
  >({
    mutationKey: ["LinkConserviceAddresses"],
    mutationFn: (variables?: Types.LinkConserviceAddressesMutationVariables) =>
      adminFetcher<
        Types.LinkConserviceAddressesMutation,
        Types.LinkConserviceAddressesMutationVariables
      >(LinkConserviceAddressesDocument, variables)(),
    ...options,
  })
}

useLinkConserviceAddressesMutation.fetcher = (
  variables: Types.LinkConserviceAddressesMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.LinkConserviceAddressesMutation,
    Types.LinkConserviceAddressesMutationVariables
  >(LinkConserviceAddressesDocument, variables, options)

export const CreateCsFinProductDocument = `
    mutation CreateCsFinProduct($input: CSFinProductInput!) {
  finproduct {
    createCsFinproduct(input: $input) {
      id
    }
  }
}
    `

export const useCreateCsFinProductMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateCsFinProductMutation,
    TError,
    Types.CreateCsFinProductMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateCsFinProductMutation,
    TError,
    Types.CreateCsFinProductMutationVariables,
    TContext
  >({
    mutationKey: ["CreateCsFinProduct"],
    mutationFn: (variables?: Types.CreateCsFinProductMutationVariables) =>
      adminFetcher<
        Types.CreateCsFinProductMutation,
        Types.CreateCsFinProductMutationVariables
      >(CreateCsFinProductDocument, variables)(),
    ...options,
  })
}

useCreateCsFinProductMutation.fetcher = (
  variables: Types.CreateCsFinProductMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.CreateCsFinProductMutation,
    Types.CreateCsFinProductMutationVariables
  >(CreateCsFinProductDocument, variables, options)

export const EditCsFinProductPricesDocument = `
    mutation EditCSFinProductPrices($id: Int!, $input: [CSPriceInput!]!) {
  finproduct {
    editCsFinproductPrices(id: $id, input: $input) {
      id
    }
  }
}
    `

export const useEditCsFinProductPricesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditCsFinProductPricesMutation,
    TError,
    Types.EditCsFinProductPricesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditCsFinProductPricesMutation,
    TError,
    Types.EditCsFinProductPricesMutationVariables,
    TContext
  >({
    mutationKey: ["EditCSFinProductPrices"],
    mutationFn: (variables?: Types.EditCsFinProductPricesMutationVariables) =>
      adminFetcher<
        Types.EditCsFinProductPricesMutation,
        Types.EditCsFinProductPricesMutationVariables
      >(EditCsFinProductPricesDocument, variables)(),
    ...options,
  })
}

useEditCsFinProductPricesMutation.fetcher = (
  variables: Types.EditCsFinProductPricesMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.EditCsFinProductPricesMutation,
    Types.EditCsFinProductPricesMutationVariables
  >(EditCsFinProductPricesDocument, variables, options)

export const CreatePpaFinProductDocument = `
    mutation CreatePpaFinProduct($input: PPAFinProductInput!) {
  finproduct {
    createPpaFinproduct(input: $input) {
      id
    }
  }
}
    `

export const useCreatePpaFinProductMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreatePpaFinProductMutation,
    TError,
    Types.CreatePpaFinProductMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreatePpaFinProductMutation,
    TError,
    Types.CreatePpaFinProductMutationVariables,
    TContext
  >({
    mutationKey: ["CreatePpaFinProduct"],
    mutationFn: (variables?: Types.CreatePpaFinProductMutationVariables) =>
      adminFetcher<
        Types.CreatePpaFinProductMutation,
        Types.CreatePpaFinProductMutationVariables
      >(CreatePpaFinProductDocument, variables)(),
    ...options,
  })
}

useCreatePpaFinProductMutation.fetcher = (
  variables: Types.CreatePpaFinProductMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.CreatePpaFinProductMutation,
    Types.CreatePpaFinProductMutationVariables
  >(CreatePpaFinProductDocument, variables, options)

export const CreateLoanFinProductDocument = `
    mutation CreateLoanFinProduct($input: LoanFinProductInput!) {
  finproduct {
    createLoanFinproduct(input: $input) {
      id
    }
  }
}
    `

export const useCreateLoanFinProductMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateLoanFinProductMutation,
    TError,
    Types.CreateLoanFinProductMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateLoanFinProductMutation,
    TError,
    Types.CreateLoanFinProductMutationVariables,
    TContext
  >({
    mutationKey: ["CreateLoanFinProduct"],
    mutationFn: (variables?: Types.CreateLoanFinProductMutationVariables) =>
      adminFetcher<
        Types.CreateLoanFinProductMutation,
        Types.CreateLoanFinProductMutationVariables
      >(CreateLoanFinProductDocument, variables)(),
    ...options,
  })
}

useCreateLoanFinProductMutation.fetcher = (
  variables: Types.CreateLoanFinProductMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.CreateLoanFinProductMutation,
    Types.CreateLoanFinProductMutationVariables
  >(CreateLoanFinProductDocument, variables, options)

export const FinProductSetNameDocument = `
    mutation FinProductSetName($id: Int!, $value: String!) {
  finproduct {
    setName(id: $id, name: $value) {
      id
    }
  }
}
    `

export const useFinProductSetNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.FinProductSetNameMutation,
    TError,
    Types.FinProductSetNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FinProductSetNameMutation,
    TError,
    Types.FinProductSetNameMutationVariables,
    TContext
  >({
    mutationKey: ["FinProductSetName"],
    mutationFn: (variables?: Types.FinProductSetNameMutationVariables) =>
      adminFetcher<
        Types.FinProductSetNameMutation,
        Types.FinProductSetNameMutationVariables
      >(FinProductSetNameDocument, variables)(),
    ...options,
  })
}

useFinProductSetNameMutation.fetcher = (
  variables: Types.FinProductSetNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.FinProductSetNameMutation,
    Types.FinProductSetNameMutationVariables
  >(FinProductSetNameDocument, variables, options)

export const FinProductSetStatusDocument = `
    mutation FinProductSetStatus($id: Int!, $value: FinProductStatus) {
  finproduct {
    setStatus(id: $id, status: $value) {
      id
    }
  }
}
    `

export const useFinProductSetStatusMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.FinProductSetStatusMutation,
    TError,
    Types.FinProductSetStatusMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FinProductSetStatusMutation,
    TError,
    Types.FinProductSetStatusMutationVariables,
    TContext
  >({
    mutationKey: ["FinProductSetStatus"],
    mutationFn: (variables?: Types.FinProductSetStatusMutationVariables) =>
      adminFetcher<
        Types.FinProductSetStatusMutation,
        Types.FinProductSetStatusMutationVariables
      >(FinProductSetStatusDocument, variables)(),
    ...options,
  })
}

useFinProductSetStatusMutation.fetcher = (
  variables: Types.FinProductSetStatusMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.FinProductSetStatusMutation,
    Types.FinProductSetStatusMutationVariables
  >(FinProductSetStatusDocument, variables, options)

export const FinProductSetDisabledDocument = `
    mutation FinProductSetDisabled($id: Int!, $value: Boolean!) {
  finproduct {
    setDisabled(id: $id, disabled: $value) {
      id
    }
  }
}
    `

export const useFinProductSetDisabledMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.FinProductSetDisabledMutation,
    TError,
    Types.FinProductSetDisabledMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FinProductSetDisabledMutation,
    TError,
    Types.FinProductSetDisabledMutationVariables,
    TContext
  >({
    mutationKey: ["FinProductSetDisabled"],
    mutationFn: (variables?: Types.FinProductSetDisabledMutationVariables) =>
      adminFetcher<
        Types.FinProductSetDisabledMutation,
        Types.FinProductSetDisabledMutationVariables
      >(FinProductSetDisabledDocument, variables)(),
    ...options,
  })
}

useFinProductSetDisabledMutation.fetcher = (
  variables: Types.FinProductSetDisabledMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.FinProductSetDisabledMutation,
    Types.FinProductSetDisabledMutationVariables
  >(FinProductSetDisabledDocument, variables, options)

export const FinProductSetIncludeStorageDocument = `
    mutation FinProductSetIncludeStorage($finproductId: Int!, $enable: Boolean!) {
  finproduct {
    setIncludeStorage(id: $finproductId, includeStorage: $enable) {
      id
    }
  }
}
    `

export const useFinProductSetIncludeStorageMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.FinProductSetIncludeStorageMutation,
    TError,
    Types.FinProductSetIncludeStorageMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FinProductSetIncludeStorageMutation,
    TError,
    Types.FinProductSetIncludeStorageMutationVariables,
    TContext
  >({
    mutationKey: ["FinProductSetIncludeStorage"],
    mutationFn: (variables?: Types.FinProductSetIncludeStorageMutationVariables) =>
      adminFetcher<
        Types.FinProductSetIncludeStorageMutation,
        Types.FinProductSetIncludeStorageMutationVariables
      >(FinProductSetIncludeStorageDocument, variables)(),
    ...options,
  })
}

useFinProductSetIncludeStorageMutation.fetcher = (
  variables: Types.FinProductSetIncludeStorageMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.FinProductSetIncludeStorageMutation,
    Types.FinProductSetIncludeStorageMutationVariables
  >(FinProductSetIncludeStorageDocument, variables, options)

export const SiteToggleFinProductDocument = `
    mutation SiteToggleFinProduct($sitePid: String!, $finproductId: Int!, $enabled: Boolean!) {
  site {
    toggleFinproduct(
      sitePid: $sitePid
      finproductId: $finproductId
      enabled: $enabled
    )
  }
}
    `

export const useSiteToggleFinProductMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SiteToggleFinProductMutation,
    TError,
    Types.SiteToggleFinProductMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SiteToggleFinProductMutation,
    TError,
    Types.SiteToggleFinProductMutationVariables,
    TContext
  >({
    mutationKey: ["SiteToggleFinProduct"],
    mutationFn: (variables?: Types.SiteToggleFinProductMutationVariables) =>
      adminFetcher<
        Types.SiteToggleFinProductMutation,
        Types.SiteToggleFinProductMutationVariables
      >(SiteToggleFinProductDocument, variables)(),
    ...options,
  })
}

useSiteToggleFinProductMutation.fetcher = (
  variables: Types.SiteToggleFinProductMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SiteToggleFinProductMutation,
    Types.SiteToggleFinProductMutationVariables
  >(SiteToggleFinProductDocument, variables, options)

export const ToggleHideFakePanelsDocument = `
    mutation ToggleHideFakePanels($sitePid: String!, $hide: Boolean!) {
  site {
    toggleHideFakePanels(sitePid: $sitePid, hide: $hide)
  }
}
    `

export const useToggleHideFakePanelsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ToggleHideFakePanelsMutation,
    TError,
    Types.ToggleHideFakePanelsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ToggleHideFakePanelsMutation,
    TError,
    Types.ToggleHideFakePanelsMutationVariables,
    TContext
  >({
    mutationKey: ["ToggleHideFakePanels"],
    mutationFn: (variables?: Types.ToggleHideFakePanelsMutationVariables) =>
      adminFetcher<
        Types.ToggleHideFakePanelsMutation,
        Types.ToggleHideFakePanelsMutationVariables
      >(ToggleHideFakePanelsDocument, variables)(),
    ...options,
  })
}

useToggleHideFakePanelsMutation.fetcher = (
  variables: Types.ToggleHideFakePanelsMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.ToggleHideFakePanelsMutation,
    Types.ToggleHideFakePanelsMutationVariables
  >(ToggleHideFakePanelsDocument, variables, options)

export const TodoItemAddDocument = `
    mutation TodoItemAdd($sitePid: String!, $content: String!, $sectionName: String!) {
  todos {
    add(sitePid: $sitePid, content: $content, sectionName: $sectionName)
  }
}
    `

export const useTodoItemAddMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TodoItemAddMutation,
    TError,
    Types.TodoItemAddMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TodoItemAddMutation,
    TError,
    Types.TodoItemAddMutationVariables,
    TContext
  >({
    mutationKey: ["TodoItemAdd"],
    mutationFn: (variables?: Types.TodoItemAddMutationVariables) =>
      adminFetcher<Types.TodoItemAddMutation, Types.TodoItemAddMutationVariables>(
        TodoItemAddDocument,
        variables,
      )(),
    ...options,
  })
}

useTodoItemAddMutation.fetcher = (
  variables: Types.TodoItemAddMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.TodoItemAddMutation, Types.TodoItemAddMutationVariables>(
    TodoItemAddDocument,
    variables,
    options,
  )

export const TodoItemDeleteDocument = `
    mutation TodoItemDelete($sitePid: String!, $todoItemId: Int!) {
  todos {
    delete(sitePid: $sitePid, todoItemId: $todoItemId)
  }
}
    `

export const useTodoItemDeleteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TodoItemDeleteMutation,
    TError,
    Types.TodoItemDeleteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TodoItemDeleteMutation,
    TError,
    Types.TodoItemDeleteMutationVariables,
    TContext
  >({
    mutationKey: ["TodoItemDelete"],
    mutationFn: (variables?: Types.TodoItemDeleteMutationVariables) =>
      adminFetcher<Types.TodoItemDeleteMutation, Types.TodoItemDeleteMutationVariables>(
        TodoItemDeleteDocument,
        variables,
      )(),
    ...options,
  })
}

useTodoItemDeleteMutation.fetcher = (
  variables: Types.TodoItemDeleteMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.TodoItemDeleteMutation, Types.TodoItemDeleteMutationVariables>(
    TodoItemDeleteDocument,
    variables,
    options,
  )

export const TodoItemSetContentDocument = `
    mutation TodoItemSetContent($sitePid: String!, $todoItemId: Int!, $content: String!) {
  todos {
    setContent(sitePid: $sitePid, todoItemId: $todoItemId, content: $content)
  }
}
    `

export const useTodoItemSetContentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TodoItemSetContentMutation,
    TError,
    Types.TodoItemSetContentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TodoItemSetContentMutation,
    TError,
    Types.TodoItemSetContentMutationVariables,
    TContext
  >({
    mutationKey: ["TodoItemSetContent"],
    mutationFn: (variables?: Types.TodoItemSetContentMutationVariables) =>
      adminFetcher<
        Types.TodoItemSetContentMutation,
        Types.TodoItemSetContentMutationVariables
      >(TodoItemSetContentDocument, variables)(),
    ...options,
  })
}

useTodoItemSetContentMutation.fetcher = (
  variables: Types.TodoItemSetContentMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.TodoItemSetContentMutation,
    Types.TodoItemSetContentMutationVariables
  >(TodoItemSetContentDocument, variables, options)

export const TodoItemSetCompleteDocument = `
    mutation TodoItemSetComplete($sitePid: String!, $todoItemId: Int!, $complete: Boolean!) {
  todos {
    setComplete(sitePid: $sitePid, todoItemId: $todoItemId, complete: $complete)
  }
}
    `

export const useTodoItemSetCompleteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TodoItemSetCompleteMutation,
    TError,
    Types.TodoItemSetCompleteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TodoItemSetCompleteMutation,
    TError,
    Types.TodoItemSetCompleteMutationVariables,
    TContext
  >({
    mutationKey: ["TodoItemSetComplete"],
    mutationFn: (variables?: Types.TodoItemSetCompleteMutationVariables) =>
      adminFetcher<
        Types.TodoItemSetCompleteMutation,
        Types.TodoItemSetCompleteMutationVariables
      >(TodoItemSetCompleteDocument, variables)(),
    ...options,
  })
}

useTodoItemSetCompleteMutation.fetcher = (
  variables: Types.TodoItemSetCompleteMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.TodoItemSetCompleteMutation,
    Types.TodoItemSetCompleteMutationVariables
  >(TodoItemSetCompleteDocument, variables, options)

export const TodoItemSetLocationDocument = `
    mutation TodoItemSetLocation($sitePid: String!, $todoItemId: Int!, $sectionName: String!, $index: Int!) {
  todos {
    setSection(
      sitePid: $sitePid
      todoItemId: $todoItemId
      sectionName: $sectionName
      index: $index
    )
  }
}
    `

export const useTodoItemSetLocationMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TodoItemSetLocationMutation,
    TError,
    Types.TodoItemSetLocationMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TodoItemSetLocationMutation,
    TError,
    Types.TodoItemSetLocationMutationVariables,
    TContext
  >({
    mutationKey: ["TodoItemSetLocation"],
    mutationFn: (variables?: Types.TodoItemSetLocationMutationVariables) =>
      adminFetcher<
        Types.TodoItemSetLocationMutation,
        Types.TodoItemSetLocationMutationVariables
      >(TodoItemSetLocationDocument, variables)(),
    ...options,
  })
}

useTodoItemSetLocationMutation.fetcher = (
  variables: Types.TodoItemSetLocationMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.TodoItemSetLocationMutation,
    Types.TodoItemSetLocationMutationVariables
  >(TodoItemSetLocationDocument, variables, options)

export const TodoSectionSetIndexDocument = `
    mutation TodoSectionSetIndex($sitePid: String!, $sectionName: String!, $index: Int!) {
  todos {
    setSectionIndex(sitePid: $sitePid, sectionName: $sectionName, index: $index)
  }
}
    `

export const useTodoSectionSetIndexMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TodoSectionSetIndexMutation,
    TError,
    Types.TodoSectionSetIndexMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TodoSectionSetIndexMutation,
    TError,
    Types.TodoSectionSetIndexMutationVariables,
    TContext
  >({
    mutationKey: ["TodoSectionSetIndex"],
    mutationFn: (variables?: Types.TodoSectionSetIndexMutationVariables) =>
      adminFetcher<
        Types.TodoSectionSetIndexMutation,
        Types.TodoSectionSetIndexMutationVariables
      >(TodoSectionSetIndexDocument, variables)(),
    ...options,
  })
}

useTodoSectionSetIndexMutation.fetcher = (
  variables: Types.TodoSectionSetIndexMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.TodoSectionSetIndexMutation,
    Types.TodoSectionSetIndexMutationVariables
  >(TodoSectionSetIndexDocument, variables, options)

export const TodoSectionSetNameDocument = `
    mutation TodoSectionSetName($sitePid: String!, $sectionName: String!, $newName: String!) {
  todos {
    setSectionName(sitePid: $sitePid, sectionName: $sectionName, newName: $newName)
  }
}
    `

export const useTodoSectionSetNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TodoSectionSetNameMutation,
    TError,
    Types.TodoSectionSetNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TodoSectionSetNameMutation,
    TError,
    Types.TodoSectionSetNameMutationVariables,
    TContext
  >({
    mutationKey: ["TodoSectionSetName"],
    mutationFn: (variables?: Types.TodoSectionSetNameMutationVariables) =>
      adminFetcher<
        Types.TodoSectionSetNameMutation,
        Types.TodoSectionSetNameMutationVariables
      >(TodoSectionSetNameDocument, variables)(),
    ...options,
  })
}

useTodoSectionSetNameMutation.fetcher = (
  variables: Types.TodoSectionSetNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.TodoSectionSetNameMutation,
    Types.TodoSectionSetNameMutationVariables
  >(TodoSectionSetNameDocument, variables, options)

export const SetHelioscopeAllocationDocument = `
    mutation SetHelioscopeAllocation($helioscopeId: Int!, $allocations: [Allocation!]!) {
  site {
    setHelioscopeAllocation(helioscopeId: $helioscopeId, allocations: $allocations)
  }
}
    `

export const useSetHelioscopeAllocationMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetHelioscopeAllocationMutation,
    TError,
    Types.SetHelioscopeAllocationMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetHelioscopeAllocationMutation,
    TError,
    Types.SetHelioscopeAllocationMutationVariables,
    TContext
  >({
    mutationKey: ["SetHelioscopeAllocation"],
    mutationFn: (variables?: Types.SetHelioscopeAllocationMutationVariables) =>
      adminFetcher<
        Types.SetHelioscopeAllocationMutation,
        Types.SetHelioscopeAllocationMutationVariables
      >(SetHelioscopeAllocationDocument, variables)(),
    ...options,
  })
}

useSetHelioscopeAllocationMutation.fetcher = (
  variables: Types.SetHelioscopeAllocationMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetHelioscopeAllocationMutation,
    Types.SetHelioscopeAllocationMutationVariables
  >(SetHelioscopeAllocationDocument, variables, options)

export const NewEvCalcDocument = `
    mutation NewEvCalc($sitePid: String!, $markupUsdPerKwh: Float!, $numChargers: Int!) {
  site {
    newEvCalc(
      sitePid: $sitePid
      markupUsdPerKwh: $markupUsdPerKwh
      numChargers: $numChargers
    ) {
      ...BasicEVCalc
    }
  }
}
    ${BasicEvCalcFragmentDoc}`

export const useNewEvCalcMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.NewEvCalcMutation,
    TError,
    Types.NewEvCalcMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.NewEvCalcMutation,
    TError,
    Types.NewEvCalcMutationVariables,
    TContext
  >({
    mutationKey: ["NewEvCalc"],
    mutationFn: (variables?: Types.NewEvCalcMutationVariables) =>
      adminFetcher<Types.NewEvCalcMutation, Types.NewEvCalcMutationVariables>(
        NewEvCalcDocument,
        variables,
      )(),
    ...options,
  })
}

useNewEvCalcMutation.fetcher = (
  variables: Types.NewEvCalcMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.NewEvCalcMutation, Types.NewEvCalcMutationVariables>(
    NewEvCalcDocument,
    variables,
    options,
  )

export const FetchTariffVersionDocument = `
    mutation FetchTariffVersion($masterTariffId: Int!, $effectiveOn: DateTime!) {
  tariff {
    fetchTariffVersion(masterTariffId: $masterTariffId, effectiveOn: $effectiveOn)
  }
}
    `

export const useFetchTariffVersionMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.FetchTariffVersionMutation,
    TError,
    Types.FetchTariffVersionMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FetchTariffVersionMutation,
    TError,
    Types.FetchTariffVersionMutationVariables,
    TContext
  >({
    mutationKey: ["FetchTariffVersion"],
    mutationFn: (variables?: Types.FetchTariffVersionMutationVariables) =>
      adminFetcher<
        Types.FetchTariffVersionMutation,
        Types.FetchTariffVersionMutationVariables
      >(FetchTariffVersionDocument, variables)(),
    ...options,
  })
}

useFetchTariffVersionMutation.fetcher = (
  variables: Types.FetchTariffVersionMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.FetchTariffVersionMutation,
    Types.FetchTariffVersionMutationVariables
  >(FetchTariffVersionDocument, variables, options)

export const UpdateHelioscopeAssumptionsDocument = `
    mutation UpdateHelioscopeAssumptions($helioscopePid: String!, $assumptions: HelioscopeAssumptionsInput!) {
  helioscopes {
    updateHelioscopeAssumptions(
      helioscopePid: $helioscopePid
      assumptions: $assumptions
    ) {
      __typename
    }
  }
}
    `

export const useUpdateHelioscopeAssumptionsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UpdateHelioscopeAssumptionsMutation,
    TError,
    Types.UpdateHelioscopeAssumptionsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateHelioscopeAssumptionsMutation,
    TError,
    Types.UpdateHelioscopeAssumptionsMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateHelioscopeAssumptions"],
    mutationFn: (variables?: Types.UpdateHelioscopeAssumptionsMutationVariables) =>
      adminFetcher<
        Types.UpdateHelioscopeAssumptionsMutation,
        Types.UpdateHelioscopeAssumptionsMutationVariables
      >(UpdateHelioscopeAssumptionsDocument, variables)(),
    ...options,
  })
}

useUpdateHelioscopeAssumptionsMutation.fetcher = (
  variables: Types.UpdateHelioscopeAssumptionsMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.UpdateHelioscopeAssumptionsMutation,
    Types.UpdateHelioscopeAssumptionsMutationVariables
  >(UpdateHelioscopeAssumptionsDocument, variables, options)

export const UpdateMultifamilyUnitsDocument = `
    mutation UpdateMultifamilyUnits($sitePid: String!, $input: MFUnitBreakdownInput!) {
  updateMfUnits(sitePid: $sitePid, input: $input)
}
    `

export const useUpdateMultifamilyUnitsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateMultifamilyUnitsMutation,
    TError,
    Types.UpdateMultifamilyUnitsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateMultifamilyUnitsMutation,
    TError,
    Types.UpdateMultifamilyUnitsMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateMultifamilyUnits"],
    mutationFn: (variables?: Types.UpdateMultifamilyUnitsMutationVariables) =>
      adminFetcher<
        Types.UpdateMultifamilyUnitsMutation,
        Types.UpdateMultifamilyUnitsMutationVariables
      >(UpdateMultifamilyUnitsDocument, variables)(),
    ...options,
  })
}

useUpdateMultifamilyUnitsMutation.fetcher = (
  variables: Types.UpdateMultifamilyUnitsMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.UpdateMultifamilyUnitsMutation,
    Types.UpdateMultifamilyUnitsMutationVariables
  >(UpdateMultifamilyUnitsDocument, variables, options)

export const SetUserEnabledDocument = `
    mutation SetUserEnabled($userId: Int!, $isEnabled: Boolean!) {
  user {
    setIsEnabled(userId: $userId, isEnabled: $isEnabled) {
      __typename
      isEnabled
      lastSeenAt
    }
  }
}
    `

export const useSetUserEnabledMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetUserEnabledMutation,
    TError,
    Types.SetUserEnabledMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetUserEnabledMutation,
    TError,
    Types.SetUserEnabledMutationVariables,
    TContext
  >({
    mutationKey: ["SetUserEnabled"],
    mutationFn: (variables?: Types.SetUserEnabledMutationVariables) =>
      adminFetcher<Types.SetUserEnabledMutation, Types.SetUserEnabledMutationVariables>(
        SetUserEnabledDocument,
        variables,
      )(),
    ...options,
  })
}

useSetUserEnabledMutation.fetcher = (
  variables: Types.SetUserEnabledMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetUserEnabledMutation, Types.SetUserEnabledMutationVariables>(
    SetUserEnabledDocument,
    variables,
    options,
  )

export const ClearSiteDrawingsDocument = `
    mutation ClearSiteDrawings($input: ClearDrawingsInput!) {
  site {
    clearDrawings(input: $input)
  }
}
    `

export const useClearSiteDrawingsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ClearSiteDrawingsMutation,
    TError,
    Types.ClearSiteDrawingsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ClearSiteDrawingsMutation,
    TError,
    Types.ClearSiteDrawingsMutationVariables,
    TContext
  >({
    mutationKey: ["ClearSiteDrawings"],
    mutationFn: (variables?: Types.ClearSiteDrawingsMutationVariables) =>
      adminFetcher<
        Types.ClearSiteDrawingsMutation,
        Types.ClearSiteDrawingsMutationVariables
      >(ClearSiteDrawingsDocument, variables)(),
    ...options,
  })
}

useClearSiteDrawingsMutation.fetcher = (
  variables: Types.ClearSiteDrawingsMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.ClearSiteDrawingsMutation,
    Types.ClearSiteDrawingsMutationVariables
  >(ClearSiteDrawingsDocument, variables, options)

export const RevertSiteToEstimatesDocument = `
    mutation RevertSiteToEstimates($input: RevertToEstimatesInput!) {
  site {
    revertToEstimates(input: $input)
  }
}
    `

export const useRevertSiteToEstimatesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RevertSiteToEstimatesMutation,
    TError,
    Types.RevertSiteToEstimatesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RevertSiteToEstimatesMutation,
    TError,
    Types.RevertSiteToEstimatesMutationVariables,
    TContext
  >({
    mutationKey: ["RevertSiteToEstimates"],
    mutationFn: (variables?: Types.RevertSiteToEstimatesMutationVariables) =>
      adminFetcher<
        Types.RevertSiteToEstimatesMutation,
        Types.RevertSiteToEstimatesMutationVariables
      >(RevertSiteToEstimatesDocument, variables)(),
    ...options,
  })
}

useRevertSiteToEstimatesMutation.fetcher = (
  variables: Types.RevertSiteToEstimatesMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.RevertSiteToEstimatesMutation,
    Types.RevertSiteToEstimatesMutationVariables
  >(RevertSiteToEstimatesDocument, variables, options)

export const PromoteTheFinmodelAnywayDocument = `
    mutation PromoteTheFinmodelAnyway($finmodelId: Int!) {
  promoteTheFinmodelAnyway(finmodelId: $finmodelId)
}
    `

export const usePromoteTheFinmodelAnywayMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.PromoteTheFinmodelAnywayMutation,
    TError,
    Types.PromoteTheFinmodelAnywayMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.PromoteTheFinmodelAnywayMutation,
    TError,
    Types.PromoteTheFinmodelAnywayMutationVariables,
    TContext
  >({
    mutationKey: ["PromoteTheFinmodelAnyway"],
    mutationFn: (variables?: Types.PromoteTheFinmodelAnywayMutationVariables) =>
      adminFetcher<
        Types.PromoteTheFinmodelAnywayMutation,
        Types.PromoteTheFinmodelAnywayMutationVariables
      >(PromoteTheFinmodelAnywayDocument, variables)(),
    ...options,
  })
}

usePromoteTheFinmodelAnywayMutation.fetcher = (
  variables: Types.PromoteTheFinmodelAnywayMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.PromoteTheFinmodelAnywayMutation,
    Types.PromoteTheFinmodelAnywayMutationVariables
  >(PromoteTheFinmodelAnywayDocument, variables, options)

export const TurnOffBillingMetersDocument = `
    mutation TurnOffBillingMeters($sitePid: String!) {
  site {
    turnOffBillingMeters(sitePid: $sitePid)
  }
}
    `

export const useTurnOffBillingMetersMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TurnOffBillingMetersMutation,
    TError,
    Types.TurnOffBillingMetersMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TurnOffBillingMetersMutation,
    TError,
    Types.TurnOffBillingMetersMutationVariables,
    TContext
  >({
    mutationKey: ["TurnOffBillingMeters"],
    mutationFn: (variables?: Types.TurnOffBillingMetersMutationVariables) =>
      adminFetcher<
        Types.TurnOffBillingMetersMutation,
        Types.TurnOffBillingMetersMutationVariables
      >(TurnOffBillingMetersDocument, variables)(),
    ...options,
  })
}

useTurnOffBillingMetersMutation.fetcher = (
  variables: Types.TurnOffBillingMetersMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.TurnOffBillingMetersMutation,
    Types.TurnOffBillingMetersMutationVariables
  >(TurnOffBillingMetersDocument, variables, options)

export const UpdateBundleRowMilestonesDocument = `
    mutation UpdateBundleRowMilestones($id: Int!, $preBidMeetingDate: Date, $bidReviewMeetingDate: Date, $contractSignedDate: Date, $ntpDate: Date, $codDate: Date, $reason: CloseReason, $customReason: String) {
  bundleRows {
    setPreBidMeetingDate(id: $id, preBidMeetingDate: $preBidMeetingDate) {
      __typename
    }
    setBidReviewMeetingDate(id: $id, bidReviewMeetingDate: $bidReviewMeetingDate) {
      __typename
    }
    setContractSignedDate(id: $id, contractSignedDate: $contractSignedDate) {
      __typename
    }
    setNtpDate(id: $id, ntpDate: $ntpDate) {
      __typename
    }
    setCodDate(id: $id, codDate: $codDate) {
      __typename
    }
    setCloseReason(bundleRowId: $id, reason: $reason, customReason: $customReason)
  }
}
    `

export const useUpdateBundleRowMilestonesMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UpdateBundleRowMilestonesMutation,
    TError,
    Types.UpdateBundleRowMilestonesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateBundleRowMilestonesMutation,
    TError,
    Types.UpdateBundleRowMilestonesMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateBundleRowMilestones"],
    mutationFn: (variables?: Types.UpdateBundleRowMilestonesMutationVariables) =>
      adminFetcher<
        Types.UpdateBundleRowMilestonesMutation,
        Types.UpdateBundleRowMilestonesMutationVariables
      >(UpdateBundleRowMilestonesDocument, variables)(),
    ...options,
  })
}

useUpdateBundleRowMilestonesMutation.fetcher = (
  variables: Types.UpdateBundleRowMilestonesMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.UpdateBundleRowMilestonesMutation,
    Types.UpdateBundleRowMilestonesMutationVariables
  >(UpdateBundleRowMilestonesDocument, variables, options)

export const FindBuildingFootprintsDocument = `
    mutation FindBuildingFootprints($sitePid: String!, $strategy: FootprintStrategy!) {
  site {
    findBuildingFootprints(sitePid: $sitePid, strategy: $strategy)
  }
}
    `

export const useFindBuildingFootprintsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.FindBuildingFootprintsMutation,
    TError,
    Types.FindBuildingFootprintsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FindBuildingFootprintsMutation,
    TError,
    Types.FindBuildingFootprintsMutationVariables,
    TContext
  >({
    mutationKey: ["FindBuildingFootprints"],
    mutationFn: (variables?: Types.FindBuildingFootprintsMutationVariables) =>
      adminFetcher<
        Types.FindBuildingFootprintsMutation,
        Types.FindBuildingFootprintsMutationVariables
      >(FindBuildingFootprintsDocument, variables)(),
    ...options,
  })
}

useFindBuildingFootprintsMutation.fetcher = (
  variables: Types.FindBuildingFootprintsMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.FindBuildingFootprintsMutation,
    Types.FindBuildingFootprintsMutationVariables
  >(FindBuildingFootprintsDocument, variables, options)

export const CreateHostingCapacityMapDocument = `
    mutation CreateHostingCapacityMap($utilityPid: String!, $hostingCapacityMap: HostingCapacityMapInputs!) {
  hostingCapacityMaps {
    create(utilityPid: $utilityPid, inputs: $hostingCapacityMap)
  }
}
    `

export const useCreateHostingCapacityMapMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateHostingCapacityMapMutation,
    TError,
    Types.CreateHostingCapacityMapMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateHostingCapacityMapMutation,
    TError,
    Types.CreateHostingCapacityMapMutationVariables,
    TContext
  >({
    mutationKey: ["CreateHostingCapacityMap"],
    mutationFn: (variables?: Types.CreateHostingCapacityMapMutationVariables) =>
      adminFetcher<
        Types.CreateHostingCapacityMapMutation,
        Types.CreateHostingCapacityMapMutationVariables
      >(CreateHostingCapacityMapDocument, variables)(),
    ...options,
  })
}

useCreateHostingCapacityMapMutation.fetcher = (
  variables: Types.CreateHostingCapacityMapMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.CreateHostingCapacityMapMutation,
    Types.CreateHostingCapacityMapMutationVariables
  >(CreateHostingCapacityMapDocument, variables, options)

export const UpdateHostingCapacityMapDocument = `
    mutation UpdateHostingCapacityMap($mapId: Int!, $hostingCapacityMap: HostingCapacityMapInputs!) {
  hostingCapacityMaps {
    update(mapId: $mapId, inputs: $hostingCapacityMap)
  }
}
    `

export const useUpdateHostingCapacityMapMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UpdateHostingCapacityMapMutation,
    TError,
    Types.UpdateHostingCapacityMapMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateHostingCapacityMapMutation,
    TError,
    Types.UpdateHostingCapacityMapMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateHostingCapacityMap"],
    mutationFn: (variables?: Types.UpdateHostingCapacityMapMutationVariables) =>
      adminFetcher<
        Types.UpdateHostingCapacityMapMutation,
        Types.UpdateHostingCapacityMapMutationVariables
      >(UpdateHostingCapacityMapDocument, variables)(),
    ...options,
  })
}

useUpdateHostingCapacityMapMutation.fetcher = (
  variables: Types.UpdateHostingCapacityMapMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.UpdateHostingCapacityMapMutation,
    Types.UpdateHostingCapacityMapMutationVariables
  >(UpdateHostingCapacityMapDocument, variables, options)

export const DeleteHostingCapacityMapDocument = `
    mutation DeleteHostingCapacityMap($mapId: Int!) {
  hostingCapacityMaps {
    delete(mapId: $mapId)
  }
}
    `

export const useDeleteHostingCapacityMapMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.DeleteHostingCapacityMapMutation,
    TError,
    Types.DeleteHostingCapacityMapMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteHostingCapacityMapMutation,
    TError,
    Types.DeleteHostingCapacityMapMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteHostingCapacityMap"],
    mutationFn: (variables?: Types.DeleteHostingCapacityMapMutationVariables) =>
      adminFetcher<
        Types.DeleteHostingCapacityMapMutation,
        Types.DeleteHostingCapacityMapMutationVariables
      >(DeleteHostingCapacityMapDocument, variables)(),
    ...options,
  })
}

useDeleteHostingCapacityMapMutation.fetcher = (
  variables: Types.DeleteHostingCapacityMapMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.DeleteHostingCapacityMapMutation,
    Types.DeleteHostingCapacityMapMutationVariables
  >(DeleteHostingCapacityMapDocument, variables, options)

export const CreateEmailDraftDocument = `
    mutation CreateEmailDraft($draftParams: CreateDraftInput!) {
  googleApi {
    createDraft(draftParams: $draftParams)
  }
}
    `

export const useCreateEmailDraftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateEmailDraftMutation,
    TError,
    Types.CreateEmailDraftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateEmailDraftMutation,
    TError,
    Types.CreateEmailDraftMutationVariables,
    TContext
  >({
    mutationKey: ["CreateEmailDraft"],
    mutationFn: (variables?: Types.CreateEmailDraftMutationVariables) =>
      adminFetcher<
        Types.CreateEmailDraftMutation,
        Types.CreateEmailDraftMutationVariables
      >(CreateEmailDraftDocument, variables)(),
    ...options,
  })
}

useCreateEmailDraftMutation.fetcher = (
  variables: Types.CreateEmailDraftMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.CreateEmailDraftMutation, Types.CreateEmailDraftMutationVariables>(
    CreateEmailDraftDocument,
    variables,
    options,
  )

export const UpdateBundleParentRowDocument = `
    mutation UpdateBundleParentRow($bundleParentRowId: Int!, $inputs: MarketplaceRowInputs!) {
  marketplace {
    updateBundleParentRow(bundleParentRowId: $bundleParentRowId, inputs: $inputs) {
      __typename
      ... on BundleParentRow {
        id
      }
      ... on Error {
        message
      }
    }
  }
}
    `

export const useUpdateBundleParentRowMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateBundleParentRowMutation,
    TError,
    Types.UpdateBundleParentRowMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateBundleParentRowMutation,
    TError,
    Types.UpdateBundleParentRowMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateBundleParentRow"],
    mutationFn: (variables?: Types.UpdateBundleParentRowMutationVariables) =>
      adminFetcher<
        Types.UpdateBundleParentRowMutation,
        Types.UpdateBundleParentRowMutationVariables
      >(UpdateBundleParentRowDocument, variables)(),
    ...options,
  })
}

useUpdateBundleParentRowMutation.fetcher = (
  variables: Types.UpdateBundleParentRowMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.UpdateBundleParentRowMutation,
    Types.UpdateBundleParentRowMutationVariables
  >(UpdateBundleParentRowDocument, variables, options)

export const GetVersionDocument = `
    query GetVersion {
  schemaVersion {
    version
  }
}
    `

export const useGetVersionQuery = <TData = Types.GetVersionQuery, TError = unknown>(
  variables?: Types.GetVersionQueryVariables,
  options?: Omit<UseQueryOptions<Types.GetVersionQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.GetVersionQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetVersionQuery, TError, TData>({
    queryKey: variables === undefined ? ["GetVersion"] : ["GetVersion", variables],
    queryFn: adminFetcher<Types.GetVersionQuery, Types.GetVersionQueryVariables>(
      GetVersionDocument,
      variables,
    ),
    ...options,
  })
}

useGetVersionQuery.getKey = (variables?: Types.GetVersionQueryVariables) =>
  variables === undefined ? ["GetVersion"] : ["GetVersion", variables]

useGetVersionQuery.rootKey = "GetVersion"

export const useSuspenseGetVersionQuery = <
  TData = Types.GetVersionQuery,
  TError = unknown,
>(
  variables?: Types.GetVersionQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetVersionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.GetVersionQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetVersionQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GetVersionSuspense"]
        : ["GetVersionSuspense", variables],
    queryFn: adminFetcher<Types.GetVersionQuery, Types.GetVersionQueryVariables>(
      GetVersionDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseGetVersionQuery.getKey = (variables?: Types.GetVersionQueryVariables) =>
  variables === undefined ? ["GetVersionSuspense"] : ["GetVersionSuspense", variables]

useSuspenseGetVersionQuery.rootKey = "GetVersion"

export const useInfiniteGetVersionQuery = <
  TData = InfiniteData<Types.GetVersionQuery>,
  TError = unknown,
>(
  variables: Types.GetVersionQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetVersionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.GetVersionQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetVersionQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GetVersion.infinite"]
            : ["GetVersion.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.GetVersionQuery, Types.GetVersionQueryVariables>(
            GetVersionDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetVersionQuery.getKey = (variables?: Types.GetVersionQueryVariables) =>
  variables === undefined ? ["GetVersion.infinite"] : ["GetVersion.infinite", variables]

useInfiniteGetVersionQuery.rootKey = "GetVersion.infinite"

export const useSuspenseInfiniteGetVersionQuery = <
  TData = InfiniteData<Types.GetVersionQuery>,
  TError = unknown,
>(
  variables: Types.GetVersionQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetVersionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetVersionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetVersionQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GetVersion.infiniteSuspense"]
            : ["GetVersion.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.GetVersionQuery, Types.GetVersionQueryVariables>(
            GetVersionDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetVersionQuery.getKey = (
  variables?: Types.GetVersionQueryVariables,
) =>
  variables === undefined
    ? ["GetVersion.infiniteSuspense"]
    : ["GetVersion.infiniteSuspense", variables]

useSuspenseInfiniteGetVersionQuery.rootKey = "GetVersion.infinite"

useGetVersionQuery.fetcher = (
  variables?: Types.GetVersionQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.GetVersionQuery, Types.GetVersionQueryVariables>(
    GetVersionDocument,
    variables,
    options,
  )

export const CurrentAdminUserDocument = `
    query CurrentAdminUser {
  currentUser {
    pid
    isAuthenticated
    isAdmin
  }
}
    `

export const useCurrentAdminUserQuery = <
  TData = Types.CurrentAdminUserQuery,
  TError = unknown,
>(
  variables?: Types.CurrentAdminUserQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CurrentAdminUserQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.CurrentAdminUserQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CurrentAdminUserQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["CurrentAdminUser"] : ["CurrentAdminUser", variables],
    queryFn: adminFetcher<
      Types.CurrentAdminUserQuery,
      Types.CurrentAdminUserQueryVariables
    >(CurrentAdminUserDocument, variables),
    ...options,
  })
}

useCurrentAdminUserQuery.getKey = (variables?: Types.CurrentAdminUserQueryVariables) =>
  variables === undefined ? ["CurrentAdminUser"] : ["CurrentAdminUser", variables]

useCurrentAdminUserQuery.rootKey = "CurrentAdminUser"

export const useSuspenseCurrentAdminUserQuery = <
  TData = Types.CurrentAdminUserQuery,
  TError = unknown,
>(
  variables?: Types.CurrentAdminUserQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CurrentAdminUserQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CurrentAdminUserQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CurrentAdminUserQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["CurrentAdminUserSuspense"]
        : ["CurrentAdminUserSuspense", variables],
    queryFn: adminFetcher<
      Types.CurrentAdminUserQuery,
      Types.CurrentAdminUserQueryVariables
    >(CurrentAdminUserDocument, variables),
    ...options,
  })
}

useSuspenseCurrentAdminUserQuery.getKey = (
  variables?: Types.CurrentAdminUserQueryVariables,
) =>
  variables === undefined
    ? ["CurrentAdminUserSuspense"]
    : ["CurrentAdminUserSuspense", variables]

useSuspenseCurrentAdminUserQuery.rootKey = "CurrentAdminUser"

export const useInfiniteCurrentAdminUserQuery = <
  TData = InfiniteData<Types.CurrentAdminUserQuery>,
  TError = unknown,
>(
  variables: Types.CurrentAdminUserQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CurrentAdminUserQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CurrentAdminUserQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CurrentAdminUserQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CurrentAdminUser.infinite"]
            : ["CurrentAdminUser.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.CurrentAdminUserQuery,
            Types.CurrentAdminUserQueryVariables
          >(CurrentAdminUserDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCurrentAdminUserQuery.getKey = (
  variables?: Types.CurrentAdminUserQueryVariables,
) =>
  variables === undefined
    ? ["CurrentAdminUser.infinite"]
    : ["CurrentAdminUser.infinite", variables]

useInfiniteCurrentAdminUserQuery.rootKey = "CurrentAdminUser.infinite"

export const useSuspenseInfiniteCurrentAdminUserQuery = <
  TData = InfiniteData<Types.CurrentAdminUserQuery>,
  TError = unknown,
>(
  variables: Types.CurrentAdminUserQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CurrentAdminUserQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CurrentAdminUserQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CurrentAdminUserQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CurrentAdminUser.infiniteSuspense"]
            : ["CurrentAdminUser.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.CurrentAdminUserQuery,
            Types.CurrentAdminUserQueryVariables
          >(CurrentAdminUserDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCurrentAdminUserQuery.getKey = (
  variables?: Types.CurrentAdminUserQueryVariables,
) =>
  variables === undefined
    ? ["CurrentAdminUser.infiniteSuspense"]
    : ["CurrentAdminUser.infiniteSuspense", variables]

useSuspenseInfiniteCurrentAdminUserQuery.rootKey = "CurrentAdminUser.infinite"

useCurrentAdminUserQuery.fetcher = (
  variables?: Types.CurrentAdminUserQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.CurrentAdminUserQuery, Types.CurrentAdminUserQueryVariables>(
    CurrentAdminUserDocument,
    variables,
    options,
  )

export const AdminPortfolioListDocument = `
    query AdminPortfolioList {
  portfolios {
    pid
    title
    numSites
    org {
      pid
      name
    }
  }
}
    `

export const useAdminPortfolioListQuery = <
  TData = Types.AdminPortfolioListQuery,
  TError = unknown,
>(
  variables?: Types.AdminPortfolioListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminPortfolioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminPortfolioListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminPortfolioListQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminPortfolioList"]
        : ["AdminPortfolioList", variables],
    queryFn: adminFetcher<
      Types.AdminPortfolioListQuery,
      Types.AdminPortfolioListQueryVariables
    >(AdminPortfolioListDocument, variables),
    ...options,
  })
}

useAdminPortfolioListQuery.getKey = (
  variables?: Types.AdminPortfolioListQueryVariables,
) =>
  variables === undefined ? ["AdminPortfolioList"] : ["AdminPortfolioList", variables]

useAdminPortfolioListQuery.rootKey = "AdminPortfolioList"

export const useSuspenseAdminPortfolioListQuery = <
  TData = Types.AdminPortfolioListQuery,
  TError = unknown,
>(
  variables?: Types.AdminPortfolioListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminPortfolioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminPortfolioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminPortfolioListQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminPortfolioListSuspense"]
        : ["AdminPortfolioListSuspense", variables],
    queryFn: adminFetcher<
      Types.AdminPortfolioListQuery,
      Types.AdminPortfolioListQueryVariables
    >(AdminPortfolioListDocument, variables),
    ...options,
  })
}

useSuspenseAdminPortfolioListQuery.getKey = (
  variables?: Types.AdminPortfolioListQueryVariables,
) =>
  variables === undefined
    ? ["AdminPortfolioListSuspense"]
    : ["AdminPortfolioListSuspense", variables]

useSuspenseAdminPortfolioListQuery.rootKey = "AdminPortfolioList"

export const useInfiniteAdminPortfolioListQuery = <
  TData = InfiniteData<Types.AdminPortfolioListQuery>,
  TError = unknown,
>(
  variables: Types.AdminPortfolioListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminPortfolioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminPortfolioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminPortfolioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminPortfolioList.infinite"]
            : ["AdminPortfolioList.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminPortfolioListQuery,
            Types.AdminPortfolioListQueryVariables
          >(AdminPortfolioListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminPortfolioListQuery.getKey = (
  variables?: Types.AdminPortfolioListQueryVariables,
) =>
  variables === undefined
    ? ["AdminPortfolioList.infinite"]
    : ["AdminPortfolioList.infinite", variables]

useInfiniteAdminPortfolioListQuery.rootKey = "AdminPortfolioList.infinite"

export const useSuspenseInfiniteAdminPortfolioListQuery = <
  TData = InfiniteData<Types.AdminPortfolioListQuery>,
  TError = unknown,
>(
  variables: Types.AdminPortfolioListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminPortfolioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminPortfolioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminPortfolioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminPortfolioList.infiniteSuspense"]
            : ["AdminPortfolioList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminPortfolioListQuery,
            Types.AdminPortfolioListQueryVariables
          >(AdminPortfolioListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminPortfolioListQuery.getKey = (
  variables?: Types.AdminPortfolioListQueryVariables,
) =>
  variables === undefined
    ? ["AdminPortfolioList.infiniteSuspense"]
    : ["AdminPortfolioList.infiniteSuspense", variables]

useSuspenseInfiniteAdminPortfolioListQuery.rootKey = "AdminPortfolioList.infinite"

useAdminPortfolioListQuery.fetcher = (
  variables?: Types.AdminPortfolioListQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AdminPortfolioListQuery, Types.AdminPortfolioListQueryVariables>(
    AdminPortfolioListDocument,
    variables,
    options,
  )

export const AdminOrgBySlugDocument = `
    query AdminOrgBySlug($slug: String, $pid: String) {
  orgBySlug(slug: $slug, pid: $pid) {
    pid
    slug
  }
}
    `

export const useAdminOrgBySlugQuery = <
  TData = Types.AdminOrgBySlugQuery,
  TError = unknown,
>(
  variables?: Types.AdminOrgBySlugQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminOrgBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminOrgBySlugQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminOrgBySlugQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["AdminOrgBySlug"] : ["AdminOrgBySlug", variables],
    queryFn: adminFetcher<
      Types.AdminOrgBySlugQuery,
      Types.AdminOrgBySlugQueryVariables
    >(AdminOrgBySlugDocument, variables),
    ...options,
  })
}

useAdminOrgBySlugQuery.getKey = (variables?: Types.AdminOrgBySlugQueryVariables) =>
  variables === undefined ? ["AdminOrgBySlug"] : ["AdminOrgBySlug", variables]

useAdminOrgBySlugQuery.rootKey = "AdminOrgBySlug"

export const useSuspenseAdminOrgBySlugQuery = <
  TData = Types.AdminOrgBySlugQuery,
  TError = unknown,
>(
  variables?: Types.AdminOrgBySlugQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminOrgBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminOrgBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminOrgBySlugQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminOrgBySlugSuspense"]
        : ["AdminOrgBySlugSuspense", variables],
    queryFn: adminFetcher<
      Types.AdminOrgBySlugQuery,
      Types.AdminOrgBySlugQueryVariables
    >(AdminOrgBySlugDocument, variables),
    ...options,
  })
}

useSuspenseAdminOrgBySlugQuery.getKey = (
  variables?: Types.AdminOrgBySlugQueryVariables,
) =>
  variables === undefined
    ? ["AdminOrgBySlugSuspense"]
    : ["AdminOrgBySlugSuspense", variables]

useSuspenseAdminOrgBySlugQuery.rootKey = "AdminOrgBySlug"

export const useInfiniteAdminOrgBySlugQuery = <
  TData = InfiniteData<Types.AdminOrgBySlugQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgBySlugQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminOrgBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminOrgBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminOrgBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminOrgBySlug.infinite"]
            : ["AdminOrgBySlug.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.AdminOrgBySlugQuery, Types.AdminOrgBySlugQueryVariables>(
            AdminOrgBySlugDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminOrgBySlugQuery.getKey = (
  variables?: Types.AdminOrgBySlugQueryVariables,
) =>
  variables === undefined
    ? ["AdminOrgBySlug.infinite"]
    : ["AdminOrgBySlug.infinite", variables]

useInfiniteAdminOrgBySlugQuery.rootKey = "AdminOrgBySlug.infinite"

export const useSuspenseInfiniteAdminOrgBySlugQuery = <
  TData = InfiniteData<Types.AdminOrgBySlugQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgBySlugQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminOrgBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminOrgBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminOrgBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminOrgBySlug.infiniteSuspense"]
            : ["AdminOrgBySlug.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.AdminOrgBySlugQuery, Types.AdminOrgBySlugQueryVariables>(
            AdminOrgBySlugDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminOrgBySlugQuery.getKey = (
  variables?: Types.AdminOrgBySlugQueryVariables,
) =>
  variables === undefined
    ? ["AdminOrgBySlug.infiniteSuspense"]
    : ["AdminOrgBySlug.infiniteSuspense", variables]

useSuspenseInfiniteAdminOrgBySlugQuery.rootKey = "AdminOrgBySlug.infinite"

useAdminOrgBySlugQuery.fetcher = (
  variables?: Types.AdminOrgBySlugQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AdminOrgBySlugQuery, Types.AdminOrgBySlugQueryVariables>(
    AdminOrgBySlugDocument,
    variables,
    options,
  )

export const OrgsAndPortfoliosDocument = `
    query OrgsAndPortfolios {
  orgs {
    pid
    name
  }
  portfolios {
    pid
    title
    numSites
    org {
      pid
    }
  }
}
    `

export const useOrgsAndPortfoliosQuery = <
  TData = Types.OrgsAndPortfoliosQuery,
  TError = unknown,
>(
  variables?: Types.OrgsAndPortfoliosQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgsAndPortfoliosQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.OrgsAndPortfoliosQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgsAndPortfoliosQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgsAndPortfolios"]
        : ["OrgsAndPortfolios", variables],
    queryFn: adminFetcher<
      Types.OrgsAndPortfoliosQuery,
      Types.OrgsAndPortfoliosQueryVariables
    >(OrgsAndPortfoliosDocument, variables),
    ...options,
  })
}

useOrgsAndPortfoliosQuery.getKey = (
  variables?: Types.OrgsAndPortfoliosQueryVariables,
) =>
  variables === undefined ? ["OrgsAndPortfolios"] : ["OrgsAndPortfolios", variables]

useOrgsAndPortfoliosQuery.rootKey = "OrgsAndPortfolios"

export const useSuspenseOrgsAndPortfoliosQuery = <
  TData = Types.OrgsAndPortfoliosQuery,
  TError = unknown,
>(
  variables?: Types.OrgsAndPortfoliosQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgsAndPortfoliosQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgsAndPortfoliosQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgsAndPortfoliosQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgsAndPortfoliosSuspense"]
        : ["OrgsAndPortfoliosSuspense", variables],
    queryFn: adminFetcher<
      Types.OrgsAndPortfoliosQuery,
      Types.OrgsAndPortfoliosQueryVariables
    >(OrgsAndPortfoliosDocument, variables),
    ...options,
  })
}

useSuspenseOrgsAndPortfoliosQuery.getKey = (
  variables?: Types.OrgsAndPortfoliosQueryVariables,
) =>
  variables === undefined
    ? ["OrgsAndPortfoliosSuspense"]
    : ["OrgsAndPortfoliosSuspense", variables]

useSuspenseOrgsAndPortfoliosQuery.rootKey = "OrgsAndPortfolios"

export const useInfiniteOrgsAndPortfoliosQuery = <
  TData = InfiniteData<Types.OrgsAndPortfoliosQuery>,
  TError = unknown,
>(
  variables: Types.OrgsAndPortfoliosQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgsAndPortfoliosQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgsAndPortfoliosQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgsAndPortfoliosQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgsAndPortfolios.infinite"]
            : ["OrgsAndPortfolios.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.OrgsAndPortfoliosQuery,
            Types.OrgsAndPortfoliosQueryVariables
          >(OrgsAndPortfoliosDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgsAndPortfoliosQuery.getKey = (
  variables?: Types.OrgsAndPortfoliosQueryVariables,
) =>
  variables === undefined
    ? ["OrgsAndPortfolios.infinite"]
    : ["OrgsAndPortfolios.infinite", variables]

useInfiniteOrgsAndPortfoliosQuery.rootKey = "OrgsAndPortfolios.infinite"

export const useSuspenseInfiniteOrgsAndPortfoliosQuery = <
  TData = InfiniteData<Types.OrgsAndPortfoliosQuery>,
  TError = unknown,
>(
  variables: Types.OrgsAndPortfoliosQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgsAndPortfoliosQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgsAndPortfoliosQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgsAndPortfoliosQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgsAndPortfolios.infiniteSuspense"]
            : ["OrgsAndPortfolios.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.OrgsAndPortfoliosQuery,
            Types.OrgsAndPortfoliosQueryVariables
          >(OrgsAndPortfoliosDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgsAndPortfoliosQuery.getKey = (
  variables?: Types.OrgsAndPortfoliosQueryVariables,
) =>
  variables === undefined
    ? ["OrgsAndPortfolios.infiniteSuspense"]
    : ["OrgsAndPortfolios.infiniteSuspense", variables]

useSuspenseInfiniteOrgsAndPortfoliosQuery.rootKey = "OrgsAndPortfolios.infinite"

useOrgsAndPortfoliosQuery.fetcher = (
  variables?: Types.OrgsAndPortfoliosQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.OrgsAndPortfoliosQuery, Types.OrgsAndPortfoliosQueryVariables>(
    OrgsAndPortfoliosDocument,
    variables,
    options,
  )

export const GetEnergyProductionDocument = `
    query GetEnergyProduction {
  energy {
    sites {
      name
      daysActive
      kwhLifetime
    }
    kwhPerDay {
      day
      kwh
    }
    currentTime
    kwhAllTime
    kwhToday
    projectedNextInterval
  }
}
    `

export const useGetEnergyProductionQuery = <
  TData = Types.GetEnergyProductionQuery,
  TError = unknown,
>(
  variables?: Types.GetEnergyProductionQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetEnergyProductionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetEnergyProductionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.GetEnergyProductionQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GetEnergyProduction"]
        : ["GetEnergyProduction", variables],
    queryFn: adminFetcher<
      Types.GetEnergyProductionQuery,
      Types.GetEnergyProductionQueryVariables
    >(GetEnergyProductionDocument, variables),
    ...options,
  })
}

useGetEnergyProductionQuery.getKey = (
  variables?: Types.GetEnergyProductionQueryVariables,
) =>
  variables === undefined ? ["GetEnergyProduction"] : ["GetEnergyProduction", variables]

useGetEnergyProductionQuery.rootKey = "GetEnergyProduction"

export const useSuspenseGetEnergyProductionQuery = <
  TData = Types.GetEnergyProductionQuery,
  TError = unknown,
>(
  variables?: Types.GetEnergyProductionQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetEnergyProductionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetEnergyProductionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetEnergyProductionQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GetEnergyProductionSuspense"]
        : ["GetEnergyProductionSuspense", variables],
    queryFn: adminFetcher<
      Types.GetEnergyProductionQuery,
      Types.GetEnergyProductionQueryVariables
    >(GetEnergyProductionDocument, variables),
    ...options,
  })
}

useSuspenseGetEnergyProductionQuery.getKey = (
  variables?: Types.GetEnergyProductionQueryVariables,
) =>
  variables === undefined
    ? ["GetEnergyProductionSuspense"]
    : ["GetEnergyProductionSuspense", variables]

useSuspenseGetEnergyProductionQuery.rootKey = "GetEnergyProduction"

export const useInfiniteGetEnergyProductionQuery = <
  TData = InfiniteData<Types.GetEnergyProductionQuery>,
  TError = unknown,
>(
  variables: Types.GetEnergyProductionQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetEnergyProductionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetEnergyProductionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetEnergyProductionQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GetEnergyProduction.infinite"]
            : ["GetEnergyProduction.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.GetEnergyProductionQuery,
            Types.GetEnergyProductionQueryVariables
          >(GetEnergyProductionDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetEnergyProductionQuery.getKey = (
  variables?: Types.GetEnergyProductionQueryVariables,
) =>
  variables === undefined
    ? ["GetEnergyProduction.infinite"]
    : ["GetEnergyProduction.infinite", variables]

useInfiniteGetEnergyProductionQuery.rootKey = "GetEnergyProduction.infinite"

export const useSuspenseInfiniteGetEnergyProductionQuery = <
  TData = InfiniteData<Types.GetEnergyProductionQuery>,
  TError = unknown,
>(
  variables: Types.GetEnergyProductionQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetEnergyProductionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetEnergyProductionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetEnergyProductionQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GetEnergyProduction.infiniteSuspense"]
            : ["GetEnergyProduction.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.GetEnergyProductionQuery,
            Types.GetEnergyProductionQueryVariables
          >(GetEnergyProductionDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetEnergyProductionQuery.getKey = (
  variables?: Types.GetEnergyProductionQueryVariables,
) =>
  variables === undefined
    ? ["GetEnergyProduction.infiniteSuspense"]
    : ["GetEnergyProduction.infiniteSuspense", variables]

useSuspenseInfiniteGetEnergyProductionQuery.rootKey = "GetEnergyProduction.infinite"

useGetEnergyProductionQuery.fetcher = (
  variables?: Types.GetEnergyProductionQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.GetEnergyProductionQuery, Types.GetEnergyProductionQueryVariables>(
    GetEnergyProductionDocument,
    variables,
    options,
  )

export const MultiPortfolioSitesDocument = `
    query MultiPortfolioSites($portfolioPids: [String!]!, $statuses: [SiteStatus!]!) {
  sitesInPortfolios(portfolioPids: $portfolioPids, statuses: $statuses) {
    ...SitesTable
    portfolio {
      pid
      title
    }
  }
}
    ${SitesTableFragmentDoc}`

export const useMultiPortfolioSitesQuery = <
  TData = Types.MultiPortfolioSitesQuery,
  TError = unknown,
>(
  variables: Types.MultiPortfolioSitesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MultiPortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MultiPortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MultiPortfolioSitesQuery, TError, TData>({
    queryKey: ["MultiPortfolioSites", variables],
    queryFn: adminFetcher<
      Types.MultiPortfolioSitesQuery,
      Types.MultiPortfolioSitesQueryVariables
    >(MultiPortfolioSitesDocument, variables),
    ...options,
  })
}

useMultiPortfolioSitesQuery.getKey = (
  variables: Types.MultiPortfolioSitesQueryVariables,
) => ["MultiPortfolioSites", variables]

useMultiPortfolioSitesQuery.rootKey = "MultiPortfolioSites"

export const useSuspenseMultiPortfolioSitesQuery = <
  TData = Types.MultiPortfolioSitesQuery,
  TError = unknown,
>(
  variables: Types.MultiPortfolioSitesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MultiPortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MultiPortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MultiPortfolioSitesQuery, TError, TData>({
    queryKey: ["MultiPortfolioSitesSuspense", variables],
    queryFn: adminFetcher<
      Types.MultiPortfolioSitesQuery,
      Types.MultiPortfolioSitesQueryVariables
    >(MultiPortfolioSitesDocument, variables),
    ...options,
  })
}

useSuspenseMultiPortfolioSitesQuery.getKey = (
  variables: Types.MultiPortfolioSitesQueryVariables,
) => ["MultiPortfolioSitesSuspense", variables]

useSuspenseMultiPortfolioSitesQuery.rootKey = "MultiPortfolioSites"

export const useInfiniteMultiPortfolioSitesQuery = <
  TData = InfiniteData<Types.MultiPortfolioSitesQuery>,
  TError = unknown,
>(
  variables: Types.MultiPortfolioSitesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MultiPortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MultiPortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MultiPortfolioSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiPortfolioSites.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.MultiPortfolioSitesQuery,
            Types.MultiPortfolioSitesQueryVariables
          >(MultiPortfolioSitesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMultiPortfolioSitesQuery.getKey = (
  variables: Types.MultiPortfolioSitesQueryVariables,
) => ["MultiPortfolioSites.infinite", variables]

useInfiniteMultiPortfolioSitesQuery.rootKey = "MultiPortfolioSites.infinite"

export const useSuspenseInfiniteMultiPortfolioSitesQuery = <
  TData = InfiniteData<Types.MultiPortfolioSitesQuery>,
  TError = unknown,
>(
  variables: Types.MultiPortfolioSitesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MultiPortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MultiPortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MultiPortfolioSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MultiPortfolioSites.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.MultiPortfolioSitesQuery,
            Types.MultiPortfolioSitesQueryVariables
          >(MultiPortfolioSitesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMultiPortfolioSitesQuery.getKey = (
  variables: Types.MultiPortfolioSitesQueryVariables,
) => ["MultiPortfolioSites.infiniteSuspense", variables]

useSuspenseInfiniteMultiPortfolioSitesQuery.rootKey = "MultiPortfolioSites.infinite"

useMultiPortfolioSitesQuery.fetcher = (
  variables: Types.MultiPortfolioSitesQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.MultiPortfolioSitesQuery, Types.MultiPortfolioSitesQueryVariables>(
    MultiPortfolioSitesDocument,
    variables,
    options,
  )

export const PortfolioSitesDocument = `
    query PortfolioSites($pid: String!, $sitePids: [String!]!) {
  portfolio(pid: $pid) {
    pid
    sites(sitePids: $sitePids) {
      ...SitesTable
    }
  }
}
    ${SitesTableFragmentDoc}`

export const usePortfolioSitesQuery = <
  TData = Types.PortfolioSitesQuery,
  TError = unknown,
>(
  variables: Types.PortfolioSitesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.PortfolioSitesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.PortfolioSitesQuery, TError, TData>({
    queryKey: ["PortfolioSites", variables],
    queryFn: adminFetcher<
      Types.PortfolioSitesQuery,
      Types.PortfolioSitesQueryVariables
    >(PortfolioSitesDocument, variables),
    ...options,
  })
}

usePortfolioSitesQuery.getKey = (variables: Types.PortfolioSitesQueryVariables) => [
  "PortfolioSites",
  variables,
]

usePortfolioSitesQuery.rootKey = "PortfolioSites"

export const useSuspensePortfolioSitesQuery = <
  TData = Types.PortfolioSitesQuery,
  TError = unknown,
>(
  variables: Types.PortfolioSitesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PortfolioSitesQuery, TError, TData>({
    queryKey: ["PortfolioSitesSuspense", variables],
    queryFn: adminFetcher<
      Types.PortfolioSitesQuery,
      Types.PortfolioSitesQueryVariables
    >(PortfolioSitesDocument, variables),
    ...options,
  })
}

useSuspensePortfolioSitesQuery.getKey = (
  variables: Types.PortfolioSitesQueryVariables,
) => ["PortfolioSitesSuspense", variables]

useSuspensePortfolioSitesQuery.rootKey = "PortfolioSites"

export const useInfinitePortfolioSitesQuery = <
  TData = InfiniteData<Types.PortfolioSitesQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioSitesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PortfolioSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioSites.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.PortfolioSitesQuery, Types.PortfolioSitesQueryVariables>(
            PortfolioSitesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePortfolioSitesQuery.getKey = (
  variables: Types.PortfolioSitesQueryVariables,
) => ["PortfolioSites.infinite", variables]

useInfinitePortfolioSitesQuery.rootKey = "PortfolioSites.infinite"

export const useSuspenseInfinitePortfolioSitesQuery = <
  TData = InfiniteData<Types.PortfolioSitesQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioSitesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PortfolioSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioSites.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.PortfolioSitesQuery, Types.PortfolioSitesQueryVariables>(
            PortfolioSitesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePortfolioSitesQuery.getKey = (
  variables: Types.PortfolioSitesQueryVariables,
) => ["PortfolioSites.infiniteSuspense", variables]

useSuspenseInfinitePortfolioSitesQuery.rootKey = "PortfolioSites.infinite"

usePortfolioSitesQuery.fetcher = (
  variables: Types.PortfolioSitesQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.PortfolioSitesQuery, Types.PortfolioSitesQueryVariables>(
    PortfolioSitesDocument,
    variables,
    options,
  )

export const SitesDocument = `
    query Sites($sitePids: [String!]!) {
  sites(sitePids: $sitePids) {
    ...SitesTable
  }
}
    ${SitesTableFragmentDoc}`

export const useSitesQuery = <TData = Types.SitesQuery, TError = unknown>(
  variables: Types.SitesQueryVariables,
  options?: Omit<UseQueryOptions<Types.SitesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SitesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SitesQuery, TError, TData>({
    queryKey: ["Sites", variables],
    queryFn: adminFetcher<Types.SitesQuery, Types.SitesQueryVariables>(
      SitesDocument,
      variables,
    ),
    ...options,
  })
}

useSitesQuery.getKey = (variables: Types.SitesQueryVariables) => ["Sites", variables]

useSitesQuery.rootKey = "Sites"

export const useSuspenseSitesQuery = <TData = Types.SitesQuery, TError = unknown>(
  variables: Types.SitesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SitesQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SitesQuery, TError, TData>({
    queryKey: ["SitesSuspense", variables],
    queryFn: adminFetcher<Types.SitesQuery, Types.SitesQueryVariables>(
      SitesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSitesQuery.getKey = (variables: Types.SitesQueryVariables) => [
  "SitesSuspense",
  variables,
]

useSuspenseSitesQuery.rootKey = "Sites"

export const useInfiniteSitesQuery = <
  TData = InfiniteData<Types.SitesQuery>,
  TError = unknown,
>(
  variables: Types.SitesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SitesQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["Sites.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SitesQuery, Types.SitesQueryVariables>(SitesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSitesQuery.getKey = (variables: Types.SitesQueryVariables) => [
  "Sites.infinite",
  variables,
]

useInfiniteSitesQuery.rootKey = "Sites.infinite"

export const useSuspenseInfiniteSitesQuery = <
  TData = InfiniteData<Types.SitesQuery>,
  TError = unknown,
>(
  variables: Types.SitesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["Sites.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SitesQuery, Types.SitesQueryVariables>(SitesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSitesQuery.getKey = (variables: Types.SitesQueryVariables) => [
  "Sites.infiniteSuspense",
  variables,
]

useSuspenseInfiniteSitesQuery.rootKey = "Sites.infinite"

useSitesQuery.fetcher = (
  variables: Types.SitesQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SitesQuery, Types.SitesQueryVariables>(
    SitesDocument,
    variables,
    options,
  )

export const GetPortfolioSiteRefsDocument = `
    query GetPortfolioSiteRefs($pid: String!) {
  portfolio(pid: $pid) {
    pid
    siteRefs {
      pid
      name
      streetAddress
    }
  }
}
    `

export const useGetPortfolioSiteRefsQuery = <
  TData = Types.GetPortfolioSiteRefsQuery,
  TError = unknown,
>(
  variables: Types.GetPortfolioSiteRefsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetPortfolioSiteRefsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetPortfolioSiteRefsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.GetPortfolioSiteRefsQuery, TError, TData>({
    queryKey: ["GetPortfolioSiteRefs", variables],
    queryFn: adminFetcher<
      Types.GetPortfolioSiteRefsQuery,
      Types.GetPortfolioSiteRefsQueryVariables
    >(GetPortfolioSiteRefsDocument, variables),
    ...options,
  })
}

useGetPortfolioSiteRefsQuery.getKey = (
  variables: Types.GetPortfolioSiteRefsQueryVariables,
) => ["GetPortfolioSiteRefs", variables]

useGetPortfolioSiteRefsQuery.rootKey = "GetPortfolioSiteRefs"

export const useSuspenseGetPortfolioSiteRefsQuery = <
  TData = Types.GetPortfolioSiteRefsQuery,
  TError = unknown,
>(
  variables: Types.GetPortfolioSiteRefsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetPortfolioSiteRefsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetPortfolioSiteRefsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetPortfolioSiteRefsQuery, TError, TData>({
    queryKey: ["GetPortfolioSiteRefsSuspense", variables],
    queryFn: adminFetcher<
      Types.GetPortfolioSiteRefsQuery,
      Types.GetPortfolioSiteRefsQueryVariables
    >(GetPortfolioSiteRefsDocument, variables),
    ...options,
  })
}

useSuspenseGetPortfolioSiteRefsQuery.getKey = (
  variables: Types.GetPortfolioSiteRefsQueryVariables,
) => ["GetPortfolioSiteRefsSuspense", variables]

useSuspenseGetPortfolioSiteRefsQuery.rootKey = "GetPortfolioSiteRefs"

export const useInfiniteGetPortfolioSiteRefsQuery = <
  TData = InfiniteData<Types.GetPortfolioSiteRefsQuery>,
  TError = unknown,
>(
  variables: Types.GetPortfolioSiteRefsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetPortfolioSiteRefsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetPortfolioSiteRefsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetPortfolioSiteRefsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetPortfolioSiteRefs.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.GetPortfolioSiteRefsQuery,
            Types.GetPortfolioSiteRefsQueryVariables
          >(GetPortfolioSiteRefsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetPortfolioSiteRefsQuery.getKey = (
  variables: Types.GetPortfolioSiteRefsQueryVariables,
) => ["GetPortfolioSiteRefs.infinite", variables]

useInfiniteGetPortfolioSiteRefsQuery.rootKey = "GetPortfolioSiteRefs.infinite"

export const useSuspenseInfiniteGetPortfolioSiteRefsQuery = <
  TData = InfiniteData<Types.GetPortfolioSiteRefsQuery>,
  TError = unknown,
>(
  variables: Types.GetPortfolioSiteRefsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetPortfolioSiteRefsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetPortfolioSiteRefsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetPortfolioSiteRefsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "GetPortfolioSiteRefs.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.GetPortfolioSiteRefsQuery,
            Types.GetPortfolioSiteRefsQueryVariables
          >(GetPortfolioSiteRefsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetPortfolioSiteRefsQuery.getKey = (
  variables: Types.GetPortfolioSiteRefsQueryVariables,
) => ["GetPortfolioSiteRefs.infiniteSuspense", variables]

useSuspenseInfiniteGetPortfolioSiteRefsQuery.rootKey = "GetPortfolioSiteRefs.infinite"

useGetPortfolioSiteRefsQuery.fetcher = (
  variables: Types.GetPortfolioSiteRefsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.GetPortfolioSiteRefsQuery,
    Types.GetPortfolioSiteRefsQueryVariables
  >(GetPortfolioSiteRefsDocument, variables, options)

export const SiteBuildingDataDocument = `
    query SiteBuildingData($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    customerId
    assetManager
    propertyName
    capRate
    buildingClass
    streetAddress
    city
    state
    zipCode
    county
    latitude
    longitude
    sqft
    yearBuilt
    numStories
    numParkingSpaces
    numUnitsTotal
    numUnitsStudio
    numUnits1Bd
    numUnits2Bd
    numUnits3Bd
    numUnits4Bd
    occupancyType
    leaseType
    metering
    meteringSetBy {
      ...UserRef
    }
    meteringSetDate
    buildingType
    costarPropertyType
    costarSecondaryType
    mappedBuildingType
    buildingClass
    leaseType
    occupancyType
    mortgageLender
    roofReplacedYr
    roofCondition
    cbsaId
    cbsaName
    timeZone
    roofSqft
    roofMaxPvKw
    carportMaxPvKw
    groundMaxPvKw
    computedRoofMaxPvKw
    computedCarportMaxPvKw
    computedGroundMaxPvKw
    buildingData {
      id
      affordableType
      source
      sourceId
      streetAddress
      city
      state
      zipCode
      county
      latitude
      longitude
      sqft
      totalBuildings
      numStories
      numParkingSpaces
      costarPropertyType
      costarSecondaryType
      buildingType
      yearBuilt
      numUnitsTotal
      numUnitsStudio
      numUnits1Bd
      numUnits2Bd
      numUnits3Bd
      numUnits4Bd
      yearRenovated
      parentCompany
      buildingClass
      buildingOpex
      energyStar
      leedCertified
      lastSalePrice
      electricPowerRating
      zoning
      propertyName
      capRate
      numUnitsTotal
      numUnitsStudio
      numUnits1Bd
      numUnits2Bd
      numUnits3Bd
      numUnits4Bd
      numVacantUnitsStudio
      numVacantUnits1Bd
      numVacantUnits2Bd
      numVacantUnits3Bd
      numVacantUnits4Bd
      avgSqftStudio
      avgSqft1Bd
      avgSqft2Bd
      avgSqft3Bd
      avgSqft4Bd
      numElevators
      taxIdPrintFormat
      parcelNumberMin
      parcelNumberMax
      rentType
      reportedOwnerName
      reportedContactName
      reportedOwnerAddress
      reportedOwnerCity
      reportedOwnerState
      reportedOwnerZipCode
      reportedOwnerPhone
      ownerName
      ownerFirstName
      ownerLastName
      contactAddress
      contactCity
      contactState
      contactZipCode
      contactPhone
      buildingClass
      tenantContacts {
        ...ReonomyContact
      }
      ownerContacts {
        ...ReonomyContact
      }
    }
  }
}
    ${UserRefFragmentDoc}
${ReonomyContactFragmentDoc}`

export const useSiteBuildingDataQuery = <
  TData = Types.SiteBuildingDataQuery,
  TError = unknown,
>(
  variables: Types.SiteBuildingDataQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteBuildingDataQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteBuildingDataQuery, TError, TData>({
    queryKey: ["SiteBuildingData", variables],
    queryFn: adminFetcher<
      Types.SiteBuildingDataQuery,
      Types.SiteBuildingDataQueryVariables
    >(SiteBuildingDataDocument, variables),
    ...options,
  })
}

useSiteBuildingDataQuery.getKey = (variables: Types.SiteBuildingDataQueryVariables) => [
  "SiteBuildingData",
  variables,
]

useSiteBuildingDataQuery.rootKey = "SiteBuildingData"

export const useSuspenseSiteBuildingDataQuery = <
  TData = Types.SiteBuildingDataQuery,
  TError = unknown,
>(
  variables: Types.SiteBuildingDataQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteBuildingDataQuery, TError, TData>({
    queryKey: ["SiteBuildingDataSuspense", variables],
    queryFn: adminFetcher<
      Types.SiteBuildingDataQuery,
      Types.SiteBuildingDataQueryVariables
    >(SiteBuildingDataDocument, variables),
    ...options,
  })
}

useSuspenseSiteBuildingDataQuery.getKey = (
  variables: Types.SiteBuildingDataQueryVariables,
) => ["SiteBuildingDataSuspense", variables]

useSuspenseSiteBuildingDataQuery.rootKey = "SiteBuildingData"

export const useInfiniteSiteBuildingDataQuery = <
  TData = InfiniteData<Types.SiteBuildingDataQuery>,
  TError = unknown,
>(
  variables: Types.SiteBuildingDataQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteBuildingDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBuildingData.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.SiteBuildingDataQuery,
            Types.SiteBuildingDataQueryVariables
          >(SiteBuildingDataDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteBuildingDataQuery.getKey = (
  variables: Types.SiteBuildingDataQueryVariables,
) => ["SiteBuildingData.infinite", variables]

useInfiniteSiteBuildingDataQuery.rootKey = "SiteBuildingData.infinite"

export const useSuspenseInfiniteSiteBuildingDataQuery = <
  TData = InfiniteData<Types.SiteBuildingDataQuery>,
  TError = unknown,
>(
  variables: Types.SiteBuildingDataQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteBuildingDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBuildingData.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.SiteBuildingDataQuery,
            Types.SiteBuildingDataQueryVariables
          >(SiteBuildingDataDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteBuildingDataQuery.getKey = (
  variables: Types.SiteBuildingDataQueryVariables,
) => ["SiteBuildingData.infiniteSuspense", variables]

useSuspenseInfiniteSiteBuildingDataQuery.rootKey = "SiteBuildingData.infinite"

useSiteBuildingDataQuery.fetcher = (
  variables: Types.SiteBuildingDataQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SiteBuildingDataQuery, Types.SiteBuildingDataQueryVariables>(
    SiteBuildingDataDocument,
    variables,
    options,
  )

export const SitePossibleTariffsDocument = `
    query SitePossibleTariffs($pid: String!, $loadCategory: LoadCategory, $preferred: Boolean! = true, $includeUnpreferred: Boolean! = false, $includeCustom: Boolean! = false) {
  site(pid: $pid) {
    pid
    possibleTariffs(
      loadCategory: $loadCategory
      preferred: $preferred
      includeUnpreferred: $includeUnpreferred
      includeCustom: $includeCustom
    ) {
      id
      name
      code
      tariffType
      customerClass
      customerCount
      customerLikelihood
      masterTariffId
      isPreferred
      hasNetMetering
      requiresPv
      requiresStorage
      minMonthlyKw
      maxMonthlyKw
      minMonthlyKwh
      maxMonthlyKwh
    }
  }
}
    `

export const useSitePossibleTariffsQuery = <
  TData = Types.SitePossibleTariffsQuery,
  TError = unknown,
>(
  variables: Types.SitePossibleTariffsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SitePossibleTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SitePossibleTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SitePossibleTariffsQuery, TError, TData>({
    queryKey: ["SitePossibleTariffs", variables],
    queryFn: adminFetcher<
      Types.SitePossibleTariffsQuery,
      Types.SitePossibleTariffsQueryVariables
    >(SitePossibleTariffsDocument, variables),
    ...options,
  })
}

useSitePossibleTariffsQuery.getKey = (
  variables: Types.SitePossibleTariffsQueryVariables,
) => ["SitePossibleTariffs", variables]

useSitePossibleTariffsQuery.rootKey = "SitePossibleTariffs"

export const useSuspenseSitePossibleTariffsQuery = <
  TData = Types.SitePossibleTariffsQuery,
  TError = unknown,
>(
  variables: Types.SitePossibleTariffsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SitePossibleTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SitePossibleTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SitePossibleTariffsQuery, TError, TData>({
    queryKey: ["SitePossibleTariffsSuspense", variables],
    queryFn: adminFetcher<
      Types.SitePossibleTariffsQuery,
      Types.SitePossibleTariffsQueryVariables
    >(SitePossibleTariffsDocument, variables),
    ...options,
  })
}

useSuspenseSitePossibleTariffsQuery.getKey = (
  variables: Types.SitePossibleTariffsQueryVariables,
) => ["SitePossibleTariffsSuspense", variables]

useSuspenseSitePossibleTariffsQuery.rootKey = "SitePossibleTariffs"

export const useInfiniteSitePossibleTariffsQuery = <
  TData = InfiniteData<Types.SitePossibleTariffsQuery>,
  TError = unknown,
>(
  variables: Types.SitePossibleTariffsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SitePossibleTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SitePossibleTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SitePossibleTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SitePossibleTariffs.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.SitePossibleTariffsQuery,
            Types.SitePossibleTariffsQueryVariables
          >(SitePossibleTariffsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSitePossibleTariffsQuery.getKey = (
  variables: Types.SitePossibleTariffsQueryVariables,
) => ["SitePossibleTariffs.infinite", variables]

useInfiniteSitePossibleTariffsQuery.rootKey = "SitePossibleTariffs.infinite"

export const useSuspenseInfiniteSitePossibleTariffsQuery = <
  TData = InfiniteData<Types.SitePossibleTariffsQuery>,
  TError = unknown,
>(
  variables: Types.SitePossibleTariffsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SitePossibleTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SitePossibleTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SitePossibleTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "SitePossibleTariffs.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.SitePossibleTariffsQuery,
            Types.SitePossibleTariffsQueryVariables
          >(SitePossibleTariffsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSitePossibleTariffsQuery.getKey = (
  variables: Types.SitePossibleTariffsQueryVariables,
) => ["SitePossibleTariffs.infiniteSuspense", variables]

useSuspenseInfiniteSitePossibleTariffsQuery.rootKey = "SitePossibleTariffs.infinite"

useSitePossibleTariffsQuery.fetcher = (
  variables: Types.SitePossibleTariffsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SitePossibleTariffsQuery, Types.SitePossibleTariffsQueryVariables>(
    SitePossibleTariffsDocument,
    variables,
    options,
  )

export const GetSiteAdminDocument = `
    query GetSiteAdmin($pid: String!) {
  site(pid: $pid) {
    id
    pid
    fullAddress
    hiddenFromCustomers
    hideFakePanels
    portfolio {
      pid
      title
      org {
        pid
        slug
      }
    }
    buildingData {
      id
      reonomyPropertyLink
    }
    primaryScenario {
      pid
      name
    }
    currentStatus {
      pid
      displayText
      dateCreated
      createdBy {
        ...UserRef
      }
    }
    footprint {
      ...Footprint
      parcelShape
      parcelShapeGeojson
      parcelShapeUpdatedAt
      parcelShapeUpdatedBy {
        ...UserRef
      }
    }
    buildingFootprintNeedsReview
    physicalCheck {
      existingSolar
      badRoof
    }
    currentPolicyChecks {
      productType
      isGood
      disqualReason
      violatedPolicy {
        pid
        name
      }
    }
    roofSqft
    roofMaxPvKw
    computedRoofMaxPvKw
    groundMaxPvKw
    carportMaxPvKw
    parcelFootprintSqft
    buildingFootprintSqft
    buildingFootprintCount
    roofObstructionsSqft
    roofUsableSqft
    roofImage {
      id
      name
    }
    attachments {
      ...AttachmentFragment
    }
    duplicateSites {
      pid
      displayName
      portfolio {
        pid
        title
        org {
          pid
          slug
          name
        }
      }
    }
  }
}
    ${UserRefFragmentDoc}
${FootprintFragmentDoc}
${AttachmentFragmentFragmentDoc}`

export const useGetSiteAdminQuery = <TData = Types.GetSiteAdminQuery, TError = unknown>(
  variables: Types.GetSiteAdminQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetSiteAdminQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.GetSiteAdminQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetSiteAdminQuery, TError, TData>({
    queryKey: ["GetSiteAdmin", variables],
    queryFn: adminFetcher<Types.GetSiteAdminQuery, Types.GetSiteAdminQueryVariables>(
      GetSiteAdminDocument,
      variables,
    ),
    ...options,
  })
}

useGetSiteAdminQuery.getKey = (variables: Types.GetSiteAdminQueryVariables) => [
  "GetSiteAdmin",
  variables,
]

useGetSiteAdminQuery.rootKey = "GetSiteAdmin"

export const useSuspenseGetSiteAdminQuery = <
  TData = Types.GetSiteAdminQuery,
  TError = unknown,
>(
  variables: Types.GetSiteAdminQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetSiteAdminQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetSiteAdminQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetSiteAdminQuery, TError, TData>({
    queryKey: ["GetSiteAdminSuspense", variables],
    queryFn: adminFetcher<Types.GetSiteAdminQuery, Types.GetSiteAdminQueryVariables>(
      GetSiteAdminDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseGetSiteAdminQuery.getKey = (variables: Types.GetSiteAdminQueryVariables) => [
  "GetSiteAdminSuspense",
  variables,
]

useSuspenseGetSiteAdminQuery.rootKey = "GetSiteAdmin"

export const useInfiniteGetSiteAdminQuery = <
  TData = InfiniteData<Types.GetSiteAdminQuery>,
  TError = unknown,
>(
  variables: Types.GetSiteAdminQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetSiteAdminQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetSiteAdminQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetSiteAdminQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetSiteAdmin.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.GetSiteAdminQuery, Types.GetSiteAdminQueryVariables>(
            GetSiteAdminDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetSiteAdminQuery.getKey = (variables: Types.GetSiteAdminQueryVariables) => [
  "GetSiteAdmin.infinite",
  variables,
]

useInfiniteGetSiteAdminQuery.rootKey = "GetSiteAdmin.infinite"

export const useSuspenseInfiniteGetSiteAdminQuery = <
  TData = InfiniteData<Types.GetSiteAdminQuery>,
  TError = unknown,
>(
  variables: Types.GetSiteAdminQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetSiteAdminQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetSiteAdminQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetSiteAdminQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetSiteAdmin.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.GetSiteAdminQuery, Types.GetSiteAdminQueryVariables>(
            GetSiteAdminDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetSiteAdminQuery.getKey = (
  variables: Types.GetSiteAdminQueryVariables,
) => ["GetSiteAdmin.infiniteSuspense", variables]

useSuspenseInfiniteGetSiteAdminQuery.rootKey = "GetSiteAdmin.infinite"

useGetSiteAdminQuery.fetcher = (
  variables: Types.GetSiteAdminQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.GetSiteAdminQuery, Types.GetSiteAdminQueryVariables>(
    GetSiteAdminDocument,
    variables,
    options,
  )

export const UserListDocument = `
    query UserList {
  users {
    ...UserListUser
  }
}
    ${UserListUserFragmentDoc}`

export const useUserListQuery = <TData = Types.UserListQuery, TError = unknown>(
  variables?: Types.UserListQueryVariables,
  options?: Omit<UseQueryOptions<Types.UserListQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.UserListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.UserListQuery, TError, TData>({
    queryKey: variables === undefined ? ["UserList"] : ["UserList", variables],
    queryFn: adminFetcher<Types.UserListQuery, Types.UserListQueryVariables>(
      UserListDocument,
      variables,
    ),
    ...options,
  })
}

useUserListQuery.getKey = (variables?: Types.UserListQueryVariables) =>
  variables === undefined ? ["UserList"] : ["UserList", variables]

useUserListQuery.rootKey = "UserList"

export const useSuspenseUserListQuery = <TData = Types.UserListQuery, TError = unknown>(
  variables?: Types.UserListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UserListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.UserListQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UserListQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["UserListSuspense"] : ["UserListSuspense", variables],
    queryFn: adminFetcher<Types.UserListQuery, Types.UserListQueryVariables>(
      UserListDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseUserListQuery.getKey = (variables?: Types.UserListQueryVariables) =>
  variables === undefined ? ["UserListSuspense"] : ["UserListSuspense", variables]

useSuspenseUserListQuery.rootKey = "UserList"

export const useInfiniteUserListQuery = <
  TData = InfiniteData<Types.UserListQuery>,
  TError = unknown,
>(
  variables: Types.UserListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UserListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.UserListQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UserListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UserList.infinite"]
            : ["UserList.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.UserListQuery, Types.UserListQueryVariables>(
            UserListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUserListQuery.getKey = (variables?: Types.UserListQueryVariables) =>
  variables === undefined ? ["UserList.infinite"] : ["UserList.infinite", variables]

useInfiniteUserListQuery.rootKey = "UserList.infinite"

export const useSuspenseInfiniteUserListQuery = <
  TData = InfiniteData<Types.UserListQuery>,
  TError = unknown,
>(
  variables: Types.UserListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UserListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UserListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UserListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UserList.infiniteSuspense"]
            : ["UserList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.UserListQuery, Types.UserListQueryVariables>(
            UserListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUserListQuery.getKey = (variables?: Types.UserListQueryVariables) =>
  variables === undefined
    ? ["UserList.infiniteSuspense"]
    : ["UserList.infiniteSuspense", variables]

useSuspenseInfiniteUserListQuery.rootKey = "UserList.infinite"

useUserListQuery.fetcher = (
  variables?: Types.UserListQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.UserListQuery, Types.UserListQueryVariables>(
    UserListDocument,
    variables,
    options,
  )

export const PortfolioDetailDocument = `
    query PortfolioDetail($pid: String!) {
  portfolio(pid: $pid) {
    pid
    title
    logoUrl
    minKw
    minKwSetBy
    minKwSetDate
    view {
      sitePids
    }
    org {
      pid
      name
      slug
    }
  }
}
    `

export const usePortfolioDetailQuery = <
  TData = Types.PortfolioDetailQuery,
  TError = unknown,
>(
  variables: Types.PortfolioDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PortfolioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.PortfolioDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.PortfolioDetailQuery, TError, TData>({
    queryKey: ["PortfolioDetail", variables],
    queryFn: adminFetcher<
      Types.PortfolioDetailQuery,
      Types.PortfolioDetailQueryVariables
    >(PortfolioDetailDocument, variables),
    ...options,
  })
}

usePortfolioDetailQuery.getKey = (variables: Types.PortfolioDetailQueryVariables) => [
  "PortfolioDetail",
  variables,
]

usePortfolioDetailQuery.rootKey = "PortfolioDetail"

export const useSuspensePortfolioDetailQuery = <
  TData = Types.PortfolioDetailQuery,
  TError = unknown,
>(
  variables: Types.PortfolioDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PortfolioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PortfolioDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PortfolioDetailQuery, TError, TData>({
    queryKey: ["PortfolioDetailSuspense", variables],
    queryFn: adminFetcher<
      Types.PortfolioDetailQuery,
      Types.PortfolioDetailQueryVariables
    >(PortfolioDetailDocument, variables),
    ...options,
  })
}

useSuspensePortfolioDetailQuery.getKey = (
  variables: Types.PortfolioDetailQueryVariables,
) => ["PortfolioDetailSuspense", variables]

useSuspensePortfolioDetailQuery.rootKey = "PortfolioDetail"

export const useInfinitePortfolioDetailQuery = <
  TData = InfiniteData<Types.PortfolioDetailQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PortfolioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PortfolioDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PortfolioDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioDetail.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.PortfolioDetailQuery, Types.PortfolioDetailQueryVariables>(
            PortfolioDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePortfolioDetailQuery.getKey = (
  variables: Types.PortfolioDetailQueryVariables,
) => ["PortfolioDetail.infinite", variables]

useInfinitePortfolioDetailQuery.rootKey = "PortfolioDetail.infinite"

export const useSuspenseInfinitePortfolioDetailQuery = <
  TData = InfiniteData<Types.PortfolioDetailQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PortfolioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PortfolioDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PortfolioDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.PortfolioDetailQuery, Types.PortfolioDetailQueryVariables>(
            PortfolioDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePortfolioDetailQuery.getKey = (
  variables: Types.PortfolioDetailQueryVariables,
) => ["PortfolioDetail.infiniteSuspense", variables]

useSuspenseInfinitePortfolioDetailQuery.rootKey = "PortfolioDetail.infinite"

usePortfolioDetailQuery.fetcher = (
  variables: Types.PortfolioDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.PortfolioDetailQuery, Types.PortfolioDetailQueryVariables>(
    PortfolioDetailDocument,
    variables,
    options,
  )

export const UtilityListDocument = `
    query UtilityList($zipCode: String) {
  tariffs {
    utilities(zipCode: $zipCode) {
      ...UtilityFragment
    }
  }
}
    ${UtilityFragmentFragmentDoc}`

export const useUtilityListQuery = <TData = Types.UtilityListQuery, TError = unknown>(
  variables?: Types.UtilityListQueryVariables,
  options?: Omit<UseQueryOptions<Types.UtilityListQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.UtilityListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.UtilityListQuery, TError, TData>({
    queryKey: variables === undefined ? ["UtilityList"] : ["UtilityList", variables],
    queryFn: adminFetcher<Types.UtilityListQuery, Types.UtilityListQueryVariables>(
      UtilityListDocument,
      variables,
    ),
    ...options,
  })
}

useUtilityListQuery.getKey = (variables?: Types.UtilityListQueryVariables) =>
  variables === undefined ? ["UtilityList"] : ["UtilityList", variables]

useUtilityListQuery.rootKey = "UtilityList"

export const useSuspenseUtilityListQuery = <
  TData = Types.UtilityListQuery,
  TError = unknown,
>(
  variables?: Types.UtilityListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UtilityListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UtilityListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UtilityListQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["UtilityListSuspense"]
        : ["UtilityListSuspense", variables],
    queryFn: adminFetcher<Types.UtilityListQuery, Types.UtilityListQueryVariables>(
      UtilityListDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseUtilityListQuery.getKey = (variables?: Types.UtilityListQueryVariables) =>
  variables === undefined ? ["UtilityListSuspense"] : ["UtilityListSuspense", variables]

useSuspenseUtilityListQuery.rootKey = "UtilityList"

export const useInfiniteUtilityListQuery = <
  TData = InfiniteData<Types.UtilityListQuery>,
  TError = unknown,
>(
  variables: Types.UtilityListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UtilityListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UtilityListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UtilityListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UtilityList.infinite"]
            : ["UtilityList.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.UtilityListQuery, Types.UtilityListQueryVariables>(
            UtilityListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUtilityListQuery.getKey = (variables?: Types.UtilityListQueryVariables) =>
  variables === undefined
    ? ["UtilityList.infinite"]
    : ["UtilityList.infinite", variables]

useInfiniteUtilityListQuery.rootKey = "UtilityList.infinite"

export const useSuspenseInfiniteUtilityListQuery = <
  TData = InfiniteData<Types.UtilityListQuery>,
  TError = unknown,
>(
  variables: Types.UtilityListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UtilityListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UtilityListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UtilityListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UtilityList.infiniteSuspense"]
            : ["UtilityList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.UtilityListQuery, Types.UtilityListQueryVariables>(
            UtilityListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUtilityListQuery.getKey = (
  variables?: Types.UtilityListQueryVariables,
) =>
  variables === undefined
    ? ["UtilityList.infiniteSuspense"]
    : ["UtilityList.infiniteSuspense", variables]

useSuspenseInfiniteUtilityListQuery.rootKey = "UtilityList.infinite"

useUtilityListQuery.fetcher = (
  variables?: Types.UtilityListQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.UtilityListQuery, Types.UtilityListQueryVariables>(
    UtilityListDocument,
    variables,
    options,
  )

export const UtilityDetailDocument = `
    query UtilityDetail($id: Int!) {
  tariffs {
    utility(id: $id) {
      ...UtilityFragment
    }
  }
}
    ${UtilityFragmentFragmentDoc}`

export const useUtilityDetailQuery = <
  TData = Types.UtilityDetailQuery,
  TError = unknown,
>(
  variables: Types.UtilityDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.UtilityDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.UtilityDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.UtilityDetailQuery, TError, TData>({
    queryKey: ["UtilityDetail", variables],
    queryFn: adminFetcher<Types.UtilityDetailQuery, Types.UtilityDetailQueryVariables>(
      UtilityDetailDocument,
      variables,
    ),
    ...options,
  })
}

useUtilityDetailQuery.getKey = (variables: Types.UtilityDetailQueryVariables) => [
  "UtilityDetail",
  variables,
]

useUtilityDetailQuery.rootKey = "UtilityDetail"

export const useSuspenseUtilityDetailQuery = <
  TData = Types.UtilityDetailQuery,
  TError = unknown,
>(
  variables: Types.UtilityDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UtilityDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UtilityDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UtilityDetailQuery, TError, TData>({
    queryKey: ["UtilityDetailSuspense", variables],
    queryFn: adminFetcher<Types.UtilityDetailQuery, Types.UtilityDetailQueryVariables>(
      UtilityDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseUtilityDetailQuery.getKey = (
  variables: Types.UtilityDetailQueryVariables,
) => ["UtilityDetailSuspense", variables]

useSuspenseUtilityDetailQuery.rootKey = "UtilityDetail"

export const useInfiniteUtilityDetailQuery = <
  TData = InfiniteData<Types.UtilityDetailQuery>,
  TError = unknown,
>(
  variables: Types.UtilityDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UtilityDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UtilityDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UtilityDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilityDetail.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.UtilityDetailQuery, Types.UtilityDetailQueryVariables>(
            UtilityDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUtilityDetailQuery.getKey = (
  variables: Types.UtilityDetailQueryVariables,
) => ["UtilityDetail.infinite", variables]

useInfiniteUtilityDetailQuery.rootKey = "UtilityDetail.infinite"

export const useSuspenseInfiniteUtilityDetailQuery = <
  TData = InfiniteData<Types.UtilityDetailQuery>,
  TError = unknown,
>(
  variables: Types.UtilityDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UtilityDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UtilityDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UtilityDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilityDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.UtilityDetailQuery, Types.UtilityDetailQueryVariables>(
            UtilityDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUtilityDetailQuery.getKey = (
  variables: Types.UtilityDetailQueryVariables,
) => ["UtilityDetail.infiniteSuspense", variables]

useSuspenseInfiniteUtilityDetailQuery.rootKey = "UtilityDetail.infinite"

useUtilityDetailQuery.fetcher = (
  variables: Types.UtilityDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.UtilityDetailQuery, Types.UtilityDetailQueryVariables>(
    UtilityDetailDocument,
    variables,
    options,
  )

export const TariffListDocument = `
    query TariffList($id: Int!) {
  tariffs {
    utility(id: $id) {
      id
      shortName
      name
      tariffs {
        id
        masterTariffId
        code
        name
        tariffType
        customerClass
        isPreferred
        requiresPv
        requiresStorage
        customerLikelihood
        hasNetMetering
        hasTouRates
        minMonthlyKwh
        maxMonthlyKwh
        minMonthlyKw
        maxMonthlyKw
      }
    }
  }
}
    `

export const useTariffListQuery = <TData = Types.TariffListQuery, TError = unknown>(
  variables: Types.TariffListQueryVariables,
  options?: Omit<UseQueryOptions<Types.TariffListQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.TariffListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.TariffListQuery, TError, TData>({
    queryKey: ["TariffList", variables],
    queryFn: adminFetcher<Types.TariffListQuery, Types.TariffListQueryVariables>(
      TariffListDocument,
      variables,
    ),
    ...options,
  })
}

useTariffListQuery.getKey = (variables: Types.TariffListQueryVariables) => [
  "TariffList",
  variables,
]

useTariffListQuery.rootKey = "TariffList"

export const useSuspenseTariffListQuery = <
  TData = Types.TariffListQuery,
  TError = unknown,
>(
  variables: Types.TariffListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.TariffListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.TariffListQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.TariffListQuery, TError, TData>({
    queryKey: ["TariffListSuspense", variables],
    queryFn: adminFetcher<Types.TariffListQuery, Types.TariffListQueryVariables>(
      TariffListDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseTariffListQuery.getKey = (variables: Types.TariffListQueryVariables) => [
  "TariffListSuspense",
  variables,
]

useSuspenseTariffListQuery.rootKey = "TariffList"

export const useInfiniteTariffListQuery = <
  TData = InfiniteData<Types.TariffListQuery>,
  TError = unknown,
>(
  variables: Types.TariffListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.TariffListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.TariffListQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.TariffListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffList.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.TariffListQuery, Types.TariffListQueryVariables>(
            TariffListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteTariffListQuery.getKey = (variables: Types.TariffListQueryVariables) => [
  "TariffList.infinite",
  variables,
]

useInfiniteTariffListQuery.rootKey = "TariffList.infinite"

export const useSuspenseInfiniteTariffListQuery = <
  TData = InfiniteData<Types.TariffListQuery>,
  TError = unknown,
>(
  variables: Types.TariffListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.TariffListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.TariffListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.TariffListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.TariffListQuery, Types.TariffListQueryVariables>(
            TariffListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteTariffListQuery.getKey = (
  variables: Types.TariffListQueryVariables,
) => ["TariffList.infiniteSuspense", variables]

useSuspenseInfiniteTariffListQuery.rootKey = "TariffList.infinite"

useTariffListQuery.fetcher = (
  variables: Types.TariffListQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.TariffListQuery, Types.TariffListQueryVariables>(
    TariffListDocument,
    variables,
    options,
  )

export const CustomTariffListDocument = `
    query CustomTariffList($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    displayName
    targetUtility {
      id
      name
    }
    customTariffs {
      id
      dateCreated
      name
      code
      customerClass
      parent {
        id
      }
      utility {
        id
        name
      }
      createdBy {
        pid
        email
      }
      archivedAt
    }
  }
}
    `

export const useCustomTariffListQuery = <
  TData = Types.CustomTariffListQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CustomTariffListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.CustomTariffListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CustomTariffListQuery, TError, TData>({
    queryKey: ["CustomTariffList", variables],
    queryFn: adminFetcher<
      Types.CustomTariffListQuery,
      Types.CustomTariffListQueryVariables
    >(CustomTariffListDocument, variables),
    ...options,
  })
}

useCustomTariffListQuery.getKey = (variables: Types.CustomTariffListQueryVariables) => [
  "CustomTariffList",
  variables,
]

useCustomTariffListQuery.rootKey = "CustomTariffList"

export const useSuspenseCustomTariffListQuery = <
  TData = Types.CustomTariffListQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CustomTariffListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CustomTariffListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CustomTariffListQuery, TError, TData>({
    queryKey: ["CustomTariffListSuspense", variables],
    queryFn: adminFetcher<
      Types.CustomTariffListQuery,
      Types.CustomTariffListQueryVariables
    >(CustomTariffListDocument, variables),
    ...options,
  })
}

useSuspenseCustomTariffListQuery.getKey = (
  variables: Types.CustomTariffListQueryVariables,
) => ["CustomTariffListSuspense", variables]

useSuspenseCustomTariffListQuery.rootKey = "CustomTariffList"

export const useInfiniteCustomTariffListQuery = <
  TData = InfiniteData<Types.CustomTariffListQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CustomTariffListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CustomTariffListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CustomTariffListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffList.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.CustomTariffListQuery,
            Types.CustomTariffListQueryVariables
          >(CustomTariffListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCustomTariffListQuery.getKey = (
  variables: Types.CustomTariffListQueryVariables,
) => ["CustomTariffList.infinite", variables]

useInfiniteCustomTariffListQuery.rootKey = "CustomTariffList.infinite"

export const useSuspenseInfiniteCustomTariffListQuery = <
  TData = InfiniteData<Types.CustomTariffListQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CustomTariffListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CustomTariffListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CustomTariffListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.CustomTariffListQuery,
            Types.CustomTariffListQueryVariables
          >(CustomTariffListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCustomTariffListQuery.getKey = (
  variables: Types.CustomTariffListQueryVariables,
) => ["CustomTariffList.infiniteSuspense", variables]

useSuspenseInfiniteCustomTariffListQuery.rootKey = "CustomTariffList.infinite"

useCustomTariffListQuery.fetcher = (
  variables: Types.CustomTariffListQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.CustomTariffListQuery, Types.CustomTariffListQueryVariables>(
    CustomTariffListDocument,
    variables,
    options,
  )

export const TariffDetailDocument = `
    query TariffDetail($tariffId: Int!) {
  tariffs {
    tariff(id: $tariffId) {
      id
      masterTariffId
      code
      name
      tariffType
      customerClass
      customerCount
      customerLikelihood
      minMonthlyKwh
      maxMonthlyKwh
      minMonthlyKw
      maxMonthlyKw
      hasTouRates
      hasNetMetering
      isPreferred
      notes
      requiresPv
      requiresStorage
      utility {
        id
        name
      }
      rateDetails {
        ...TariffDetailFragment
      }
      history {
        dateLastUpdated
        versions {
          isDownloaded
          tariffId
          effectiveDate
          endDate
        }
      }
    }
  }
}
    ${TariffDetailFragmentFragmentDoc}`

export const useTariffDetailQuery = <TData = Types.TariffDetailQuery, TError = unknown>(
  variables: Types.TariffDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.TariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.TariffDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.TariffDetailQuery, TError, TData>({
    queryKey: ["TariffDetail", variables],
    queryFn: adminFetcher<Types.TariffDetailQuery, Types.TariffDetailQueryVariables>(
      TariffDetailDocument,
      variables,
    ),
    ...options,
  })
}

useTariffDetailQuery.getKey = (variables: Types.TariffDetailQueryVariables) => [
  "TariffDetail",
  variables,
]

useTariffDetailQuery.rootKey = "TariffDetail"

export const useSuspenseTariffDetailQuery = <
  TData = Types.TariffDetailQuery,
  TError = unknown,
>(
  variables: Types.TariffDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.TariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.TariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.TariffDetailQuery, TError, TData>({
    queryKey: ["TariffDetailSuspense", variables],
    queryFn: adminFetcher<Types.TariffDetailQuery, Types.TariffDetailQueryVariables>(
      TariffDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseTariffDetailQuery.getKey = (variables: Types.TariffDetailQueryVariables) => [
  "TariffDetailSuspense",
  variables,
]

useSuspenseTariffDetailQuery.rootKey = "TariffDetail"

export const useInfiniteTariffDetailQuery = <
  TData = InfiniteData<Types.TariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.TariffDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.TariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.TariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.TariffDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffDetail.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.TariffDetailQuery, Types.TariffDetailQueryVariables>(
            TariffDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteTariffDetailQuery.getKey = (variables: Types.TariffDetailQueryVariables) => [
  "TariffDetail.infinite",
  variables,
]

useInfiniteTariffDetailQuery.rootKey = "TariffDetail.infinite"

export const useSuspenseInfiniteTariffDetailQuery = <
  TData = InfiniteData<Types.TariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.TariffDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.TariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.TariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.TariffDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.TariffDetailQuery, Types.TariffDetailQueryVariables>(
            TariffDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteTariffDetailQuery.getKey = (
  variables: Types.TariffDetailQueryVariables,
) => ["TariffDetail.infiniteSuspense", variables]

useSuspenseInfiniteTariffDetailQuery.rootKey = "TariffDetail.infinite"

useTariffDetailQuery.fetcher = (
  variables: Types.TariffDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.TariffDetailQuery, Types.TariffDetailQueryVariables>(
    TariffDetailDocument,
    variables,
    options,
  )

export const SiteHeaderDocument = `
    query SiteHeader($sitePid: String!) {
  site(pid: $sitePid) {
    id
    pid
    customerId
    propertyName
    streetAddress
    city
    state
    zipCode
    portfolio {
      pid
      title
      org {
        pid
        name
        slug
      }
    }
    status
    hiddenFromCustomers
    statusCheckPassed
    policyViolations {
      policy {
        pid
      }
    }
  }
}
    `

export const useSiteHeaderQuery = <TData = Types.SiteHeaderQuery, TError = unknown>(
  variables: Types.SiteHeaderQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteHeaderQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteHeaderQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteHeaderQuery, TError, TData>({
    queryKey: ["SiteHeader", variables],
    queryFn: adminFetcher<Types.SiteHeaderQuery, Types.SiteHeaderQueryVariables>(
      SiteHeaderDocument,
      variables,
    ),
    ...options,
  })
}

useSiteHeaderQuery.getKey = (variables: Types.SiteHeaderQueryVariables) => [
  "SiteHeader",
  variables,
]

useSiteHeaderQuery.rootKey = "SiteHeader"

export const useSuspenseSiteHeaderQuery = <
  TData = Types.SiteHeaderQuery,
  TError = unknown,
>(
  variables: Types.SiteHeaderQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteHeaderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteHeaderQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteHeaderQuery, TError, TData>({
    queryKey: ["SiteHeaderSuspense", variables],
    queryFn: adminFetcher<Types.SiteHeaderQuery, Types.SiteHeaderQueryVariables>(
      SiteHeaderDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteHeaderQuery.getKey = (variables: Types.SiteHeaderQueryVariables) => [
  "SiteHeaderSuspense",
  variables,
]

useSuspenseSiteHeaderQuery.rootKey = "SiteHeader"

export const useInfiniteSiteHeaderQuery = <
  TData = InfiniteData<Types.SiteHeaderQuery>,
  TError = unknown,
>(
  variables: Types.SiteHeaderQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteHeaderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteHeaderQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteHeaderQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteHeader.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SiteHeaderQuery, Types.SiteHeaderQueryVariables>(
            SiteHeaderDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteHeaderQuery.getKey = (variables: Types.SiteHeaderQueryVariables) => [
  "SiteHeader.infinite",
  variables,
]

useInfiniteSiteHeaderQuery.rootKey = "SiteHeader.infinite"

export const useSuspenseInfiniteSiteHeaderQuery = <
  TData = InfiniteData<Types.SiteHeaderQuery>,
  TError = unknown,
>(
  variables: Types.SiteHeaderQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteHeaderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteHeaderQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteHeaderQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteHeader.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SiteHeaderQuery, Types.SiteHeaderQueryVariables>(
            SiteHeaderDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteHeaderQuery.getKey = (
  variables: Types.SiteHeaderQueryVariables,
) => ["SiteHeader.infiniteSuspense", variables]

useSuspenseInfiniteSiteHeaderQuery.rootKey = "SiteHeader.infinite"

useSiteHeaderQuery.fetcher = (
  variables: Types.SiteHeaderQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SiteHeaderQuery, Types.SiteHeaderQueryVariables>(
    SiteHeaderDocument,
    variables,
    options,
  )

export const GetEtbDetailDocument = `
    query GetEtbDetail($sitePid: String!, $etbId: Int!) {
  site(pid: $sitePid) {
    pid
    activeMeters {
      id
      meters {
        ...BauMeter
      }
    }
    etbReport(id: $etbId) {
      id
      dateCreated
      projectName
      proposalDate
      taxType
      ownerSector
      federalIncomeTaxRate
      stateIncomeTaxRate
      discountRate
      electricityEscalationRate
      scenario {
        ...ETBScenarioRef
      }
      combinedCosts {
        ...CostsFragment
      }
      meters {
        id
        name
        streetAddress
        city
        state
        zipCode
        utilityName
        genabilityUtility: utility {
          id
          name
          shortName
        }
        tariffFullDescriptionBefore
        genabilityTariffBefore: tariffBefore {
          id
          name
          code
        }
        tariffFullDescriptionAfter
        genabilityTariffAfter: tariffAfter {
          id
          name
          code
        }
        costs {
          ...CostsFragment
        }
        pv {
          cost
          powerRatingKwDc
          powerRatingKwAc
          degradationRate
          tilt
          azimuth
        }
        pvType
        bauMeter {
          id
          archivedAt
        }
      }
      transactions {
        name
        termYears
        irr10Year
        irr20Year
        irrTerm
        lcoeBefore
        lcoeAfter
        lcoePv
        npvTerm
        paybackYears
        roiTerm
        demandSavingsTerm
        energySavingsTerm
        totalSavingsTerm
        pvGenerationKwhTerm
        storageDischargeKwhTerm
        storageConsumptionKwh
        totalCost
        totalIncentives
        netCost
        upfrontCost
        ppaTerm
        ppaStartingRate
        ppaEscalationRate
        series {
          name
          series
        }
      }
      roofPvKw
      carportPvKw
    }
  }
}
    ${BauMeterFragmentDoc}
${BauTariffFragmentDoc}
${EtbScenarioRefFragmentDoc}
${CostsFragmentFragmentDoc}`

export const useGetEtbDetailQuery = <TData = Types.GetEtbDetailQuery, TError = unknown>(
  variables: Types.GetEtbDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetEtbDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.GetEtbDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetEtbDetailQuery, TError, TData>({
    queryKey: ["GetEtbDetail", variables],
    queryFn: adminFetcher<Types.GetEtbDetailQuery, Types.GetEtbDetailQueryVariables>(
      GetEtbDetailDocument,
      variables,
    ),
    ...options,
  })
}

useGetEtbDetailQuery.getKey = (variables: Types.GetEtbDetailQueryVariables) => [
  "GetEtbDetail",
  variables,
]

useGetEtbDetailQuery.rootKey = "GetEtbDetail"

export const useSuspenseGetEtbDetailQuery = <
  TData = Types.GetEtbDetailQuery,
  TError = unknown,
>(
  variables: Types.GetEtbDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetEtbDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetEtbDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetEtbDetailQuery, TError, TData>({
    queryKey: ["GetEtbDetailSuspense", variables],
    queryFn: adminFetcher<Types.GetEtbDetailQuery, Types.GetEtbDetailQueryVariables>(
      GetEtbDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseGetEtbDetailQuery.getKey = (variables: Types.GetEtbDetailQueryVariables) => [
  "GetEtbDetailSuspense",
  variables,
]

useSuspenseGetEtbDetailQuery.rootKey = "GetEtbDetail"

export const useInfiniteGetEtbDetailQuery = <
  TData = InfiniteData<Types.GetEtbDetailQuery>,
  TError = unknown,
>(
  variables: Types.GetEtbDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetEtbDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetEtbDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetEtbDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetEtbDetail.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.GetEtbDetailQuery, Types.GetEtbDetailQueryVariables>(
            GetEtbDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetEtbDetailQuery.getKey = (variables: Types.GetEtbDetailQueryVariables) => [
  "GetEtbDetail.infinite",
  variables,
]

useInfiniteGetEtbDetailQuery.rootKey = "GetEtbDetail.infinite"

export const useSuspenseInfiniteGetEtbDetailQuery = <
  TData = InfiniteData<Types.GetEtbDetailQuery>,
  TError = unknown,
>(
  variables: Types.GetEtbDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetEtbDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetEtbDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetEtbDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetEtbDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.GetEtbDetailQuery, Types.GetEtbDetailQueryVariables>(
            GetEtbDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetEtbDetailQuery.getKey = (
  variables: Types.GetEtbDetailQueryVariables,
) => ["GetEtbDetail.infiniteSuspense", variables]

useSuspenseInfiniteGetEtbDetailQuery.rootKey = "GetEtbDetail.infinite"

useGetEtbDetailQuery.fetcher = (
  variables: Types.GetEtbDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.GetEtbDetailQuery, Types.GetEtbDetailQueryVariables>(
    GetEtbDetailDocument,
    variables,
    options,
  )

export const EtbIndexDocument = `
    query EtbIndex($pid: String!) {
  site(pid: $pid) {
    pid
    etbReports {
      id
      projectName
      dateCreated
      proposalDate
      scenario {
        ...ETBScenarioRef
      }
    }
    etbScenarioOptions {
      ...ETBScenarioRef
    }
  }
}
    ${EtbScenarioRefFragmentDoc}`

export const useEtbIndexQuery = <TData = Types.EtbIndexQuery, TError = unknown>(
  variables: Types.EtbIndexQueryVariables,
  options?: Omit<UseQueryOptions<Types.EtbIndexQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.EtbIndexQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.EtbIndexQuery, TError, TData>({
    queryKey: ["EtbIndex", variables],
    queryFn: adminFetcher<Types.EtbIndexQuery, Types.EtbIndexQueryVariables>(
      EtbIndexDocument,
      variables,
    ),
    ...options,
  })
}

useEtbIndexQuery.getKey = (variables: Types.EtbIndexQueryVariables) => [
  "EtbIndex",
  variables,
]

useEtbIndexQuery.rootKey = "EtbIndex"

export const useSuspenseEtbIndexQuery = <TData = Types.EtbIndexQuery, TError = unknown>(
  variables: Types.EtbIndexQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.EtbIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.EtbIndexQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.EtbIndexQuery, TError, TData>({
    queryKey: ["EtbIndexSuspense", variables],
    queryFn: adminFetcher<Types.EtbIndexQuery, Types.EtbIndexQueryVariables>(
      EtbIndexDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseEtbIndexQuery.getKey = (variables: Types.EtbIndexQueryVariables) => [
  "EtbIndexSuspense",
  variables,
]

useSuspenseEtbIndexQuery.rootKey = "EtbIndex"

export const useInfiniteEtbIndexQuery = <
  TData = InfiniteData<Types.EtbIndexQuery>,
  TError = unknown,
>(
  variables: Types.EtbIndexQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.EtbIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.EtbIndexQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.EtbIndexQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["EtbIndex.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.EtbIndexQuery, Types.EtbIndexQueryVariables>(
            EtbIndexDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteEtbIndexQuery.getKey = (variables: Types.EtbIndexQueryVariables) => [
  "EtbIndex.infinite",
  variables,
]

useInfiniteEtbIndexQuery.rootKey = "EtbIndex.infinite"

export const useSuspenseInfiniteEtbIndexQuery = <
  TData = InfiniteData<Types.EtbIndexQuery>,
  TError = unknown,
>(
  variables: Types.EtbIndexQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.EtbIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.EtbIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.EtbIndexQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["EtbIndex.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.EtbIndexQuery, Types.EtbIndexQueryVariables>(
            EtbIndexDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteEtbIndexQuery.getKey = (variables: Types.EtbIndexQueryVariables) => [
  "EtbIndex.infiniteSuspense",
  variables,
]

useSuspenseInfiniteEtbIndexQuery.rootKey = "EtbIndex.infinite"

useEtbIndexQuery.fetcher = (
  variables: Types.EtbIndexQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.EtbIndexQuery, Types.EtbIndexQueryVariables>(
    EtbIndexDocument,
    variables,
    options,
  )

export const HelioscopeIndexDocument = `
    query HelioscopeIndex($pid: String!) {
  site(pid: $pid) {
    pid
    activeMeters {
      meters {
        label
      }
    }
    helioscopes {
      id
      pid
      createdAt
      createdBy {
        ...UserRef
      }
      isTrashed
      roofPvKw
      carportPvKw
      carportPvThreeHighKw
      carportPvSixHighKw
      groundPvKw
      roofIsMax
      carportIsMax
      annualProductionAcKwh
      monthlyProductionAcKwh
      systemProductivityKwhPerKw
      allocations
      pvSpecs {
        pvType
        sizeKw
      }
      pdf {
        ...AttachmentFragment
      }
      roofImage {
        ...AttachmentFragment
      }
      roofImageHref
      productionCsv {
        ...AttachmentFragment
      }
      dcNameplateKw
      acNameplateKw
      loadRatio
      moduleType
      inverterType
      rackingTiltDegrees
      rackingManufacturer
      rackingConnectionType
      notes
    }
  }
}
    ${UserRefFragmentDoc}
${AttachmentFragmentFragmentDoc}`

export const useHelioscopeIndexQuery = <
  TData = Types.HelioscopeIndexQuery,
  TError = unknown,
>(
  variables: Types.HelioscopeIndexQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.HelioscopeIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.HelioscopeIndexQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.HelioscopeIndexQuery, TError, TData>({
    queryKey: ["HelioscopeIndex", variables],
    queryFn: adminFetcher<
      Types.HelioscopeIndexQuery,
      Types.HelioscopeIndexQueryVariables
    >(HelioscopeIndexDocument, variables),
    ...options,
  })
}

useHelioscopeIndexQuery.getKey = (variables: Types.HelioscopeIndexQueryVariables) => [
  "HelioscopeIndex",
  variables,
]

useHelioscopeIndexQuery.rootKey = "HelioscopeIndex"

export const useSuspenseHelioscopeIndexQuery = <
  TData = Types.HelioscopeIndexQuery,
  TError = unknown,
>(
  variables: Types.HelioscopeIndexQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.HelioscopeIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.HelioscopeIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.HelioscopeIndexQuery, TError, TData>({
    queryKey: ["HelioscopeIndexSuspense", variables],
    queryFn: adminFetcher<
      Types.HelioscopeIndexQuery,
      Types.HelioscopeIndexQueryVariables
    >(HelioscopeIndexDocument, variables),
    ...options,
  })
}

useSuspenseHelioscopeIndexQuery.getKey = (
  variables: Types.HelioscopeIndexQueryVariables,
) => ["HelioscopeIndexSuspense", variables]

useSuspenseHelioscopeIndexQuery.rootKey = "HelioscopeIndex"

export const useInfiniteHelioscopeIndexQuery = <
  TData = InfiniteData<Types.HelioscopeIndexQuery>,
  TError = unknown,
>(
  variables: Types.HelioscopeIndexQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.HelioscopeIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.HelioscopeIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.HelioscopeIndexQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HelioscopeIndex.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.HelioscopeIndexQuery, Types.HelioscopeIndexQueryVariables>(
            HelioscopeIndexDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteHelioscopeIndexQuery.getKey = (
  variables: Types.HelioscopeIndexQueryVariables,
) => ["HelioscopeIndex.infinite", variables]

useInfiniteHelioscopeIndexQuery.rootKey = "HelioscopeIndex.infinite"

export const useSuspenseInfiniteHelioscopeIndexQuery = <
  TData = InfiniteData<Types.HelioscopeIndexQuery>,
  TError = unknown,
>(
  variables: Types.HelioscopeIndexQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.HelioscopeIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.HelioscopeIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.HelioscopeIndexQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HelioscopeIndex.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.HelioscopeIndexQuery, Types.HelioscopeIndexQueryVariables>(
            HelioscopeIndexDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteHelioscopeIndexQuery.getKey = (
  variables: Types.HelioscopeIndexQueryVariables,
) => ["HelioscopeIndex.infiniteSuspense", variables]

useSuspenseInfiniteHelioscopeIndexQuery.rootKey = "HelioscopeIndex.infinite"

useHelioscopeIndexQuery.fetcher = (
  variables: Types.HelioscopeIndexQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.HelioscopeIndexQuery, Types.HelioscopeIndexQueryVariables>(
    HelioscopeIndexDocument,
    variables,
    options,
  )

export const HelioscopeUploadDocument = `
    query HelioscopeUpload($pid: String!) {
  site(pid: $pid) {
    pid
    scenarios {
      ...SmallScenarioFragment
    }
  }
}
    ${SmallScenarioFragmentFragmentDoc}
${UserRefFragmentDoc}`

export const useHelioscopeUploadQuery = <
  TData = Types.HelioscopeUploadQuery,
  TError = unknown,
>(
  variables: Types.HelioscopeUploadQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.HelioscopeUploadQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.HelioscopeUploadQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.HelioscopeUploadQuery, TError, TData>({
    queryKey: ["HelioscopeUpload", variables],
    queryFn: adminFetcher<
      Types.HelioscopeUploadQuery,
      Types.HelioscopeUploadQueryVariables
    >(HelioscopeUploadDocument, variables),
    ...options,
  })
}

useHelioscopeUploadQuery.getKey = (variables: Types.HelioscopeUploadQueryVariables) => [
  "HelioscopeUpload",
  variables,
]

useHelioscopeUploadQuery.rootKey = "HelioscopeUpload"

export const useSuspenseHelioscopeUploadQuery = <
  TData = Types.HelioscopeUploadQuery,
  TError = unknown,
>(
  variables: Types.HelioscopeUploadQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.HelioscopeUploadQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.HelioscopeUploadQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.HelioscopeUploadQuery, TError, TData>({
    queryKey: ["HelioscopeUploadSuspense", variables],
    queryFn: adminFetcher<
      Types.HelioscopeUploadQuery,
      Types.HelioscopeUploadQueryVariables
    >(HelioscopeUploadDocument, variables),
    ...options,
  })
}

useSuspenseHelioscopeUploadQuery.getKey = (
  variables: Types.HelioscopeUploadQueryVariables,
) => ["HelioscopeUploadSuspense", variables]

useSuspenseHelioscopeUploadQuery.rootKey = "HelioscopeUpload"

export const useInfiniteHelioscopeUploadQuery = <
  TData = InfiniteData<Types.HelioscopeUploadQuery>,
  TError = unknown,
>(
  variables: Types.HelioscopeUploadQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.HelioscopeUploadQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.HelioscopeUploadQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.HelioscopeUploadQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HelioscopeUpload.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.HelioscopeUploadQuery,
            Types.HelioscopeUploadQueryVariables
          >(HelioscopeUploadDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteHelioscopeUploadQuery.getKey = (
  variables: Types.HelioscopeUploadQueryVariables,
) => ["HelioscopeUpload.infinite", variables]

useInfiniteHelioscopeUploadQuery.rootKey = "HelioscopeUpload.infinite"

export const useSuspenseInfiniteHelioscopeUploadQuery = <
  TData = InfiniteData<Types.HelioscopeUploadQuery>,
  TError = unknown,
>(
  variables: Types.HelioscopeUploadQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.HelioscopeUploadQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.HelioscopeUploadQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.HelioscopeUploadQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HelioscopeUpload.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.HelioscopeUploadQuery,
            Types.HelioscopeUploadQueryVariables
          >(HelioscopeUploadDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteHelioscopeUploadQuery.getKey = (
  variables: Types.HelioscopeUploadQueryVariables,
) => ["HelioscopeUpload.infiniteSuspense", variables]

useSuspenseInfiniteHelioscopeUploadQuery.rootKey = "HelioscopeUpload.infinite"

useHelioscopeUploadQuery.fetcher = (
  variables: Types.HelioscopeUploadQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.HelioscopeUploadQuery, Types.HelioscopeUploadQueryVariables>(
    HelioscopeUploadDocument,
    variables,
    options,
  )

export const LuxDetailDocument = `
    query LuxDetail($sitePid: String!, $id: Int!) {
  site(pid: $sitePid) {
    pid
    lux(id: $id) {
      ...Lux
    }
  }
}
    ${LuxFragmentDoc}
${SlimLuxFragmentDoc}
${PvConfigFragmentDoc}
${BatteryConfigFragmentDoc}
${LuxMeterFragmentDoc}
${CostSummaryFragmentDoc}`

export const useLuxDetailQuery = <TData = Types.LuxDetailQuery, TError = unknown>(
  variables: Types.LuxDetailQueryVariables,
  options?: Omit<UseQueryOptions<Types.LuxDetailQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.LuxDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.LuxDetailQuery, TError, TData>({
    queryKey: ["LuxDetail", variables],
    queryFn: adminFetcher<Types.LuxDetailQuery, Types.LuxDetailQueryVariables>(
      LuxDetailDocument,
      variables,
    ),
    ...options,
  })
}

useLuxDetailQuery.getKey = (variables: Types.LuxDetailQueryVariables) => [
  "LuxDetail",
  variables,
]

useLuxDetailQuery.rootKey = "LuxDetail"

export const useSuspenseLuxDetailQuery = <
  TData = Types.LuxDetailQuery,
  TError = unknown,
>(
  variables: Types.LuxDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.LuxDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.LuxDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.LuxDetailQuery, TError, TData>({
    queryKey: ["LuxDetailSuspense", variables],
    queryFn: adminFetcher<Types.LuxDetailQuery, Types.LuxDetailQueryVariables>(
      LuxDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseLuxDetailQuery.getKey = (variables: Types.LuxDetailQueryVariables) => [
  "LuxDetailSuspense",
  variables,
]

useSuspenseLuxDetailQuery.rootKey = "LuxDetail"

export const useInfiniteLuxDetailQuery = <
  TData = InfiniteData<Types.LuxDetailQuery>,
  TError = unknown,
>(
  variables: Types.LuxDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.LuxDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.LuxDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.LuxDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["LuxDetail.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.LuxDetailQuery, Types.LuxDetailQueryVariables>(
            LuxDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteLuxDetailQuery.getKey = (variables: Types.LuxDetailQueryVariables) => [
  "LuxDetail.infinite",
  variables,
]

useInfiniteLuxDetailQuery.rootKey = "LuxDetail.infinite"

export const useSuspenseInfiniteLuxDetailQuery = <
  TData = InfiniteData<Types.LuxDetailQuery>,
  TError = unknown,
>(
  variables: Types.LuxDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.LuxDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.LuxDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.LuxDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["LuxDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.LuxDetailQuery, Types.LuxDetailQueryVariables>(
            LuxDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteLuxDetailQuery.getKey = (
  variables: Types.LuxDetailQueryVariables,
) => ["LuxDetail.infiniteSuspense", variables]

useSuspenseInfiniteLuxDetailQuery.rootKey = "LuxDetail.infinite"

useLuxDetailQuery.fetcher = (
  variables: Types.LuxDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.LuxDetailQuery, Types.LuxDetailQueryVariables>(
    LuxDetailDocument,
    variables,
    options,
  )

export const GetPortfolioOverridesDocument = `
    query GetPortfolioOverrides($portfolioPid: String!) {
  portfolio(pid: $portfolioPid) {
    pid
    overrides {
      ...Override
    }
  }
  finmodelSettings {
    possibleOverrides {
      name
      type
    }
  }
}
    ${OverrideFragmentDoc}`

export const useGetPortfolioOverridesQuery = <
  TData = Types.GetPortfolioOverridesQuery,
  TError = unknown,
>(
  variables: Types.GetPortfolioOverridesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetPortfolioOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetPortfolioOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.GetPortfolioOverridesQuery, TError, TData>({
    queryKey: ["GetPortfolioOverrides", variables],
    queryFn: adminFetcher<
      Types.GetPortfolioOverridesQuery,
      Types.GetPortfolioOverridesQueryVariables
    >(GetPortfolioOverridesDocument, variables),
    ...options,
  })
}

useGetPortfolioOverridesQuery.getKey = (
  variables: Types.GetPortfolioOverridesQueryVariables,
) => ["GetPortfolioOverrides", variables]

useGetPortfolioOverridesQuery.rootKey = "GetPortfolioOverrides"

export const useSuspenseGetPortfolioOverridesQuery = <
  TData = Types.GetPortfolioOverridesQuery,
  TError = unknown,
>(
  variables: Types.GetPortfolioOverridesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetPortfolioOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetPortfolioOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetPortfolioOverridesQuery, TError, TData>({
    queryKey: ["GetPortfolioOverridesSuspense", variables],
    queryFn: adminFetcher<
      Types.GetPortfolioOverridesQuery,
      Types.GetPortfolioOverridesQueryVariables
    >(GetPortfolioOverridesDocument, variables),
    ...options,
  })
}

useSuspenseGetPortfolioOverridesQuery.getKey = (
  variables: Types.GetPortfolioOverridesQueryVariables,
) => ["GetPortfolioOverridesSuspense", variables]

useSuspenseGetPortfolioOverridesQuery.rootKey = "GetPortfolioOverrides"

export const useInfiniteGetPortfolioOverridesQuery = <
  TData = InfiniteData<Types.GetPortfolioOverridesQuery>,
  TError = unknown,
>(
  variables: Types.GetPortfolioOverridesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetPortfolioOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetPortfolioOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetPortfolioOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetPortfolioOverrides.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.GetPortfolioOverridesQuery,
            Types.GetPortfolioOverridesQueryVariables
          >(GetPortfolioOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetPortfolioOverridesQuery.getKey = (
  variables: Types.GetPortfolioOverridesQueryVariables,
) => ["GetPortfolioOverrides.infinite", variables]

useInfiniteGetPortfolioOverridesQuery.rootKey = "GetPortfolioOverrides.infinite"

export const useSuspenseInfiniteGetPortfolioOverridesQuery = <
  TData = InfiniteData<Types.GetPortfolioOverridesQuery>,
  TError = unknown,
>(
  variables: Types.GetPortfolioOverridesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetPortfolioOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetPortfolioOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetPortfolioOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "GetPortfolioOverrides.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.GetPortfolioOverridesQuery,
            Types.GetPortfolioOverridesQueryVariables
          >(GetPortfolioOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetPortfolioOverridesQuery.getKey = (
  variables: Types.GetPortfolioOverridesQueryVariables,
) => ["GetPortfolioOverrides.infiniteSuspense", variables]

useSuspenseInfiniteGetPortfolioOverridesQuery.rootKey = "GetPortfolioOverrides.infinite"

useGetPortfolioOverridesQuery.fetcher = (
  variables: Types.GetPortfolioOverridesQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.GetPortfolioOverridesQuery,
    Types.GetPortfolioOverridesQueryVariables
  >(GetPortfolioOverridesDocument, variables, options)

export const GetSiteOverridesDocument = `
    query GetSiteOverrides($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    overrides {
      ...Override
    }
    portfolio {
      pid
      overrides {
        ...Override
      }
    }
  }
  finmodelSettings {
    possibleOverrides {
      name
      type
    }
  }
}
    ${OverrideFragmentDoc}`

export const useGetSiteOverridesQuery = <
  TData = Types.GetSiteOverridesQuery,
  TError = unknown,
>(
  variables: Types.GetSiteOverridesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetSiteOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.GetSiteOverridesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetSiteOverridesQuery, TError, TData>({
    queryKey: ["GetSiteOverrides", variables],
    queryFn: adminFetcher<
      Types.GetSiteOverridesQuery,
      Types.GetSiteOverridesQueryVariables
    >(GetSiteOverridesDocument, variables),
    ...options,
  })
}

useGetSiteOverridesQuery.getKey = (variables: Types.GetSiteOverridesQueryVariables) => [
  "GetSiteOverrides",
  variables,
]

useGetSiteOverridesQuery.rootKey = "GetSiteOverrides"

export const useSuspenseGetSiteOverridesQuery = <
  TData = Types.GetSiteOverridesQuery,
  TError = unknown,
>(
  variables: Types.GetSiteOverridesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetSiteOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetSiteOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetSiteOverridesQuery, TError, TData>({
    queryKey: ["GetSiteOverridesSuspense", variables],
    queryFn: adminFetcher<
      Types.GetSiteOverridesQuery,
      Types.GetSiteOverridesQueryVariables
    >(GetSiteOverridesDocument, variables),
    ...options,
  })
}

useSuspenseGetSiteOverridesQuery.getKey = (
  variables: Types.GetSiteOverridesQueryVariables,
) => ["GetSiteOverridesSuspense", variables]

useSuspenseGetSiteOverridesQuery.rootKey = "GetSiteOverrides"

export const useInfiniteGetSiteOverridesQuery = <
  TData = InfiniteData<Types.GetSiteOverridesQuery>,
  TError = unknown,
>(
  variables: Types.GetSiteOverridesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetSiteOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetSiteOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetSiteOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetSiteOverrides.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.GetSiteOverridesQuery,
            Types.GetSiteOverridesQueryVariables
          >(GetSiteOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetSiteOverridesQuery.getKey = (
  variables: Types.GetSiteOverridesQueryVariables,
) => ["GetSiteOverrides.infinite", variables]

useInfiniteGetSiteOverridesQuery.rootKey = "GetSiteOverrides.infinite"

export const useSuspenseInfiniteGetSiteOverridesQuery = <
  TData = InfiniteData<Types.GetSiteOverridesQuery>,
  TError = unknown,
>(
  variables: Types.GetSiteOverridesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetSiteOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetSiteOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetSiteOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetSiteOverrides.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.GetSiteOverridesQuery,
            Types.GetSiteOverridesQueryVariables
          >(GetSiteOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetSiteOverridesQuery.getKey = (
  variables: Types.GetSiteOverridesQueryVariables,
) => ["GetSiteOverrides.infiniteSuspense", variables]

useSuspenseInfiniteGetSiteOverridesQuery.rootKey = "GetSiteOverrides.infinite"

useGetSiteOverridesQuery.fetcher = (
  variables: Types.GetSiteOverridesQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.GetSiteOverridesQuery, Types.GetSiteOverridesQueryVariables>(
    GetSiteOverridesDocument,
    variables,
    options,
  )

export const GetFinancingGiverOrgOverridesDocument = `
    query GetFinancingGiverOrgOverrides($orgPid: String!) {
  org(orgPid: $orgPid) {
    pid
    overrides {
      ...Override
    }
  }
  finmodelSettings {
    possibleOverrides {
      name
      type
    }
  }
}
    ${OverrideFragmentDoc}`

export const useGetFinancingGiverOrgOverridesQuery = <
  TData = Types.GetFinancingGiverOrgOverridesQuery,
  TError = unknown,
>(
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetFinancingGiverOrgOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetFinancingGiverOrgOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.GetFinancingGiverOrgOverridesQuery, TError, TData>({
    queryKey: ["GetFinancingGiverOrgOverrides", variables],
    queryFn: adminFetcher<
      Types.GetFinancingGiverOrgOverridesQuery,
      Types.GetFinancingGiverOrgOverridesQueryVariables
    >(GetFinancingGiverOrgOverridesDocument, variables),
    ...options,
  })
}

useGetFinancingGiverOrgOverridesQuery.getKey = (
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
) => ["GetFinancingGiverOrgOverrides", variables]

useGetFinancingGiverOrgOverridesQuery.rootKey = "GetFinancingGiverOrgOverrides"

export const useSuspenseGetFinancingGiverOrgOverridesQuery = <
  TData = Types.GetFinancingGiverOrgOverridesQuery,
  TError = unknown,
>(
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetFinancingGiverOrgOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetFinancingGiverOrgOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetFinancingGiverOrgOverridesQuery, TError, TData>({
    queryKey: ["GetFinancingGiverOrgOverridesSuspense", variables],
    queryFn: adminFetcher<
      Types.GetFinancingGiverOrgOverridesQuery,
      Types.GetFinancingGiverOrgOverridesQueryVariables
    >(GetFinancingGiverOrgOverridesDocument, variables),
    ...options,
  })
}

useSuspenseGetFinancingGiverOrgOverridesQuery.getKey = (
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
) => ["GetFinancingGiverOrgOverridesSuspense", variables]

useSuspenseGetFinancingGiverOrgOverridesQuery.rootKey = "GetFinancingGiverOrgOverrides"

export const useInfiniteGetFinancingGiverOrgOverridesQuery = <
  TData = InfiniteData<Types.GetFinancingGiverOrgOverridesQuery>,
  TError = unknown,
>(
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetFinancingGiverOrgOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetFinancingGiverOrgOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetFinancingGiverOrgOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "GetFinancingGiverOrgOverrides.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.GetFinancingGiverOrgOverridesQuery,
            Types.GetFinancingGiverOrgOverridesQueryVariables
          >(GetFinancingGiverOrgOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetFinancingGiverOrgOverridesQuery.getKey = (
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
) => ["GetFinancingGiverOrgOverrides.infinite", variables]

useInfiniteGetFinancingGiverOrgOverridesQuery.rootKey =
  "GetFinancingGiverOrgOverrides.infinite"

export const useSuspenseInfiniteGetFinancingGiverOrgOverridesQuery = <
  TData = InfiniteData<Types.GetFinancingGiverOrgOverridesQuery>,
  TError = unknown,
>(
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.GetFinancingGiverOrgOverridesQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetFinancingGiverOrgOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.GetFinancingGiverOrgOverridesQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "GetFinancingGiverOrgOverrides.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.GetFinancingGiverOrgOverridesQuery,
            Types.GetFinancingGiverOrgOverridesQueryVariables
          >(GetFinancingGiverOrgOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetFinancingGiverOrgOverridesQuery.getKey = (
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
) => ["GetFinancingGiverOrgOverrides.infiniteSuspense", variables]

useSuspenseInfiniteGetFinancingGiverOrgOverridesQuery.rootKey =
  "GetFinancingGiverOrgOverrides.infinite"

useGetFinancingGiverOrgOverridesQuery.fetcher = (
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.GetFinancingGiverOrgOverridesQuery,
    Types.GetFinancingGiverOrgOverridesQueryVariables
  >(GetFinancingGiverOrgOverridesDocument, variables, options)

export const FinproductOverridesDocument = `
    query FinproductOverrides($finProductId: Int!) {
  finproduct(finproductId: $finProductId) {
    id
    finmodelInputOverrides {
      ...Override
    }
  }
  finmodelSettings {
    possibleOverrides {
      name
      type
    }
  }
}
    ${OverrideFragmentDoc}`

export const useFinproductOverridesQuery = <
  TData = Types.FinproductOverridesQuery,
  TError = unknown,
>(
  variables: Types.FinproductOverridesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinproductOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FinproductOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FinproductOverridesQuery, TError, TData>({
    queryKey: ["FinproductOverrides", variables],
    queryFn: adminFetcher<
      Types.FinproductOverridesQuery,
      Types.FinproductOverridesQueryVariables
    >(FinproductOverridesDocument, variables),
    ...options,
  })
}

useFinproductOverridesQuery.getKey = (
  variables: Types.FinproductOverridesQueryVariables,
) => ["FinproductOverrides", variables]

useFinproductOverridesQuery.rootKey = "FinproductOverrides"

export const useSuspenseFinproductOverridesQuery = <
  TData = Types.FinproductOverridesQuery,
  TError = unknown,
>(
  variables: Types.FinproductOverridesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinproductOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinproductOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinproductOverridesQuery, TError, TData>({
    queryKey: ["FinproductOverridesSuspense", variables],
    queryFn: adminFetcher<
      Types.FinproductOverridesQuery,
      Types.FinproductOverridesQueryVariables
    >(FinproductOverridesDocument, variables),
    ...options,
  })
}

useSuspenseFinproductOverridesQuery.getKey = (
  variables: Types.FinproductOverridesQueryVariables,
) => ["FinproductOverridesSuspense", variables]

useSuspenseFinproductOverridesQuery.rootKey = "FinproductOverrides"

export const useInfiniteFinproductOverridesQuery = <
  TData = InfiniteData<Types.FinproductOverridesQuery>,
  TError = unknown,
>(
  variables: Types.FinproductOverridesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinproductOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinproductOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinproductOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinproductOverrides.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinproductOverridesQuery,
            Types.FinproductOverridesQueryVariables
          >(FinproductOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinproductOverridesQuery.getKey = (
  variables: Types.FinproductOverridesQueryVariables,
) => ["FinproductOverrides.infinite", variables]

useInfiniteFinproductOverridesQuery.rootKey = "FinproductOverrides.infinite"

export const useSuspenseInfiniteFinproductOverridesQuery = <
  TData = InfiniteData<Types.FinproductOverridesQuery>,
  TError = unknown,
>(
  variables: Types.FinproductOverridesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinproductOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinproductOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinproductOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "FinproductOverrides.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinproductOverridesQuery,
            Types.FinproductOverridesQueryVariables
          >(FinproductOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinproductOverridesQuery.getKey = (
  variables: Types.FinproductOverridesQueryVariables,
) => ["FinproductOverrides.infiniteSuspense", variables]

useSuspenseInfiniteFinproductOverridesQuery.rootKey = "FinproductOverrides.infinite"

useFinproductOverridesQuery.fetcher = (
  variables: Types.FinproductOverridesQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.FinproductOverridesQuery, Types.FinproductOverridesQueryVariables>(
    FinproductOverridesDocument,
    variables,
    options,
  )

export const SiteUtilityDocument = `
    query SiteUtility($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    zipCode
    thirdPartySupplier {
      id
      name
    }
    targetUtility {
      id
      name
      shortName
    }
    targetUtilitySetBy {
      ...UserRef
    }
    dateTargetUtilitySet
    utilityName
    probableUtilities {
      id
      name
    }
    isMultifamily
    ...Metering
    customTariffs {
      id
      name
      code
      parent {
        id
      }
    }
  }
}
    ${UserRefFragmentDoc}
${MeteringFragmentDoc}`

export const useSiteUtilityQuery = <TData = Types.SiteUtilityQuery, TError = unknown>(
  variables: Types.SiteUtilityQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteUtilityQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteUtilityQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteUtilityQuery, TError, TData>({
    queryKey: ["SiteUtility", variables],
    queryFn: adminFetcher<Types.SiteUtilityQuery, Types.SiteUtilityQueryVariables>(
      SiteUtilityDocument,
      variables,
    ),
    ...options,
  })
}

useSiteUtilityQuery.getKey = (variables: Types.SiteUtilityQueryVariables) => [
  "SiteUtility",
  variables,
]

useSiteUtilityQuery.rootKey = "SiteUtility"

export const useSuspenseSiteUtilityQuery = <
  TData = Types.SiteUtilityQuery,
  TError = unknown,
>(
  variables: Types.SiteUtilityQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteUtilityQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteUtilityQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteUtilityQuery, TError, TData>({
    queryKey: ["SiteUtilitySuspense", variables],
    queryFn: adminFetcher<Types.SiteUtilityQuery, Types.SiteUtilityQueryVariables>(
      SiteUtilityDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteUtilityQuery.getKey = (variables: Types.SiteUtilityQueryVariables) => [
  "SiteUtilitySuspense",
  variables,
]

useSuspenseSiteUtilityQuery.rootKey = "SiteUtility"

export const useInfiniteSiteUtilityQuery = <
  TData = InfiniteData<Types.SiteUtilityQuery>,
  TError = unknown,
>(
  variables: Types.SiteUtilityQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteUtilityQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteUtilityQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteUtilityQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteUtility.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SiteUtilityQuery, Types.SiteUtilityQueryVariables>(
            SiteUtilityDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteUtilityQuery.getKey = (variables: Types.SiteUtilityQueryVariables) => [
  "SiteUtility.infinite",
  variables,
]

useInfiniteSiteUtilityQuery.rootKey = "SiteUtility.infinite"

export const useSuspenseInfiniteSiteUtilityQuery = <
  TData = InfiniteData<Types.SiteUtilityQuery>,
  TError = unknown,
>(
  variables: Types.SiteUtilityQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteUtilityQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteUtilityQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteUtilityQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteUtility.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SiteUtilityQuery, Types.SiteUtilityQueryVariables>(
            SiteUtilityDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteUtilityQuery.getKey = (
  variables: Types.SiteUtilityQueryVariables,
) => ["SiteUtility.infiniteSuspense", variables]

useSuspenseInfiniteSiteUtilityQuery.rootKey = "SiteUtility.infinite"

useSiteUtilityQuery.fetcher = (
  variables: Types.SiteUtilityQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SiteUtilityQuery, Types.SiteUtilityQueryVariables>(
    SiteUtilityDocument,
    variables,
    options,
  )

export const UtilitySearchDocument = `
    query UtilitySearch($name: String!) {
  tariffs {
    searchGenabilityByUtilityName(name: $name) {
      id
      name
    }
  }
}
    `

export const useUtilitySearchQuery = <
  TData = Types.UtilitySearchQuery,
  TError = unknown,
>(
  variables: Types.UtilitySearchQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.UtilitySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.UtilitySearchQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.UtilitySearchQuery, TError, TData>({
    queryKey: ["UtilitySearch", variables],
    queryFn: adminFetcher<Types.UtilitySearchQuery, Types.UtilitySearchQueryVariables>(
      UtilitySearchDocument,
      variables,
    ),
    ...options,
  })
}

useUtilitySearchQuery.getKey = (variables: Types.UtilitySearchQueryVariables) => [
  "UtilitySearch",
  variables,
]

useUtilitySearchQuery.rootKey = "UtilitySearch"

export const useSuspenseUtilitySearchQuery = <
  TData = Types.UtilitySearchQuery,
  TError = unknown,
>(
  variables: Types.UtilitySearchQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UtilitySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UtilitySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UtilitySearchQuery, TError, TData>({
    queryKey: ["UtilitySearchSuspense", variables],
    queryFn: adminFetcher<Types.UtilitySearchQuery, Types.UtilitySearchQueryVariables>(
      UtilitySearchDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseUtilitySearchQuery.getKey = (
  variables: Types.UtilitySearchQueryVariables,
) => ["UtilitySearchSuspense", variables]

useSuspenseUtilitySearchQuery.rootKey = "UtilitySearch"

export const useInfiniteUtilitySearchQuery = <
  TData = InfiniteData<Types.UtilitySearchQuery>,
  TError = unknown,
>(
  variables: Types.UtilitySearchQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UtilitySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UtilitySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UtilitySearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilitySearch.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.UtilitySearchQuery, Types.UtilitySearchQueryVariables>(
            UtilitySearchDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUtilitySearchQuery.getKey = (
  variables: Types.UtilitySearchQueryVariables,
) => ["UtilitySearch.infinite", variables]

useInfiniteUtilitySearchQuery.rootKey = "UtilitySearch.infinite"

export const useSuspenseInfiniteUtilitySearchQuery = <
  TData = InfiniteData<Types.UtilitySearchQuery>,
  TError = unknown,
>(
  variables: Types.UtilitySearchQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UtilitySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UtilitySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UtilitySearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilitySearch.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.UtilitySearchQuery, Types.UtilitySearchQueryVariables>(
            UtilitySearchDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUtilitySearchQuery.getKey = (
  variables: Types.UtilitySearchQueryVariables,
) => ["UtilitySearch.infiniteSuspense", variables]

useSuspenseInfiniteUtilitySearchQuery.rootKey = "UtilitySearch.infinite"

useUtilitySearchQuery.fetcher = (
  variables: Types.UtilitySearchQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.UtilitySearchQuery, Types.UtilitySearchQueryVariables>(
    UtilitySearchDocument,
    variables,
    options,
  )

export const SiteUtilityByLabelDocument = `
    query SiteUtilityByLabel($sitePid: String!, $label: String!) {
  site(pid: $sitePid) {
    pid
    utilityByLabel(label: $label) {
      id
      name
    }
    probableUtilities {
      id
      name
    }
  }
}
    `

export const useSiteUtilityByLabelQuery = <
  TData = Types.SiteUtilityByLabelQuery,
  TError = unknown,
>(
  variables: Types.SiteUtilityByLabelQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteUtilityByLabelQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteUtilityByLabelQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteUtilityByLabelQuery, TError, TData>({
    queryKey: ["SiteUtilityByLabel", variables],
    queryFn: adminFetcher<
      Types.SiteUtilityByLabelQuery,
      Types.SiteUtilityByLabelQueryVariables
    >(SiteUtilityByLabelDocument, variables),
    ...options,
  })
}

useSiteUtilityByLabelQuery.getKey = (
  variables: Types.SiteUtilityByLabelQueryVariables,
) => ["SiteUtilityByLabel", variables]

useSiteUtilityByLabelQuery.rootKey = "SiteUtilityByLabel"

export const useSuspenseSiteUtilityByLabelQuery = <
  TData = Types.SiteUtilityByLabelQuery,
  TError = unknown,
>(
  variables: Types.SiteUtilityByLabelQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteUtilityByLabelQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteUtilityByLabelQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteUtilityByLabelQuery, TError, TData>({
    queryKey: ["SiteUtilityByLabelSuspense", variables],
    queryFn: adminFetcher<
      Types.SiteUtilityByLabelQuery,
      Types.SiteUtilityByLabelQueryVariables
    >(SiteUtilityByLabelDocument, variables),
    ...options,
  })
}

useSuspenseSiteUtilityByLabelQuery.getKey = (
  variables: Types.SiteUtilityByLabelQueryVariables,
) => ["SiteUtilityByLabelSuspense", variables]

useSuspenseSiteUtilityByLabelQuery.rootKey = "SiteUtilityByLabel"

export const useInfiniteSiteUtilityByLabelQuery = <
  TData = InfiniteData<Types.SiteUtilityByLabelQuery>,
  TError = unknown,
>(
  variables: Types.SiteUtilityByLabelQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteUtilityByLabelQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteUtilityByLabelQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteUtilityByLabelQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteUtilityByLabel.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.SiteUtilityByLabelQuery,
            Types.SiteUtilityByLabelQueryVariables
          >(SiteUtilityByLabelDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteUtilityByLabelQuery.getKey = (
  variables: Types.SiteUtilityByLabelQueryVariables,
) => ["SiteUtilityByLabel.infinite", variables]

useInfiniteSiteUtilityByLabelQuery.rootKey = "SiteUtilityByLabel.infinite"

export const useSuspenseInfiniteSiteUtilityByLabelQuery = <
  TData = InfiniteData<Types.SiteUtilityByLabelQuery>,
  TError = unknown,
>(
  variables: Types.SiteUtilityByLabelQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteUtilityByLabelQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteUtilityByLabelQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteUtilityByLabelQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteUtilityByLabel.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.SiteUtilityByLabelQuery,
            Types.SiteUtilityByLabelQueryVariables
          >(SiteUtilityByLabelDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteUtilityByLabelQuery.getKey = (
  variables: Types.SiteUtilityByLabelQueryVariables,
) => ["SiteUtilityByLabel.infiniteSuspense", variables]

useSuspenseInfiniteSiteUtilityByLabelQuery.rootKey = "SiteUtilityByLabel.infinite"

useSiteUtilityByLabelQuery.fetcher = (
  variables: Types.SiteUtilityByLabelQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SiteUtilityByLabelQuery, Types.SiteUtilityByLabelQueryVariables>(
    SiteUtilityByLabelDocument,
    variables,
    options,
  )

export const ThirdPartySuppliersDocument = `
    query ThirdPartySuppliers {
  thirdPartySuppliers {
    id
    pid
    name
  }
}
    `

export const useThirdPartySuppliersQuery = <
  TData = Types.ThirdPartySuppliersQuery,
  TError = unknown,
>(
  variables?: Types.ThirdPartySuppliersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ThirdPartySuppliersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ThirdPartySuppliersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ThirdPartySuppliersQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["ThirdPartySuppliers"]
        : ["ThirdPartySuppliers", variables],
    queryFn: adminFetcher<
      Types.ThirdPartySuppliersQuery,
      Types.ThirdPartySuppliersQueryVariables
    >(ThirdPartySuppliersDocument, variables),
    ...options,
  })
}

useThirdPartySuppliersQuery.getKey = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
) =>
  variables === undefined ? ["ThirdPartySuppliers"] : ["ThirdPartySuppliers", variables]

useThirdPartySuppliersQuery.rootKey = "ThirdPartySuppliers"

export const useSuspenseThirdPartySuppliersQuery = <
  TData = Types.ThirdPartySuppliersQuery,
  TError = unknown,
>(
  variables?: Types.ThirdPartySuppliersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ThirdPartySuppliersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ThirdPartySuppliersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ThirdPartySuppliersQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["ThirdPartySuppliersSuspense"]
        : ["ThirdPartySuppliersSuspense", variables],
    queryFn: adminFetcher<
      Types.ThirdPartySuppliersQuery,
      Types.ThirdPartySuppliersQueryVariables
    >(ThirdPartySuppliersDocument, variables),
    ...options,
  })
}

useSuspenseThirdPartySuppliersQuery.getKey = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
) =>
  variables === undefined
    ? ["ThirdPartySuppliersSuspense"]
    : ["ThirdPartySuppliersSuspense", variables]

useSuspenseThirdPartySuppliersQuery.rootKey = "ThirdPartySuppliers"

export const useInfiniteThirdPartySuppliersQuery = <
  TData = InfiniteData<Types.ThirdPartySuppliersQuery>,
  TError = unknown,
>(
  variables: Types.ThirdPartySuppliersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ThirdPartySuppliersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ThirdPartySuppliersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ThirdPartySuppliersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ThirdPartySuppliers.infinite"]
            : ["ThirdPartySuppliers.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.ThirdPartySuppliersQuery,
            Types.ThirdPartySuppliersQueryVariables
          >(ThirdPartySuppliersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteThirdPartySuppliersQuery.getKey = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
) =>
  variables === undefined
    ? ["ThirdPartySuppliers.infinite"]
    : ["ThirdPartySuppliers.infinite", variables]

useInfiniteThirdPartySuppliersQuery.rootKey = "ThirdPartySuppliers.infinite"

export const useSuspenseInfiniteThirdPartySuppliersQuery = <
  TData = InfiniteData<Types.ThirdPartySuppliersQuery>,
  TError = unknown,
>(
  variables: Types.ThirdPartySuppliersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ThirdPartySuppliersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ThirdPartySuppliersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ThirdPartySuppliersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ThirdPartySuppliers.infiniteSuspense"]
            : ["ThirdPartySuppliers.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.ThirdPartySuppliersQuery,
            Types.ThirdPartySuppliersQueryVariables
          >(ThirdPartySuppliersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteThirdPartySuppliersQuery.getKey = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
) =>
  variables === undefined
    ? ["ThirdPartySuppliers.infiniteSuspense"]
    : ["ThirdPartySuppliers.infiniteSuspense", variables]

useSuspenseInfiniteThirdPartySuppliersQuery.rootKey = "ThirdPartySuppliers.infinite"

useThirdPartySuppliersQuery.fetcher = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.ThirdPartySuppliersQuery, Types.ThirdPartySuppliersQueryVariables>(
    ThirdPartySuppliersDocument,
    variables,
    options,
  )

export const TariffRateDetailDocument = `
    query TariffRateDetail($sitePid: String!, $tariffParentId: Int!) {
  site(pid: $sitePid) {
    pid
    tariffDetail(tariffParentId: $tariffParentId) {
      ...TariffDetailFragment
    }
  }
}
    ${TariffDetailFragmentFragmentDoc}`

export const useTariffRateDetailQuery = <
  TData = Types.TariffRateDetailQuery,
  TError = unknown,
>(
  variables: Types.TariffRateDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.TariffRateDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.TariffRateDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.TariffRateDetailQuery, TError, TData>({
    queryKey: ["TariffRateDetail", variables],
    queryFn: adminFetcher<
      Types.TariffRateDetailQuery,
      Types.TariffRateDetailQueryVariables
    >(TariffRateDetailDocument, variables),
    ...options,
  })
}

useTariffRateDetailQuery.getKey = (variables: Types.TariffRateDetailQueryVariables) => [
  "TariffRateDetail",
  variables,
]

useTariffRateDetailQuery.rootKey = "TariffRateDetail"

export const useSuspenseTariffRateDetailQuery = <
  TData = Types.TariffRateDetailQuery,
  TError = unknown,
>(
  variables: Types.TariffRateDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.TariffRateDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.TariffRateDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.TariffRateDetailQuery, TError, TData>({
    queryKey: ["TariffRateDetailSuspense", variables],
    queryFn: adminFetcher<
      Types.TariffRateDetailQuery,
      Types.TariffRateDetailQueryVariables
    >(TariffRateDetailDocument, variables),
    ...options,
  })
}

useSuspenseTariffRateDetailQuery.getKey = (
  variables: Types.TariffRateDetailQueryVariables,
) => ["TariffRateDetailSuspense", variables]

useSuspenseTariffRateDetailQuery.rootKey = "TariffRateDetail"

export const useInfiniteTariffRateDetailQuery = <
  TData = InfiniteData<Types.TariffRateDetailQuery>,
  TError = unknown,
>(
  variables: Types.TariffRateDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.TariffRateDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.TariffRateDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.TariffRateDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffRateDetail.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.TariffRateDetailQuery,
            Types.TariffRateDetailQueryVariables
          >(TariffRateDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteTariffRateDetailQuery.getKey = (
  variables: Types.TariffRateDetailQueryVariables,
) => ["TariffRateDetail.infinite", variables]

useInfiniteTariffRateDetailQuery.rootKey = "TariffRateDetail.infinite"

export const useSuspenseInfiniteTariffRateDetailQuery = <
  TData = InfiniteData<Types.TariffRateDetailQuery>,
  TError = unknown,
>(
  variables: Types.TariffRateDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.TariffRateDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.TariffRateDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.TariffRateDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffRateDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.TariffRateDetailQuery,
            Types.TariffRateDetailQueryVariables
          >(TariffRateDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteTariffRateDetailQuery.getKey = (
  variables: Types.TariffRateDetailQueryVariables,
) => ["TariffRateDetail.infiniteSuspense", variables]

useSuspenseInfiniteTariffRateDetailQuery.rootKey = "TariffRateDetail.infinite"

useTariffRateDetailQuery.fetcher = (
  variables: Types.TariffRateDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.TariffRateDetailQuery, Types.TariffRateDetailQueryVariables>(
    TariffRateDetailDocument,
    variables,
    options,
  )

export const AdminSearchDocument = `
    query AdminSearch($q: String!) {
  search(q: $q) {
    id
    pid
    name
    fullAddress
    portfolio {
      pid
      title
    }
  }
}
    `

export const useAdminSearchQuery = <TData = Types.AdminSearchQuery, TError = unknown>(
  variables: Types.AdminSearchQueryVariables,
  options?: Omit<UseQueryOptions<Types.AdminSearchQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.AdminSearchQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminSearchQuery, TError, TData>({
    queryKey: ["AdminSearch", variables],
    queryFn: adminFetcher<Types.AdminSearchQuery, Types.AdminSearchQueryVariables>(
      AdminSearchDocument,
      variables,
    ),
    ...options,
  })
}

useAdminSearchQuery.getKey = (variables: Types.AdminSearchQueryVariables) => [
  "AdminSearch",
  variables,
]

useAdminSearchQuery.rootKey = "AdminSearch"

export const useSuspenseAdminSearchQuery = <
  TData = Types.AdminSearchQuery,
  TError = unknown,
>(
  variables: Types.AdminSearchQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSearchQuery, TError, TData>({
    queryKey: ["AdminSearchSuspense", variables],
    queryFn: adminFetcher<Types.AdminSearchQuery, Types.AdminSearchQueryVariables>(
      AdminSearchDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseAdminSearchQuery.getKey = (variables: Types.AdminSearchQueryVariables) => [
  "AdminSearchSuspense",
  variables,
]

useSuspenseAdminSearchQuery.rootKey = "AdminSearch"

export const useInfiniteAdminSearchQuery = <
  TData = InfiniteData<Types.AdminSearchQuery>,
  TError = unknown,
>(
  variables: Types.AdminSearchQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSearch.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.AdminSearchQuery, Types.AdminSearchQueryVariables>(
            AdminSearchDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSearchQuery.getKey = (variables: Types.AdminSearchQueryVariables) => [
  "AdminSearch.infinite",
  variables,
]

useInfiniteAdminSearchQuery.rootKey = "AdminSearch.infinite"

export const useSuspenseInfiniteAdminSearchQuery = <
  TData = InfiniteData<Types.AdminSearchQuery>,
  TError = unknown,
>(
  variables: Types.AdminSearchQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSearch.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.AdminSearchQuery, Types.AdminSearchQueryVariables>(
            AdminSearchDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSearchQuery.getKey = (
  variables: Types.AdminSearchQueryVariables,
) => ["AdminSearch.infiniteSuspense", variables]

useSuspenseInfiniteAdminSearchQuery.rootKey = "AdminSearch.infinite"

useAdminSearchQuery.fetcher = (
  variables: Types.AdminSearchQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AdminSearchQuery, Types.AdminSearchQueryVariables>(
    AdminSearchDocument,
    variables,
    options,
  )

export const CostarOwnerNameSearchDocument = `
    query CostarOwnerNameSearch($q: String!) {
  searchForOwnerName(q: $q) {
    ownerName
    countSitesInPortfolio
    countSitesNotInPortfolio
    primaryState
  }
}
    `

export const useCostarOwnerNameSearchQuery = <
  TData = Types.CostarOwnerNameSearchQuery,
  TError = unknown,
>(
  variables: Types.CostarOwnerNameSearchQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CostarOwnerNameSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.CostarOwnerNameSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.CostarOwnerNameSearchQuery, TError, TData>({
    queryKey: ["CostarOwnerNameSearch", variables],
    queryFn: adminFetcher<
      Types.CostarOwnerNameSearchQuery,
      Types.CostarOwnerNameSearchQueryVariables
    >(CostarOwnerNameSearchDocument, variables),
    ...options,
  })
}

useCostarOwnerNameSearchQuery.getKey = (
  variables: Types.CostarOwnerNameSearchQueryVariables,
) => ["CostarOwnerNameSearch", variables]

useCostarOwnerNameSearchQuery.rootKey = "CostarOwnerNameSearch"

export const useSuspenseCostarOwnerNameSearchQuery = <
  TData = Types.CostarOwnerNameSearchQuery,
  TError = unknown,
>(
  variables: Types.CostarOwnerNameSearchQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CostarOwnerNameSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CostarOwnerNameSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CostarOwnerNameSearchQuery, TError, TData>({
    queryKey: ["CostarOwnerNameSearchSuspense", variables],
    queryFn: adminFetcher<
      Types.CostarOwnerNameSearchQuery,
      Types.CostarOwnerNameSearchQueryVariables
    >(CostarOwnerNameSearchDocument, variables),
    ...options,
  })
}

useSuspenseCostarOwnerNameSearchQuery.getKey = (
  variables: Types.CostarOwnerNameSearchQueryVariables,
) => ["CostarOwnerNameSearchSuspense", variables]

useSuspenseCostarOwnerNameSearchQuery.rootKey = "CostarOwnerNameSearch"

export const useInfiniteCostarOwnerNameSearchQuery = <
  TData = InfiniteData<Types.CostarOwnerNameSearchQuery>,
  TError = unknown,
>(
  variables: Types.CostarOwnerNameSearchQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CostarOwnerNameSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CostarOwnerNameSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CostarOwnerNameSearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CostarOwnerNameSearch.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.CostarOwnerNameSearchQuery,
            Types.CostarOwnerNameSearchQueryVariables
          >(CostarOwnerNameSearchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCostarOwnerNameSearchQuery.getKey = (
  variables: Types.CostarOwnerNameSearchQueryVariables,
) => ["CostarOwnerNameSearch.infinite", variables]

useInfiniteCostarOwnerNameSearchQuery.rootKey = "CostarOwnerNameSearch.infinite"

export const useSuspenseInfiniteCostarOwnerNameSearchQuery = <
  TData = InfiniteData<Types.CostarOwnerNameSearchQuery>,
  TError = unknown,
>(
  variables: Types.CostarOwnerNameSearchQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CostarOwnerNameSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CostarOwnerNameSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CostarOwnerNameSearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "CostarOwnerNameSearch.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.CostarOwnerNameSearchQuery,
            Types.CostarOwnerNameSearchQueryVariables
          >(CostarOwnerNameSearchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCostarOwnerNameSearchQuery.getKey = (
  variables: Types.CostarOwnerNameSearchQueryVariables,
) => ["CostarOwnerNameSearch.infiniteSuspense", variables]

useSuspenseInfiniteCostarOwnerNameSearchQuery.rootKey = "CostarOwnerNameSearch.infinite"

useCostarOwnerNameSearchQuery.fetcher = (
  variables: Types.CostarOwnerNameSearchQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.CostarOwnerNameSearchQuery,
    Types.CostarOwnerNameSearchQueryVariables
  >(CostarOwnerNameSearchDocument, variables, options)

export const CostarSitesByOwnerNameDocument = `
    query CostarSitesByOwnerName($name: String!) {
  sitesByOwnerName(name: $name) {
    pid
    displayName
    costarPropertyType
    city
    state
  }
}
    `

export const useCostarSitesByOwnerNameQuery = <
  TData = Types.CostarSitesByOwnerNameQuery,
  TError = unknown,
>(
  variables: Types.CostarSitesByOwnerNameQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CostarSitesByOwnerNameQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.CostarSitesByOwnerNameQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.CostarSitesByOwnerNameQuery, TError, TData>({
    queryKey: ["CostarSitesByOwnerName", variables],
    queryFn: adminFetcher<
      Types.CostarSitesByOwnerNameQuery,
      Types.CostarSitesByOwnerNameQueryVariables
    >(CostarSitesByOwnerNameDocument, variables),
    ...options,
  })
}

useCostarSitesByOwnerNameQuery.getKey = (
  variables: Types.CostarSitesByOwnerNameQueryVariables,
) => ["CostarSitesByOwnerName", variables]

useCostarSitesByOwnerNameQuery.rootKey = "CostarSitesByOwnerName"

export const useSuspenseCostarSitesByOwnerNameQuery = <
  TData = Types.CostarSitesByOwnerNameQuery,
  TError = unknown,
>(
  variables: Types.CostarSitesByOwnerNameQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CostarSitesByOwnerNameQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CostarSitesByOwnerNameQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CostarSitesByOwnerNameQuery, TError, TData>({
    queryKey: ["CostarSitesByOwnerNameSuspense", variables],
    queryFn: adminFetcher<
      Types.CostarSitesByOwnerNameQuery,
      Types.CostarSitesByOwnerNameQueryVariables
    >(CostarSitesByOwnerNameDocument, variables),
    ...options,
  })
}

useSuspenseCostarSitesByOwnerNameQuery.getKey = (
  variables: Types.CostarSitesByOwnerNameQueryVariables,
) => ["CostarSitesByOwnerNameSuspense", variables]

useSuspenseCostarSitesByOwnerNameQuery.rootKey = "CostarSitesByOwnerName"

export const useInfiniteCostarSitesByOwnerNameQuery = <
  TData = InfiniteData<Types.CostarSitesByOwnerNameQuery>,
  TError = unknown,
>(
  variables: Types.CostarSitesByOwnerNameQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CostarSitesByOwnerNameQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CostarSitesByOwnerNameQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CostarSitesByOwnerNameQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CostarSitesByOwnerName.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.CostarSitesByOwnerNameQuery,
            Types.CostarSitesByOwnerNameQueryVariables
          >(CostarSitesByOwnerNameDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCostarSitesByOwnerNameQuery.getKey = (
  variables: Types.CostarSitesByOwnerNameQueryVariables,
) => ["CostarSitesByOwnerName.infinite", variables]

useInfiniteCostarSitesByOwnerNameQuery.rootKey = "CostarSitesByOwnerName.infinite"

export const useSuspenseInfiniteCostarSitesByOwnerNameQuery = <
  TData = InfiniteData<Types.CostarSitesByOwnerNameQuery>,
  TError = unknown,
>(
  variables: Types.CostarSitesByOwnerNameQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CostarSitesByOwnerNameQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CostarSitesByOwnerNameQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CostarSitesByOwnerNameQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "CostarSitesByOwnerName.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.CostarSitesByOwnerNameQuery,
            Types.CostarSitesByOwnerNameQueryVariables
          >(CostarSitesByOwnerNameDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCostarSitesByOwnerNameQuery.getKey = (
  variables: Types.CostarSitesByOwnerNameQueryVariables,
) => ["CostarSitesByOwnerName.infiniteSuspense", variables]

useSuspenseInfiniteCostarSitesByOwnerNameQuery.rootKey =
  "CostarSitesByOwnerName.infinite"

useCostarSitesByOwnerNameQuery.fetcher = (
  variables: Types.CostarSitesByOwnerNameQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.CostarSitesByOwnerNameQuery,
    Types.CostarSitesByOwnerNameQueryVariables
  >(CostarSitesByOwnerNameDocument, variables, options)

export const ScenarioListDocument = `
    query ScenarioList($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    scenarios {
      ...ScenarioFragment
    }
  }
}
    ${ScenarioFragmentFragmentDoc}
${SmallScenarioFragmentFragmentDoc}
${UserRefFragmentDoc}`

export const useScenarioListQuery = <TData = Types.ScenarioListQuery, TError = unknown>(
  variables: Types.ScenarioListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ScenarioListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ScenarioListQuery, TError, TData>({
    queryKey: ["ScenarioList", variables],
    queryFn: adminFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
      ScenarioListDocument,
      variables,
    ),
    ...options,
  })
}

useScenarioListQuery.getKey = (variables: Types.ScenarioListQueryVariables) => [
  "ScenarioList",
  variables,
]

useScenarioListQuery.rootKey = "ScenarioList"

export const useSuspenseScenarioListQuery = <
  TData = Types.ScenarioListQuery,
  TError = unknown,
>(
  variables: Types.ScenarioListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ScenarioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ScenarioListQuery, TError, TData>({
    queryKey: ["ScenarioListSuspense", variables],
    queryFn: adminFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
      ScenarioListDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseScenarioListQuery.getKey = (variables: Types.ScenarioListQueryVariables) => [
  "ScenarioListSuspense",
  variables,
]

useSuspenseScenarioListQuery.rootKey = "ScenarioList"

export const useInfiniteScenarioListQuery = <
  TData = InfiniteData<Types.ScenarioListQuery>,
  TError = unknown,
>(
  variables: Types.ScenarioListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ScenarioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ScenarioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ScenarioList.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
            ScenarioListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteScenarioListQuery.getKey = (variables: Types.ScenarioListQueryVariables) => [
  "ScenarioList.infinite",
  variables,
]

useInfiniteScenarioListQuery.rootKey = "ScenarioList.infinite"

export const useSuspenseInfiniteScenarioListQuery = <
  TData = InfiniteData<Types.ScenarioListQuery>,
  TError = unknown,
>(
  variables: Types.ScenarioListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ScenarioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ScenarioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ScenarioList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
            ScenarioListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteScenarioListQuery.getKey = (
  variables: Types.ScenarioListQueryVariables,
) => ["ScenarioList.infiniteSuspense", variables]

useSuspenseInfiniteScenarioListQuery.rootKey = "ScenarioList.infinite"

useScenarioListQuery.fetcher = (
  variables: Types.ScenarioListQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
    ScenarioListDocument,
    variables,
    options,
  )

export const ScenarioDetailDocument = `
    query ScenarioDetail($sitePid: String!, $scenarioPid: String!) {
  site(pid: $sitePid) {
    pid
    attachments {
      ...AttachmentFragment
    }
    helioscopes {
      ...ScenarioHelioscopeFragment
    }
    etbReports {
      ...ScenarioEtbFragment
    }
    scenario(pid: $scenarioPid) {
      ...ScenarioFragment
      etbs {
        id
        projectName
        dateCreated
      }
      configs {
        id
        createdAt
        finproduct {
          __typename
          financier {
            pid
            name
          }
          id
          createdAt
          name
          type
        }
        finproductType
        goal
        roofKw
        carportKw
        groundKw
        battery {
          sizeKw
          sizeKwh
          vendor
          equipmentCost
          laborCost
        }
        helioscope {
          ...ScenarioHelioscopeFragment
        }
        etb {
          ...ScenarioEtbFragment
        }
      }
    }
  }
}
    ${AttachmentFragmentFragmentDoc}
${UserRefFragmentDoc}
${ScenarioHelioscopeFragmentFragmentDoc}
${ScenarioEtbFragmentFragmentDoc}
${ScenarioFragmentFragmentDoc}
${SmallScenarioFragmentFragmentDoc}`

export const useScenarioDetailQuery = <
  TData = Types.ScenarioDetailQuery,
  TError = unknown,
>(
  variables: Types.ScenarioDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ScenarioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ScenarioDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ScenarioDetailQuery, TError, TData>({
    queryKey: ["ScenarioDetail", variables],
    queryFn: adminFetcher<
      Types.ScenarioDetailQuery,
      Types.ScenarioDetailQueryVariables
    >(ScenarioDetailDocument, variables),
    ...options,
  })
}

useScenarioDetailQuery.getKey = (variables: Types.ScenarioDetailQueryVariables) => [
  "ScenarioDetail",
  variables,
]

useScenarioDetailQuery.rootKey = "ScenarioDetail"

export const useSuspenseScenarioDetailQuery = <
  TData = Types.ScenarioDetailQuery,
  TError = unknown,
>(
  variables: Types.ScenarioDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ScenarioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ScenarioDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ScenarioDetailQuery, TError, TData>({
    queryKey: ["ScenarioDetailSuspense", variables],
    queryFn: adminFetcher<
      Types.ScenarioDetailQuery,
      Types.ScenarioDetailQueryVariables
    >(ScenarioDetailDocument, variables),
    ...options,
  })
}

useSuspenseScenarioDetailQuery.getKey = (
  variables: Types.ScenarioDetailQueryVariables,
) => ["ScenarioDetailSuspense", variables]

useSuspenseScenarioDetailQuery.rootKey = "ScenarioDetail"

export const useInfiniteScenarioDetailQuery = <
  TData = InfiniteData<Types.ScenarioDetailQuery>,
  TError = unknown,
>(
  variables: Types.ScenarioDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ScenarioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ScenarioDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ScenarioDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ScenarioDetail.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.ScenarioDetailQuery, Types.ScenarioDetailQueryVariables>(
            ScenarioDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteScenarioDetailQuery.getKey = (
  variables: Types.ScenarioDetailQueryVariables,
) => ["ScenarioDetail.infinite", variables]

useInfiniteScenarioDetailQuery.rootKey = "ScenarioDetail.infinite"

export const useSuspenseInfiniteScenarioDetailQuery = <
  TData = InfiniteData<Types.ScenarioDetailQuery>,
  TError = unknown,
>(
  variables: Types.ScenarioDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ScenarioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ScenarioDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ScenarioDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ScenarioDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.ScenarioDetailQuery, Types.ScenarioDetailQueryVariables>(
            ScenarioDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteScenarioDetailQuery.getKey = (
  variables: Types.ScenarioDetailQueryVariables,
) => ["ScenarioDetail.infiniteSuspense", variables]

useSuspenseInfiniteScenarioDetailQuery.rootKey = "ScenarioDetail.infinite"

useScenarioDetailQuery.fetcher = (
  variables: Types.ScenarioDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.ScenarioDetailQuery, Types.ScenarioDetailQueryVariables>(
    ScenarioDetailDocument,
    variables,
    options,
  )

export const PortfolioUsersDocument = `
    query PortfolioUsers($portfolioPid: String!) {
  portfolio(pid: $portfolioPid) {
    pid
    title
    users {
      id
      email
    }
  }
}
    `

export const usePortfolioUsersQuery = <
  TData = Types.PortfolioUsersQuery,
  TError = unknown,
>(
  variables: Types.PortfolioUsersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.PortfolioUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.PortfolioUsersQuery, TError, TData>({
    queryKey: ["PortfolioUsers", variables],
    queryFn: adminFetcher<
      Types.PortfolioUsersQuery,
      Types.PortfolioUsersQueryVariables
    >(PortfolioUsersDocument, variables),
    ...options,
  })
}

usePortfolioUsersQuery.getKey = (variables: Types.PortfolioUsersQueryVariables) => [
  "PortfolioUsers",
  variables,
]

usePortfolioUsersQuery.rootKey = "PortfolioUsers"

export const useSuspensePortfolioUsersQuery = <
  TData = Types.PortfolioUsersQuery,
  TError = unknown,
>(
  variables: Types.PortfolioUsersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PortfolioUsersQuery, TError, TData>({
    queryKey: ["PortfolioUsersSuspense", variables],
    queryFn: adminFetcher<
      Types.PortfolioUsersQuery,
      Types.PortfolioUsersQueryVariables
    >(PortfolioUsersDocument, variables),
    ...options,
  })
}

useSuspensePortfolioUsersQuery.getKey = (
  variables: Types.PortfolioUsersQueryVariables,
) => ["PortfolioUsersSuspense", variables]

useSuspensePortfolioUsersQuery.rootKey = "PortfolioUsers"

export const useInfinitePortfolioUsersQuery = <
  TData = InfiniteData<Types.PortfolioUsersQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioUsersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PortfolioUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioUsers.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.PortfolioUsersQuery, Types.PortfolioUsersQueryVariables>(
            PortfolioUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePortfolioUsersQuery.getKey = (
  variables: Types.PortfolioUsersQueryVariables,
) => ["PortfolioUsers.infinite", variables]

useInfinitePortfolioUsersQuery.rootKey = "PortfolioUsers.infinite"

export const useSuspenseInfinitePortfolioUsersQuery = <
  TData = InfiniteData<Types.PortfolioUsersQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioUsersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PortfolioUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioUsers.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.PortfolioUsersQuery, Types.PortfolioUsersQueryVariables>(
            PortfolioUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePortfolioUsersQuery.getKey = (
  variables: Types.PortfolioUsersQueryVariables,
) => ["PortfolioUsers.infiniteSuspense", variables]

useSuspenseInfinitePortfolioUsersQuery.rootKey = "PortfolioUsers.infinite"

usePortfolioUsersQuery.fetcher = (
  variables: Types.PortfolioUsersQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.PortfolioUsersQuery, Types.PortfolioUsersQueryVariables>(
    PortfolioUsersDocument,
    variables,
    options,
  )

export const SiteChangelogDocument = `
    query SiteChangelog($sitePid: String!) {
  changelogs {
    site(sitePid: $sitePid) {
      id
      transactionId
      dateCreated
      columnName
      oldVal
      newVal
      changedBy {
        ...UserRef
      }
    }
  }
}
    ${UserRefFragmentDoc}`

export const useSiteChangelogQuery = <
  TData = Types.SiteChangelogQuery,
  TError = unknown,
>(
  variables: Types.SiteChangelogQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteChangelogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteChangelogQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteChangelogQuery, TError, TData>({
    queryKey: ["SiteChangelog", variables],
    queryFn: adminFetcher<Types.SiteChangelogQuery, Types.SiteChangelogQueryVariables>(
      SiteChangelogDocument,
      variables,
    ),
    ...options,
  })
}

useSiteChangelogQuery.getKey = (variables: Types.SiteChangelogQueryVariables) => [
  "SiteChangelog",
  variables,
]

useSiteChangelogQuery.rootKey = "SiteChangelog"

export const useSuspenseSiteChangelogQuery = <
  TData = Types.SiteChangelogQuery,
  TError = unknown,
>(
  variables: Types.SiteChangelogQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteChangelogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteChangelogQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteChangelogQuery, TError, TData>({
    queryKey: ["SiteChangelogSuspense", variables],
    queryFn: adminFetcher<Types.SiteChangelogQuery, Types.SiteChangelogQueryVariables>(
      SiteChangelogDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteChangelogQuery.getKey = (
  variables: Types.SiteChangelogQueryVariables,
) => ["SiteChangelogSuspense", variables]

useSuspenseSiteChangelogQuery.rootKey = "SiteChangelog"

export const useInfiniteSiteChangelogQuery = <
  TData = InfiniteData<Types.SiteChangelogQuery>,
  TError = unknown,
>(
  variables: Types.SiteChangelogQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteChangelogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteChangelogQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteChangelogQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteChangelog.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SiteChangelogQuery, Types.SiteChangelogQueryVariables>(
            SiteChangelogDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteChangelogQuery.getKey = (
  variables: Types.SiteChangelogQueryVariables,
) => ["SiteChangelog.infinite", variables]

useInfiniteSiteChangelogQuery.rootKey = "SiteChangelog.infinite"

export const useSuspenseInfiniteSiteChangelogQuery = <
  TData = InfiniteData<Types.SiteChangelogQuery>,
  TError = unknown,
>(
  variables: Types.SiteChangelogQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteChangelogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteChangelogQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteChangelogQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteChangelog.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SiteChangelogQuery, Types.SiteChangelogQueryVariables>(
            SiteChangelogDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteChangelogQuery.getKey = (
  variables: Types.SiteChangelogQueryVariables,
) => ["SiteChangelog.infiniteSuspense", variables]

useSuspenseInfiniteSiteChangelogQuery.rootKey = "SiteChangelog.infinite"

useSiteChangelogQuery.fetcher = (
  variables: Types.SiteChangelogQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SiteChangelogQuery, Types.SiteChangelogQueryVariables>(
    SiteChangelogDocument,
    variables,
    options,
  )

export const SiteAttachmentsDocument = `
    query SiteAttachments($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    attachments {
      ...AttachmentFragment
    }
  }
}
    ${AttachmentFragmentFragmentDoc}
${UserRefFragmentDoc}`

export const useSiteAttachmentsQuery = <
  TData = Types.SiteAttachmentsQuery,
  TError = unknown,
>(
  variables: Types.SiteAttachmentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteAttachmentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteAttachmentsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteAttachmentsQuery, TError, TData>({
    queryKey: ["SiteAttachments", variables],
    queryFn: adminFetcher<
      Types.SiteAttachmentsQuery,
      Types.SiteAttachmentsQueryVariables
    >(SiteAttachmentsDocument, variables),
    ...options,
  })
}

useSiteAttachmentsQuery.getKey = (variables: Types.SiteAttachmentsQueryVariables) => [
  "SiteAttachments",
  variables,
]

useSiteAttachmentsQuery.rootKey = "SiteAttachments"

export const useSuspenseSiteAttachmentsQuery = <
  TData = Types.SiteAttachmentsQuery,
  TError = unknown,
>(
  variables: Types.SiteAttachmentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteAttachmentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteAttachmentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteAttachmentsQuery, TError, TData>({
    queryKey: ["SiteAttachmentsSuspense", variables],
    queryFn: adminFetcher<
      Types.SiteAttachmentsQuery,
      Types.SiteAttachmentsQueryVariables
    >(SiteAttachmentsDocument, variables),
    ...options,
  })
}

useSuspenseSiteAttachmentsQuery.getKey = (
  variables: Types.SiteAttachmentsQueryVariables,
) => ["SiteAttachmentsSuspense", variables]

useSuspenseSiteAttachmentsQuery.rootKey = "SiteAttachments"

export const useInfiniteSiteAttachmentsQuery = <
  TData = InfiniteData<Types.SiteAttachmentsQuery>,
  TError = unknown,
>(
  variables: Types.SiteAttachmentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteAttachmentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteAttachmentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteAttachmentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteAttachments.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SiteAttachmentsQuery, Types.SiteAttachmentsQueryVariables>(
            SiteAttachmentsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteAttachmentsQuery.getKey = (
  variables: Types.SiteAttachmentsQueryVariables,
) => ["SiteAttachments.infinite", variables]

useInfiniteSiteAttachmentsQuery.rootKey = "SiteAttachments.infinite"

export const useSuspenseInfiniteSiteAttachmentsQuery = <
  TData = InfiniteData<Types.SiteAttachmentsQuery>,
  TError = unknown,
>(
  variables: Types.SiteAttachmentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteAttachmentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteAttachmentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteAttachmentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteAttachments.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SiteAttachmentsQuery, Types.SiteAttachmentsQueryVariables>(
            SiteAttachmentsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteAttachmentsQuery.getKey = (
  variables: Types.SiteAttachmentsQueryVariables,
) => ["SiteAttachments.infiniteSuspense", variables]

useSuspenseInfiniteSiteAttachmentsQuery.rootKey = "SiteAttachments.infinite"

useSiteAttachmentsQuery.fetcher = (
  variables: Types.SiteAttachmentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SiteAttachmentsQuery, Types.SiteAttachmentsQueryVariables>(
    SiteAttachmentsDocument,
    variables,
    options,
  )

export const AttachmentAsCsvDocument = `
    query AttachmentAsCsv($sitePid: String!, $attachmentId: Int!) {
  site(pid: $sitePid) {
    pid
    isMultifamily
    attachment(id: $attachmentId) {
      ...AttachmentFragment
      csvPeek
    }
  }
}
    ${AttachmentFragmentFragmentDoc}
${UserRefFragmentDoc}`

export const useAttachmentAsCsvQuery = <
  TData = Types.AttachmentAsCsvQuery,
  TError = unknown,
>(
  variables: Types.AttachmentAsCsvQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AttachmentAsCsvQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AttachmentAsCsvQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AttachmentAsCsvQuery, TError, TData>({
    queryKey: ["AttachmentAsCsv", variables],
    queryFn: adminFetcher<
      Types.AttachmentAsCsvQuery,
      Types.AttachmentAsCsvQueryVariables
    >(AttachmentAsCsvDocument, variables),
    ...options,
  })
}

useAttachmentAsCsvQuery.getKey = (variables: Types.AttachmentAsCsvQueryVariables) => [
  "AttachmentAsCsv",
  variables,
]

useAttachmentAsCsvQuery.rootKey = "AttachmentAsCsv"

export const useSuspenseAttachmentAsCsvQuery = <
  TData = Types.AttachmentAsCsvQuery,
  TError = unknown,
>(
  variables: Types.AttachmentAsCsvQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AttachmentAsCsvQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AttachmentAsCsvQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AttachmentAsCsvQuery, TError, TData>({
    queryKey: ["AttachmentAsCsvSuspense", variables],
    queryFn: adminFetcher<
      Types.AttachmentAsCsvQuery,
      Types.AttachmentAsCsvQueryVariables
    >(AttachmentAsCsvDocument, variables),
    ...options,
  })
}

useSuspenseAttachmentAsCsvQuery.getKey = (
  variables: Types.AttachmentAsCsvQueryVariables,
) => ["AttachmentAsCsvSuspense", variables]

useSuspenseAttachmentAsCsvQuery.rootKey = "AttachmentAsCsv"

export const useInfiniteAttachmentAsCsvQuery = <
  TData = InfiniteData<Types.AttachmentAsCsvQuery>,
  TError = unknown,
>(
  variables: Types.AttachmentAsCsvQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AttachmentAsCsvQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AttachmentAsCsvQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AttachmentAsCsvQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AttachmentAsCsv.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.AttachmentAsCsvQuery, Types.AttachmentAsCsvQueryVariables>(
            AttachmentAsCsvDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAttachmentAsCsvQuery.getKey = (
  variables: Types.AttachmentAsCsvQueryVariables,
) => ["AttachmentAsCsv.infinite", variables]

useInfiniteAttachmentAsCsvQuery.rootKey = "AttachmentAsCsv.infinite"

export const useSuspenseInfiniteAttachmentAsCsvQuery = <
  TData = InfiniteData<Types.AttachmentAsCsvQuery>,
  TError = unknown,
>(
  variables: Types.AttachmentAsCsvQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AttachmentAsCsvQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AttachmentAsCsvQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AttachmentAsCsvQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AttachmentAsCsv.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.AttachmentAsCsvQuery, Types.AttachmentAsCsvQueryVariables>(
            AttachmentAsCsvDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAttachmentAsCsvQuery.getKey = (
  variables: Types.AttachmentAsCsvQueryVariables,
) => ["AttachmentAsCsv.infiniteSuspense", variables]

useSuspenseInfiniteAttachmentAsCsvQuery.rootKey = "AttachmentAsCsv.infinite"

useAttachmentAsCsvQuery.fetcher = (
  variables: Types.AttachmentAsCsvQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AttachmentAsCsvQuery, Types.AttachmentAsCsvQueryVariables>(
    AttachmentAsCsvDocument,
    variables,
    options,
  )

export const CleanMetersDocument = `
    query CleanMeters($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    isMultifamily
    cleanMeters {
      all {
        id
        dateCreated
        source
        label
        address
        dirtyIntervals {
          units
          totalKwh
          rollup {
            season
            days
            numbers
          }
        }
      }
    }
  }
}
    `

export const useCleanMetersQuery = <TData = Types.CleanMetersQuery, TError = unknown>(
  variables: Types.CleanMetersQueryVariables,
  options?: Omit<UseQueryOptions<Types.CleanMetersQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.CleanMetersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CleanMetersQuery, TError, TData>({
    queryKey: ["CleanMeters", variables],
    queryFn: adminFetcher<Types.CleanMetersQuery, Types.CleanMetersQueryVariables>(
      CleanMetersDocument,
      variables,
    ),
    ...options,
  })
}

useCleanMetersQuery.getKey = (variables: Types.CleanMetersQueryVariables) => [
  "CleanMeters",
  variables,
]

useCleanMetersQuery.rootKey = "CleanMeters"

export const useSuspenseCleanMetersQuery = <
  TData = Types.CleanMetersQuery,
  TError = unknown,
>(
  variables: Types.CleanMetersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CleanMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CleanMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CleanMetersQuery, TError, TData>({
    queryKey: ["CleanMetersSuspense", variables],
    queryFn: adminFetcher<Types.CleanMetersQuery, Types.CleanMetersQueryVariables>(
      CleanMetersDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseCleanMetersQuery.getKey = (variables: Types.CleanMetersQueryVariables) => [
  "CleanMetersSuspense",
  variables,
]

useSuspenseCleanMetersQuery.rootKey = "CleanMeters"

export const useInfiniteCleanMetersQuery = <
  TData = InfiniteData<Types.CleanMetersQuery>,
  TError = unknown,
>(
  variables: Types.CleanMetersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CleanMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CleanMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CleanMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CleanMeters.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.CleanMetersQuery, Types.CleanMetersQueryVariables>(
            CleanMetersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCleanMetersQuery.getKey = (variables: Types.CleanMetersQueryVariables) => [
  "CleanMeters.infinite",
  variables,
]

useInfiniteCleanMetersQuery.rootKey = "CleanMeters.infinite"

export const useSuspenseInfiniteCleanMetersQuery = <
  TData = InfiniteData<Types.CleanMetersQuery>,
  TError = unknown,
>(
  variables: Types.CleanMetersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CleanMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CleanMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CleanMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CleanMeters.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.CleanMetersQuery, Types.CleanMetersQueryVariables>(
            CleanMetersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCleanMetersQuery.getKey = (
  variables: Types.CleanMetersQueryVariables,
) => ["CleanMeters.infiniteSuspense", variables]

useSuspenseInfiniteCleanMetersQuery.rootKey = "CleanMeters.infinite"

useCleanMetersQuery.fetcher = (
  variables: Types.CleanMetersQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.CleanMetersQuery, Types.CleanMetersQueryVariables>(
    CleanMetersDocument,
    variables,
    options,
  )

export const SiteStatusCheckDocument = `
    query SiteStatusCheck($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    statusCheck {
      desc
      hint
      status
    }
  }
}
    `

export const useSiteStatusCheckQuery = <
  TData = Types.SiteStatusCheckQuery,
  TError = unknown,
>(
  variables: Types.SiteStatusCheckQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteStatusCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteStatusCheckQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteStatusCheckQuery, TError, TData>({
    queryKey: ["SiteStatusCheck", variables],
    queryFn: adminFetcher<
      Types.SiteStatusCheckQuery,
      Types.SiteStatusCheckQueryVariables
    >(SiteStatusCheckDocument, variables),
    ...options,
  })
}

useSiteStatusCheckQuery.getKey = (variables: Types.SiteStatusCheckQueryVariables) => [
  "SiteStatusCheck",
  variables,
]

useSiteStatusCheckQuery.rootKey = "SiteStatusCheck"

export const useSuspenseSiteStatusCheckQuery = <
  TData = Types.SiteStatusCheckQuery,
  TError = unknown,
>(
  variables: Types.SiteStatusCheckQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteStatusCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteStatusCheckQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteStatusCheckQuery, TError, TData>({
    queryKey: ["SiteStatusCheckSuspense", variables],
    queryFn: adminFetcher<
      Types.SiteStatusCheckQuery,
      Types.SiteStatusCheckQueryVariables
    >(SiteStatusCheckDocument, variables),
    ...options,
  })
}

useSuspenseSiteStatusCheckQuery.getKey = (
  variables: Types.SiteStatusCheckQueryVariables,
) => ["SiteStatusCheckSuspense", variables]

useSuspenseSiteStatusCheckQuery.rootKey = "SiteStatusCheck"

export const useInfiniteSiteStatusCheckQuery = <
  TData = InfiniteData<Types.SiteStatusCheckQuery>,
  TError = unknown,
>(
  variables: Types.SiteStatusCheckQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteStatusCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteStatusCheckQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteStatusCheckQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteStatusCheck.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SiteStatusCheckQuery, Types.SiteStatusCheckQueryVariables>(
            SiteStatusCheckDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteStatusCheckQuery.getKey = (
  variables: Types.SiteStatusCheckQueryVariables,
) => ["SiteStatusCheck.infinite", variables]

useInfiniteSiteStatusCheckQuery.rootKey = "SiteStatusCheck.infinite"

export const useSuspenseInfiniteSiteStatusCheckQuery = <
  TData = InfiniteData<Types.SiteStatusCheckQuery>,
  TError = unknown,
>(
  variables: Types.SiteStatusCheckQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteStatusCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteStatusCheckQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteStatusCheckQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteStatusCheck.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SiteStatusCheckQuery, Types.SiteStatusCheckQueryVariables>(
            SiteStatusCheckDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteStatusCheckQuery.getKey = (
  variables: Types.SiteStatusCheckQueryVariables,
) => ["SiteStatusCheck.infiniteSuspense", variables]

useSuspenseInfiniteSiteStatusCheckQuery.rootKey = "SiteStatusCheck.infinite"

useSiteStatusCheckQuery.fetcher = (
  variables: Types.SiteStatusCheckQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SiteStatusCheckQuery, Types.SiteStatusCheckQueryVariables>(
    SiteStatusCheckDocument,
    variables,
    options,
  )

export const FailuresDocument = `
    query Failures($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    failures {
      __typename
      ... on FinmodelFailure {
        id
        createdAt
        name
        message
        scenario {
          ...ScenarioRef
        }
      }
      ... on GuessTariffFailure {
        createdAt
        name
        message
      }
      ... on BuildingFootprintFailure {
        createdAt
        name
        message
      }
      ... on LoadEstimateFailure {
        createdAt: dateCreated
        errorClass
        errorMessage
      }
      ... on ParcelDataFailure {
        createdAt
        name
        message
      }
    }
  }
}
    ${ScenarioRefFragmentDoc}`

export const useFailuresQuery = <TData = Types.FailuresQuery, TError = unknown>(
  variables: Types.FailuresQueryVariables,
  options?: Omit<UseQueryOptions<Types.FailuresQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.FailuresQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FailuresQuery, TError, TData>({
    queryKey: ["Failures", variables],
    queryFn: adminFetcher<Types.FailuresQuery, Types.FailuresQueryVariables>(
      FailuresDocument,
      variables,
    ),
    ...options,
  })
}

useFailuresQuery.getKey = (variables: Types.FailuresQueryVariables) => [
  "Failures",
  variables,
]

useFailuresQuery.rootKey = "Failures"

export const useSuspenseFailuresQuery = <TData = Types.FailuresQuery, TError = unknown>(
  variables: Types.FailuresQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FailuresQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.FailuresQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FailuresQuery, TError, TData>({
    queryKey: ["FailuresSuspense", variables],
    queryFn: adminFetcher<Types.FailuresQuery, Types.FailuresQueryVariables>(
      FailuresDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseFailuresQuery.getKey = (variables: Types.FailuresQueryVariables) => [
  "FailuresSuspense",
  variables,
]

useSuspenseFailuresQuery.rootKey = "Failures"

export const useInfiniteFailuresQuery = <
  TData = InfiniteData<Types.FailuresQuery>,
  TError = unknown,
>(
  variables: Types.FailuresQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FailuresQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.FailuresQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FailuresQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["Failures.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.FailuresQuery, Types.FailuresQueryVariables>(
            FailuresDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFailuresQuery.getKey = (variables: Types.FailuresQueryVariables) => [
  "Failures.infinite",
  variables,
]

useInfiniteFailuresQuery.rootKey = "Failures.infinite"

export const useSuspenseInfiniteFailuresQuery = <
  TData = InfiniteData<Types.FailuresQuery>,
  TError = unknown,
>(
  variables: Types.FailuresQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FailuresQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FailuresQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FailuresQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["Failures.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.FailuresQuery, Types.FailuresQueryVariables>(
            FailuresDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFailuresQuery.getKey = (variables: Types.FailuresQueryVariables) => [
  "Failures.infiniteSuspense",
  variables,
]

useSuspenseInfiniteFailuresQuery.rootKey = "Failures.infinite"

useFailuresQuery.fetcher = (
  variables: Types.FailuresQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.FailuresQuery, Types.FailuresQueryVariables>(
    FailuresDocument,
    variables,
    options,
  )

export const BillDocumentsDocument = `
    query BillDocuments($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    isMultifamily
    targetUtility {
      id
      name
      shortName
    }
    documentsV0 {
      id
      pid
      fileName
      dateCreated
      ownerEmail
      contentType
      tag
      downloadHref
      viewHref
    }
    billSuggestions {
      id
      document {
        pid
      }
    }
  }
}
    `

export const useBillDocumentsQuery = <
  TData = Types.BillDocumentsQuery,
  TError = unknown,
>(
  variables: Types.BillDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BillDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BillDocumentsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BillDocumentsQuery, TError, TData>({
    queryKey: ["BillDocuments", variables],
    queryFn: adminFetcher<Types.BillDocumentsQuery, Types.BillDocumentsQueryVariables>(
      BillDocumentsDocument,
      variables,
    ),
    ...options,
  })
}

useBillDocumentsQuery.getKey = (variables: Types.BillDocumentsQueryVariables) => [
  "BillDocuments",
  variables,
]

useBillDocumentsQuery.rootKey = "BillDocuments"

export const useSuspenseBillDocumentsQuery = <
  TData = Types.BillDocumentsQuery,
  TError = unknown,
>(
  variables: Types.BillDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BillDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BillDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BillDocumentsQuery, TError, TData>({
    queryKey: ["BillDocumentsSuspense", variables],
    queryFn: adminFetcher<Types.BillDocumentsQuery, Types.BillDocumentsQueryVariables>(
      BillDocumentsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseBillDocumentsQuery.getKey = (
  variables: Types.BillDocumentsQueryVariables,
) => ["BillDocumentsSuspense", variables]

useSuspenseBillDocumentsQuery.rootKey = "BillDocuments"

export const useInfiniteBillDocumentsQuery = <
  TData = InfiniteData<Types.BillDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.BillDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BillDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BillDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BillDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BillDocuments.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.BillDocumentsQuery, Types.BillDocumentsQueryVariables>(
            BillDocumentsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBillDocumentsQuery.getKey = (
  variables: Types.BillDocumentsQueryVariables,
) => ["BillDocuments.infinite", variables]

useInfiniteBillDocumentsQuery.rootKey = "BillDocuments.infinite"

export const useSuspenseInfiniteBillDocumentsQuery = <
  TData = InfiniteData<Types.BillDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.BillDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BillDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BillDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BillDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BillDocuments.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.BillDocumentsQuery, Types.BillDocumentsQueryVariables>(
            BillDocumentsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBillDocumentsQuery.getKey = (
  variables: Types.BillDocumentsQueryVariables,
) => ["BillDocuments.infiniteSuspense", variables]

useSuspenseInfiniteBillDocumentsQuery.rootKey = "BillDocuments.infinite"

useBillDocumentsQuery.fetcher = (
  variables: Types.BillDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.BillDocumentsQuery, Types.BillDocumentsQueryVariables>(
    BillDocumentsDocument,
    variables,
    options,
  )

export const OrgsDocument = `
    query Orgs {
  orgs {
    pid
    name
  }
}
    `

export const useOrgsQuery = <TData = Types.OrgsQuery, TError = unknown>(
  variables?: Types.OrgsQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgsQuery, TError, TData>({
    queryKey: variables === undefined ? ["Orgs"] : ["Orgs", variables],
    queryFn: adminFetcher<Types.OrgsQuery, Types.OrgsQueryVariables>(
      OrgsDocument,
      variables,
    ),
    ...options,
  })
}

useOrgsQuery.getKey = (variables?: Types.OrgsQueryVariables) =>
  variables === undefined ? ["Orgs"] : ["Orgs", variables]

useOrgsQuery.rootKey = "Orgs"

export const useSuspenseOrgsQuery = <TData = Types.OrgsQuery, TError = unknown>(
  variables?: Types.OrgsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgsQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgsQuery, TError, TData>({
    queryKey: variables === undefined ? ["OrgsSuspense"] : ["OrgsSuspense", variables],
    queryFn: adminFetcher<Types.OrgsQuery, Types.OrgsQueryVariables>(
      OrgsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgsQuery.getKey = (variables?: Types.OrgsQueryVariables) =>
  variables === undefined ? ["OrgsSuspense"] : ["OrgsSuspense", variables]

useSuspenseOrgsQuery.rootKey = "Orgs"

export const useInfiniteOrgsQuery = <
  TData = InfiniteData<Types.OrgsQuery>,
  TError = unknown,
>(
  variables: Types.OrgsQueryVariables,
  options: Omit<UseInfiniteQueryOptions<Types.OrgsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgsQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["Orgs.infinite"]
            : ["Orgs.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.OrgsQuery, Types.OrgsQueryVariables>(OrgsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgsQuery.getKey = (variables?: Types.OrgsQueryVariables) =>
  variables === undefined ? ["Orgs.infinite"] : ["Orgs.infinite", variables]

useInfiniteOrgsQuery.rootKey = "Orgs.infinite"

export const useSuspenseInfiniteOrgsQuery = <
  TData = InfiniteData<Types.OrgsQuery>,
  TError = unknown,
>(
  variables: Types.OrgsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["Orgs.infiniteSuspense"]
            : ["Orgs.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.OrgsQuery, Types.OrgsQueryVariables>(OrgsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgsQuery.getKey = (variables?: Types.OrgsQueryVariables) =>
  variables === undefined
    ? ["Orgs.infiniteSuspense"]
    : ["Orgs.infiniteSuspense", variables]

useSuspenseInfiniteOrgsQuery.rootKey = "Orgs.infinite"

useOrgsQuery.fetcher = (
  variables?: Types.OrgsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.OrgsQuery, Types.OrgsQueryVariables>(
    OrgsDocument,
    variables,
    options,
  )

export const OrgsDetailListDocument = `
    query OrgsDetailList {
  orgs {
    ...BasicOrgDetails
  }
  financierOrgs {
    ...BasicOrgDetails
  }
}
    ${BasicOrgDetailsFragmentDoc}`

export const useOrgsDetailListQuery = <
  TData = Types.OrgsDetailListQuery,
  TError = unknown,
>(
  variables?: Types.OrgsDetailListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgsDetailListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.OrgsDetailListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgsDetailListQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["OrgsDetailList"] : ["OrgsDetailList", variables],
    queryFn: adminFetcher<
      Types.OrgsDetailListQuery,
      Types.OrgsDetailListQueryVariables
    >(OrgsDetailListDocument, variables),
    ...options,
  })
}

useOrgsDetailListQuery.getKey = (variables?: Types.OrgsDetailListQueryVariables) =>
  variables === undefined ? ["OrgsDetailList"] : ["OrgsDetailList", variables]

useOrgsDetailListQuery.rootKey = "OrgsDetailList"

export const useSuspenseOrgsDetailListQuery = <
  TData = Types.OrgsDetailListQuery,
  TError = unknown,
>(
  variables?: Types.OrgsDetailListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgsDetailListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgsDetailListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgsDetailListQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgsDetailListSuspense"]
        : ["OrgsDetailListSuspense", variables],
    queryFn: adminFetcher<
      Types.OrgsDetailListQuery,
      Types.OrgsDetailListQueryVariables
    >(OrgsDetailListDocument, variables),
    ...options,
  })
}

useSuspenseOrgsDetailListQuery.getKey = (
  variables?: Types.OrgsDetailListQueryVariables,
) =>
  variables === undefined
    ? ["OrgsDetailListSuspense"]
    : ["OrgsDetailListSuspense", variables]

useSuspenseOrgsDetailListQuery.rootKey = "OrgsDetailList"

export const useInfiniteOrgsDetailListQuery = <
  TData = InfiniteData<Types.OrgsDetailListQuery>,
  TError = unknown,
>(
  variables: Types.OrgsDetailListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgsDetailListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgsDetailListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgsDetailListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgsDetailList.infinite"]
            : ["OrgsDetailList.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.OrgsDetailListQuery, Types.OrgsDetailListQueryVariables>(
            OrgsDetailListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgsDetailListQuery.getKey = (
  variables?: Types.OrgsDetailListQueryVariables,
) =>
  variables === undefined
    ? ["OrgsDetailList.infinite"]
    : ["OrgsDetailList.infinite", variables]

useInfiniteOrgsDetailListQuery.rootKey = "OrgsDetailList.infinite"

export const useSuspenseInfiniteOrgsDetailListQuery = <
  TData = InfiniteData<Types.OrgsDetailListQuery>,
  TError = unknown,
>(
  variables: Types.OrgsDetailListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgsDetailListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgsDetailListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgsDetailListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgsDetailList.infiniteSuspense"]
            : ["OrgsDetailList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.OrgsDetailListQuery, Types.OrgsDetailListQueryVariables>(
            OrgsDetailListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgsDetailListQuery.getKey = (
  variables?: Types.OrgsDetailListQueryVariables,
) =>
  variables === undefined
    ? ["OrgsDetailList.infiniteSuspense"]
    : ["OrgsDetailList.infiniteSuspense", variables]

useSuspenseInfiniteOrgsDetailListQuery.rootKey = "OrgsDetailList.infinite"

useOrgsDetailListQuery.fetcher = (
  variables?: Types.OrgsDetailListQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.OrgsDetailListQuery, Types.OrgsDetailListQueryVariables>(
    OrgsDetailListDocument,
    variables,
    options,
  )

export const SiteIraIncentiveDataDocument = `
    query SiteIraIncentiveData($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    isLowIncomeCommunity
    isEnergyCommunity
  }
}
    `

export const useSiteIraIncentiveDataQuery = <
  TData = Types.SiteIraIncentiveDataQuery,
  TError = unknown,
>(
  variables: Types.SiteIraIncentiveDataQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteIraIncentiveDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SiteIraIncentiveDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SiteIraIncentiveDataQuery, TError, TData>({
    queryKey: ["SiteIraIncentiveData", variables],
    queryFn: adminFetcher<
      Types.SiteIraIncentiveDataQuery,
      Types.SiteIraIncentiveDataQueryVariables
    >(SiteIraIncentiveDataDocument, variables),
    ...options,
  })
}

useSiteIraIncentiveDataQuery.getKey = (
  variables: Types.SiteIraIncentiveDataQueryVariables,
) => ["SiteIraIncentiveData", variables]

useSiteIraIncentiveDataQuery.rootKey = "SiteIraIncentiveData"

export const useSuspenseSiteIraIncentiveDataQuery = <
  TData = Types.SiteIraIncentiveDataQuery,
  TError = unknown,
>(
  variables: Types.SiteIraIncentiveDataQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteIraIncentiveDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteIraIncentiveDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteIraIncentiveDataQuery, TError, TData>({
    queryKey: ["SiteIraIncentiveDataSuspense", variables],
    queryFn: adminFetcher<
      Types.SiteIraIncentiveDataQuery,
      Types.SiteIraIncentiveDataQueryVariables
    >(SiteIraIncentiveDataDocument, variables),
    ...options,
  })
}

useSuspenseSiteIraIncentiveDataQuery.getKey = (
  variables: Types.SiteIraIncentiveDataQueryVariables,
) => ["SiteIraIncentiveDataSuspense", variables]

useSuspenseSiteIraIncentiveDataQuery.rootKey = "SiteIraIncentiveData"

export const useInfiniteSiteIraIncentiveDataQuery = <
  TData = InfiniteData<Types.SiteIraIncentiveDataQuery>,
  TError = unknown,
>(
  variables: Types.SiteIraIncentiveDataQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteIraIncentiveDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteIraIncentiveDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteIraIncentiveDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteIraIncentiveData.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.SiteIraIncentiveDataQuery,
            Types.SiteIraIncentiveDataQueryVariables
          >(SiteIraIncentiveDataDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteIraIncentiveDataQuery.getKey = (
  variables: Types.SiteIraIncentiveDataQueryVariables,
) => ["SiteIraIncentiveData.infinite", variables]

useInfiniteSiteIraIncentiveDataQuery.rootKey = "SiteIraIncentiveData.infinite"

export const useSuspenseInfiniteSiteIraIncentiveDataQuery = <
  TData = InfiniteData<Types.SiteIraIncentiveDataQuery>,
  TError = unknown,
>(
  variables: Types.SiteIraIncentiveDataQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteIraIncentiveDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteIraIncentiveDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteIraIncentiveDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "SiteIraIncentiveData.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.SiteIraIncentiveDataQuery,
            Types.SiteIraIncentiveDataQueryVariables
          >(SiteIraIncentiveDataDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteIraIncentiveDataQuery.getKey = (
  variables: Types.SiteIraIncentiveDataQueryVariables,
) => ["SiteIraIncentiveData.infiniteSuspense", variables]

useSuspenseInfiniteSiteIraIncentiveDataQuery.rootKey = "SiteIraIncentiveData.infinite"

useSiteIraIncentiveDataQuery.fetcher = (
  variables: Types.SiteIraIncentiveDataQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SiteIraIncentiveDataQuery,
    Types.SiteIraIncentiveDataQueryVariables
  >(SiteIraIncentiveDataDocument, variables, options)

export const SiteLeaseTypeDocument = `
    query SiteLeaseType($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    leaseType
  }
}
    `

export const useSiteLeaseTypeQuery = <
  TData = Types.SiteLeaseTypeQuery,
  TError = unknown,
>(
  variables: Types.SiteLeaseTypeQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteLeaseTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteLeaseTypeQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteLeaseTypeQuery, TError, TData>({
    queryKey: ["SiteLeaseType", variables],
    queryFn: adminFetcher<Types.SiteLeaseTypeQuery, Types.SiteLeaseTypeQueryVariables>(
      SiteLeaseTypeDocument,
      variables,
    ),
    ...options,
  })
}

useSiteLeaseTypeQuery.getKey = (variables: Types.SiteLeaseTypeQueryVariables) => [
  "SiteLeaseType",
  variables,
]

useSiteLeaseTypeQuery.rootKey = "SiteLeaseType"

export const useSuspenseSiteLeaseTypeQuery = <
  TData = Types.SiteLeaseTypeQuery,
  TError = unknown,
>(
  variables: Types.SiteLeaseTypeQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteLeaseTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteLeaseTypeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteLeaseTypeQuery, TError, TData>({
    queryKey: ["SiteLeaseTypeSuspense", variables],
    queryFn: adminFetcher<Types.SiteLeaseTypeQuery, Types.SiteLeaseTypeQueryVariables>(
      SiteLeaseTypeDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteLeaseTypeQuery.getKey = (
  variables: Types.SiteLeaseTypeQueryVariables,
) => ["SiteLeaseTypeSuspense", variables]

useSuspenseSiteLeaseTypeQuery.rootKey = "SiteLeaseType"

export const useInfiniteSiteLeaseTypeQuery = <
  TData = InfiniteData<Types.SiteLeaseTypeQuery>,
  TError = unknown,
>(
  variables: Types.SiteLeaseTypeQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteLeaseTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteLeaseTypeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteLeaseTypeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteLeaseType.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SiteLeaseTypeQuery, Types.SiteLeaseTypeQueryVariables>(
            SiteLeaseTypeDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteLeaseTypeQuery.getKey = (
  variables: Types.SiteLeaseTypeQueryVariables,
) => ["SiteLeaseType.infinite", variables]

useInfiniteSiteLeaseTypeQuery.rootKey = "SiteLeaseType.infinite"

export const useSuspenseInfiniteSiteLeaseTypeQuery = <
  TData = InfiniteData<Types.SiteLeaseTypeQuery>,
  TError = unknown,
>(
  variables: Types.SiteLeaseTypeQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteLeaseTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteLeaseTypeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteLeaseTypeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteLeaseType.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SiteLeaseTypeQuery, Types.SiteLeaseTypeQueryVariables>(
            SiteLeaseTypeDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteLeaseTypeQuery.getKey = (
  variables: Types.SiteLeaseTypeQueryVariables,
) => ["SiteLeaseType.infiniteSuspense", variables]

useSuspenseInfiniteSiteLeaseTypeQuery.rootKey = "SiteLeaseType.infinite"

useSiteLeaseTypeQuery.fetcher = (
  variables: Types.SiteLeaseTypeQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SiteLeaseTypeQuery, Types.SiteLeaseTypeQueryVariables>(
    SiteLeaseTypeDocument,
    variables,
    options,
  )

export const SiteOccupancyTypeDocument = `
    query SiteOccupancyType($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    occupancyType
  }
}
    `

export const useSiteOccupancyTypeQuery = <
  TData = Types.SiteOccupancyTypeQuery,
  TError = unknown,
>(
  variables: Types.SiteOccupancyTypeQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteOccupancyTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteOccupancyTypeQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteOccupancyTypeQuery, TError, TData>({
    queryKey: ["SiteOccupancyType", variables],
    queryFn: adminFetcher<
      Types.SiteOccupancyTypeQuery,
      Types.SiteOccupancyTypeQueryVariables
    >(SiteOccupancyTypeDocument, variables),
    ...options,
  })
}

useSiteOccupancyTypeQuery.getKey = (
  variables: Types.SiteOccupancyTypeQueryVariables,
) => ["SiteOccupancyType", variables]

useSiteOccupancyTypeQuery.rootKey = "SiteOccupancyType"

export const useSuspenseSiteOccupancyTypeQuery = <
  TData = Types.SiteOccupancyTypeQuery,
  TError = unknown,
>(
  variables: Types.SiteOccupancyTypeQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteOccupancyTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteOccupancyTypeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteOccupancyTypeQuery, TError, TData>({
    queryKey: ["SiteOccupancyTypeSuspense", variables],
    queryFn: adminFetcher<
      Types.SiteOccupancyTypeQuery,
      Types.SiteOccupancyTypeQueryVariables
    >(SiteOccupancyTypeDocument, variables),
    ...options,
  })
}

useSuspenseSiteOccupancyTypeQuery.getKey = (
  variables: Types.SiteOccupancyTypeQueryVariables,
) => ["SiteOccupancyTypeSuspense", variables]

useSuspenseSiteOccupancyTypeQuery.rootKey = "SiteOccupancyType"

export const useInfiniteSiteOccupancyTypeQuery = <
  TData = InfiniteData<Types.SiteOccupancyTypeQuery>,
  TError = unknown,
>(
  variables: Types.SiteOccupancyTypeQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteOccupancyTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteOccupancyTypeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteOccupancyTypeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteOccupancyType.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.SiteOccupancyTypeQuery,
            Types.SiteOccupancyTypeQueryVariables
          >(SiteOccupancyTypeDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteOccupancyTypeQuery.getKey = (
  variables: Types.SiteOccupancyTypeQueryVariables,
) => ["SiteOccupancyType.infinite", variables]

useInfiniteSiteOccupancyTypeQuery.rootKey = "SiteOccupancyType.infinite"

export const useSuspenseInfiniteSiteOccupancyTypeQuery = <
  TData = InfiniteData<Types.SiteOccupancyTypeQuery>,
  TError = unknown,
>(
  variables: Types.SiteOccupancyTypeQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteOccupancyTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteOccupancyTypeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteOccupancyTypeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteOccupancyType.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.SiteOccupancyTypeQuery,
            Types.SiteOccupancyTypeQueryVariables
          >(SiteOccupancyTypeDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteOccupancyTypeQuery.getKey = (
  variables: Types.SiteOccupancyTypeQueryVariables,
) => ["SiteOccupancyType.infiniteSuspense", variables]

useSuspenseInfiniteSiteOccupancyTypeQuery.rootKey = "SiteOccupancyType.infinite"

useSiteOccupancyTypeQuery.fetcher = (
  variables: Types.SiteOccupancyTypeQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SiteOccupancyTypeQuery, Types.SiteOccupancyTypeQueryVariables>(
    SiteOccupancyTypeDocument,
    variables,
    options,
  )

export const OrgDetailDocument = `
    query OrgDetail($orgPid: String!) {
  org(orgPid: $orgPid) {
    ...BasicOrgDetails
    portfolios {
      pid
      title
    }
    allowedEmailDomains
    labels {
      pid
      name
      themeColor
    }
    financing {
      ...FinRel
    }
    finproducts {
      __typename
      financier {
        pid
        name
      }
      id
      createdAt
      name
      type
      activeOverrides {
        ...Override
      }
      targetOrg {
        pid
        name
      }
      targetSite {
        pid
        name
        streetAddress
      }
      disabledAt
      includeStorage
      status
      csPrices {
        utility {
          name
          id
          shortName
        }
        state
        usdPerKwPerYear
        escalator
        termYears
        minPvKw
        maxPvKw
        pvType
      }
    }
  }
}
    ${BasicOrgDetailsFragmentDoc}
${FinRelFragmentDoc}
${OverrideFragmentDoc}`

export const useOrgDetailQuery = <TData = Types.OrgDetailQuery, TError = unknown>(
  variables: Types.OrgDetailQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgDetailQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgDetailQuery, TError, TData>({
    queryKey: ["OrgDetail", variables],
    queryFn: adminFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
      OrgDetailDocument,
      variables,
    ),
    ...options,
  })
}

useOrgDetailQuery.getKey = (variables: Types.OrgDetailQueryVariables) => [
  "OrgDetail",
  variables,
]

useOrgDetailQuery.rootKey = "OrgDetail"

export const useSuspenseOrgDetailQuery = <
  TData = Types.OrgDetailQuery,
  TError = unknown,
>(
  variables: Types.OrgDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgDetailQuery, TError, TData>({
    queryKey: ["OrgDetailSuspense", variables],
    queryFn: adminFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
      OrgDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgDetailQuery.getKey = (variables: Types.OrgDetailQueryVariables) => [
  "OrgDetailSuspense",
  variables,
]

useSuspenseOrgDetailQuery.rootKey = "OrgDetail"

export const useInfiniteOrgDetailQuery = <
  TData = InfiniteData<Types.OrgDetailQuery>,
  TError = unknown,
>(
  variables: Types.OrgDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgDetail.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
            OrgDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgDetailQuery.getKey = (variables: Types.OrgDetailQueryVariables) => [
  "OrgDetail.infinite",
  variables,
]

useInfiniteOrgDetailQuery.rootKey = "OrgDetail.infinite"

export const useSuspenseInfiniteOrgDetailQuery = <
  TData = InfiniteData<Types.OrgDetailQuery>,
  TError = unknown,
>(
  variables: Types.OrgDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
            OrgDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgDetailQuery.getKey = (
  variables: Types.OrgDetailQueryVariables,
) => ["OrgDetail.infiniteSuspense", variables]

useSuspenseInfiniteOrgDetailQuery.rootKey = "OrgDetail.infinite"

useOrgDetailQuery.fetcher = (
  variables: Types.OrgDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
    OrgDetailDocument,
    variables,
    options,
  )

export const OrgUsersDocument = `
    query OrgUsers($orgPid: String!) {
  org(orgPid: $orgPid) {
    pid
    name
    logoUrl
    logomarkUrl
    users {
      ...UserListUser
    }
  }
}
    ${UserListUserFragmentDoc}`

export const useOrgUsersQuery = <TData = Types.OrgUsersQuery, TError = unknown>(
  variables: Types.OrgUsersQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgUsersQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgUsersQuery, TError, TData>({
    queryKey: ["OrgUsers", variables],
    queryFn: adminFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
      OrgUsersDocument,
      variables,
    ),
    ...options,
  })
}

useOrgUsersQuery.getKey = (variables: Types.OrgUsersQueryVariables) => [
  "OrgUsers",
  variables,
]

useOrgUsersQuery.rootKey = "OrgUsers"

export const useSuspenseOrgUsersQuery = <TData = Types.OrgUsersQuery, TError = unknown>(
  variables: Types.OrgUsersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgUsersQuery, TError, TData>({
    queryKey: ["OrgUsersSuspense", variables],
    queryFn: adminFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
      OrgUsersDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgUsersQuery.getKey = (variables: Types.OrgUsersQueryVariables) => [
  "OrgUsersSuspense",
  variables,
]

useSuspenseOrgUsersQuery.rootKey = "OrgUsers"

export const useInfiniteOrgUsersQuery = <
  TData = InfiniteData<Types.OrgUsersQuery>,
  TError = unknown,
>(
  variables: Types.OrgUsersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgUsers.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
            OrgUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgUsersQuery.getKey = (variables: Types.OrgUsersQueryVariables) => [
  "OrgUsers.infinite",
  variables,
]

useInfiniteOrgUsersQuery.rootKey = "OrgUsers.infinite"

export const useSuspenseInfiniteOrgUsersQuery = <
  TData = InfiniteData<Types.OrgUsersQuery>,
  TError = unknown,
>(
  variables: Types.OrgUsersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgUsers.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
            OrgUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgUsersQuery.getKey = (variables: Types.OrgUsersQueryVariables) => [
  "OrgUsers.infiniteSuspense",
  variables,
]

useSuspenseInfiniteOrgUsersQuery.rootKey = "OrgUsers.infinite"

useOrgUsersQuery.fetcher = (
  variables: Types.OrgUsersQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
    OrgUsersDocument,
    variables,
    options,
  )

export const FinmodelFailureIndexDocument = `
    query FinmodelFailureIndex {
  finmodelFailureIndex {
    fmv
    name
    failureCount
  }
}
    `

export const useFinmodelFailureIndexQuery = <
  TData = Types.FinmodelFailureIndexQuery,
  TError = unknown,
>(
  variables?: Types.FinmodelFailureIndexQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelFailureIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FinmodelFailureIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelFailureIndexQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["FinmodelFailureIndex"]
        : ["FinmodelFailureIndex", variables],
    queryFn: adminFetcher<
      Types.FinmodelFailureIndexQuery,
      Types.FinmodelFailureIndexQueryVariables
    >(FinmodelFailureIndexDocument, variables),
    ...options,
  })
}

useFinmodelFailureIndexQuery.getKey = (
  variables?: Types.FinmodelFailureIndexQueryVariables,
) =>
  variables === undefined
    ? ["FinmodelFailureIndex"]
    : ["FinmodelFailureIndex", variables]

useFinmodelFailureIndexQuery.rootKey = "FinmodelFailureIndex"

export const useSuspenseFinmodelFailureIndexQuery = <
  TData = Types.FinmodelFailureIndexQuery,
  TError = unknown,
>(
  variables?: Types.FinmodelFailureIndexQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelFailureIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelFailureIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelFailureIndexQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["FinmodelFailureIndexSuspense"]
        : ["FinmodelFailureIndexSuspense", variables],
    queryFn: adminFetcher<
      Types.FinmodelFailureIndexQuery,
      Types.FinmodelFailureIndexQueryVariables
    >(FinmodelFailureIndexDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelFailureIndexQuery.getKey = (
  variables?: Types.FinmodelFailureIndexQueryVariables,
) =>
  variables === undefined
    ? ["FinmodelFailureIndexSuspense"]
    : ["FinmodelFailureIndexSuspense", variables]

useSuspenseFinmodelFailureIndexQuery.rootKey = "FinmodelFailureIndex"

export const useInfiniteFinmodelFailureIndexQuery = <
  TData = InfiniteData<Types.FinmodelFailureIndexQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureIndexQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelFailureIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelFailureIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelFailureIndexQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["FinmodelFailureIndex.infinite"]
            : ["FinmodelFailureIndex.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelFailureIndexQuery,
            Types.FinmodelFailureIndexQueryVariables
          >(FinmodelFailureIndexDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelFailureIndexQuery.getKey = (
  variables?: Types.FinmodelFailureIndexQueryVariables,
) =>
  variables === undefined
    ? ["FinmodelFailureIndex.infinite"]
    : ["FinmodelFailureIndex.infinite", variables]

useInfiniteFinmodelFailureIndexQuery.rootKey = "FinmodelFailureIndex.infinite"

export const useSuspenseInfiniteFinmodelFailureIndexQuery = <
  TData = InfiniteData<Types.FinmodelFailureIndexQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureIndexQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelFailureIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelFailureIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelFailureIndexQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["FinmodelFailureIndex.infiniteSuspense"]
            : ["FinmodelFailureIndex.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelFailureIndexQuery,
            Types.FinmodelFailureIndexQueryVariables
          >(FinmodelFailureIndexDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelFailureIndexQuery.getKey = (
  variables?: Types.FinmodelFailureIndexQueryVariables,
) =>
  variables === undefined
    ? ["FinmodelFailureIndex.infiniteSuspense"]
    : ["FinmodelFailureIndex.infiniteSuspense", variables]

useSuspenseInfiniteFinmodelFailureIndexQuery.rootKey = "FinmodelFailureIndex.infinite"

useFinmodelFailureIndexQuery.fetcher = (
  variables?: Types.FinmodelFailureIndexQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.FinmodelFailureIndexQuery,
    Types.FinmodelFailureIndexQueryVariables
  >(FinmodelFailureIndexDocument, variables, options)

export const FinmodelFailureViewDocument = `
    query FinmodelFailureView($filterBy: FinmodelFailureTableFilter!, $sortBy: [FinmodelFailureTableSort!]) {
  finmodelFailures {
    view(filterBy: $filterBy, sortBy: $sortBy) {
      count
      keys
    }
  }
}
    `

export const useFinmodelFailureViewQuery = <
  TData = Types.FinmodelFailureViewQuery,
  TError = unknown,
>(
  variables: Types.FinmodelFailureViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelFailureViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FinmodelFailureViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelFailureViewQuery, TError, TData>({
    queryKey: ["FinmodelFailureView", variables],
    queryFn: adminFetcher<
      Types.FinmodelFailureViewQuery,
      Types.FinmodelFailureViewQueryVariables
    >(FinmodelFailureViewDocument, variables),
    ...options,
  })
}

useFinmodelFailureViewQuery.getKey = (
  variables: Types.FinmodelFailureViewQueryVariables,
) => ["FinmodelFailureView", variables]

useFinmodelFailureViewQuery.rootKey = "FinmodelFailureView"

export const useSuspenseFinmodelFailureViewQuery = <
  TData = Types.FinmodelFailureViewQuery,
  TError = unknown,
>(
  variables: Types.FinmodelFailureViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelFailureViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelFailureViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelFailureViewQuery, TError, TData>({
    queryKey: ["FinmodelFailureViewSuspense", variables],
    queryFn: adminFetcher<
      Types.FinmodelFailureViewQuery,
      Types.FinmodelFailureViewQueryVariables
    >(FinmodelFailureViewDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelFailureViewQuery.getKey = (
  variables: Types.FinmodelFailureViewQueryVariables,
) => ["FinmodelFailureViewSuspense", variables]

useSuspenseFinmodelFailureViewQuery.rootKey = "FinmodelFailureView"

export const useInfiniteFinmodelFailureViewQuery = <
  TData = InfiniteData<Types.FinmodelFailureViewQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelFailureViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelFailureViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelFailureViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelFailureView.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelFailureViewQuery,
            Types.FinmodelFailureViewQueryVariables
          >(FinmodelFailureViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelFailureViewQuery.getKey = (
  variables: Types.FinmodelFailureViewQueryVariables,
) => ["FinmodelFailureView.infinite", variables]

useInfiniteFinmodelFailureViewQuery.rootKey = "FinmodelFailureView.infinite"

export const useSuspenseInfiniteFinmodelFailureViewQuery = <
  TData = InfiniteData<Types.FinmodelFailureViewQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelFailureViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelFailureViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelFailureViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "FinmodelFailureView.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelFailureViewQuery,
            Types.FinmodelFailureViewQueryVariables
          >(FinmodelFailureViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelFailureViewQuery.getKey = (
  variables: Types.FinmodelFailureViewQueryVariables,
) => ["FinmodelFailureView.infiniteSuspense", variables]

useSuspenseInfiniteFinmodelFailureViewQuery.rootKey = "FinmodelFailureView.infinite"

useFinmodelFailureViewQuery.fetcher = (
  variables: Types.FinmodelFailureViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.FinmodelFailureViewQuery, Types.FinmodelFailureViewQueryVariables>(
    FinmodelFailureViewDocument,
    variables,
    options,
  )

export const FinmodelFailureRowDocument = `
    query FinmodelFailureRow($keys: [Int!]!) {
  finmodelFailures {
    rows(keys: $keys) {
      id
      createdAt
      fmv
      name
      message
    }
  }
}
    `

export const useFinmodelFailureRowQuery = <
  TData = Types.FinmodelFailureRowQuery,
  TError = unknown,
>(
  variables: Types.FinmodelFailureRowQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelFailureRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.FinmodelFailureRowQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelFailureRowQuery, TError, TData>({
    queryKey: ["FinmodelFailureRow", variables],
    queryFn: adminFetcher<
      Types.FinmodelFailureRowQuery,
      Types.FinmodelFailureRowQueryVariables
    >(FinmodelFailureRowDocument, variables),
    ...options,
  })
}

useFinmodelFailureRowQuery.getKey = (
  variables: Types.FinmodelFailureRowQueryVariables,
) => ["FinmodelFailureRow", variables]

useFinmodelFailureRowQuery.rootKey = "FinmodelFailureRow"

export const useSuspenseFinmodelFailureRowQuery = <
  TData = Types.FinmodelFailureRowQuery,
  TError = unknown,
>(
  variables: Types.FinmodelFailureRowQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelFailureRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelFailureRowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelFailureRowQuery, TError, TData>({
    queryKey: ["FinmodelFailureRowSuspense", variables],
    queryFn: adminFetcher<
      Types.FinmodelFailureRowQuery,
      Types.FinmodelFailureRowQueryVariables
    >(FinmodelFailureRowDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelFailureRowQuery.getKey = (
  variables: Types.FinmodelFailureRowQueryVariables,
) => ["FinmodelFailureRowSuspense", variables]

useSuspenseFinmodelFailureRowQuery.rootKey = "FinmodelFailureRow"

export const useInfiniteFinmodelFailureRowQuery = <
  TData = InfiniteData<Types.FinmodelFailureRowQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureRowQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelFailureRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelFailureRowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelFailureRowQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelFailureRow.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelFailureRowQuery,
            Types.FinmodelFailureRowQueryVariables
          >(FinmodelFailureRowDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelFailureRowQuery.getKey = (
  variables: Types.FinmodelFailureRowQueryVariables,
) => ["FinmodelFailureRow.infinite", variables]

useInfiniteFinmodelFailureRowQuery.rootKey = "FinmodelFailureRow.infinite"

export const useSuspenseInfiniteFinmodelFailureRowQuery = <
  TData = InfiniteData<Types.FinmodelFailureRowQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureRowQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelFailureRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelFailureRowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelFailureRowQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelFailureRow.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelFailureRowQuery,
            Types.FinmodelFailureRowQueryVariables
          >(FinmodelFailureRowDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelFailureRowQuery.getKey = (
  variables: Types.FinmodelFailureRowQueryVariables,
) => ["FinmodelFailureRow.infiniteSuspense", variables]

useSuspenseInfiniteFinmodelFailureRowQuery.rootKey = "FinmodelFailureRow.infinite"

useFinmodelFailureRowQuery.fetcher = (
  variables: Types.FinmodelFailureRowQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.FinmodelFailureRowQuery, Types.FinmodelFailureRowQueryVariables>(
    FinmodelFailureRowDocument,
    variables,
    options,
  )

export const FinmodelFailureDetailDocument = `
    query FinmodelFailureDetail($id: Int!) {
  finmodelFailure(id: $id) {
    ...FinmodelFailureDetail
    finmodel {
      id
      fmvStr
    }
  }
}
    ${FinmodelFailureDetailFragmentDoc}
${SiteRefFragmentDoc}
${ScenarioRefFragmentDoc}
${FinmodelSourceFragmentFragmentDoc}`

export const useFinmodelFailureDetailQuery = <
  TData = Types.FinmodelFailureDetailQuery,
  TError = unknown,
>(
  variables: Types.FinmodelFailureDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelFailureDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FinmodelFailureDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelFailureDetailQuery, TError, TData>({
    queryKey: ["FinmodelFailureDetail", variables],
    queryFn: adminFetcher<
      Types.FinmodelFailureDetailQuery,
      Types.FinmodelFailureDetailQueryVariables
    >(FinmodelFailureDetailDocument, variables),
    ...options,
  })
}

useFinmodelFailureDetailQuery.getKey = (
  variables: Types.FinmodelFailureDetailQueryVariables,
) => ["FinmodelFailureDetail", variables]

useFinmodelFailureDetailQuery.rootKey = "FinmodelFailureDetail"

export const useSuspenseFinmodelFailureDetailQuery = <
  TData = Types.FinmodelFailureDetailQuery,
  TError = unknown,
>(
  variables: Types.FinmodelFailureDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelFailureDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelFailureDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelFailureDetailQuery, TError, TData>({
    queryKey: ["FinmodelFailureDetailSuspense", variables],
    queryFn: adminFetcher<
      Types.FinmodelFailureDetailQuery,
      Types.FinmodelFailureDetailQueryVariables
    >(FinmodelFailureDetailDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelFailureDetailQuery.getKey = (
  variables: Types.FinmodelFailureDetailQueryVariables,
) => ["FinmodelFailureDetailSuspense", variables]

useSuspenseFinmodelFailureDetailQuery.rootKey = "FinmodelFailureDetail"

export const useInfiniteFinmodelFailureDetailQuery = <
  TData = InfiniteData<Types.FinmodelFailureDetailQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelFailureDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelFailureDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelFailureDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelFailureDetail.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelFailureDetailQuery,
            Types.FinmodelFailureDetailQueryVariables
          >(FinmodelFailureDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelFailureDetailQuery.getKey = (
  variables: Types.FinmodelFailureDetailQueryVariables,
) => ["FinmodelFailureDetail.infinite", variables]

useInfiniteFinmodelFailureDetailQuery.rootKey = "FinmodelFailureDetail.infinite"

export const useSuspenseInfiniteFinmodelFailureDetailQuery = <
  TData = InfiniteData<Types.FinmodelFailureDetailQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelFailureDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelFailureDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelFailureDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "FinmodelFailureDetail.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelFailureDetailQuery,
            Types.FinmodelFailureDetailQueryVariables
          >(FinmodelFailureDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelFailureDetailQuery.getKey = (
  variables: Types.FinmodelFailureDetailQueryVariables,
) => ["FinmodelFailureDetail.infiniteSuspense", variables]

useSuspenseInfiniteFinmodelFailureDetailQuery.rootKey = "FinmodelFailureDetail.infinite"

useFinmodelFailureDetailQuery.fetcher = (
  variables: Types.FinmodelFailureDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.FinmodelFailureDetailQuery,
    Types.FinmodelFailureDetailQueryVariables
  >(FinmodelFailureDetailDocument, variables, options)

export const OpenInvitesDocument = `
    query OpenInvites($portfolioPid: String) {
  openInvites(portfolioPid: $portfolioPid) {
    id
    pid
    url
    expiresAt
    isExpired
    createdAt
    createdBy {
      pid
      name
      email
    }
    portfolio {
      pid
      title
    }
  }
  portfolios {
    pid
    title
  }
}
    `

export const useOpenInvitesQuery = <TData = Types.OpenInvitesQuery, TError = unknown>(
  variables?: Types.OpenInvitesQueryVariables,
  options?: Omit<UseQueryOptions<Types.OpenInvitesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OpenInvitesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OpenInvitesQuery, TError, TData>({
    queryKey: variables === undefined ? ["OpenInvites"] : ["OpenInvites", variables],
    queryFn: adminFetcher<Types.OpenInvitesQuery, Types.OpenInvitesQueryVariables>(
      OpenInvitesDocument,
      variables,
    ),
    ...options,
  })
}

useOpenInvitesQuery.getKey = (variables?: Types.OpenInvitesQueryVariables) =>
  variables === undefined ? ["OpenInvites"] : ["OpenInvites", variables]

useOpenInvitesQuery.rootKey = "OpenInvites"

export const useSuspenseOpenInvitesQuery = <
  TData = Types.OpenInvitesQuery,
  TError = unknown,
>(
  variables?: Types.OpenInvitesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OpenInvitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OpenInvitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OpenInvitesQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OpenInvitesSuspense"]
        : ["OpenInvitesSuspense", variables],
    queryFn: adminFetcher<Types.OpenInvitesQuery, Types.OpenInvitesQueryVariables>(
      OpenInvitesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOpenInvitesQuery.getKey = (variables?: Types.OpenInvitesQueryVariables) =>
  variables === undefined ? ["OpenInvitesSuspense"] : ["OpenInvitesSuspense", variables]

useSuspenseOpenInvitesQuery.rootKey = "OpenInvites"

export const useInfiniteOpenInvitesQuery = <
  TData = InfiniteData<Types.OpenInvitesQuery>,
  TError = unknown,
>(
  variables: Types.OpenInvitesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OpenInvitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OpenInvitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OpenInvitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OpenInvites.infinite"]
            : ["OpenInvites.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.OpenInvitesQuery, Types.OpenInvitesQueryVariables>(
            OpenInvitesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOpenInvitesQuery.getKey = (variables?: Types.OpenInvitesQueryVariables) =>
  variables === undefined
    ? ["OpenInvites.infinite"]
    : ["OpenInvites.infinite", variables]

useInfiniteOpenInvitesQuery.rootKey = "OpenInvites.infinite"

export const useSuspenseInfiniteOpenInvitesQuery = <
  TData = InfiniteData<Types.OpenInvitesQuery>,
  TError = unknown,
>(
  variables: Types.OpenInvitesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OpenInvitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OpenInvitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OpenInvitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OpenInvites.infiniteSuspense"]
            : ["OpenInvites.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.OpenInvitesQuery, Types.OpenInvitesQueryVariables>(
            OpenInvitesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOpenInvitesQuery.getKey = (
  variables?: Types.OpenInvitesQueryVariables,
) =>
  variables === undefined
    ? ["OpenInvites.infiniteSuspense"]
    : ["OpenInvites.infiniteSuspense", variables]

useSuspenseInfiniteOpenInvitesQuery.rootKey = "OpenInvites.infinite"

useOpenInvitesQuery.fetcher = (
  variables?: Types.OpenInvitesQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.OpenInvitesQuery, Types.OpenInvitesQueryVariables>(
    OpenInvitesDocument,
    variables,
    options,
  )

export const SitePoliciesDocument = `
    query SitePolicies($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    state
    targetUtility {
      id
      name
      shortName
    }
    policies {
      ...PolicyListItem
    }
  }
}
    ${PolicyListItemFragmentDoc}
${RuleFragmentDoc}
${RuleValueFragmentDoc}
${PolicyConditionFragmentDoc}`

export const useSitePoliciesQuery = <TData = Types.SitePoliciesQuery, TError = unknown>(
  variables: Types.SitePoliciesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SitePoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SitePoliciesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SitePoliciesQuery, TError, TData>({
    queryKey: ["SitePolicies", variables],
    queryFn: adminFetcher<Types.SitePoliciesQuery, Types.SitePoliciesQueryVariables>(
      SitePoliciesDocument,
      variables,
    ),
    ...options,
  })
}

useSitePoliciesQuery.getKey = (variables: Types.SitePoliciesQueryVariables) => [
  "SitePolicies",
  variables,
]

useSitePoliciesQuery.rootKey = "SitePolicies"

export const useSuspenseSitePoliciesQuery = <
  TData = Types.SitePoliciesQuery,
  TError = unknown,
>(
  variables: Types.SitePoliciesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SitePoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SitePoliciesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SitePoliciesQuery, TError, TData>({
    queryKey: ["SitePoliciesSuspense", variables],
    queryFn: adminFetcher<Types.SitePoliciesQuery, Types.SitePoliciesQueryVariables>(
      SitePoliciesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSitePoliciesQuery.getKey = (variables: Types.SitePoliciesQueryVariables) => [
  "SitePoliciesSuspense",
  variables,
]

useSuspenseSitePoliciesQuery.rootKey = "SitePolicies"

export const useInfiniteSitePoliciesQuery = <
  TData = InfiniteData<Types.SitePoliciesQuery>,
  TError = unknown,
>(
  variables: Types.SitePoliciesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SitePoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SitePoliciesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SitePoliciesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SitePolicies.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SitePoliciesQuery, Types.SitePoliciesQueryVariables>(
            SitePoliciesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSitePoliciesQuery.getKey = (variables: Types.SitePoliciesQueryVariables) => [
  "SitePolicies.infinite",
  variables,
]

useInfiniteSitePoliciesQuery.rootKey = "SitePolicies.infinite"

export const useSuspenseInfiniteSitePoliciesQuery = <
  TData = InfiniteData<Types.SitePoliciesQuery>,
  TError = unknown,
>(
  variables: Types.SitePoliciesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SitePoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SitePoliciesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SitePoliciesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SitePolicies.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SitePoliciesQuery, Types.SitePoliciesQueryVariables>(
            SitePoliciesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSitePoliciesQuery.getKey = (
  variables: Types.SitePoliciesQueryVariables,
) => ["SitePolicies.infiniteSuspense", variables]

useSuspenseInfiniteSitePoliciesQuery.rootKey = "SitePolicies.infinite"

useSitePoliciesQuery.fetcher = (
  variables: Types.SitePoliciesQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SitePoliciesQuery, Types.SitePoliciesQueryVariables>(
    SitePoliciesDocument,
    variables,
    options,
  )

export const AllPoliciesDocument = `
    query AllPolicies {
  policies {
    all {
      ...PolicyListItem
    }
  }
}
    ${PolicyListItemFragmentDoc}
${RuleFragmentDoc}
${RuleValueFragmentDoc}
${PolicyConditionFragmentDoc}`

export const useAllPoliciesQuery = <TData = Types.AllPoliciesQuery, TError = unknown>(
  variables?: Types.AllPoliciesQueryVariables,
  options?: Omit<UseQueryOptions<Types.AllPoliciesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.AllPoliciesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AllPoliciesQuery, TError, TData>({
    queryKey: variables === undefined ? ["AllPolicies"] : ["AllPolicies", variables],
    queryFn: adminFetcher<Types.AllPoliciesQuery, Types.AllPoliciesQueryVariables>(
      AllPoliciesDocument,
      variables,
    ),
    ...options,
  })
}

useAllPoliciesQuery.getKey = (variables?: Types.AllPoliciesQueryVariables) =>
  variables === undefined ? ["AllPolicies"] : ["AllPolicies", variables]

useAllPoliciesQuery.rootKey = "AllPolicies"

export const useSuspenseAllPoliciesQuery = <
  TData = Types.AllPoliciesQuery,
  TError = unknown,
>(
  variables?: Types.AllPoliciesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AllPoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AllPoliciesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AllPoliciesQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AllPoliciesSuspense"]
        : ["AllPoliciesSuspense", variables],
    queryFn: adminFetcher<Types.AllPoliciesQuery, Types.AllPoliciesQueryVariables>(
      AllPoliciesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseAllPoliciesQuery.getKey = (variables?: Types.AllPoliciesQueryVariables) =>
  variables === undefined ? ["AllPoliciesSuspense"] : ["AllPoliciesSuspense", variables]

useSuspenseAllPoliciesQuery.rootKey = "AllPolicies"

export const useInfiniteAllPoliciesQuery = <
  TData = InfiniteData<Types.AllPoliciesQuery>,
  TError = unknown,
>(
  variables: Types.AllPoliciesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AllPoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AllPoliciesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AllPoliciesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AllPolicies.infinite"]
            : ["AllPolicies.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.AllPoliciesQuery, Types.AllPoliciesQueryVariables>(
            AllPoliciesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAllPoliciesQuery.getKey = (variables?: Types.AllPoliciesQueryVariables) =>
  variables === undefined
    ? ["AllPolicies.infinite"]
    : ["AllPolicies.infinite", variables]

useInfiniteAllPoliciesQuery.rootKey = "AllPolicies.infinite"

export const useSuspenseInfiniteAllPoliciesQuery = <
  TData = InfiniteData<Types.AllPoliciesQuery>,
  TError = unknown,
>(
  variables: Types.AllPoliciesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AllPoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AllPoliciesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AllPoliciesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AllPolicies.infiniteSuspense"]
            : ["AllPolicies.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.AllPoliciesQuery, Types.AllPoliciesQueryVariables>(
            AllPoliciesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAllPoliciesQuery.getKey = (
  variables?: Types.AllPoliciesQueryVariables,
) =>
  variables === undefined
    ? ["AllPolicies.infiniteSuspense"]
    : ["AllPolicies.infiniteSuspense", variables]

useSuspenseInfiniteAllPoliciesQuery.rootKey = "AllPolicies.infinite"

useAllPoliciesQuery.fetcher = (
  variables?: Types.AllPoliciesQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AllPoliciesQuery, Types.AllPoliciesQueryVariables>(
    AllPoliciesDocument,
    variables,
    options,
  )

export const PolicyDetailDocument = `
    query PolicyDetail($policyPid: String!) {
  policies {
    policy(policyPid: $policyPid) {
      pid
      name
      userVisible
      description
      publicDescription
      status
      startDate
      endDate
      application {
        status
      }
      jurisdiction {
        scope
        name
      }
      rules {
        ...Rule
      }
      conditions {
        ...PolicyCondition
      }
      sources {
        text
        url
      }
    }
  }
}
    ${RuleFragmentDoc}
${RuleValueFragmentDoc}
${PolicyConditionFragmentDoc}`

export const usePolicyDetailQuery = <TData = Types.PolicyDetailQuery, TError = unknown>(
  variables: Types.PolicyDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PolicyDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.PolicyDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.PolicyDetailQuery, TError, TData>({
    queryKey: ["PolicyDetail", variables],
    queryFn: adminFetcher<Types.PolicyDetailQuery, Types.PolicyDetailQueryVariables>(
      PolicyDetailDocument,
      variables,
    ),
    ...options,
  })
}

usePolicyDetailQuery.getKey = (variables: Types.PolicyDetailQueryVariables) => [
  "PolicyDetail",
  variables,
]

usePolicyDetailQuery.rootKey = "PolicyDetail"

export const useSuspensePolicyDetailQuery = <
  TData = Types.PolicyDetailQuery,
  TError = unknown,
>(
  variables: Types.PolicyDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PolicyDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PolicyDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PolicyDetailQuery, TError, TData>({
    queryKey: ["PolicyDetailSuspense", variables],
    queryFn: adminFetcher<Types.PolicyDetailQuery, Types.PolicyDetailQueryVariables>(
      PolicyDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspensePolicyDetailQuery.getKey = (variables: Types.PolicyDetailQueryVariables) => [
  "PolicyDetailSuspense",
  variables,
]

useSuspensePolicyDetailQuery.rootKey = "PolicyDetail"

export const useInfinitePolicyDetailQuery = <
  TData = InfiniteData<Types.PolicyDetailQuery>,
  TError = unknown,
>(
  variables: Types.PolicyDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PolicyDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PolicyDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PolicyDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PolicyDetail.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.PolicyDetailQuery, Types.PolicyDetailQueryVariables>(
            PolicyDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePolicyDetailQuery.getKey = (variables: Types.PolicyDetailQueryVariables) => [
  "PolicyDetail.infinite",
  variables,
]

useInfinitePolicyDetailQuery.rootKey = "PolicyDetail.infinite"

export const useSuspenseInfinitePolicyDetailQuery = <
  TData = InfiniteData<Types.PolicyDetailQuery>,
  TError = unknown,
>(
  variables: Types.PolicyDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PolicyDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PolicyDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PolicyDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PolicyDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.PolicyDetailQuery, Types.PolicyDetailQueryVariables>(
            PolicyDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePolicyDetailQuery.getKey = (
  variables: Types.PolicyDetailQueryVariables,
) => ["PolicyDetail.infiniteSuspense", variables]

useSuspenseInfinitePolicyDetailQuery.rootKey = "PolicyDetail.infinite"

usePolicyDetailQuery.fetcher = (
  variables: Types.PolicyDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.PolicyDetailQuery, Types.PolicyDetailQueryVariables>(
    PolicyDetailDocument,
    variables,
    options,
  )

export const SitePolicyViolationsDocument = `
    query SitePolicyViolations($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    policyViolations {
      policy {
        pid
        name
      }
      violatedRule {
        id
        description
        units
        value {
          ...RuleValue
        }
      }
      value {
        ...RuleValue
      }
    }
  }
}
    ${RuleValueFragmentDoc}`

export const useSitePolicyViolationsQuery = <
  TData = Types.SitePolicyViolationsQuery,
  TError = unknown,
>(
  variables: Types.SitePolicyViolationsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SitePolicyViolationsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SitePolicyViolationsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SitePolicyViolationsQuery, TError, TData>({
    queryKey: ["SitePolicyViolations", variables],
    queryFn: adminFetcher<
      Types.SitePolicyViolationsQuery,
      Types.SitePolicyViolationsQueryVariables
    >(SitePolicyViolationsDocument, variables),
    ...options,
  })
}

useSitePolicyViolationsQuery.getKey = (
  variables: Types.SitePolicyViolationsQueryVariables,
) => ["SitePolicyViolations", variables]

useSitePolicyViolationsQuery.rootKey = "SitePolicyViolations"

export const useSuspenseSitePolicyViolationsQuery = <
  TData = Types.SitePolicyViolationsQuery,
  TError = unknown,
>(
  variables: Types.SitePolicyViolationsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SitePolicyViolationsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SitePolicyViolationsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SitePolicyViolationsQuery, TError, TData>({
    queryKey: ["SitePolicyViolationsSuspense", variables],
    queryFn: adminFetcher<
      Types.SitePolicyViolationsQuery,
      Types.SitePolicyViolationsQueryVariables
    >(SitePolicyViolationsDocument, variables),
    ...options,
  })
}

useSuspenseSitePolicyViolationsQuery.getKey = (
  variables: Types.SitePolicyViolationsQueryVariables,
) => ["SitePolicyViolationsSuspense", variables]

useSuspenseSitePolicyViolationsQuery.rootKey = "SitePolicyViolations"

export const useInfiniteSitePolicyViolationsQuery = <
  TData = InfiniteData<Types.SitePolicyViolationsQuery>,
  TError = unknown,
>(
  variables: Types.SitePolicyViolationsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SitePolicyViolationsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SitePolicyViolationsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SitePolicyViolationsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SitePolicyViolations.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.SitePolicyViolationsQuery,
            Types.SitePolicyViolationsQueryVariables
          >(SitePolicyViolationsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSitePolicyViolationsQuery.getKey = (
  variables: Types.SitePolicyViolationsQueryVariables,
) => ["SitePolicyViolations.infinite", variables]

useInfiniteSitePolicyViolationsQuery.rootKey = "SitePolicyViolations.infinite"

export const useSuspenseInfiniteSitePolicyViolationsQuery = <
  TData = InfiniteData<Types.SitePolicyViolationsQuery>,
  TError = unknown,
>(
  variables: Types.SitePolicyViolationsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SitePolicyViolationsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SitePolicyViolationsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SitePolicyViolationsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "SitePolicyViolations.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.SitePolicyViolationsQuery,
            Types.SitePolicyViolationsQueryVariables
          >(SitePolicyViolationsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSitePolicyViolationsQuery.getKey = (
  variables: Types.SitePolicyViolationsQueryVariables,
) => ["SitePolicyViolations.infiniteSuspense", variables]

useSuspenseInfiniteSitePolicyViolationsQuery.rootKey = "SitePolicyViolations.infinite"

useSitePolicyViolationsQuery.fetcher = (
  variables: Types.SitePolicyViolationsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SitePolicyViolationsQuery,
    Types.SitePolicyViolationsQueryVariables
  >(SitePolicyViolationsDocument, variables, options)

export const MultiMeterDocument = `
    query MultiMeter($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    isMultifamily
    usingBillingMeters
    orgSlug
    bauMeters {
      ...BauMeter
      hasTruth
    }
    activeMeters {
      id
      createdAt
      createdBy {
        pid
        email
      }
      meters {
        ...BauMeter
      }
    }
    thirdPartySupplier {
      id
      name
    }
    targetUtility {
      id
      name
      shortName
    }
    targetUtilitySetBy {
      ...UserRef
    }
    dateTargetUtilitySet
    utilityName
    probableUtilities {
      id
      name
    }
    customTariffs {
      id
      name
      code
      parent {
        id
      }
    }
  }
}
    ${BauMeterFragmentDoc}
${BauTariffFragmentDoc}
${UserRefFragmentDoc}`

export const useMultiMeterQuery = <TData = Types.MultiMeterQuery, TError = unknown>(
  variables: Types.MultiMeterQueryVariables,
  options?: Omit<UseQueryOptions<Types.MultiMeterQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.MultiMeterQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.MultiMeterQuery, TError, TData>({
    queryKey: ["MultiMeter", variables],
    queryFn: adminFetcher<Types.MultiMeterQuery, Types.MultiMeterQueryVariables>(
      MultiMeterDocument,
      variables,
    ),
    ...options,
  })
}

useMultiMeterQuery.getKey = (variables: Types.MultiMeterQueryVariables) => [
  "MultiMeter",
  variables,
]

useMultiMeterQuery.rootKey = "MultiMeter"

export const useSuspenseMultiMeterQuery = <
  TData = Types.MultiMeterQuery,
  TError = unknown,
>(
  variables: Types.MultiMeterQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MultiMeterQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.MultiMeterQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MultiMeterQuery, TError, TData>({
    queryKey: ["MultiMeterSuspense", variables],
    queryFn: adminFetcher<Types.MultiMeterQuery, Types.MultiMeterQueryVariables>(
      MultiMeterDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseMultiMeterQuery.getKey = (variables: Types.MultiMeterQueryVariables) => [
  "MultiMeterSuspense",
  variables,
]

useSuspenseMultiMeterQuery.rootKey = "MultiMeter"

export const useInfiniteMultiMeterQuery = <
  TData = InfiniteData<Types.MultiMeterQuery>,
  TError = unknown,
>(
  variables: Types.MultiMeterQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MultiMeterQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.MultiMeterQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MultiMeterQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiMeter.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.MultiMeterQuery, Types.MultiMeterQueryVariables>(
            MultiMeterDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMultiMeterQuery.getKey = (variables: Types.MultiMeterQueryVariables) => [
  "MultiMeter.infinite",
  variables,
]

useInfiniteMultiMeterQuery.rootKey = "MultiMeter.infinite"

export const useSuspenseInfiniteMultiMeterQuery = <
  TData = InfiniteData<Types.MultiMeterQuery>,
  TError = unknown,
>(
  variables: Types.MultiMeterQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MultiMeterQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MultiMeterQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MultiMeterQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiMeter.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.MultiMeterQuery, Types.MultiMeterQueryVariables>(
            MultiMeterDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMultiMeterQuery.getKey = (
  variables: Types.MultiMeterQueryVariables,
) => ["MultiMeter.infiniteSuspense", variables]

useSuspenseInfiniteMultiMeterQuery.rootKey = "MultiMeter.infinite"

useMultiMeterQuery.fetcher = (
  variables: Types.MultiMeterQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.MultiMeterQuery, Types.MultiMeterQueryVariables>(
    MultiMeterDocument,
    variables,
    options,
  )

export const MultiMeterDetailDocument = `
    query MultiMeterDetail($id: Int!) {
  bauMeter(id: $id) {
    id
    formattedBill {
      ...FormattedBill
    }
    formattedPostBill {
      ...FormattedBill
    }
    tariff {
      name
      code
    }
    tariffUsdPerKwh
    postTariff {
      name
      code
    }
  }
}
    ${FormattedBillFragmentDoc}
${BillSectionFragmentDoc}
${DecodedRateFragmentDoc}
${CostOrTaxItemFragmentDoc}`

export const useMultiMeterDetailQuery = <
  TData = Types.MultiMeterDetailQuery,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.MultiMeterDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.MultiMeterDetailQuery, TError, TData>({
    queryKey: ["MultiMeterDetail", variables],
    queryFn: adminFetcher<
      Types.MultiMeterDetailQuery,
      Types.MultiMeterDetailQueryVariables
    >(MultiMeterDetailDocument, variables),
    ...options,
  })
}

useMultiMeterDetailQuery.getKey = (variables: Types.MultiMeterDetailQueryVariables) => [
  "MultiMeterDetail",
  variables,
]

useMultiMeterDetailQuery.rootKey = "MultiMeterDetail"

export const useSuspenseMultiMeterDetailQuery = <
  TData = Types.MultiMeterDetailQuery,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MultiMeterDetailQuery, TError, TData>({
    queryKey: ["MultiMeterDetailSuspense", variables],
    queryFn: adminFetcher<
      Types.MultiMeterDetailQuery,
      Types.MultiMeterDetailQueryVariables
    >(MultiMeterDetailDocument, variables),
    ...options,
  })
}

useSuspenseMultiMeterDetailQuery.getKey = (
  variables: Types.MultiMeterDetailQueryVariables,
) => ["MultiMeterDetailSuspense", variables]

useSuspenseMultiMeterDetailQuery.rootKey = "MultiMeterDetail"

export const useInfiniteMultiMeterDetailQuery = <
  TData = InfiniteData<Types.MultiMeterDetailQuery>,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MultiMeterDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiMeterDetail.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.MultiMeterDetailQuery,
            Types.MultiMeterDetailQueryVariables
          >(MultiMeterDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMultiMeterDetailQuery.getKey = (
  variables: Types.MultiMeterDetailQueryVariables,
) => ["MultiMeterDetail.infinite", variables]

useInfiniteMultiMeterDetailQuery.rootKey = "MultiMeterDetail.infinite"

export const useSuspenseInfiniteMultiMeterDetailQuery = <
  TData = InfiniteData<Types.MultiMeterDetailQuery>,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MultiMeterDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiMeterDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.MultiMeterDetailQuery,
            Types.MultiMeterDetailQueryVariables
          >(MultiMeterDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMultiMeterDetailQuery.getKey = (
  variables: Types.MultiMeterDetailQueryVariables,
) => ["MultiMeterDetail.infiniteSuspense", variables]

useSuspenseInfiniteMultiMeterDetailQuery.rootKey = "MultiMeterDetail.infinite"

useMultiMeterDetailQuery.fetcher = (
  variables: Types.MultiMeterDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.MultiMeterDetailQuery, Types.MultiMeterDetailQueryVariables>(
    MultiMeterDetailDocument,
    variables,
    options,
  )

export const BauMeterNameThatTariffDetailDocument = `
    query BauMeterNameThatTariffDetail($id: Int!, $tariffId: Int!, $documentBillingMonthId: Int!) {
  bauMeter(id: $id) {
    nameThatTariffDetail(
      tariffId: $tariffId
      documentBillingMonthId: $documentBillingMonthId
    ) {
      tariff {
        id
        masterTariffId
        code
        name
      }
      result {
        ...NameThatTariffResult
      }
      groups {
        group
        items {
          cost
          usage
          name
          rate
        }
      }
    }
  }
}
    ${NameThatTariffResultFragmentDoc}
${DeltaFragmentDoc}`

export const useBauMeterNameThatTariffDetailQuery = <
  TData = Types.BauMeterNameThatTariffDetailQuery,
  TError = unknown,
>(
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BauMeterNameThatTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BauMeterNameThatTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BauMeterNameThatTariffDetailQuery, TError, TData>({
    queryKey: ["BauMeterNameThatTariffDetail", variables],
    queryFn: adminFetcher<
      Types.BauMeterNameThatTariffDetailQuery,
      Types.BauMeterNameThatTariffDetailQueryVariables
    >(BauMeterNameThatTariffDetailDocument, variables),
    ...options,
  })
}

useBauMeterNameThatTariffDetailQuery.getKey = (
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
) => ["BauMeterNameThatTariffDetail", variables]

useBauMeterNameThatTariffDetailQuery.rootKey = "BauMeterNameThatTariffDetail"

export const useSuspenseBauMeterNameThatTariffDetailQuery = <
  TData = Types.BauMeterNameThatTariffDetailQuery,
  TError = unknown,
>(
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BauMeterNameThatTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BauMeterNameThatTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BauMeterNameThatTariffDetailQuery, TError, TData>({
    queryKey: ["BauMeterNameThatTariffDetailSuspense", variables],
    queryFn: adminFetcher<
      Types.BauMeterNameThatTariffDetailQuery,
      Types.BauMeterNameThatTariffDetailQueryVariables
    >(BauMeterNameThatTariffDetailDocument, variables),
    ...options,
  })
}

useSuspenseBauMeterNameThatTariffDetailQuery.getKey = (
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
) => ["BauMeterNameThatTariffDetailSuspense", variables]

useSuspenseBauMeterNameThatTariffDetailQuery.rootKey = "BauMeterNameThatTariffDetail"

export const useInfiniteBauMeterNameThatTariffDetailQuery = <
  TData = InfiniteData<Types.BauMeterNameThatTariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BauMeterNameThatTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BauMeterNameThatTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BauMeterNameThatTariffDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "BauMeterNameThatTariffDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.BauMeterNameThatTariffDetailQuery,
            Types.BauMeterNameThatTariffDetailQueryVariables
          >(BauMeterNameThatTariffDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBauMeterNameThatTariffDetailQuery.getKey = (
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
) => ["BauMeterNameThatTariffDetail.infinite", variables]

useInfiniteBauMeterNameThatTariffDetailQuery.rootKey =
  "BauMeterNameThatTariffDetail.infinite"

export const useSuspenseInfiniteBauMeterNameThatTariffDetailQuery = <
  TData = InfiniteData<Types.BauMeterNameThatTariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.BauMeterNameThatTariffDetailQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BauMeterNameThatTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.BauMeterNameThatTariffDetailQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "BauMeterNameThatTariffDetail.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.BauMeterNameThatTariffDetailQuery,
            Types.BauMeterNameThatTariffDetailQueryVariables
          >(BauMeterNameThatTariffDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBauMeterNameThatTariffDetailQuery.getKey = (
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
) => ["BauMeterNameThatTariffDetail.infiniteSuspense", variables]

useSuspenseInfiniteBauMeterNameThatTariffDetailQuery.rootKey =
  "BauMeterNameThatTariffDetail.infinite"

useBauMeterNameThatTariffDetailQuery.fetcher = (
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.BauMeterNameThatTariffDetailQuery,
    Types.BauMeterNameThatTariffDetailQueryVariables
  >(BauMeterNameThatTariffDetailDocument, variables, options)

export const TestCustomTariffDocument = `
    query TestCustomTariff($id: Int!, $cfg: String!, $utilityId: Int!) {
  bauMeter(id: $id) {
    id
    testCustomTariff(cfg: $cfg, utilityId: $utilityId) {
      ...FormattedBill
    }
  }
}
    ${FormattedBillFragmentDoc}
${BillSectionFragmentDoc}
${DecodedRateFragmentDoc}
${CostOrTaxItemFragmentDoc}`

export const useTestCustomTariffQuery = <
  TData = Types.TestCustomTariffQuery,
  TError = unknown,
>(
  variables: Types.TestCustomTariffQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.TestCustomTariffQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.TestCustomTariffQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.TestCustomTariffQuery, TError, TData>({
    queryKey: ["TestCustomTariff", variables],
    queryFn: adminFetcher<
      Types.TestCustomTariffQuery,
      Types.TestCustomTariffQueryVariables
    >(TestCustomTariffDocument, variables),
    ...options,
  })
}

useTestCustomTariffQuery.getKey = (variables: Types.TestCustomTariffQueryVariables) => [
  "TestCustomTariff",
  variables,
]

useTestCustomTariffQuery.rootKey = "TestCustomTariff"

export const useSuspenseTestCustomTariffQuery = <
  TData = Types.TestCustomTariffQuery,
  TError = unknown,
>(
  variables: Types.TestCustomTariffQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.TestCustomTariffQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.TestCustomTariffQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.TestCustomTariffQuery, TError, TData>({
    queryKey: ["TestCustomTariffSuspense", variables],
    queryFn: adminFetcher<
      Types.TestCustomTariffQuery,
      Types.TestCustomTariffQueryVariables
    >(TestCustomTariffDocument, variables),
    ...options,
  })
}

useSuspenseTestCustomTariffQuery.getKey = (
  variables: Types.TestCustomTariffQueryVariables,
) => ["TestCustomTariffSuspense", variables]

useSuspenseTestCustomTariffQuery.rootKey = "TestCustomTariff"

export const useInfiniteTestCustomTariffQuery = <
  TData = InfiniteData<Types.TestCustomTariffQuery>,
  TError = unknown,
>(
  variables: Types.TestCustomTariffQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.TestCustomTariffQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.TestCustomTariffQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.TestCustomTariffQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TestCustomTariff.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.TestCustomTariffQuery,
            Types.TestCustomTariffQueryVariables
          >(TestCustomTariffDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteTestCustomTariffQuery.getKey = (
  variables: Types.TestCustomTariffQueryVariables,
) => ["TestCustomTariff.infinite", variables]

useInfiniteTestCustomTariffQuery.rootKey = "TestCustomTariff.infinite"

export const useSuspenseInfiniteTestCustomTariffQuery = <
  TData = InfiniteData<Types.TestCustomTariffQuery>,
  TError = unknown,
>(
  variables: Types.TestCustomTariffQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.TestCustomTariffQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.TestCustomTariffQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.TestCustomTariffQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TestCustomTariff.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.TestCustomTariffQuery,
            Types.TestCustomTariffQueryVariables
          >(TestCustomTariffDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteTestCustomTariffQuery.getKey = (
  variables: Types.TestCustomTariffQueryVariables,
) => ["TestCustomTariff.infiniteSuspense", variables]

useSuspenseInfiniteTestCustomTariffQuery.rootKey = "TestCustomTariff.infinite"

useTestCustomTariffQuery.fetcher = (
  variables: Types.TestCustomTariffQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.TestCustomTariffQuery, Types.TestCustomTariffQueryVariables>(
    TestCustomTariffDocument,
    variables,
    options,
  )

export const BauMeterIntervalsDocument = `
    query BauMeterIntervals($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    bauMeters {
      ...BauMeterIntervals
    }
  }
}
    ${BauMeterIntervalsFragmentDoc}`

export const useBauMeterIntervalsQuery = <
  TData = Types.BauMeterIntervalsQuery,
  TError = unknown,
>(
  variables: Types.BauMeterIntervalsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BauMeterIntervalsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BauMeterIntervalsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BauMeterIntervalsQuery, TError, TData>({
    queryKey: ["BauMeterIntervals", variables],
    queryFn: adminFetcher<
      Types.BauMeterIntervalsQuery,
      Types.BauMeterIntervalsQueryVariables
    >(BauMeterIntervalsDocument, variables),
    ...options,
  })
}

useBauMeterIntervalsQuery.getKey = (
  variables: Types.BauMeterIntervalsQueryVariables,
) => ["BauMeterIntervals", variables]

useBauMeterIntervalsQuery.rootKey = "BauMeterIntervals"

export const useSuspenseBauMeterIntervalsQuery = <
  TData = Types.BauMeterIntervalsQuery,
  TError = unknown,
>(
  variables: Types.BauMeterIntervalsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BauMeterIntervalsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BauMeterIntervalsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BauMeterIntervalsQuery, TError, TData>({
    queryKey: ["BauMeterIntervalsSuspense", variables],
    queryFn: adminFetcher<
      Types.BauMeterIntervalsQuery,
      Types.BauMeterIntervalsQueryVariables
    >(BauMeterIntervalsDocument, variables),
    ...options,
  })
}

useSuspenseBauMeterIntervalsQuery.getKey = (
  variables: Types.BauMeterIntervalsQueryVariables,
) => ["BauMeterIntervalsSuspense", variables]

useSuspenseBauMeterIntervalsQuery.rootKey = "BauMeterIntervals"

export const useInfiniteBauMeterIntervalsQuery = <
  TData = InfiniteData<Types.BauMeterIntervalsQuery>,
  TError = unknown,
>(
  variables: Types.BauMeterIntervalsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BauMeterIntervalsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BauMeterIntervalsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BauMeterIntervalsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BauMeterIntervals.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.BauMeterIntervalsQuery,
            Types.BauMeterIntervalsQueryVariables
          >(BauMeterIntervalsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBauMeterIntervalsQuery.getKey = (
  variables: Types.BauMeterIntervalsQueryVariables,
) => ["BauMeterIntervals.infinite", variables]

useInfiniteBauMeterIntervalsQuery.rootKey = "BauMeterIntervals.infinite"

export const useSuspenseInfiniteBauMeterIntervalsQuery = <
  TData = InfiniteData<Types.BauMeterIntervalsQuery>,
  TError = unknown,
>(
  variables: Types.BauMeterIntervalsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BauMeterIntervalsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BauMeterIntervalsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BauMeterIntervalsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BauMeterIntervals.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.BauMeterIntervalsQuery,
            Types.BauMeterIntervalsQueryVariables
          >(BauMeterIntervalsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBauMeterIntervalsQuery.getKey = (
  variables: Types.BauMeterIntervalsQueryVariables,
) => ["BauMeterIntervals.infiniteSuspense", variables]

useSuspenseInfiniteBauMeterIntervalsQuery.rootKey = "BauMeterIntervals.infinite"

useBauMeterIntervalsQuery.fetcher = (
  variables: Types.BauMeterIntervalsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.BauMeterIntervalsQuery, Types.BauMeterIntervalsQueryVariables>(
    BauMeterIntervalsDocument,
    variables,
    options,
  )

export const MeterSetDocument = `
    query MeterSet($sitePid: String!, $id: Int!) {
  site(pid: $sitePid) {
    pid
    isMultifamily
    meterSet(id: $id) {
      id
      meters {
        hasTruth
        ...BauMeter
        ...BauMeterIntervals
      }
    }
    customTariffs {
      id
      name
      code
      parent {
        id
      }
    }
  }
}
    ${BauMeterFragmentDoc}
${BauTariffFragmentDoc}
${BauMeterIntervalsFragmentDoc}`

export const useMeterSetQuery = <TData = Types.MeterSetQuery, TError = unknown>(
  variables: Types.MeterSetQueryVariables,
  options?: Omit<UseQueryOptions<Types.MeterSetQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.MeterSetQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.MeterSetQuery, TError, TData>({
    queryKey: ["MeterSet", variables],
    queryFn: adminFetcher<Types.MeterSetQuery, Types.MeterSetQueryVariables>(
      MeterSetDocument,
      variables,
    ),
    ...options,
  })
}

useMeterSetQuery.getKey = (variables: Types.MeterSetQueryVariables) => [
  "MeterSet",
  variables,
]

useMeterSetQuery.rootKey = "MeterSet"

export const useSuspenseMeterSetQuery = <TData = Types.MeterSetQuery, TError = unknown>(
  variables: Types.MeterSetQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MeterSetQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.MeterSetQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MeterSetQuery, TError, TData>({
    queryKey: ["MeterSetSuspense", variables],
    queryFn: adminFetcher<Types.MeterSetQuery, Types.MeterSetQueryVariables>(
      MeterSetDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseMeterSetQuery.getKey = (variables: Types.MeterSetQueryVariables) => [
  "MeterSetSuspense",
  variables,
]

useSuspenseMeterSetQuery.rootKey = "MeterSet"

export const useInfiniteMeterSetQuery = <
  TData = InfiniteData<Types.MeterSetQuery>,
  TError = unknown,
>(
  variables: Types.MeterSetQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MeterSetQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.MeterSetQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MeterSetQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MeterSet.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.MeterSetQuery, Types.MeterSetQueryVariables>(
            MeterSetDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMeterSetQuery.getKey = (variables: Types.MeterSetQueryVariables) => [
  "MeterSet.infinite",
  variables,
]

useInfiniteMeterSetQuery.rootKey = "MeterSet.infinite"

export const useSuspenseInfiniteMeterSetQuery = <
  TData = InfiniteData<Types.MeterSetQuery>,
  TError = unknown,
>(
  variables: Types.MeterSetQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MeterSetQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MeterSetQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MeterSetQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MeterSet.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.MeterSetQuery, Types.MeterSetQueryVariables>(
            MeterSetDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMeterSetQuery.getKey = (variables: Types.MeterSetQueryVariables) => [
  "MeterSet.infiniteSuspense",
  variables,
]

useSuspenseInfiniteMeterSetQuery.rootKey = "MeterSet.infinite"

useMeterSetQuery.fetcher = (
  variables: Types.MeterSetQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.MeterSetQuery, Types.MeterSetQueryVariables>(
    MeterSetDocument,
    variables,
    options,
  )

export const AdminNotesDocument = `
    query AdminNotes($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    notes {
      pid
      text
      createdAt
      createdBy {
        ...UserRef
      }
      updatedAt
      updatedBy {
        ...UserRef
      }
      trashedAt
      trashedBy {
        ...UserRef
      }
      hiddenFromCustomers
    }
  }
}
    ${UserRefFragmentDoc}`

export const useAdminNotesQuery = <TData = Types.AdminNotesQuery, TError = unknown>(
  variables: Types.AdminNotesQueryVariables,
  options?: Omit<UseQueryOptions<Types.AdminNotesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.AdminNotesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminNotesQuery, TError, TData>({
    queryKey: ["AdminNotes", variables],
    queryFn: adminFetcher<Types.AdminNotesQuery, Types.AdminNotesQueryVariables>(
      AdminNotesDocument,
      variables,
    ),
    ...options,
  })
}

useAdminNotesQuery.getKey = (variables: Types.AdminNotesQueryVariables) => [
  "AdminNotes",
  variables,
]

useAdminNotesQuery.rootKey = "AdminNotes"

export const useSuspenseAdminNotesQuery = <
  TData = Types.AdminNotesQuery,
  TError = unknown,
>(
  variables: Types.AdminNotesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminNotesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.AdminNotesQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminNotesQuery, TError, TData>({
    queryKey: ["AdminNotesSuspense", variables],
    queryFn: adminFetcher<Types.AdminNotesQuery, Types.AdminNotesQueryVariables>(
      AdminNotesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseAdminNotesQuery.getKey = (variables: Types.AdminNotesQueryVariables) => [
  "AdminNotesSuspense",
  variables,
]

useSuspenseAdminNotesQuery.rootKey = "AdminNotes"

export const useInfiniteAdminNotesQuery = <
  TData = InfiniteData<Types.AdminNotesQuery>,
  TError = unknown,
>(
  variables: Types.AdminNotesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminNotesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.AdminNotesQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminNotesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminNotes.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.AdminNotesQuery, Types.AdminNotesQueryVariables>(
            AdminNotesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminNotesQuery.getKey = (variables: Types.AdminNotesQueryVariables) => [
  "AdminNotes.infinite",
  variables,
]

useInfiniteAdminNotesQuery.rootKey = "AdminNotes.infinite"

export const useSuspenseInfiniteAdminNotesQuery = <
  TData = InfiniteData<Types.AdminNotesQuery>,
  TError = unknown,
>(
  variables: Types.AdminNotesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminNotesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminNotesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminNotesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminNotes.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.AdminNotesQuery, Types.AdminNotesQueryVariables>(
            AdminNotesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminNotesQuery.getKey = (
  variables: Types.AdminNotesQueryVariables,
) => ["AdminNotes.infiniteSuspense", variables]

useSuspenseInfiniteAdminNotesQuery.rootKey = "AdminNotes.infinite"

useAdminNotesQuery.fetcher = (
  variables: Types.AdminNotesQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AdminNotesQuery, Types.AdminNotesQueryVariables>(
    AdminNotesDocument,
    variables,
    options,
  )

export const BatchPhysicalCheckDocument = `
    query BatchPhysicalCheck($orgPid: String!, $propertyPids: [String!]!, $siteFields: [SiteChangelogColumn!]!, $buildingDataFields: [BuildingDataChangelogColumn!]!) {
  org(orgPid: $orgPid) {
    pid
    name
  }
  sites(sitePids: $propertyPids) {
    pid
    portfolio {
      name
    }
    changelog(fields: $siteFields) {
      all {
        column
        obj {
          ...Changelog
        }
      }
    }
    buildingData {
      changelog(fields: $buildingDataFields) {
        all {
          column
          obj {
            ...Changelog
          }
        }
      }
    }
    notes {
      pid
      text
      createdAt
      createdBy {
        ...UserRef
      }
      updatedAt
      updatedBy {
        ...UserRef
      }
    }
    currentStatus {
      pid
      status
    }
    slopedRoof
    propertyName
    numberOfBuildings
    sqft
    numStories
    fullAddress
    roofMaxPvKw
    computedRoofMaxPvKw
    explainRoofHeuristic
    explainSlopedHeuristic
    computedCarportMaxPvKw
    explainCarportHeuristic
    explainCarportParkingSpaceHeuristic
    computedGroundMaxPvKw
    explainGroundHeuristic
    impliedCarportLinearFt
    impliedUsableRoofSqft
    impliedUsableRoofSqftExplanation
    roofSqft
    usableRoofPct
    usableRoofSqft
    setbackRoofSqft
    roofKeepoutsSqft
    groundAvailableSqft
    groundMaxPvKw
    usableCarportFt
    numParkingSpaces
    carportMaxPvKw
    existingSolarDetectionScore
    physicalCheck {
      existingSolar
    }
    footprint {
      latitude
      longitude
      roofShape
      roofShapeGeojson
      roofSetbackShape
      roofSetbackShapeGeojson
      keepoutsShape
      keepoutsShapeGeojson
      parcelShape
      parcelShapeGeojson
      carportShape
      carportShapeGeojson
      groundmountShape
      groundmountShapeGeojson
      batteryPadShape
      batteryPadShapeGeojson
      bounds
    }
  }
}
    ${ChangelogFragmentDoc}
${UserRefFragmentDoc}`

export const useBatchPhysicalCheckQuery = <
  TData = Types.BatchPhysicalCheckQuery,
  TError = unknown,
>(
  variables: Types.BatchPhysicalCheckQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BatchPhysicalCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BatchPhysicalCheckQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BatchPhysicalCheckQuery, TError, TData>({
    queryKey: ["BatchPhysicalCheck", variables],
    queryFn: adminFetcher<
      Types.BatchPhysicalCheckQuery,
      Types.BatchPhysicalCheckQueryVariables
    >(BatchPhysicalCheckDocument, variables),
    ...options,
  })
}

useBatchPhysicalCheckQuery.getKey = (
  variables: Types.BatchPhysicalCheckQueryVariables,
) => ["BatchPhysicalCheck", variables]

useBatchPhysicalCheckQuery.rootKey = "BatchPhysicalCheck"

export const useSuspenseBatchPhysicalCheckQuery = <
  TData = Types.BatchPhysicalCheckQuery,
  TError = unknown,
>(
  variables: Types.BatchPhysicalCheckQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BatchPhysicalCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BatchPhysicalCheckQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BatchPhysicalCheckQuery, TError, TData>({
    queryKey: ["BatchPhysicalCheckSuspense", variables],
    queryFn: adminFetcher<
      Types.BatchPhysicalCheckQuery,
      Types.BatchPhysicalCheckQueryVariables
    >(BatchPhysicalCheckDocument, variables),
    ...options,
  })
}

useSuspenseBatchPhysicalCheckQuery.getKey = (
  variables: Types.BatchPhysicalCheckQueryVariables,
) => ["BatchPhysicalCheckSuspense", variables]

useSuspenseBatchPhysicalCheckQuery.rootKey = "BatchPhysicalCheck"

export const useInfiniteBatchPhysicalCheckQuery = <
  TData = InfiniteData<Types.BatchPhysicalCheckQuery>,
  TError = unknown,
>(
  variables: Types.BatchPhysicalCheckQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BatchPhysicalCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BatchPhysicalCheckQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BatchPhysicalCheckQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BatchPhysicalCheck.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.BatchPhysicalCheckQuery,
            Types.BatchPhysicalCheckQueryVariables
          >(BatchPhysicalCheckDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBatchPhysicalCheckQuery.getKey = (
  variables: Types.BatchPhysicalCheckQueryVariables,
) => ["BatchPhysicalCheck.infinite", variables]

useInfiniteBatchPhysicalCheckQuery.rootKey = "BatchPhysicalCheck.infinite"

export const useSuspenseInfiniteBatchPhysicalCheckQuery = <
  TData = InfiniteData<Types.BatchPhysicalCheckQuery>,
  TError = unknown,
>(
  variables: Types.BatchPhysicalCheckQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BatchPhysicalCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BatchPhysicalCheckQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BatchPhysicalCheckQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BatchPhysicalCheck.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.BatchPhysicalCheckQuery,
            Types.BatchPhysicalCheckQueryVariables
          >(BatchPhysicalCheckDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBatchPhysicalCheckQuery.getKey = (
  variables: Types.BatchPhysicalCheckQueryVariables,
) => ["BatchPhysicalCheck.infiniteSuspense", variables]

useSuspenseInfiniteBatchPhysicalCheckQuery.rootKey = "BatchPhysicalCheck.infinite"

useBatchPhysicalCheckQuery.fetcher = (
  variables: Types.BatchPhysicalCheckQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.BatchPhysicalCheckQuery, Types.BatchPhysicalCheckQueryVariables>(
    BatchPhysicalCheckDocument,
    variables,
    options,
  )

export const ClosestParcelsDocument = `
    query ClosestParcels($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    closestSites {
      pid
      name
      fullAddress
      footprint {
        latitude
        longitude
        parcelShape
      }
    }
  }
}
    `

export const useClosestParcelsQuery = <
  TData = Types.ClosestParcelsQuery,
  TError = unknown,
>(
  variables: Types.ClosestParcelsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ClosestParcelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ClosestParcelsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ClosestParcelsQuery, TError, TData>({
    queryKey: ["ClosestParcels", variables],
    queryFn: adminFetcher<
      Types.ClosestParcelsQuery,
      Types.ClosestParcelsQueryVariables
    >(ClosestParcelsDocument, variables),
    ...options,
  })
}

useClosestParcelsQuery.getKey = (variables: Types.ClosestParcelsQueryVariables) => [
  "ClosestParcels",
  variables,
]

useClosestParcelsQuery.rootKey = "ClosestParcels"

export const useSuspenseClosestParcelsQuery = <
  TData = Types.ClosestParcelsQuery,
  TError = unknown,
>(
  variables: Types.ClosestParcelsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ClosestParcelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ClosestParcelsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ClosestParcelsQuery, TError, TData>({
    queryKey: ["ClosestParcelsSuspense", variables],
    queryFn: adminFetcher<
      Types.ClosestParcelsQuery,
      Types.ClosestParcelsQueryVariables
    >(ClosestParcelsDocument, variables),
    ...options,
  })
}

useSuspenseClosestParcelsQuery.getKey = (
  variables: Types.ClosestParcelsQueryVariables,
) => ["ClosestParcelsSuspense", variables]

useSuspenseClosestParcelsQuery.rootKey = "ClosestParcels"

export const useInfiniteClosestParcelsQuery = <
  TData = InfiniteData<Types.ClosestParcelsQuery>,
  TError = unknown,
>(
  variables: Types.ClosestParcelsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ClosestParcelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ClosestParcelsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ClosestParcelsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ClosestParcels.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.ClosestParcelsQuery, Types.ClosestParcelsQueryVariables>(
            ClosestParcelsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteClosestParcelsQuery.getKey = (
  variables: Types.ClosestParcelsQueryVariables,
) => ["ClosestParcels.infinite", variables]

useInfiniteClosestParcelsQuery.rootKey = "ClosestParcels.infinite"

export const useSuspenseInfiniteClosestParcelsQuery = <
  TData = InfiniteData<Types.ClosestParcelsQuery>,
  TError = unknown,
>(
  variables: Types.ClosestParcelsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ClosestParcelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ClosestParcelsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ClosestParcelsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ClosestParcels.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.ClosestParcelsQuery, Types.ClosestParcelsQueryVariables>(
            ClosestParcelsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteClosestParcelsQuery.getKey = (
  variables: Types.ClosestParcelsQueryVariables,
) => ["ClosestParcels.infiniteSuspense", variables]

useSuspenseInfiniteClosestParcelsQuery.rootKey = "ClosestParcels.infinite"

useClosestParcelsQuery.fetcher = (
  variables: Types.ClosestParcelsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.ClosestParcelsQuery, Types.ClosestParcelsQueryVariables>(
    ClosestParcelsDocument,
    variables,
    options,
  )

export const GeocodedLocationDocument = `
    query GeocodedLocation($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    fullAddress
    footprint {
      geocodedLocation {
        latitude
        longitude
      }
    }
  }
}
    `

export const useGeocodedLocationQuery = <
  TData = Types.GeocodedLocationQuery,
  TError = unknown,
>(
  variables: Types.GeocodedLocationQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GeocodedLocationQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.GeocodedLocationQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GeocodedLocationQuery, TError, TData>({
    queryKey: ["GeocodedLocation", variables],
    queryFn: adminFetcher<
      Types.GeocodedLocationQuery,
      Types.GeocodedLocationQueryVariables
    >(GeocodedLocationDocument, variables),
    ...options,
  })
}

useGeocodedLocationQuery.getKey = (variables: Types.GeocodedLocationQueryVariables) => [
  "GeocodedLocation",
  variables,
]

useGeocodedLocationQuery.rootKey = "GeocodedLocation"

export const useSuspenseGeocodedLocationQuery = <
  TData = Types.GeocodedLocationQuery,
  TError = unknown,
>(
  variables: Types.GeocodedLocationQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GeocodedLocationQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GeocodedLocationQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GeocodedLocationQuery, TError, TData>({
    queryKey: ["GeocodedLocationSuspense", variables],
    queryFn: adminFetcher<
      Types.GeocodedLocationQuery,
      Types.GeocodedLocationQueryVariables
    >(GeocodedLocationDocument, variables),
    ...options,
  })
}

useSuspenseGeocodedLocationQuery.getKey = (
  variables: Types.GeocodedLocationQueryVariables,
) => ["GeocodedLocationSuspense", variables]

useSuspenseGeocodedLocationQuery.rootKey = "GeocodedLocation"

export const useInfiniteGeocodedLocationQuery = <
  TData = InfiniteData<Types.GeocodedLocationQuery>,
  TError = unknown,
>(
  variables: Types.GeocodedLocationQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GeocodedLocationQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GeocodedLocationQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GeocodedLocationQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GeocodedLocation.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.GeocodedLocationQuery,
            Types.GeocodedLocationQueryVariables
          >(GeocodedLocationDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGeocodedLocationQuery.getKey = (
  variables: Types.GeocodedLocationQueryVariables,
) => ["GeocodedLocation.infinite", variables]

useInfiniteGeocodedLocationQuery.rootKey = "GeocodedLocation.infinite"

export const useSuspenseInfiniteGeocodedLocationQuery = <
  TData = InfiniteData<Types.GeocodedLocationQuery>,
  TError = unknown,
>(
  variables: Types.GeocodedLocationQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GeocodedLocationQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GeocodedLocationQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GeocodedLocationQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GeocodedLocation.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.GeocodedLocationQuery,
            Types.GeocodedLocationQueryVariables
          >(GeocodedLocationDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGeocodedLocationQuery.getKey = (
  variables: Types.GeocodedLocationQueryVariables,
) => ["GeocodedLocation.infiniteSuspense", variables]

useSuspenseInfiniteGeocodedLocationQuery.rootKey = "GeocodedLocation.infinite"

useGeocodedLocationQuery.fetcher = (
  variables: Types.GeocodedLocationQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.GeocodedLocationQuery, Types.GeocodedLocationQueryVariables>(
    GeocodedLocationDocument,
    variables,
    options,
  )

export const ActiveResultListDocument = `
    query ActiveResultList($sitePid: String!) {
  site(pid: $sitePid) {
    activeResults {
      id
      pid
      createdAt
      scenario {
        pid
        name
        solarLocation
        loadCategory
        includeStorage
      }
      financing {
        id
        name
        type
        financierName
        financierPid
        financierLogo
        financierLogomark
        isThirdParty
        status
      }
      result {
        __typename
        ... on PhysicalCheck {
          createdAt
          roofIsGood
          carportIsGood
          existingSolar
          badRoof
          groundmountIsGood
        }
        ... on PolicyCheck {
          createdAt
          productType
          isGood
          disqualReason
        }
        ... on RevampFinmodelFailure {
          id
          createdAt
          fmvStr
          details {
            label
            description
          }
        }
        ... on RevampFinmodel {
          ...RevampFinmodel
          moreDetails {
            pvCostPerWatt
            storageCostPerKwh
            totalInstallCost
          }
        }
        ... on RevampCS {
          ...RevampCS
        }
        ... on Viability {
          isAllowed
          viabilityType: type
          reason
          level
        }
      }
      helioscope {
        ...Helioscope
      }
    }
  }
}
    ${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${RevampCsFragmentDoc}
${HelioscopeFragmentDoc}`

export const useActiveResultListQuery = <
  TData = Types.ActiveResultListQuery,
  TError = unknown,
>(
  variables: Types.ActiveResultListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ActiveResultListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ActiveResultListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ActiveResultListQuery, TError, TData>({
    queryKey: ["ActiveResultList", variables],
    queryFn: adminFetcher<
      Types.ActiveResultListQuery,
      Types.ActiveResultListQueryVariables
    >(ActiveResultListDocument, variables),
    ...options,
  })
}

useActiveResultListQuery.getKey = (variables: Types.ActiveResultListQueryVariables) => [
  "ActiveResultList",
  variables,
]

useActiveResultListQuery.rootKey = "ActiveResultList"

export const useSuspenseActiveResultListQuery = <
  TData = Types.ActiveResultListQuery,
  TError = unknown,
>(
  variables: Types.ActiveResultListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ActiveResultListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ActiveResultListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ActiveResultListQuery, TError, TData>({
    queryKey: ["ActiveResultListSuspense", variables],
    queryFn: adminFetcher<
      Types.ActiveResultListQuery,
      Types.ActiveResultListQueryVariables
    >(ActiveResultListDocument, variables),
    ...options,
  })
}

useSuspenseActiveResultListQuery.getKey = (
  variables: Types.ActiveResultListQueryVariables,
) => ["ActiveResultListSuspense", variables]

useSuspenseActiveResultListQuery.rootKey = "ActiveResultList"

export const useInfiniteActiveResultListQuery = <
  TData = InfiniteData<Types.ActiveResultListQuery>,
  TError = unknown,
>(
  variables: Types.ActiveResultListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ActiveResultListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ActiveResultListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ActiveResultListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ActiveResultList.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.ActiveResultListQuery,
            Types.ActiveResultListQueryVariables
          >(ActiveResultListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteActiveResultListQuery.getKey = (
  variables: Types.ActiveResultListQueryVariables,
) => ["ActiveResultList.infinite", variables]

useInfiniteActiveResultListQuery.rootKey = "ActiveResultList.infinite"

export const useSuspenseInfiniteActiveResultListQuery = <
  TData = InfiniteData<Types.ActiveResultListQuery>,
  TError = unknown,
>(
  variables: Types.ActiveResultListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ActiveResultListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ActiveResultListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ActiveResultListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ActiveResultList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.ActiveResultListQuery,
            Types.ActiveResultListQueryVariables
          >(ActiveResultListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteActiveResultListQuery.getKey = (
  variables: Types.ActiveResultListQueryVariables,
) => ["ActiveResultList.infiniteSuspense", variables]

useSuspenseInfiniteActiveResultListQuery.rootKey = "ActiveResultList.infinite"

useActiveResultListQuery.fetcher = (
  variables: Types.ActiveResultListQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.ActiveResultListQuery, Types.ActiveResultListQueryVariables>(
    ActiveResultListDocument,
    variables,
    options,
  )

export const ResultDetailDocument = `
    query ResultDetail($sitePid: String!, $id: Int!) {
  site(pid: $sitePid) {
    result(id: $id) {
      ...Result
      ...ResultWithEnergyData
    }
  }
}
    ${ResultFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${RevampCsFragmentDoc}
${HelioscopeFragmentDoc}
${ResultWithEnergyDataFragmentDoc}`

export const useResultDetailQuery = <TData = Types.ResultDetailQuery, TError = unknown>(
  variables: Types.ResultDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ResultDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ResultDetailQuery, TError, TData>({
    queryKey: ["ResultDetail", variables],
    queryFn: adminFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
      ResultDetailDocument,
      variables,
    ),
    ...options,
  })
}

useResultDetailQuery.getKey = (variables: Types.ResultDetailQueryVariables) => [
  "ResultDetail",
  variables,
]

useResultDetailQuery.rootKey = "ResultDetail"

export const useSuspenseResultDetailQuery = <
  TData = Types.ResultDetailQuery,
  TError = unknown,
>(
  variables: Types.ResultDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ResultDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ResultDetailQuery, TError, TData>({
    queryKey: ["ResultDetailSuspense", variables],
    queryFn: adminFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
      ResultDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseResultDetailQuery.getKey = (variables: Types.ResultDetailQueryVariables) => [
  "ResultDetailSuspense",
  variables,
]

useSuspenseResultDetailQuery.rootKey = "ResultDetail"

export const useInfiniteResultDetailQuery = <
  TData = InfiniteData<Types.ResultDetailQuery>,
  TError = unknown,
>(
  variables: Types.ResultDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ResultDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ResultDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultDetail.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
            ResultDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteResultDetailQuery.getKey = (variables: Types.ResultDetailQueryVariables) => [
  "ResultDetail.infinite",
  variables,
]

useInfiniteResultDetailQuery.rootKey = "ResultDetail.infinite"

export const useSuspenseInfiniteResultDetailQuery = <
  TData = InfiniteData<Types.ResultDetailQuery>,
  TError = unknown,
>(
  variables: Types.ResultDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ResultDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ResultDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
            ResultDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteResultDetailQuery.getKey = (
  variables: Types.ResultDetailQueryVariables,
) => ["ResultDetail.infiniteSuspense", variables]

useSuspenseInfiniteResultDetailQuery.rootKey = "ResultDetail.infinite"

useResultDetailQuery.fetcher = (
  variables: Types.ResultDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
    ResultDetailDocument,
    variables,
    options,
  )

export const ResultTableDocument = `
    query ResultTable($slug: String!, $filterBy: ResultTableFilter, $sortBy: [ResultTableSort!]) {
  orgBySlug(slug: $slug) {
    resultTable {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
      }
    }
  }
}
    `

export const useResultTableQuery = <TData = Types.ResultTableQuery, TError = unknown>(
  variables: Types.ResultTableQueryVariables,
  options?: Omit<UseQueryOptions<Types.ResultTableQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.ResultTableQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ResultTableQuery, TError, TData>({
    queryKey: ["ResultTable", variables],
    queryFn: adminFetcher<Types.ResultTableQuery, Types.ResultTableQueryVariables>(
      ResultTableDocument,
      variables,
    ),
    ...options,
  })
}

useResultTableQuery.getKey = (variables: Types.ResultTableQueryVariables) => [
  "ResultTable",
  variables,
]

useResultTableQuery.rootKey = "ResultTable"

export const useSuspenseResultTableQuery = <
  TData = Types.ResultTableQuery,
  TError = unknown,
>(
  variables: Types.ResultTableQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ResultTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ResultTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ResultTableQuery, TError, TData>({
    queryKey: ["ResultTableSuspense", variables],
    queryFn: adminFetcher<Types.ResultTableQuery, Types.ResultTableQueryVariables>(
      ResultTableDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseResultTableQuery.getKey = (variables: Types.ResultTableQueryVariables) => [
  "ResultTableSuspense",
  variables,
]

useSuspenseResultTableQuery.rootKey = "ResultTable"

export const useInfiniteResultTableQuery = <
  TData = InfiniteData<Types.ResultTableQuery>,
  TError = unknown,
>(
  variables: Types.ResultTableQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ResultTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ResultTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ResultTableQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultTable.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.ResultTableQuery, Types.ResultTableQueryVariables>(
            ResultTableDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteResultTableQuery.getKey = (variables: Types.ResultTableQueryVariables) => [
  "ResultTable.infinite",
  variables,
]

useInfiniteResultTableQuery.rootKey = "ResultTable.infinite"

export const useSuspenseInfiniteResultTableQuery = <
  TData = InfiniteData<Types.ResultTableQuery>,
  TError = unknown,
>(
  variables: Types.ResultTableQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ResultTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ResultTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ResultTableQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultTable.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.ResultTableQuery, Types.ResultTableQueryVariables>(
            ResultTableDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteResultTableQuery.getKey = (
  variables: Types.ResultTableQueryVariables,
) => ["ResultTable.infiniteSuspense", variables]

useSuspenseInfiniteResultTableQuery.rootKey = "ResultTable.infinite"

useResultTableQuery.fetcher = (
  variables: Types.ResultTableQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.ResultTableQuery, Types.ResultTableQueryVariables>(
    ResultTableDocument,
    variables,
    options,
  )

export const ResultTableRowsDocument = `
    query ResultTableRows($slug: String!, $ids: [Int!]!) {
  orgBySlug(slug: $slug) {
    resultTable {
      rows(keys: $ids) {
        id
        createdAt
        sitePid
        siteName
        siteStreetAddress
        siteCity
        siteState
        siteStatus
        siteHiddenFromCustomers
        scenarioPid
        scenarioName
        scenarioSolarLocation
        scenarioIncludeStorage
        finproductType
        financierName
        financierPid
        finmodelId
        finmodelFmv
        finmodelNoi
        failureId
        failureFmv
        failureName
        failureMessage
        policyCheckIsGood
        physicalCheckRoofIsGood
        physicalCheckCarportIsGood
        physicalCheckGroundmountIsGood
      }
    }
  }
}
    `

export const useResultTableRowsQuery = <
  TData = Types.ResultTableRowsQuery,
  TError = unknown,
>(
  variables: Types.ResultTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ResultTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ResultTableRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ResultTableRowsQuery, TError, TData>({
    queryKey: ["ResultTableRows", variables],
    queryFn: adminFetcher<
      Types.ResultTableRowsQuery,
      Types.ResultTableRowsQueryVariables
    >(ResultTableRowsDocument, variables),
    ...options,
  })
}

useResultTableRowsQuery.getKey = (variables: Types.ResultTableRowsQueryVariables) => [
  "ResultTableRows",
  variables,
]

useResultTableRowsQuery.rootKey = "ResultTableRows"

export const useSuspenseResultTableRowsQuery = <
  TData = Types.ResultTableRowsQuery,
  TError = unknown,
>(
  variables: Types.ResultTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ResultTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ResultTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ResultTableRowsQuery, TError, TData>({
    queryKey: ["ResultTableRowsSuspense", variables],
    queryFn: adminFetcher<
      Types.ResultTableRowsQuery,
      Types.ResultTableRowsQueryVariables
    >(ResultTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseResultTableRowsQuery.getKey = (
  variables: Types.ResultTableRowsQueryVariables,
) => ["ResultTableRowsSuspense", variables]

useSuspenseResultTableRowsQuery.rootKey = "ResultTableRows"

export const useInfiniteResultTableRowsQuery = <
  TData = InfiniteData<Types.ResultTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.ResultTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ResultTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ResultTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ResultTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultTableRows.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.ResultTableRowsQuery, Types.ResultTableRowsQueryVariables>(
            ResultTableRowsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteResultTableRowsQuery.getKey = (
  variables: Types.ResultTableRowsQueryVariables,
) => ["ResultTableRows.infinite", variables]

useInfiniteResultTableRowsQuery.rootKey = "ResultTableRows.infinite"

export const useSuspenseInfiniteResultTableRowsQuery = <
  TData = InfiniteData<Types.ResultTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.ResultTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ResultTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ResultTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ResultTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultTableRows.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.ResultTableRowsQuery, Types.ResultTableRowsQueryVariables>(
            ResultTableRowsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteResultTableRowsQuery.getKey = (
  variables: Types.ResultTableRowsQueryVariables,
) => ["ResultTableRows.infiniteSuspense", variables]

useSuspenseInfiniteResultTableRowsQuery.rootKey = "ResultTableRows.infinite"

useResultTableRowsQuery.fetcher = (
  variables: Types.ResultTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.ResultTableRowsQuery, Types.ResultTableRowsQueryVariables>(
    ResultTableRowsDocument,
    variables,
    options,
  )

export const FinmodelTableDocument = `
    query FinmodelTable($sitePid: String!, $filterBy: FinmodelTableFilter, $sortBy: [FinmodelTableSort!]) {
  site(pid: $sitePid) {
    finmodelTable {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
      }
    }
  }
}
    `

export const useFinmodelTableQuery = <
  TData = Types.FinmodelTableQuery,
  TError = unknown,
>(
  variables: Types.FinmodelTableQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.FinmodelTableQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelTableQuery, TError, TData>({
    queryKey: ["FinmodelTable", variables],
    queryFn: adminFetcher<Types.FinmodelTableQuery, Types.FinmodelTableQueryVariables>(
      FinmodelTableDocument,
      variables,
    ),
    ...options,
  })
}

useFinmodelTableQuery.getKey = (variables: Types.FinmodelTableQueryVariables) => [
  "FinmodelTable",
  variables,
]

useFinmodelTableQuery.rootKey = "FinmodelTable"

export const useSuspenseFinmodelTableQuery = <
  TData = Types.FinmodelTableQuery,
  TError = unknown,
>(
  variables: Types.FinmodelTableQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelTableQuery, TError, TData>({
    queryKey: ["FinmodelTableSuspense", variables],
    queryFn: adminFetcher<Types.FinmodelTableQuery, Types.FinmodelTableQueryVariables>(
      FinmodelTableDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseFinmodelTableQuery.getKey = (
  variables: Types.FinmodelTableQueryVariables,
) => ["FinmodelTableSuspense", variables]

useSuspenseFinmodelTableQuery.rootKey = "FinmodelTable"

export const useInfiniteFinmodelTableQuery = <
  TData = InfiniteData<Types.FinmodelTableQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelTableQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelTableQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelTable.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.FinmodelTableQuery, Types.FinmodelTableQueryVariables>(
            FinmodelTableDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelTableQuery.getKey = (
  variables: Types.FinmodelTableQueryVariables,
) => ["FinmodelTable.infinite", variables]

useInfiniteFinmodelTableQuery.rootKey = "FinmodelTable.infinite"

export const useSuspenseInfiniteFinmodelTableQuery = <
  TData = InfiniteData<Types.FinmodelTableQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelTableQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelTableQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelTable.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.FinmodelTableQuery, Types.FinmodelTableQueryVariables>(
            FinmodelTableDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelTableQuery.getKey = (
  variables: Types.FinmodelTableQueryVariables,
) => ["FinmodelTable.infiniteSuspense", variables]

useSuspenseInfiniteFinmodelTableQuery.rootKey = "FinmodelTable.infinite"

useFinmodelTableQuery.fetcher = (
  variables: Types.FinmodelTableQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.FinmodelTableQuery, Types.FinmodelTableQueryVariables>(
    FinmodelTableDocument,
    variables,
    options,
  )

export const FinmodelTableRowsDocument = `
    query FinmodelTableRows($sitePid: String!, $ids: [Int!]!) {
  site(pid: $sitePid) {
    finmodelTable {
      rows(keys: $ids) {
        id
        createdAt
        name
        fmvStr
        type
        buildingOwnerNoiLifetimeUsd
        scenarioPid
        scenarioName
        scenarioSolarLocation
        scenarioIncludeStorage
      }
    }
  }
}
    `

export const useFinmodelTableRowsQuery = <
  TData = Types.FinmodelTableRowsQuery,
  TError = unknown,
>(
  variables: Types.FinmodelTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.FinmodelTableRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelTableRowsQuery, TError, TData>({
    queryKey: ["FinmodelTableRows", variables],
    queryFn: adminFetcher<
      Types.FinmodelTableRowsQuery,
      Types.FinmodelTableRowsQueryVariables
    >(FinmodelTableRowsDocument, variables),
    ...options,
  })
}

useFinmodelTableRowsQuery.getKey = (
  variables: Types.FinmodelTableRowsQueryVariables,
) => ["FinmodelTableRows", variables]

useFinmodelTableRowsQuery.rootKey = "FinmodelTableRows"

export const useSuspenseFinmodelTableRowsQuery = <
  TData = Types.FinmodelTableRowsQuery,
  TError = unknown,
>(
  variables: Types.FinmodelTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelTableRowsQuery, TError, TData>({
    queryKey: ["FinmodelTableRowsSuspense", variables],
    queryFn: adminFetcher<
      Types.FinmodelTableRowsQuery,
      Types.FinmodelTableRowsQueryVariables
    >(FinmodelTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelTableRowsQuery.getKey = (
  variables: Types.FinmodelTableRowsQueryVariables,
) => ["FinmodelTableRowsSuspense", variables]

useSuspenseFinmodelTableRowsQuery.rootKey = "FinmodelTableRows"

export const useInfiniteFinmodelTableRowsQuery = <
  TData = InfiniteData<Types.FinmodelTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelTableRows.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelTableRowsQuery,
            Types.FinmodelTableRowsQueryVariables
          >(FinmodelTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelTableRowsQuery.getKey = (
  variables: Types.FinmodelTableRowsQueryVariables,
) => ["FinmodelTableRows.infinite", variables]

useInfiniteFinmodelTableRowsQuery.rootKey = "FinmodelTableRows.infinite"

export const useSuspenseInfiniteFinmodelTableRowsQuery = <
  TData = InfiniteData<Types.FinmodelTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelTableRows.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelTableRowsQuery,
            Types.FinmodelTableRowsQueryVariables
          >(FinmodelTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelTableRowsQuery.getKey = (
  variables: Types.FinmodelTableRowsQueryVariables,
) => ["FinmodelTableRows.infiniteSuspense", variables]

useSuspenseInfiniteFinmodelTableRowsQuery.rootKey = "FinmodelTableRows.infinite"

useFinmodelTableRowsQuery.fetcher = (
  variables: Types.FinmodelTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.FinmodelTableRowsQuery, Types.FinmodelTableRowsQueryVariables>(
    FinmodelTableRowsDocument,
    variables,
    options,
  )

export const FinmodelDetail2Document = `
    query FinmodelDetail2($sitePid: String!, $id: Int!) {
  site(pid: $sitePid) {
    finmodel(id: $id) {
      ...AdminFinmodelDetail
    }
  }
}
    ${AdminFinmodelDetailFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}`

export const useFinmodelDetail2Query = <
  TData = Types.FinmodelDetail2Query,
  TError = unknown,
>(
  variables: Types.FinmodelDetail2QueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelDetail2Query, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.FinmodelDetail2Query, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelDetail2Query, TError, TData>({
    queryKey: ["FinmodelDetail2", variables],
    queryFn: adminFetcher<
      Types.FinmodelDetail2Query,
      Types.FinmodelDetail2QueryVariables
    >(FinmodelDetail2Document, variables),
    ...options,
  })
}

useFinmodelDetail2Query.getKey = (variables: Types.FinmodelDetail2QueryVariables) => [
  "FinmodelDetail2",
  variables,
]

useFinmodelDetail2Query.rootKey = "FinmodelDetail2"

export const useSuspenseFinmodelDetail2Query = <
  TData = Types.FinmodelDetail2Query,
  TError = unknown,
>(
  variables: Types.FinmodelDetail2QueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelDetail2Query, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelDetail2Query,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelDetail2Query, TError, TData>({
    queryKey: ["FinmodelDetail2Suspense", variables],
    queryFn: adminFetcher<
      Types.FinmodelDetail2Query,
      Types.FinmodelDetail2QueryVariables
    >(FinmodelDetail2Document, variables),
    ...options,
  })
}

useSuspenseFinmodelDetail2Query.getKey = (
  variables: Types.FinmodelDetail2QueryVariables,
) => ["FinmodelDetail2Suspense", variables]

useSuspenseFinmodelDetail2Query.rootKey = "FinmodelDetail2"

export const useInfiniteFinmodelDetail2Query = <
  TData = InfiniteData<Types.FinmodelDetail2Query>,
  TError = unknown,
>(
  variables: Types.FinmodelDetail2QueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelDetail2Query, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelDetail2Query,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelDetail2Query, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelDetail2.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.FinmodelDetail2Query, Types.FinmodelDetail2QueryVariables>(
            FinmodelDetail2Document,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelDetail2Query.getKey = (
  variables: Types.FinmodelDetail2QueryVariables,
) => ["FinmodelDetail2.infinite", variables]

useInfiniteFinmodelDetail2Query.rootKey = "FinmodelDetail2.infinite"

export const useSuspenseInfiniteFinmodelDetail2Query = <
  TData = InfiniteData<Types.FinmodelDetail2Query>,
  TError = unknown,
>(
  variables: Types.FinmodelDetail2QueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelDetail2Query, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelDetail2Query,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelDetail2Query, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelDetail2.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.FinmodelDetail2Query, Types.FinmodelDetail2QueryVariables>(
            FinmodelDetail2Document,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelDetail2Query.getKey = (
  variables: Types.FinmodelDetail2QueryVariables,
) => ["FinmodelDetail2.infiniteSuspense", variables]

useSuspenseInfiniteFinmodelDetail2Query.rootKey = "FinmodelDetail2.infinite"

useFinmodelDetail2Query.fetcher = (
  variables: Types.FinmodelDetail2QueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.FinmodelDetail2Query, Types.FinmodelDetail2QueryVariables>(
    FinmodelDetail2Document,
    variables,
    options,
  )

export const FinmodelLineageDocument = `
    query FinmodelLineage($sitePid: String!, $id: Int!) {
  site(pid: $sitePid) {
    finmodel(id: $id) {
      id
      moreDetails {
        lineage {
          ...AdminFinmodelDetail
        }
      }
    }
  }
}
    ${AdminFinmodelDetailFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}`

export const useFinmodelLineageQuery = <
  TData = Types.FinmodelLineageQuery,
  TError = unknown,
>(
  variables: Types.FinmodelLineageQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelLineageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.FinmodelLineageQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelLineageQuery, TError, TData>({
    queryKey: ["FinmodelLineage", variables],
    queryFn: adminFetcher<
      Types.FinmodelLineageQuery,
      Types.FinmodelLineageQueryVariables
    >(FinmodelLineageDocument, variables),
    ...options,
  })
}

useFinmodelLineageQuery.getKey = (variables: Types.FinmodelLineageQueryVariables) => [
  "FinmodelLineage",
  variables,
]

useFinmodelLineageQuery.rootKey = "FinmodelLineage"

export const useSuspenseFinmodelLineageQuery = <
  TData = Types.FinmodelLineageQuery,
  TError = unknown,
>(
  variables: Types.FinmodelLineageQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelLineageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelLineageQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelLineageQuery, TError, TData>({
    queryKey: ["FinmodelLineageSuspense", variables],
    queryFn: adminFetcher<
      Types.FinmodelLineageQuery,
      Types.FinmodelLineageQueryVariables
    >(FinmodelLineageDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelLineageQuery.getKey = (
  variables: Types.FinmodelLineageQueryVariables,
) => ["FinmodelLineageSuspense", variables]

useSuspenseFinmodelLineageQuery.rootKey = "FinmodelLineage"

export const useInfiniteFinmodelLineageQuery = <
  TData = InfiniteData<Types.FinmodelLineageQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelLineageQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelLineageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelLineageQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelLineageQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelLineage.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.FinmodelLineageQuery, Types.FinmodelLineageQueryVariables>(
            FinmodelLineageDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelLineageQuery.getKey = (
  variables: Types.FinmodelLineageQueryVariables,
) => ["FinmodelLineage.infinite", variables]

useInfiniteFinmodelLineageQuery.rootKey = "FinmodelLineage.infinite"

export const useSuspenseInfiniteFinmodelLineageQuery = <
  TData = InfiniteData<Types.FinmodelLineageQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelLineageQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelLineageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelLineageQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelLineageQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelLineage.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.FinmodelLineageQuery, Types.FinmodelLineageQueryVariables>(
            FinmodelLineageDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelLineageQuery.getKey = (
  variables: Types.FinmodelLineageQueryVariables,
) => ["FinmodelLineage.infiniteSuspense", variables]

useSuspenseInfiniteFinmodelLineageQuery.rootKey = "FinmodelLineage.infinite"

useFinmodelLineageQuery.fetcher = (
  variables: Types.FinmodelLineageQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.FinmodelLineageQuery, Types.FinmodelLineageQueryVariables>(
    FinmodelLineageDocument,
    variables,
    options,
  )

export const SiteLabelsDocument = `
    query SiteLabels($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    labels {
      pid
      name
      themeColor
    }
    portfolio {
      pid
      org {
        labels {
          pid
          name
          themeColor
        }
      }
    }
  }
}
    `

export const useSiteLabelsQuery = <TData = Types.SiteLabelsQuery, TError = unknown>(
  variables: Types.SiteLabelsQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteLabelsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteLabelsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteLabelsQuery, TError, TData>({
    queryKey: ["SiteLabels", variables],
    queryFn: adminFetcher<Types.SiteLabelsQuery, Types.SiteLabelsQueryVariables>(
      SiteLabelsDocument,
      variables,
    ),
    ...options,
  })
}

useSiteLabelsQuery.getKey = (variables: Types.SiteLabelsQueryVariables) => [
  "SiteLabels",
  variables,
]

useSiteLabelsQuery.rootKey = "SiteLabels"

export const useSuspenseSiteLabelsQuery = <
  TData = Types.SiteLabelsQuery,
  TError = unknown,
>(
  variables: Types.SiteLabelsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteLabelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteLabelsQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteLabelsQuery, TError, TData>({
    queryKey: ["SiteLabelsSuspense", variables],
    queryFn: adminFetcher<Types.SiteLabelsQuery, Types.SiteLabelsQueryVariables>(
      SiteLabelsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteLabelsQuery.getKey = (variables: Types.SiteLabelsQueryVariables) => [
  "SiteLabelsSuspense",
  variables,
]

useSuspenseSiteLabelsQuery.rootKey = "SiteLabels"

export const useInfiniteSiteLabelsQuery = <
  TData = InfiniteData<Types.SiteLabelsQuery>,
  TError = unknown,
>(
  variables: Types.SiteLabelsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteLabelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteLabelsQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteLabelsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteLabels.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SiteLabelsQuery, Types.SiteLabelsQueryVariables>(
            SiteLabelsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteLabelsQuery.getKey = (variables: Types.SiteLabelsQueryVariables) => [
  "SiteLabels.infinite",
  variables,
]

useInfiniteSiteLabelsQuery.rootKey = "SiteLabels.infinite"

export const useSuspenseInfiniteSiteLabelsQuery = <
  TData = InfiniteData<Types.SiteLabelsQuery>,
  TError = unknown,
>(
  variables: Types.SiteLabelsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteLabelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteLabelsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteLabelsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteLabels.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SiteLabelsQuery, Types.SiteLabelsQueryVariables>(
            SiteLabelsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteLabelsQuery.getKey = (
  variables: Types.SiteLabelsQueryVariables,
) => ["SiteLabels.infiniteSuspense", variables]

useSuspenseInfiniteSiteLabelsQuery.rootKey = "SiteLabels.infinite"

useSiteLabelsQuery.fetcher = (
  variables: Types.SiteLabelsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SiteLabelsQuery, Types.SiteLabelsQueryVariables>(
    SiteLabelsDocument,
    variables,
    options,
  )

export const Xl2RenderDocument = `
    query Xl2Render {
  xl2
}
    `

export const useXl2RenderQuery = <TData = Types.Xl2RenderQuery, TError = unknown>(
  variables?: Types.Xl2RenderQueryVariables,
  options?: Omit<UseQueryOptions<Types.Xl2RenderQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.Xl2RenderQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.Xl2RenderQuery, TError, TData>({
    queryKey: variables === undefined ? ["Xl2Render"] : ["Xl2Render", variables],
    queryFn: adminFetcher<Types.Xl2RenderQuery, Types.Xl2RenderQueryVariables>(
      Xl2RenderDocument,
      variables,
    ),
    ...options,
  })
}

useXl2RenderQuery.getKey = (variables?: Types.Xl2RenderQueryVariables) =>
  variables === undefined ? ["Xl2Render"] : ["Xl2Render", variables]

useXl2RenderQuery.rootKey = "Xl2Render"

export const useSuspenseXl2RenderQuery = <
  TData = Types.Xl2RenderQuery,
  TError = unknown,
>(
  variables?: Types.Xl2RenderQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.Xl2RenderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.Xl2RenderQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.Xl2RenderQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["Xl2RenderSuspense"]
        : ["Xl2RenderSuspense", variables],
    queryFn: adminFetcher<Types.Xl2RenderQuery, Types.Xl2RenderQueryVariables>(
      Xl2RenderDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseXl2RenderQuery.getKey = (variables?: Types.Xl2RenderQueryVariables) =>
  variables === undefined ? ["Xl2RenderSuspense"] : ["Xl2RenderSuspense", variables]

useSuspenseXl2RenderQuery.rootKey = "Xl2Render"

export const useInfiniteXl2RenderQuery = <
  TData = InfiniteData<Types.Xl2RenderQuery>,
  TError = unknown,
>(
  variables: Types.Xl2RenderQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.Xl2RenderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.Xl2RenderQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.Xl2RenderQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["Xl2Render.infinite"]
            : ["Xl2Render.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.Xl2RenderQuery, Types.Xl2RenderQueryVariables>(
            Xl2RenderDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteXl2RenderQuery.getKey = (variables?: Types.Xl2RenderQueryVariables) =>
  variables === undefined ? ["Xl2Render.infinite"] : ["Xl2Render.infinite", variables]

useInfiniteXl2RenderQuery.rootKey = "Xl2Render.infinite"

export const useSuspenseInfiniteXl2RenderQuery = <
  TData = InfiniteData<Types.Xl2RenderQuery>,
  TError = unknown,
>(
  variables: Types.Xl2RenderQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.Xl2RenderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.Xl2RenderQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.Xl2RenderQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["Xl2Render.infiniteSuspense"]
            : ["Xl2Render.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.Xl2RenderQuery, Types.Xl2RenderQueryVariables>(
            Xl2RenderDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteXl2RenderQuery.getKey = (
  variables?: Types.Xl2RenderQueryVariables,
) =>
  variables === undefined
    ? ["Xl2Render.infiniteSuspense"]
    : ["Xl2Render.infiniteSuspense", variables]

useSuspenseInfiniteXl2RenderQuery.rootKey = "Xl2Render.infinite"

useXl2RenderQuery.fetcher = (
  variables?: Types.Xl2RenderQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.Xl2RenderQuery, Types.Xl2RenderQueryVariables>(
    Xl2RenderDocument,
    variables,
    options,
  )

export const TaskCountsDocument = `
    query TaskCounts($orgPid: String, $sitePid: String) {
  taskCounts(orgPid: $orgPid, sitePid: $sitePid) {
    name
    runningCount
    pendingCount
    successCount
    failureCount
  }
}
    `

export const useTaskCountsQuery = <TData = Types.TaskCountsQuery, TError = unknown>(
  variables?: Types.TaskCountsQueryVariables,
  options?: Omit<UseQueryOptions<Types.TaskCountsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.TaskCountsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.TaskCountsQuery, TError, TData>({
    queryKey: variables === undefined ? ["TaskCounts"] : ["TaskCounts", variables],
    queryFn: adminFetcher<Types.TaskCountsQuery, Types.TaskCountsQueryVariables>(
      TaskCountsDocument,
      variables,
    ),
    ...options,
  })
}

useTaskCountsQuery.getKey = (variables?: Types.TaskCountsQueryVariables) =>
  variables === undefined ? ["TaskCounts"] : ["TaskCounts", variables]

useTaskCountsQuery.rootKey = "TaskCounts"

export const useSuspenseTaskCountsQuery = <
  TData = Types.TaskCountsQuery,
  TError = unknown,
>(
  variables?: Types.TaskCountsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.TaskCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.TaskCountsQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.TaskCountsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["TaskCountsSuspense"]
        : ["TaskCountsSuspense", variables],
    queryFn: adminFetcher<Types.TaskCountsQuery, Types.TaskCountsQueryVariables>(
      TaskCountsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseTaskCountsQuery.getKey = (variables?: Types.TaskCountsQueryVariables) =>
  variables === undefined ? ["TaskCountsSuspense"] : ["TaskCountsSuspense", variables]

useSuspenseTaskCountsQuery.rootKey = "TaskCounts"

export const useInfiniteTaskCountsQuery = <
  TData = InfiniteData<Types.TaskCountsQuery>,
  TError = unknown,
>(
  variables: Types.TaskCountsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.TaskCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.TaskCountsQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.TaskCountsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["TaskCounts.infinite"]
            : ["TaskCounts.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.TaskCountsQuery, Types.TaskCountsQueryVariables>(
            TaskCountsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteTaskCountsQuery.getKey = (variables?: Types.TaskCountsQueryVariables) =>
  variables === undefined ? ["TaskCounts.infinite"] : ["TaskCounts.infinite", variables]

useInfiniteTaskCountsQuery.rootKey = "TaskCounts.infinite"

export const useSuspenseInfiniteTaskCountsQuery = <
  TData = InfiniteData<Types.TaskCountsQuery>,
  TError = unknown,
>(
  variables: Types.TaskCountsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.TaskCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.TaskCountsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.TaskCountsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["TaskCounts.infiniteSuspense"]
            : ["TaskCounts.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.TaskCountsQuery, Types.TaskCountsQueryVariables>(
            TaskCountsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteTaskCountsQuery.getKey = (
  variables?: Types.TaskCountsQueryVariables,
) =>
  variables === undefined
    ? ["TaskCounts.infiniteSuspense"]
    : ["TaskCounts.infiniteSuspense", variables]

useSuspenseInfiniteTaskCountsQuery.rootKey = "TaskCounts.infinite"

useTaskCountsQuery.fetcher = (
  variables?: Types.TaskCountsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.TaskCountsQuery, Types.TaskCountsQueryVariables>(
    TaskCountsDocument,
    variables,
    options,
  )

export const OplogCountsDocument = `
    query OplogCounts {
  oplogCounts {
    name
    runningCount
    pendingCount
    successCount
    failureCount
  }
}
    `

export const useOplogCountsQuery = <TData = Types.OplogCountsQuery, TError = unknown>(
  variables?: Types.OplogCountsQueryVariables,
  options?: Omit<UseQueryOptions<Types.OplogCountsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OplogCountsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OplogCountsQuery, TError, TData>({
    queryKey: variables === undefined ? ["OplogCounts"] : ["OplogCounts", variables],
    queryFn: adminFetcher<Types.OplogCountsQuery, Types.OplogCountsQueryVariables>(
      OplogCountsDocument,
      variables,
    ),
    ...options,
  })
}

useOplogCountsQuery.getKey = (variables?: Types.OplogCountsQueryVariables) =>
  variables === undefined ? ["OplogCounts"] : ["OplogCounts", variables]

useOplogCountsQuery.rootKey = "OplogCounts"

export const useSuspenseOplogCountsQuery = <
  TData = Types.OplogCountsQuery,
  TError = unknown,
>(
  variables?: Types.OplogCountsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OplogCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OplogCountsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OplogCountsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OplogCountsSuspense"]
        : ["OplogCountsSuspense", variables],
    queryFn: adminFetcher<Types.OplogCountsQuery, Types.OplogCountsQueryVariables>(
      OplogCountsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOplogCountsQuery.getKey = (variables?: Types.OplogCountsQueryVariables) =>
  variables === undefined ? ["OplogCountsSuspense"] : ["OplogCountsSuspense", variables]

useSuspenseOplogCountsQuery.rootKey = "OplogCounts"

export const useInfiniteOplogCountsQuery = <
  TData = InfiniteData<Types.OplogCountsQuery>,
  TError = unknown,
>(
  variables: Types.OplogCountsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OplogCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OplogCountsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OplogCountsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OplogCounts.infinite"]
            : ["OplogCounts.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.OplogCountsQuery, Types.OplogCountsQueryVariables>(
            OplogCountsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOplogCountsQuery.getKey = (variables?: Types.OplogCountsQueryVariables) =>
  variables === undefined
    ? ["OplogCounts.infinite"]
    : ["OplogCounts.infinite", variables]

useInfiniteOplogCountsQuery.rootKey = "OplogCounts.infinite"

export const useSuspenseInfiniteOplogCountsQuery = <
  TData = InfiniteData<Types.OplogCountsQuery>,
  TError = unknown,
>(
  variables: Types.OplogCountsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OplogCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OplogCountsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OplogCountsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OplogCounts.infiniteSuspense"]
            : ["OplogCounts.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.OplogCountsQuery, Types.OplogCountsQueryVariables>(
            OplogCountsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOplogCountsQuery.getKey = (
  variables?: Types.OplogCountsQueryVariables,
) =>
  variables === undefined
    ? ["OplogCounts.infiniteSuspense"]
    : ["OplogCounts.infiniteSuspense", variables]

useSuspenseInfiniteOplogCountsQuery.rootKey = "OplogCounts.infinite"

useOplogCountsQuery.fetcher = (
  variables?: Types.OplogCountsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.OplogCountsQuery, Types.OplogCountsQueryVariables>(
    OplogCountsDocument,
    variables,
    options,
  )

export const ActiveBoostsDocument = `
    query ActiveBoosts($orgPid: String!) {
  activeBoosts(orgPid: $orgPid)
}
    `

export const useActiveBoostsQuery = <TData = Types.ActiveBoostsQuery, TError = unknown>(
  variables: Types.ActiveBoostsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ActiveBoostsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ActiveBoostsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ActiveBoostsQuery, TError, TData>({
    queryKey: ["ActiveBoosts", variables],
    queryFn: adminFetcher<Types.ActiveBoostsQuery, Types.ActiveBoostsQueryVariables>(
      ActiveBoostsDocument,
      variables,
    ),
    ...options,
  })
}

useActiveBoostsQuery.getKey = (variables: Types.ActiveBoostsQueryVariables) => [
  "ActiveBoosts",
  variables,
]

useActiveBoostsQuery.rootKey = "ActiveBoosts"

export const useSuspenseActiveBoostsQuery = <
  TData = Types.ActiveBoostsQuery,
  TError = unknown,
>(
  variables: Types.ActiveBoostsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ActiveBoostsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ActiveBoostsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ActiveBoostsQuery, TError, TData>({
    queryKey: ["ActiveBoostsSuspense", variables],
    queryFn: adminFetcher<Types.ActiveBoostsQuery, Types.ActiveBoostsQueryVariables>(
      ActiveBoostsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseActiveBoostsQuery.getKey = (variables: Types.ActiveBoostsQueryVariables) => [
  "ActiveBoostsSuspense",
  variables,
]

useSuspenseActiveBoostsQuery.rootKey = "ActiveBoosts"

export const useInfiniteActiveBoostsQuery = <
  TData = InfiniteData<Types.ActiveBoostsQuery>,
  TError = unknown,
>(
  variables: Types.ActiveBoostsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ActiveBoostsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ActiveBoostsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ActiveBoostsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ActiveBoosts.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.ActiveBoostsQuery, Types.ActiveBoostsQueryVariables>(
            ActiveBoostsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteActiveBoostsQuery.getKey = (variables: Types.ActiveBoostsQueryVariables) => [
  "ActiveBoosts.infinite",
  variables,
]

useInfiniteActiveBoostsQuery.rootKey = "ActiveBoosts.infinite"

export const useSuspenseInfiniteActiveBoostsQuery = <
  TData = InfiniteData<Types.ActiveBoostsQuery>,
  TError = unknown,
>(
  variables: Types.ActiveBoostsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ActiveBoostsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ActiveBoostsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ActiveBoostsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ActiveBoosts.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.ActiveBoostsQuery, Types.ActiveBoostsQueryVariables>(
            ActiveBoostsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteActiveBoostsQuery.getKey = (
  variables: Types.ActiveBoostsQueryVariables,
) => ["ActiveBoosts.infiniteSuspense", variables]

useSuspenseInfiniteActiveBoostsQuery.rootKey = "ActiveBoosts.infinite"

useActiveBoostsQuery.fetcher = (
  variables: Types.ActiveBoostsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.ActiveBoostsQuery, Types.ActiveBoostsQueryVariables>(
    ActiveBoostsDocument,
    variables,
    options,
  )

export const OrgPriorityDocument = `
    query OrgPriority($orgPid: String!) {
  orgPriority(orgPid: $orgPid)
}
    `

export const useOrgPriorityQuery = <TData = Types.OrgPriorityQuery, TError = unknown>(
  variables: Types.OrgPriorityQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgPriorityQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgPriorityQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgPriorityQuery, TError, TData>({
    queryKey: ["OrgPriority", variables],
    queryFn: adminFetcher<Types.OrgPriorityQuery, Types.OrgPriorityQueryVariables>(
      OrgPriorityDocument,
      variables,
    ),
    ...options,
  })
}

useOrgPriorityQuery.getKey = (variables: Types.OrgPriorityQueryVariables) => [
  "OrgPriority",
  variables,
]

useOrgPriorityQuery.rootKey = "OrgPriority"

export const useSuspenseOrgPriorityQuery = <
  TData = Types.OrgPriorityQuery,
  TError = unknown,
>(
  variables: Types.OrgPriorityQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgPriorityQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgPriorityQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgPriorityQuery, TError, TData>({
    queryKey: ["OrgPrioritySuspense", variables],
    queryFn: adminFetcher<Types.OrgPriorityQuery, Types.OrgPriorityQueryVariables>(
      OrgPriorityDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgPriorityQuery.getKey = (variables: Types.OrgPriorityQueryVariables) => [
  "OrgPrioritySuspense",
  variables,
]

useSuspenseOrgPriorityQuery.rootKey = "OrgPriority"

export const useInfiniteOrgPriorityQuery = <
  TData = InfiniteData<Types.OrgPriorityQuery>,
  TError = unknown,
>(
  variables: Types.OrgPriorityQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgPriorityQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgPriorityQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgPriorityQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgPriority.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.OrgPriorityQuery, Types.OrgPriorityQueryVariables>(
            OrgPriorityDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgPriorityQuery.getKey = (variables: Types.OrgPriorityQueryVariables) => [
  "OrgPriority.infinite",
  variables,
]

useInfiniteOrgPriorityQuery.rootKey = "OrgPriority.infinite"

export const useSuspenseInfiniteOrgPriorityQuery = <
  TData = InfiniteData<Types.OrgPriorityQuery>,
  TError = unknown,
>(
  variables: Types.OrgPriorityQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgPriorityQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgPriorityQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgPriorityQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgPriority.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.OrgPriorityQuery, Types.OrgPriorityQueryVariables>(
            OrgPriorityDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgPriorityQuery.getKey = (
  variables: Types.OrgPriorityQueryVariables,
) => ["OrgPriority.infiniteSuspense", variables]

useSuspenseInfiniteOrgPriorityQuery.rootKey = "OrgPriority.infinite"

useOrgPriorityQuery.fetcher = (
  variables: Types.OrgPriorityQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.OrgPriorityQuery, Types.OrgPriorityQueryVariables>(
    OrgPriorityDocument,
    variables,
    options,
  )

export const ConserviceSearchDocument = `
    query ConserviceSearch($sitePid: String!, $onlyHouse: Boolean!) {
  site(pid: $sitePid) {
    portfolio {
      pid
      org {
        pid
      }
    }
    conserviceSearch(onlyHouse: $onlyHouse) {
      name
      count
      matched
    }
  }
}
    `

export const useConserviceSearchQuery = <
  TData = Types.ConserviceSearchQuery,
  TError = unknown,
>(
  variables: Types.ConserviceSearchQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ConserviceSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ConserviceSearchQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ConserviceSearchQuery, TError, TData>({
    queryKey: ["ConserviceSearch", variables],
    queryFn: adminFetcher<
      Types.ConserviceSearchQuery,
      Types.ConserviceSearchQueryVariables
    >(ConserviceSearchDocument, variables),
    ...options,
  })
}

useConserviceSearchQuery.getKey = (variables: Types.ConserviceSearchQueryVariables) => [
  "ConserviceSearch",
  variables,
]

useConserviceSearchQuery.rootKey = "ConserviceSearch"

export const useSuspenseConserviceSearchQuery = <
  TData = Types.ConserviceSearchQuery,
  TError = unknown,
>(
  variables: Types.ConserviceSearchQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ConserviceSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ConserviceSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ConserviceSearchQuery, TError, TData>({
    queryKey: ["ConserviceSearchSuspense", variables],
    queryFn: adminFetcher<
      Types.ConserviceSearchQuery,
      Types.ConserviceSearchQueryVariables
    >(ConserviceSearchDocument, variables),
    ...options,
  })
}

useSuspenseConserviceSearchQuery.getKey = (
  variables: Types.ConserviceSearchQueryVariables,
) => ["ConserviceSearchSuspense", variables]

useSuspenseConserviceSearchQuery.rootKey = "ConserviceSearch"

export const useInfiniteConserviceSearchQuery = <
  TData = InfiniteData<Types.ConserviceSearchQuery>,
  TError = unknown,
>(
  variables: Types.ConserviceSearchQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ConserviceSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ConserviceSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ConserviceSearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ConserviceSearch.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.ConserviceSearchQuery,
            Types.ConserviceSearchQueryVariables
          >(ConserviceSearchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteConserviceSearchQuery.getKey = (
  variables: Types.ConserviceSearchQueryVariables,
) => ["ConserviceSearch.infinite", variables]

useInfiniteConserviceSearchQuery.rootKey = "ConserviceSearch.infinite"

export const useSuspenseInfiniteConserviceSearchQuery = <
  TData = InfiniteData<Types.ConserviceSearchQuery>,
  TError = unknown,
>(
  variables: Types.ConserviceSearchQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ConserviceSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ConserviceSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ConserviceSearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ConserviceSearch.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.ConserviceSearchQuery,
            Types.ConserviceSearchQueryVariables
          >(ConserviceSearchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteConserviceSearchQuery.getKey = (
  variables: Types.ConserviceSearchQueryVariables,
) => ["ConserviceSearch.infiniteSuspense", variables]

useSuspenseInfiniteConserviceSearchQuery.rootKey = "ConserviceSearch.infinite"

useConserviceSearchQuery.fetcher = (
  variables: Types.ConserviceSearchQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.ConserviceSearchQuery, Types.ConserviceSearchQueryVariables>(
    ConserviceSearchDocument,
    variables,
    options,
  )

export const SiteResultConfigDocument = `
    query SiteResultConfig($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    resultConfig {
      finproduct {
        id
        type
        name
        financier {
          name
          pid
        }
      }
      disabledAt
      finrelDisabledAt
    }
  }
}
    `

export const useSiteResultConfigQuery = <
  TData = Types.SiteResultConfigQuery,
  TError = unknown,
>(
  variables: Types.SiteResultConfigQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteResultConfigQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteResultConfigQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteResultConfigQuery, TError, TData>({
    queryKey: ["SiteResultConfig", variables],
    queryFn: adminFetcher<
      Types.SiteResultConfigQuery,
      Types.SiteResultConfigQueryVariables
    >(SiteResultConfigDocument, variables),
    ...options,
  })
}

useSiteResultConfigQuery.getKey = (variables: Types.SiteResultConfigQueryVariables) => [
  "SiteResultConfig",
  variables,
]

useSiteResultConfigQuery.rootKey = "SiteResultConfig"

export const useSuspenseSiteResultConfigQuery = <
  TData = Types.SiteResultConfigQuery,
  TError = unknown,
>(
  variables: Types.SiteResultConfigQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteResultConfigQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteResultConfigQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteResultConfigQuery, TError, TData>({
    queryKey: ["SiteResultConfigSuspense", variables],
    queryFn: adminFetcher<
      Types.SiteResultConfigQuery,
      Types.SiteResultConfigQueryVariables
    >(SiteResultConfigDocument, variables),
    ...options,
  })
}

useSuspenseSiteResultConfigQuery.getKey = (
  variables: Types.SiteResultConfigQueryVariables,
) => ["SiteResultConfigSuspense", variables]

useSuspenseSiteResultConfigQuery.rootKey = "SiteResultConfig"

export const useInfiniteSiteResultConfigQuery = <
  TData = InfiniteData<Types.SiteResultConfigQuery>,
  TError = unknown,
>(
  variables: Types.SiteResultConfigQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteResultConfigQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteResultConfigQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteResultConfigQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteResultConfig.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.SiteResultConfigQuery,
            Types.SiteResultConfigQueryVariables
          >(SiteResultConfigDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteResultConfigQuery.getKey = (
  variables: Types.SiteResultConfigQueryVariables,
) => ["SiteResultConfig.infinite", variables]

useInfiniteSiteResultConfigQuery.rootKey = "SiteResultConfig.infinite"

export const useSuspenseInfiniteSiteResultConfigQuery = <
  TData = InfiniteData<Types.SiteResultConfigQuery>,
  TError = unknown,
>(
  variables: Types.SiteResultConfigQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteResultConfigQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteResultConfigQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteResultConfigQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteResultConfig.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.SiteResultConfigQuery,
            Types.SiteResultConfigQueryVariables
          >(SiteResultConfigDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteResultConfigQuery.getKey = (
  variables: Types.SiteResultConfigQueryVariables,
) => ["SiteResultConfig.infiniteSuspense", variables]

useSuspenseInfiniteSiteResultConfigQuery.rootKey = "SiteResultConfig.infinite"

useSiteResultConfigQuery.fetcher = (
  variables: Types.SiteResultConfigQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SiteResultConfigQuery, Types.SiteResultConfigQueryVariables>(
    SiteResultConfigDocument,
    variables,
    options,
  )

export const SiteTodosDocument = `
    query SiteTodos($sitePid: String!) {
  site(pid: $sitePid) {
    id
    pid
    todos {
      id
      content
      index
      createdAt
      createdBy {
        pid
        email
      }
      updatedAt
      updatedBy {
        pid
        email
      }
      completedAt
      completedBy {
        pid
        email
      }
      section {
        id
        name
        index
      }
    }
  }
}
    `

export const useSiteTodosQuery = <TData = Types.SiteTodosQuery, TError = unknown>(
  variables: Types.SiteTodosQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteTodosQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteTodosQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteTodosQuery, TError, TData>({
    queryKey: ["SiteTodos", variables],
    queryFn: adminFetcher<Types.SiteTodosQuery, Types.SiteTodosQueryVariables>(
      SiteTodosDocument,
      variables,
    ),
    ...options,
  })
}

useSiteTodosQuery.getKey = (variables: Types.SiteTodosQueryVariables) => [
  "SiteTodos",
  variables,
]

useSiteTodosQuery.rootKey = "SiteTodos"

export const useSuspenseSiteTodosQuery = <
  TData = Types.SiteTodosQuery,
  TError = unknown,
>(
  variables: Types.SiteTodosQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteTodosQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteTodosQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteTodosQuery, TError, TData>({
    queryKey: ["SiteTodosSuspense", variables],
    queryFn: adminFetcher<Types.SiteTodosQuery, Types.SiteTodosQueryVariables>(
      SiteTodosDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteTodosQuery.getKey = (variables: Types.SiteTodosQueryVariables) => [
  "SiteTodosSuspense",
  variables,
]

useSuspenseSiteTodosQuery.rootKey = "SiteTodos"

export const useInfiniteSiteTodosQuery = <
  TData = InfiniteData<Types.SiteTodosQuery>,
  TError = unknown,
>(
  variables: Types.SiteTodosQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteTodosQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteTodosQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteTodosQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteTodos.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SiteTodosQuery, Types.SiteTodosQueryVariables>(
            SiteTodosDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteTodosQuery.getKey = (variables: Types.SiteTodosQueryVariables) => [
  "SiteTodos.infinite",
  variables,
]

useInfiniteSiteTodosQuery.rootKey = "SiteTodos.infinite"

export const useSuspenseInfiniteSiteTodosQuery = <
  TData = InfiniteData<Types.SiteTodosQuery>,
  TError = unknown,
>(
  variables: Types.SiteTodosQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteTodosQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteTodosQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteTodosQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteTodos.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SiteTodosQuery, Types.SiteTodosQueryVariables>(
            SiteTodosDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteTodosQuery.getKey = (
  variables: Types.SiteTodosQueryVariables,
) => ["SiteTodos.infiniteSuspense", variables]

useSuspenseInfiniteSiteTodosQuery.rootKey = "SiteTodos.infinite"

useSiteTodosQuery.fetcher = (
  variables: Types.SiteTodosQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SiteTodosQuery, Types.SiteTodosQueryVariables>(
    SiteTodosDocument,
    variables,
    options,
  )

export const SiteEspmDocument = `
    query SiteEspm($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    espm {
      id
      createdAt
      lastUpdatedAt
      externalPropertyId
      xml
      accounts {
        id
        createdAt
        lastUpdatedAt
        externalAccountId
        xml
      }
      espmMeters {
        id
        createdAt
        lastUpdatedAt
        externalMeterId
        xml
        unitOfMeasure
        meterType
        espmConsumptions {
          id
          createdAt
          lastUpdatedAt
          externalConsumptionId
          xml
          start
          end
          usageKwh
          costUsd
        }
      }
    }
  }
}
    `

export const useSiteEspmQuery = <TData = Types.SiteEspmQuery, TError = unknown>(
  variables: Types.SiteEspmQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteEspmQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteEspmQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteEspmQuery, TError, TData>({
    queryKey: ["SiteEspm", variables],
    queryFn: adminFetcher<Types.SiteEspmQuery, Types.SiteEspmQueryVariables>(
      SiteEspmDocument,
      variables,
    ),
    ...options,
  })
}

useSiteEspmQuery.getKey = (variables: Types.SiteEspmQueryVariables) => [
  "SiteEspm",
  variables,
]

useSiteEspmQuery.rootKey = "SiteEspm"

export const useSuspenseSiteEspmQuery = <TData = Types.SiteEspmQuery, TError = unknown>(
  variables: Types.SiteEspmQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteEspmQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteEspmQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteEspmQuery, TError, TData>({
    queryKey: ["SiteEspmSuspense", variables],
    queryFn: adminFetcher<Types.SiteEspmQuery, Types.SiteEspmQueryVariables>(
      SiteEspmDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteEspmQuery.getKey = (variables: Types.SiteEspmQueryVariables) => [
  "SiteEspmSuspense",
  variables,
]

useSuspenseSiteEspmQuery.rootKey = "SiteEspm"

export const useInfiniteSiteEspmQuery = <
  TData = InfiniteData<Types.SiteEspmQuery>,
  TError = unknown,
>(
  variables: Types.SiteEspmQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteEspmQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteEspmQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteEspmQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteEspm.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SiteEspmQuery, Types.SiteEspmQueryVariables>(
            SiteEspmDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteEspmQuery.getKey = (variables: Types.SiteEspmQueryVariables) => [
  "SiteEspm.infinite",
  variables,
]

useInfiniteSiteEspmQuery.rootKey = "SiteEspm.infinite"

export const useSuspenseInfiniteSiteEspmQuery = <
  TData = InfiniteData<Types.SiteEspmQuery>,
  TError = unknown,
>(
  variables: Types.SiteEspmQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteEspmQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteEspmQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteEspmQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteEspm.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.SiteEspmQuery, Types.SiteEspmQueryVariables>(
            SiteEspmDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteEspmQuery.getKey = (variables: Types.SiteEspmQueryVariables) => [
  "SiteEspm.infiniteSuspense",
  variables,
]

useSuspenseInfiniteSiteEspmQuery.rootKey = "SiteEspm.infinite"

useSiteEspmQuery.fetcher = (
  variables: Types.SiteEspmQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SiteEspmQuery, Types.SiteEspmQueryVariables>(
    SiteEspmDocument,
    variables,
    options,
  )

export const TaskHistoryDocument = `
    query TaskHistory {
  taskHistory {
    timestamps
    taskNames
    series {
      taskName
      counts
    }
  }
}
    `

export const useTaskHistoryQuery = <TData = Types.TaskHistoryQuery, TError = unknown>(
  variables?: Types.TaskHistoryQueryVariables,
  options?: Omit<UseQueryOptions<Types.TaskHistoryQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.TaskHistoryQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.TaskHistoryQuery, TError, TData>({
    queryKey: variables === undefined ? ["TaskHistory"] : ["TaskHistory", variables],
    queryFn: adminFetcher<Types.TaskHistoryQuery, Types.TaskHistoryQueryVariables>(
      TaskHistoryDocument,
      variables,
    ),
    ...options,
  })
}

useTaskHistoryQuery.getKey = (variables?: Types.TaskHistoryQueryVariables) =>
  variables === undefined ? ["TaskHistory"] : ["TaskHistory", variables]

useTaskHistoryQuery.rootKey = "TaskHistory"

export const useSuspenseTaskHistoryQuery = <
  TData = Types.TaskHistoryQuery,
  TError = unknown,
>(
  variables?: Types.TaskHistoryQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.TaskHistoryQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.TaskHistoryQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.TaskHistoryQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["TaskHistorySuspense"]
        : ["TaskHistorySuspense", variables],
    queryFn: adminFetcher<Types.TaskHistoryQuery, Types.TaskHistoryQueryVariables>(
      TaskHistoryDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseTaskHistoryQuery.getKey = (variables?: Types.TaskHistoryQueryVariables) =>
  variables === undefined ? ["TaskHistorySuspense"] : ["TaskHistorySuspense", variables]

useSuspenseTaskHistoryQuery.rootKey = "TaskHistory"

export const useInfiniteTaskHistoryQuery = <
  TData = InfiniteData<Types.TaskHistoryQuery>,
  TError = unknown,
>(
  variables: Types.TaskHistoryQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.TaskHistoryQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.TaskHistoryQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.TaskHistoryQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["TaskHistory.infinite"]
            : ["TaskHistory.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.TaskHistoryQuery, Types.TaskHistoryQueryVariables>(
            TaskHistoryDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteTaskHistoryQuery.getKey = (variables?: Types.TaskHistoryQueryVariables) =>
  variables === undefined
    ? ["TaskHistory.infinite"]
    : ["TaskHistory.infinite", variables]

useInfiniteTaskHistoryQuery.rootKey = "TaskHistory.infinite"

export const useSuspenseInfiniteTaskHistoryQuery = <
  TData = InfiniteData<Types.TaskHistoryQuery>,
  TError = unknown,
>(
  variables: Types.TaskHistoryQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.TaskHistoryQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.TaskHistoryQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.TaskHistoryQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["TaskHistory.infiniteSuspense"]
            : ["TaskHistory.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.TaskHistoryQuery, Types.TaskHistoryQueryVariables>(
            TaskHistoryDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteTaskHistoryQuery.getKey = (
  variables?: Types.TaskHistoryQueryVariables,
) =>
  variables === undefined
    ? ["TaskHistory.infiniteSuspense"]
    : ["TaskHistory.infiniteSuspense", variables]

useSuspenseInfiniteTaskHistoryQuery.rootKey = "TaskHistory.infinite"

useTaskHistoryQuery.fetcher = (
  variables?: Types.TaskHistoryQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.TaskHistoryQuery, Types.TaskHistoryQueryVariables>(
    TaskHistoryDocument,
    variables,
    options,
  )

export const AdminBundleRowViewDocument = `
    query AdminBundleRowView {
  bundleRows {
    view {
      count
      keys
    }
  }
}
    `

export const useAdminBundleRowViewQuery = <
  TData = Types.AdminBundleRowViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBundleRowViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBundleRowViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminBundleRowViewQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminBundleRowViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBundleRowView"]
        : ["AdminBundleRowView", variables],
    queryFn: adminFetcher<
      Types.AdminBundleRowViewQuery,
      Types.AdminBundleRowViewQueryVariables
    >(AdminBundleRowViewDocument, variables),
    ...options,
  })
}

useAdminBundleRowViewQuery.getKey = (
  variables?: Types.AdminBundleRowViewQueryVariables,
) =>
  variables === undefined ? ["AdminBundleRowView"] : ["AdminBundleRowView", variables]

useAdminBundleRowViewQuery.rootKey = "AdminBundleRowView"

export const useSuspenseAdminBundleRowViewQuery = <
  TData = Types.AdminBundleRowViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBundleRowViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBundleRowViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBundleRowViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBundleRowViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBundleRowViewSuspense"]
        : ["AdminBundleRowViewSuspense", variables],
    queryFn: adminFetcher<
      Types.AdminBundleRowViewQuery,
      Types.AdminBundleRowViewQueryVariables
    >(AdminBundleRowViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminBundleRowViewQuery.getKey = (
  variables?: Types.AdminBundleRowViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBundleRowViewSuspense"]
    : ["AdminBundleRowViewSuspense", variables]

useSuspenseAdminBundleRowViewQuery.rootKey = "AdminBundleRowView"

export const useInfiniteAdminBundleRowViewQuery = <
  TData = InfiniteData<Types.AdminBundleRowViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleRowViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBundleRowViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBundleRowViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBundleRowViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBundleRowView.infinite"]
            : ["AdminBundleRowView.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminBundleRowViewQuery,
            Types.AdminBundleRowViewQueryVariables
          >(AdminBundleRowViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBundleRowViewQuery.getKey = (
  variables?: Types.AdminBundleRowViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBundleRowView.infinite"]
    : ["AdminBundleRowView.infinite", variables]

useInfiniteAdminBundleRowViewQuery.rootKey = "AdminBundleRowView.infinite"

export const useSuspenseInfiniteAdminBundleRowViewQuery = <
  TData = InfiniteData<Types.AdminBundleRowViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleRowViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminBundleRowViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBundleRowViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminBundleRowViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBundleRowView.infiniteSuspense"]
            : ["AdminBundleRowView.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminBundleRowViewQuery,
            Types.AdminBundleRowViewQueryVariables
          >(AdminBundleRowViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBundleRowViewQuery.getKey = (
  variables?: Types.AdminBundleRowViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBundleRowView.infiniteSuspense"]
    : ["AdminBundleRowView.infiniteSuspense", variables]

useSuspenseInfiniteAdminBundleRowViewQuery.rootKey = "AdminBundleRowView.infinite"

useAdminBundleRowViewQuery.fetcher = (
  variables?: Types.AdminBundleRowViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AdminBundleRowViewQuery, Types.AdminBundleRowViewQueryVariables>(
    AdminBundleRowViewDocument,
    variables,
    options,
  )

export const AdminBundleRowsDocument = `
    query AdminBundleRows($keys: [Int!]!) {
  bundleRows {
    rows(keys: $keys) {
      id
      createdAt
      bundleName
      finproductType
      sitePid
      siteName
      receiverPid
      receiverName
      financierPid
      financierName
      portfolioPid
      portfolioName
      biddingOpenAt
      biddingClosedAt
      preBidMeetingDate
      bidReviewMeetingDate
      contractSignedDate
      ntpDate
      codDate
      state
      reason
      customReason
      leDevFee
      pvKw
    }
  }
}
    `

export const useAdminBundleRowsQuery = <
  TData = Types.AdminBundleRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBundleRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBundleRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminBundleRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminBundleRowsQuery, TError, TData>({
    queryKey: ["AdminBundleRows", variables],
    queryFn: adminFetcher<
      Types.AdminBundleRowsQuery,
      Types.AdminBundleRowsQueryVariables
    >(AdminBundleRowsDocument, variables),
    ...options,
  })
}

useAdminBundleRowsQuery.getKey = (variables: Types.AdminBundleRowsQueryVariables) => [
  "AdminBundleRows",
  variables,
]

useAdminBundleRowsQuery.rootKey = "AdminBundleRows"

export const useSuspenseAdminBundleRowsQuery = <
  TData = Types.AdminBundleRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBundleRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBundleRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBundleRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBundleRowsQuery, TError, TData>({
    queryKey: ["AdminBundleRowsSuspense", variables],
    queryFn: adminFetcher<
      Types.AdminBundleRowsQuery,
      Types.AdminBundleRowsQueryVariables
    >(AdminBundleRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminBundleRowsQuery.getKey = (
  variables: Types.AdminBundleRowsQueryVariables,
) => ["AdminBundleRowsSuspense", variables]

useSuspenseAdminBundleRowsQuery.rootKey = "AdminBundleRows"

export const useInfiniteAdminBundleRowsQuery = <
  TData = InfiniteData<Types.AdminBundleRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBundleRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBundleRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBundleRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminBundleRows.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.AdminBundleRowsQuery, Types.AdminBundleRowsQueryVariables>(
            AdminBundleRowsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBundleRowsQuery.getKey = (
  variables: Types.AdminBundleRowsQueryVariables,
) => ["AdminBundleRows.infinite", variables]

useInfiniteAdminBundleRowsQuery.rootKey = "AdminBundleRows.infinite"

export const useSuspenseInfiniteAdminBundleRowsQuery = <
  TData = InfiniteData<Types.AdminBundleRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminBundleRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBundleRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminBundleRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminBundleRows.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.AdminBundleRowsQuery, Types.AdminBundleRowsQueryVariables>(
            AdminBundleRowsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBundleRowsQuery.getKey = (
  variables: Types.AdminBundleRowsQueryVariables,
) => ["AdminBundleRows.infiniteSuspense", variables]

useSuspenseInfiniteAdminBundleRowsQuery.rootKey = "AdminBundleRows.infinite"

useAdminBundleRowsQuery.fetcher = (
  variables: Types.AdminBundleRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AdminBundleRowsQuery, Types.AdminBundleRowsQueryVariables>(
    AdminBundleRowsDocument,
    variables,
    options,
  )

export const SolarCaptchaSitesDocument = `
    query SolarCaptchaSites($sitePids: [String!]!) {
  sites(sitePids: $sitePids) {
    pid
    fullAddress
    staticMapUrl
    existingSolarDetectionScore
    latitude
    longitude
    orgSlug
  }
}
    `

export const useSolarCaptchaSitesQuery = <
  TData = Types.SolarCaptchaSitesQuery,
  TError = unknown,
>(
  variables: Types.SolarCaptchaSitesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SolarCaptchaSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SolarCaptchaSitesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SolarCaptchaSitesQuery, TError, TData>({
    queryKey: ["SolarCaptchaSites", variables],
    queryFn: adminFetcher<
      Types.SolarCaptchaSitesQuery,
      Types.SolarCaptchaSitesQueryVariables
    >(SolarCaptchaSitesDocument, variables),
    ...options,
  })
}

useSolarCaptchaSitesQuery.getKey = (
  variables: Types.SolarCaptchaSitesQueryVariables,
) => ["SolarCaptchaSites", variables]

useSolarCaptchaSitesQuery.rootKey = "SolarCaptchaSites"

export const useSuspenseSolarCaptchaSitesQuery = <
  TData = Types.SolarCaptchaSitesQuery,
  TError = unknown,
>(
  variables: Types.SolarCaptchaSitesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SolarCaptchaSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SolarCaptchaSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SolarCaptchaSitesQuery, TError, TData>({
    queryKey: ["SolarCaptchaSitesSuspense", variables],
    queryFn: adminFetcher<
      Types.SolarCaptchaSitesQuery,
      Types.SolarCaptchaSitesQueryVariables
    >(SolarCaptchaSitesDocument, variables),
    ...options,
  })
}

useSuspenseSolarCaptchaSitesQuery.getKey = (
  variables: Types.SolarCaptchaSitesQueryVariables,
) => ["SolarCaptchaSitesSuspense", variables]

useSuspenseSolarCaptchaSitesQuery.rootKey = "SolarCaptchaSites"

export const useInfiniteSolarCaptchaSitesQuery = <
  TData = InfiniteData<Types.SolarCaptchaSitesQuery>,
  TError = unknown,
>(
  variables: Types.SolarCaptchaSitesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SolarCaptchaSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SolarCaptchaSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SolarCaptchaSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SolarCaptchaSites.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.SolarCaptchaSitesQuery,
            Types.SolarCaptchaSitesQueryVariables
          >(SolarCaptchaSitesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSolarCaptchaSitesQuery.getKey = (
  variables: Types.SolarCaptchaSitesQueryVariables,
) => ["SolarCaptchaSites.infinite", variables]

useInfiniteSolarCaptchaSitesQuery.rootKey = "SolarCaptchaSites.infinite"

export const useSuspenseInfiniteSolarCaptchaSitesQuery = <
  TData = InfiniteData<Types.SolarCaptchaSitesQuery>,
  TError = unknown,
>(
  variables: Types.SolarCaptchaSitesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SolarCaptchaSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SolarCaptchaSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SolarCaptchaSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SolarCaptchaSites.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.SolarCaptchaSitesQuery,
            Types.SolarCaptchaSitesQueryVariables
          >(SolarCaptchaSitesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSolarCaptchaSitesQuery.getKey = (
  variables: Types.SolarCaptchaSitesQueryVariables,
) => ["SolarCaptchaSites.infiniteSuspense", variables]

useSuspenseInfiniteSolarCaptchaSitesQuery.rootKey = "SolarCaptchaSites.infinite"

useSolarCaptchaSitesQuery.fetcher = (
  variables: Types.SolarCaptchaSitesQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SolarCaptchaSitesQuery, Types.SolarCaptchaSitesQueryVariables>(
    SolarCaptchaSitesDocument,
    variables,
    options,
  )

export const PreviewBuildingFootprintsDocument = `
    query PreviewBuildingFootprints($sitePid: String!, $strategy: FootprintStrategy!) {
  site(pid: $sitePid) {
    previewBuildingFootprints(strategy: $strategy) {
      __typename
      ... on Footprint {
        latitude
        longitude
        bounds
        roofShape
        roofShapeGeojson
        parcelShape
        parcelShapeGeojson
      }
      ... on Error {
        message
      }
    }
  }
}
    `

export const usePreviewBuildingFootprintsQuery = <
  TData = Types.PreviewBuildingFootprintsQuery,
  TError = unknown,
>(
  variables: Types.PreviewBuildingFootprintsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PreviewBuildingFootprintsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.PreviewBuildingFootprintsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.PreviewBuildingFootprintsQuery, TError, TData>({
    queryKey: ["PreviewBuildingFootprints", variables],
    queryFn: adminFetcher<
      Types.PreviewBuildingFootprintsQuery,
      Types.PreviewBuildingFootprintsQueryVariables
    >(PreviewBuildingFootprintsDocument, variables),
    ...options,
  })
}

usePreviewBuildingFootprintsQuery.getKey = (
  variables: Types.PreviewBuildingFootprintsQueryVariables,
) => ["PreviewBuildingFootprints", variables]

usePreviewBuildingFootprintsQuery.rootKey = "PreviewBuildingFootprints"

export const useSuspensePreviewBuildingFootprintsQuery = <
  TData = Types.PreviewBuildingFootprintsQuery,
  TError = unknown,
>(
  variables: Types.PreviewBuildingFootprintsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PreviewBuildingFootprintsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PreviewBuildingFootprintsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PreviewBuildingFootprintsQuery, TError, TData>({
    queryKey: ["PreviewBuildingFootprintsSuspense", variables],
    queryFn: adminFetcher<
      Types.PreviewBuildingFootprintsQuery,
      Types.PreviewBuildingFootprintsQueryVariables
    >(PreviewBuildingFootprintsDocument, variables),
    ...options,
  })
}

useSuspensePreviewBuildingFootprintsQuery.getKey = (
  variables: Types.PreviewBuildingFootprintsQueryVariables,
) => ["PreviewBuildingFootprintsSuspense", variables]

useSuspensePreviewBuildingFootprintsQuery.rootKey = "PreviewBuildingFootprints"

export const useInfinitePreviewBuildingFootprintsQuery = <
  TData = InfiniteData<Types.PreviewBuildingFootprintsQuery>,
  TError = unknown,
>(
  variables: Types.PreviewBuildingFootprintsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PreviewBuildingFootprintsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PreviewBuildingFootprintsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PreviewBuildingFootprintsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PreviewBuildingFootprints.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.PreviewBuildingFootprintsQuery,
            Types.PreviewBuildingFootprintsQueryVariables
          >(PreviewBuildingFootprintsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePreviewBuildingFootprintsQuery.getKey = (
  variables: Types.PreviewBuildingFootprintsQueryVariables,
) => ["PreviewBuildingFootprints.infinite", variables]

useInfinitePreviewBuildingFootprintsQuery.rootKey = "PreviewBuildingFootprints.infinite"

export const useSuspenseInfinitePreviewBuildingFootprintsQuery = <
  TData = InfiniteData<Types.PreviewBuildingFootprintsQuery>,
  TError = unknown,
>(
  variables: Types.PreviewBuildingFootprintsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.PreviewBuildingFootprintsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PreviewBuildingFootprintsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PreviewBuildingFootprintsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "PreviewBuildingFootprints.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.PreviewBuildingFootprintsQuery,
            Types.PreviewBuildingFootprintsQueryVariables
          >(PreviewBuildingFootprintsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePreviewBuildingFootprintsQuery.getKey = (
  variables: Types.PreviewBuildingFootprintsQueryVariables,
) => ["PreviewBuildingFootprints.infiniteSuspense", variables]

useSuspenseInfinitePreviewBuildingFootprintsQuery.rootKey =
  "PreviewBuildingFootprints.infinite"

usePreviewBuildingFootprintsQuery.fetcher = (
  variables: Types.PreviewBuildingFootprintsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.PreviewBuildingFootprintsQuery,
    Types.PreviewBuildingFootprintsQueryVariables
  >(PreviewBuildingFootprintsDocument, variables, options)

export const ListHostingCapacityMapsDocument = `
    query ListHostingCapacityMaps {
  hostingCapacityMaps {
    all {
      id
      utility {
        id
        pid
        name
        shortName
      }
      url
      requiresLogin
      types
    }
  }
}
    `

export const useListHostingCapacityMapsQuery = <
  TData = Types.ListHostingCapacityMapsQuery,
  TError = unknown,
>(
  variables?: Types.ListHostingCapacityMapsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ListHostingCapacityMapsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ListHostingCapacityMapsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ListHostingCapacityMapsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["ListHostingCapacityMaps"]
        : ["ListHostingCapacityMaps", variables],
    queryFn: adminFetcher<
      Types.ListHostingCapacityMapsQuery,
      Types.ListHostingCapacityMapsQueryVariables
    >(ListHostingCapacityMapsDocument, variables),
    ...options,
  })
}

useListHostingCapacityMapsQuery.getKey = (
  variables?: Types.ListHostingCapacityMapsQueryVariables,
) =>
  variables === undefined
    ? ["ListHostingCapacityMaps"]
    : ["ListHostingCapacityMaps", variables]

useListHostingCapacityMapsQuery.rootKey = "ListHostingCapacityMaps"

export const useSuspenseListHostingCapacityMapsQuery = <
  TData = Types.ListHostingCapacityMapsQuery,
  TError = unknown,
>(
  variables?: Types.ListHostingCapacityMapsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ListHostingCapacityMapsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ListHostingCapacityMapsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ListHostingCapacityMapsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["ListHostingCapacityMapsSuspense"]
        : ["ListHostingCapacityMapsSuspense", variables],
    queryFn: adminFetcher<
      Types.ListHostingCapacityMapsQuery,
      Types.ListHostingCapacityMapsQueryVariables
    >(ListHostingCapacityMapsDocument, variables),
    ...options,
  })
}

useSuspenseListHostingCapacityMapsQuery.getKey = (
  variables?: Types.ListHostingCapacityMapsQueryVariables,
) =>
  variables === undefined
    ? ["ListHostingCapacityMapsSuspense"]
    : ["ListHostingCapacityMapsSuspense", variables]

useSuspenseListHostingCapacityMapsQuery.rootKey = "ListHostingCapacityMaps"

export const useInfiniteListHostingCapacityMapsQuery = <
  TData = InfiniteData<Types.ListHostingCapacityMapsQuery>,
  TError = unknown,
>(
  variables: Types.ListHostingCapacityMapsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ListHostingCapacityMapsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ListHostingCapacityMapsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ListHostingCapacityMapsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ListHostingCapacityMaps.infinite"]
            : ["ListHostingCapacityMaps.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.ListHostingCapacityMapsQuery,
            Types.ListHostingCapacityMapsQueryVariables
          >(ListHostingCapacityMapsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteListHostingCapacityMapsQuery.getKey = (
  variables?: Types.ListHostingCapacityMapsQueryVariables,
) =>
  variables === undefined
    ? ["ListHostingCapacityMaps.infinite"]
    : ["ListHostingCapacityMaps.infinite", variables]

useInfiniteListHostingCapacityMapsQuery.rootKey = "ListHostingCapacityMaps.infinite"

export const useSuspenseInfiniteListHostingCapacityMapsQuery = <
  TData = InfiniteData<Types.ListHostingCapacityMapsQuery>,
  TError = unknown,
>(
  variables: Types.ListHostingCapacityMapsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ListHostingCapacityMapsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ListHostingCapacityMapsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ListHostingCapacityMapsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ListHostingCapacityMaps.infiniteSuspense"]
            : ["ListHostingCapacityMaps.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.ListHostingCapacityMapsQuery,
            Types.ListHostingCapacityMapsQueryVariables
          >(ListHostingCapacityMapsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteListHostingCapacityMapsQuery.getKey = (
  variables?: Types.ListHostingCapacityMapsQueryVariables,
) =>
  variables === undefined
    ? ["ListHostingCapacityMaps.infiniteSuspense"]
    : ["ListHostingCapacityMaps.infiniteSuspense", variables]

useSuspenseInfiniteListHostingCapacityMapsQuery.rootKey =
  "ListHostingCapacityMaps.infinite"

useListHostingCapacityMapsQuery.fetcher = (
  variables?: Types.ListHostingCapacityMapsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.ListHostingCapacityMapsQuery,
    Types.ListHostingCapacityMapsQueryVariables
  >(ListHostingCapacityMapsDocument, variables, options)

export const BauMeterBillComparisonDocument = `
    query BauMeterBillComparison($sitePid: String!, $meterId: Int!) {
  site(pid: $sitePid) {
    pid
    isMultifamily
    customTariffs {
      id
      name
      code
      parent {
        id
      }
    }
  }
  bauMeter(id: $meterId) {
    loadCategory
    tariff {
      id
      code
      name
    }
    documentBillingMonths {
      id
      start
      end
    }
  }
}
    `

export const useBauMeterBillComparisonQuery = <
  TData = Types.BauMeterBillComparisonQuery,
  TError = unknown,
>(
  variables: Types.BauMeterBillComparisonQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BauMeterBillComparisonQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BauMeterBillComparisonQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BauMeterBillComparisonQuery, TError, TData>({
    queryKey: ["BauMeterBillComparison", variables],
    queryFn: adminFetcher<
      Types.BauMeterBillComparisonQuery,
      Types.BauMeterBillComparisonQueryVariables
    >(BauMeterBillComparisonDocument, variables),
    ...options,
  })
}

useBauMeterBillComparisonQuery.getKey = (
  variables: Types.BauMeterBillComparisonQueryVariables,
) => ["BauMeterBillComparison", variables]

useBauMeterBillComparisonQuery.rootKey = "BauMeterBillComparison"

export const useSuspenseBauMeterBillComparisonQuery = <
  TData = Types.BauMeterBillComparisonQuery,
  TError = unknown,
>(
  variables: Types.BauMeterBillComparisonQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BauMeterBillComparisonQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BauMeterBillComparisonQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BauMeterBillComparisonQuery, TError, TData>({
    queryKey: ["BauMeterBillComparisonSuspense", variables],
    queryFn: adminFetcher<
      Types.BauMeterBillComparisonQuery,
      Types.BauMeterBillComparisonQueryVariables
    >(BauMeterBillComparisonDocument, variables),
    ...options,
  })
}

useSuspenseBauMeterBillComparisonQuery.getKey = (
  variables: Types.BauMeterBillComparisonQueryVariables,
) => ["BauMeterBillComparisonSuspense", variables]

useSuspenseBauMeterBillComparisonQuery.rootKey = "BauMeterBillComparison"

export const useInfiniteBauMeterBillComparisonQuery = <
  TData = InfiniteData<Types.BauMeterBillComparisonQuery>,
  TError = unknown,
>(
  variables: Types.BauMeterBillComparisonQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BauMeterBillComparisonQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BauMeterBillComparisonQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BauMeterBillComparisonQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BauMeterBillComparison.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.BauMeterBillComparisonQuery,
            Types.BauMeterBillComparisonQueryVariables
          >(BauMeterBillComparisonDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBauMeterBillComparisonQuery.getKey = (
  variables: Types.BauMeterBillComparisonQueryVariables,
) => ["BauMeterBillComparison.infinite", variables]

useInfiniteBauMeterBillComparisonQuery.rootKey = "BauMeterBillComparison.infinite"

export const useSuspenseInfiniteBauMeterBillComparisonQuery = <
  TData = InfiniteData<Types.BauMeterBillComparisonQuery>,
  TError = unknown,
>(
  variables: Types.BauMeterBillComparisonQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BauMeterBillComparisonQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BauMeterBillComparisonQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BauMeterBillComparisonQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "BauMeterBillComparison.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.BauMeterBillComparisonQuery,
            Types.BauMeterBillComparisonQueryVariables
          >(BauMeterBillComparisonDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBauMeterBillComparisonQuery.getKey = (
  variables: Types.BauMeterBillComparisonQueryVariables,
) => ["BauMeterBillComparison.infiniteSuspense", variables]

useSuspenseInfiniteBauMeterBillComparisonQuery.rootKey =
  "BauMeterBillComparison.infinite"

useBauMeterBillComparisonQuery.fetcher = (
  variables: Types.BauMeterBillComparisonQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.BauMeterBillComparisonQuery,
    Types.BauMeterBillComparisonQueryVariables
  >(BauMeterBillComparisonDocument, variables, options)

export const AdminMarketplaceViewDocument = `
    query AdminMarketplaceView($filterBy: AdminMarketplaceTableFilter, $sortBy: [AdminMarketplaceTableSort!]) {
  marketplace {
    view(filterBy: $filterBy, sortBy: $sortBy) {
      keys
      count
      stats {
        buildingOwners
        assetManagers
        bidPackageNames
        siteStates
        financiers
        portfolios
        salesOwners
        opsOwners
      }
    }
  }
}
    `

export const useAdminMarketplaceViewQuery = <
  TData = Types.AdminMarketplaceViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminMarketplaceViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminMarketplaceViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminMarketplaceViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminMarketplaceViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminMarketplaceView"]
        : ["AdminMarketplaceView", variables],
    queryFn: adminFetcher<
      Types.AdminMarketplaceViewQuery,
      Types.AdminMarketplaceViewQueryVariables
    >(AdminMarketplaceViewDocument, variables),
    ...options,
  })
}

useAdminMarketplaceViewQuery.getKey = (
  variables?: Types.AdminMarketplaceViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminMarketplaceView"]
    : ["AdminMarketplaceView", variables]

useAdminMarketplaceViewQuery.rootKey = "AdminMarketplaceView"

export const useSuspenseAdminMarketplaceViewQuery = <
  TData = Types.AdminMarketplaceViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminMarketplaceViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminMarketplaceViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminMarketplaceViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminMarketplaceViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminMarketplaceViewSuspense"]
        : ["AdminMarketplaceViewSuspense", variables],
    queryFn: adminFetcher<
      Types.AdminMarketplaceViewQuery,
      Types.AdminMarketplaceViewQueryVariables
    >(AdminMarketplaceViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminMarketplaceViewQuery.getKey = (
  variables?: Types.AdminMarketplaceViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminMarketplaceViewSuspense"]
    : ["AdminMarketplaceViewSuspense", variables]

useSuspenseAdminMarketplaceViewQuery.rootKey = "AdminMarketplaceView"

export const useInfiniteAdminMarketplaceViewQuery = <
  TData = InfiniteData<Types.AdminMarketplaceViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminMarketplaceViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminMarketplaceViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminMarketplaceViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminMarketplaceView.infinite"]
            : ["AdminMarketplaceView.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminMarketplaceViewQuery,
            Types.AdminMarketplaceViewQueryVariables
          >(AdminMarketplaceViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminMarketplaceViewQuery.getKey = (
  variables?: Types.AdminMarketplaceViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminMarketplaceView.infinite"]
    : ["AdminMarketplaceView.infinite", variables]

useInfiniteAdminMarketplaceViewQuery.rootKey = "AdminMarketplaceView.infinite"

export const useSuspenseInfiniteAdminMarketplaceViewQuery = <
  TData = InfiniteData<Types.AdminMarketplaceViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminMarketplaceViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminMarketplaceViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminMarketplaceViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminMarketplaceView.infiniteSuspense"]
            : ["AdminMarketplaceView.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminMarketplaceViewQuery,
            Types.AdminMarketplaceViewQueryVariables
          >(AdminMarketplaceViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminMarketplaceViewQuery.getKey = (
  variables?: Types.AdminMarketplaceViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminMarketplaceView.infiniteSuspense"]
    : ["AdminMarketplaceView.infiniteSuspense", variables]

useSuspenseInfiniteAdminMarketplaceViewQuery.rootKey = "AdminMarketplaceView.infinite"

useAdminMarketplaceViewQuery.fetcher = (
  variables?: Types.AdminMarketplaceViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.AdminMarketplaceViewQuery,
    Types.AdminMarketplaceViewQueryVariables
  >(AdminMarketplaceViewDocument, variables, options)

export const AdminMarketplaceRowsDocument = `
    query AdminMarketplaceRows($keys: [Int!]!) {
  marketplace {
    rows(keys: $keys) {
      id
      orgSlug
      orgName
      portfolioName
      sitePid
      bidPackagePid
      bidPackageName
      name
      city
      state
      zipCode
      streetAddress
      county
      cbsaName
      assetManager
      packageRowStatus
      packageRowIsClosed
      packageRowClosedAt
      packageRowCloseReason
      packageRowCloseCustomReason
      isAwarded
      buildingType
      yearBuilt
      sqft
      buildingFootprintSqft
      roofSqft
      usableRoofSqft
      numStories
      numUnitsTotal
      buildingClass
      roofReplacedYr
      roofCondition
      salePlannedYear
      salePlans
      buildingLegalName
      propertyFundName
      leaseSignatory
      counselApproval
      leaseApprovers
      externalLeaseApprovers
      needsTenantApproval
      roofRightsHolder
      propertyLenders
      propertyInsurer
      constructionEmail
      propertySiteVisitContacts
      leasePropertyLegalDescription
      leaseLegalOwnerName
      signatureBlocks
      isFannieMaeLender
      isFmGlobalInsurer
      utilityShortName
      finproductType
      biddingOpenAt
      biddingClosedAt
      bidReviewMeetingDate
      estimatedNtp
      estimatedCod
      bidCount
      bidderCount
      awardedTo
      roofRentY1Usd
      noiLifetimeUsd
      roofRentEscalator
      pvSizeKwDc
      lumenRevenue
      lumenFeeUsdPerWatt
      hubspotRecordId
      salesOwnerEmail
      salesOwnerPid
      opsOwnerEmail
      opsOwnerPid
      rofoSignedDate
      rofoSignedEstimated
      rofoSignedActual
      preBidMeetingEstimated
      preBidMeetingActual
      preBidMeetingDate
      biddingOpenedEstimated
      biddingOpenedActual
      biddingOpenedDate
      biddingClosedEstimated
      biddingClosedActual
      biddingClosedDate
      bidsPresentedEstimated
      bidsPresentedActual
      bidsPresentedDate
      ixSubmittedEstimated
      ixSubmittedActual
      ixSubmittedDate
      ixApprovedEstimated
      ixApprovedActual
      ixApprovedDate
      csProgramSubmittedEstimated
      csProgramSubmittedActual
      csProgramSubmittedDate
      csProgramApprovedEstimated
      csProgramApprovedActual
      csProgramApprovedDate
      awardedToDeveloperEstimated
      awardedToDeveloperActual
      awardedToDeveloperDate
      leaseSignedEstimated
      leaseSignedActual
      leaseSignedDate
      ntpEstimated
      ntpActual
      ntpDate
      codEstimated
      codActual
      codDate
    }
  }
}
    `

export const useAdminMarketplaceRowsQuery = <
  TData = Types.AdminMarketplaceRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminMarketplaceRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminMarketplaceRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminMarketplaceRowsQuery, TError, TData>({
    queryKey: ["AdminMarketplaceRows", variables],
    queryFn: adminFetcher<
      Types.AdminMarketplaceRowsQuery,
      Types.AdminMarketplaceRowsQueryVariables
    >(AdminMarketplaceRowsDocument, variables),
    ...options,
  })
}

useAdminMarketplaceRowsQuery.getKey = (
  variables: Types.AdminMarketplaceRowsQueryVariables,
) => ["AdminMarketplaceRows", variables]

useAdminMarketplaceRowsQuery.rootKey = "AdminMarketplaceRows"

export const useSuspenseAdminMarketplaceRowsQuery = <
  TData = Types.AdminMarketplaceRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminMarketplaceRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminMarketplaceRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminMarketplaceRowsQuery, TError, TData>({
    queryKey: ["AdminMarketplaceRowsSuspense", variables],
    queryFn: adminFetcher<
      Types.AdminMarketplaceRowsQuery,
      Types.AdminMarketplaceRowsQueryVariables
    >(AdminMarketplaceRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminMarketplaceRowsQuery.getKey = (
  variables: Types.AdminMarketplaceRowsQueryVariables,
) => ["AdminMarketplaceRowsSuspense", variables]

useSuspenseAdminMarketplaceRowsQuery.rootKey = "AdminMarketplaceRows"

export const useInfiniteAdminMarketplaceRowsQuery = <
  TData = InfiniteData<Types.AdminMarketplaceRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminMarketplaceRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminMarketplaceRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminMarketplaceRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminMarketplaceRows.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminMarketplaceRowsQuery,
            Types.AdminMarketplaceRowsQueryVariables
          >(AdminMarketplaceRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminMarketplaceRowsQuery.getKey = (
  variables: Types.AdminMarketplaceRowsQueryVariables,
) => ["AdminMarketplaceRows.infinite", variables]

useInfiniteAdminMarketplaceRowsQuery.rootKey = "AdminMarketplaceRows.infinite"

export const useSuspenseInfiniteAdminMarketplaceRowsQuery = <
  TData = InfiniteData<Types.AdminMarketplaceRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminMarketplaceRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminMarketplaceRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminMarketplaceRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminMarketplaceRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminMarketplaceRowsQuery,
            Types.AdminMarketplaceRowsQueryVariables
          >(AdminMarketplaceRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminMarketplaceRowsQuery.getKey = (
  variables: Types.AdminMarketplaceRowsQueryVariables,
) => ["AdminMarketplaceRows.infiniteSuspense", variables]

useSuspenseInfiniteAdminMarketplaceRowsQuery.rootKey = "AdminMarketplaceRows.infinite"

useAdminMarketplaceRowsQuery.fetcher = (
  variables: Types.AdminMarketplaceRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.AdminMarketplaceRowsQuery,
    Types.AdminMarketplaceRowsQueryVariables
  >(AdminMarketplaceRowsDocument, variables, options)

export const AdminMarketplaceSiteDocumentsDocument = `
    query AdminMarketplaceSiteDocuments($keys: [Int!]!) {
  bundleRowParentDocuments(
    bundleParentRowIds: $keys
    includeTags: [SITE__ROOF, SITE__ROOF_WARRANTY, SITE__STRUCTURAL_DRAWINGS, SITE__ELECTRICAL_DIAGRAMS, SITE__WIRING_INSTRUCTIONS]
  ) {
    sitePid
    documents {
      pid
      fileName
      downloadHref
      tag
    }
  }
}
    `

export const useAdminMarketplaceSiteDocumentsQuery = <
  TData = Types.AdminMarketplaceSiteDocumentsQuery,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminMarketplaceSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminMarketplaceSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminMarketplaceSiteDocumentsQuery, TError, TData>({
    queryKey: ["AdminMarketplaceSiteDocuments", variables],
    queryFn: adminFetcher<
      Types.AdminMarketplaceSiteDocumentsQuery,
      Types.AdminMarketplaceSiteDocumentsQueryVariables
    >(AdminMarketplaceSiteDocumentsDocument, variables),
    ...options,
  })
}

useAdminMarketplaceSiteDocumentsQuery.getKey = (
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
) => ["AdminMarketplaceSiteDocuments", variables]

useAdminMarketplaceSiteDocumentsQuery.rootKey = "AdminMarketplaceSiteDocuments"

export const useSuspenseAdminMarketplaceSiteDocumentsQuery = <
  TData = Types.AdminMarketplaceSiteDocumentsQuery,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminMarketplaceSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminMarketplaceSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminMarketplaceSiteDocumentsQuery, TError, TData>({
    queryKey: ["AdminMarketplaceSiteDocumentsSuspense", variables],
    queryFn: adminFetcher<
      Types.AdminMarketplaceSiteDocumentsQuery,
      Types.AdminMarketplaceSiteDocumentsQueryVariables
    >(AdminMarketplaceSiteDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseAdminMarketplaceSiteDocumentsQuery.getKey = (
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
) => ["AdminMarketplaceSiteDocumentsSuspense", variables]

useSuspenseAdminMarketplaceSiteDocumentsQuery.rootKey = "AdminMarketplaceSiteDocuments"

export const useInfiniteAdminMarketplaceSiteDocumentsQuery = <
  TData = InfiniteData<Types.AdminMarketplaceSiteDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminMarketplaceSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminMarketplaceSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminMarketplaceSiteDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminMarketplaceSiteDocuments.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminMarketplaceSiteDocumentsQuery,
            Types.AdminMarketplaceSiteDocumentsQueryVariables
          >(AdminMarketplaceSiteDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminMarketplaceSiteDocumentsQuery.getKey = (
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
) => ["AdminMarketplaceSiteDocuments.infinite", variables]

useInfiniteAdminMarketplaceSiteDocumentsQuery.rootKey =
  "AdminMarketplaceSiteDocuments.infinite"

export const useSuspenseInfiniteAdminMarketplaceSiteDocumentsQuery = <
  TData = InfiniteData<Types.AdminMarketplaceSiteDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminMarketplaceSiteDocumentsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminMarketplaceSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminMarketplaceSiteDocumentsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminMarketplaceSiteDocuments.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminMarketplaceSiteDocumentsQuery,
            Types.AdminMarketplaceSiteDocumentsQueryVariables
          >(AdminMarketplaceSiteDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminMarketplaceSiteDocumentsQuery.getKey = (
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
) => ["AdminMarketplaceSiteDocuments.infiniteSuspense", variables]

useSuspenseInfiniteAdminMarketplaceSiteDocumentsQuery.rootKey =
  "AdminMarketplaceSiteDocuments.infinite"

useAdminMarketplaceSiteDocumentsQuery.fetcher = (
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.AdminMarketplaceSiteDocumentsQuery,
    Types.AdminMarketplaceSiteDocumentsQueryVariables
  >(AdminMarketplaceSiteDocumentsDocument, variables, options)

export const ActiveAdminsDocument = `
    query ActiveAdmins {
  activeAdmins {
    pid
    isAdmin
    name
    email
  }
}
    `

export const useActiveAdminsQuery = <TData = Types.ActiveAdminsQuery, TError = unknown>(
  variables?: Types.ActiveAdminsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ActiveAdminsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ActiveAdminsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ActiveAdminsQuery, TError, TData>({
    queryKey: variables === undefined ? ["ActiveAdmins"] : ["ActiveAdmins", variables],
    queryFn: adminFetcher<Types.ActiveAdminsQuery, Types.ActiveAdminsQueryVariables>(
      ActiveAdminsDocument,
      variables,
    ),
    ...options,
  })
}

useActiveAdminsQuery.getKey = (variables?: Types.ActiveAdminsQueryVariables) =>
  variables === undefined ? ["ActiveAdmins"] : ["ActiveAdmins", variables]

useActiveAdminsQuery.rootKey = "ActiveAdmins"

export const useSuspenseActiveAdminsQuery = <
  TData = Types.ActiveAdminsQuery,
  TError = unknown,
>(
  variables?: Types.ActiveAdminsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ActiveAdminsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ActiveAdminsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ActiveAdminsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["ActiveAdminsSuspense"]
        : ["ActiveAdminsSuspense", variables],
    queryFn: adminFetcher<Types.ActiveAdminsQuery, Types.ActiveAdminsQueryVariables>(
      ActiveAdminsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseActiveAdminsQuery.getKey = (variables?: Types.ActiveAdminsQueryVariables) =>
  variables === undefined
    ? ["ActiveAdminsSuspense"]
    : ["ActiveAdminsSuspense", variables]

useSuspenseActiveAdminsQuery.rootKey = "ActiveAdmins"

export const useInfiniteActiveAdminsQuery = <
  TData = InfiniteData<Types.ActiveAdminsQuery>,
  TError = unknown,
>(
  variables: Types.ActiveAdminsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ActiveAdminsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ActiveAdminsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ActiveAdminsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ActiveAdmins.infinite"]
            : ["ActiveAdmins.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.ActiveAdminsQuery, Types.ActiveAdminsQueryVariables>(
            ActiveAdminsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteActiveAdminsQuery.getKey = (variables?: Types.ActiveAdminsQueryVariables) =>
  variables === undefined
    ? ["ActiveAdmins.infinite"]
    : ["ActiveAdmins.infinite", variables]

useInfiniteActiveAdminsQuery.rootKey = "ActiveAdmins.infinite"

export const useSuspenseInfiniteActiveAdminsQuery = <
  TData = InfiniteData<Types.ActiveAdminsQuery>,
  TError = unknown,
>(
  variables: Types.ActiveAdminsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ActiveAdminsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ActiveAdminsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ActiveAdminsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ActiveAdmins.infiniteSuspense"]
            : ["ActiveAdmins.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.ActiveAdminsQuery, Types.ActiveAdminsQueryVariables>(
            ActiveAdminsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteActiveAdminsQuery.getKey = (
  variables?: Types.ActiveAdminsQueryVariables,
) =>
  variables === undefined
    ? ["ActiveAdmins.infiniteSuspense"]
    : ["ActiveAdmins.infiniteSuspense", variables]

useSuspenseInfiniteActiveAdminsQuery.rootKey = "ActiveAdmins.infinite"

useActiveAdminsQuery.fetcher = (
  variables?: Types.ActiveAdminsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.ActiveAdminsQuery, Types.ActiveAdminsQueryVariables>(
    ActiveAdminsDocument,
    variables,
    options,
  )

export const AdminUserDetailDocument = `
    query AdminUserDetail($userId: Int!) {
  user(userId: $userId) {
    ...UserListUser
    pageViews {
      pathname
      createdAt
    }
  }
}
    ${UserListUserFragmentDoc}`

export const useAdminUserDetailQuery = <
  TData = Types.AdminUserDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminUserDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminUserDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminUserDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminUserDetailQuery, TError, TData>({
    queryKey: ["AdminUserDetail", variables],
    queryFn: adminFetcher<
      Types.AdminUserDetailQuery,
      Types.AdminUserDetailQueryVariables
    >(AdminUserDetailDocument, variables),
    ...options,
  })
}

useAdminUserDetailQuery.getKey = (variables: Types.AdminUserDetailQueryVariables) => [
  "AdminUserDetail",
  variables,
]

useAdminUserDetailQuery.rootKey = "AdminUserDetail"

export const useSuspenseAdminUserDetailQuery = <
  TData = Types.AdminUserDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminUserDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminUserDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminUserDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminUserDetailQuery, TError, TData>({
    queryKey: ["AdminUserDetailSuspense", variables],
    queryFn: adminFetcher<
      Types.AdminUserDetailQuery,
      Types.AdminUserDetailQueryVariables
    >(AdminUserDetailDocument, variables),
    ...options,
  })
}

useSuspenseAdminUserDetailQuery.getKey = (
  variables: Types.AdminUserDetailQueryVariables,
) => ["AdminUserDetailSuspense", variables]

useSuspenseAdminUserDetailQuery.rootKey = "AdminUserDetail"

export const useInfiniteAdminUserDetailQuery = <
  TData = InfiniteData<Types.AdminUserDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminUserDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminUserDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminUserDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminUserDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminUserDetail.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.AdminUserDetailQuery, Types.AdminUserDetailQueryVariables>(
            AdminUserDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminUserDetailQuery.getKey = (
  variables: Types.AdminUserDetailQueryVariables,
) => ["AdminUserDetail.infinite", variables]

useInfiniteAdminUserDetailQuery.rootKey = "AdminUserDetail.infinite"

export const useSuspenseInfiniteAdminUserDetailQuery = <
  TData = InfiniteData<Types.AdminUserDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminUserDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminUserDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminUserDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminUserDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminUserDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.AdminUserDetailQuery, Types.AdminUserDetailQueryVariables>(
            AdminUserDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminUserDetailQuery.getKey = (
  variables: Types.AdminUserDetailQueryVariables,
) => ["AdminUserDetail.infiniteSuspense", variables]

useSuspenseInfiniteAdminUserDetailQuery.rootKey = "AdminUserDetail.infinite"

useAdminUserDetailQuery.fetcher = (
  variables: Types.AdminUserDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AdminUserDetailQuery, Types.AdminUserDetailQueryVariables>(
    AdminUserDetailDocument,
    variables,
    options,
  )

export const AdminSitesTableViewDocument = `
    query AdminSitesTableView($filterBy: AdminSitesTableFilter, $sortBy: [AdminSitesTableSort!]) {
  allSites {
    view(filterBy: $filterBy, sortBy: $sortBy) {
      keys
      count
    }
  }
}
    `

export const useAdminSitesTableViewQuery = <
  TData = Types.AdminSitesTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminSitesTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSitesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminSitesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminSitesTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminSitesTableView"]
        : ["AdminSitesTableView", variables],
    queryFn: adminFetcher<
      Types.AdminSitesTableViewQuery,
      Types.AdminSitesTableViewQueryVariables
    >(AdminSitesTableViewDocument, variables),
    ...options,
  })
}

useAdminSitesTableViewQuery.getKey = (
  variables?: Types.AdminSitesTableViewQueryVariables,
) =>
  variables === undefined ? ["AdminSitesTableView"] : ["AdminSitesTableView", variables]

useAdminSitesTableViewQuery.rootKey = "AdminSitesTableView"

export const useSuspenseAdminSitesTableViewQuery = <
  TData = Types.AdminSitesTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminSitesTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSitesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSitesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSitesTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminSitesTableViewSuspense"]
        : ["AdminSitesTableViewSuspense", variables],
    queryFn: adminFetcher<
      Types.AdminSitesTableViewQuery,
      Types.AdminSitesTableViewQueryVariables
    >(AdminSitesTableViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminSitesTableViewQuery.getKey = (
  variables?: Types.AdminSitesTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableViewSuspense"]
    : ["AdminSitesTableViewSuspense", variables]

useSuspenseAdminSitesTableViewQuery.rootKey = "AdminSitesTableView"

export const useInfiniteAdminSitesTableViewQuery = <
  TData = InfiniteData<Types.AdminSitesTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminSitesTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSitesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSitesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSitesTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminSitesTableView.infinite"]
            : ["AdminSitesTableView.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminSitesTableViewQuery,
            Types.AdminSitesTableViewQueryVariables
          >(AdminSitesTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSitesTableViewQuery.getKey = (
  variables?: Types.AdminSitesTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableView.infinite"]
    : ["AdminSitesTableView.infinite", variables]

useInfiniteAdminSitesTableViewQuery.rootKey = "AdminSitesTableView.infinite"

export const useSuspenseInfiniteAdminSitesTableViewQuery = <
  TData = InfiniteData<Types.AdminSitesTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminSitesTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSitesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSitesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSitesTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminSitesTableView.infiniteSuspense"]
            : ["AdminSitesTableView.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminSitesTableViewQuery,
            Types.AdminSitesTableViewQueryVariables
          >(AdminSitesTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSitesTableViewQuery.getKey = (
  variables?: Types.AdminSitesTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableView.infiniteSuspense"]
    : ["AdminSitesTableView.infiniteSuspense", variables]

useSuspenseInfiniteAdminSitesTableViewQuery.rootKey = "AdminSitesTableView.infinite"

useAdminSitesTableViewQuery.fetcher = (
  variables?: Types.AdminSitesTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AdminSitesTableViewQuery, Types.AdminSitesTableViewQueryVariables>(
    AdminSitesTableViewDocument,
    variables,
    options,
  )

export const AdminSitesTableStatsDocument = `
    query AdminSitesTableStats {
  allSites {
    view {
      stats {
        orgNames
        portfolios
        utilityNames
        cities
      }
    }
  }
}
    `

export const useAdminSitesTableStatsQuery = <
  TData = Types.AdminSitesTableStatsQuery,
  TError = unknown,
>(
  variables?: Types.AdminSitesTableStatsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSitesTableStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminSitesTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminSitesTableStatsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminSitesTableStats"]
        : ["AdminSitesTableStats", variables],
    queryFn: adminFetcher<
      Types.AdminSitesTableStatsQuery,
      Types.AdminSitesTableStatsQueryVariables
    >(AdminSitesTableStatsDocument, variables),
    ...options,
  })
}

useAdminSitesTableStatsQuery.getKey = (
  variables?: Types.AdminSitesTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableStats"]
    : ["AdminSitesTableStats", variables]

useAdminSitesTableStatsQuery.rootKey = "AdminSitesTableStats"

export const useSuspenseAdminSitesTableStatsQuery = <
  TData = Types.AdminSitesTableStatsQuery,
  TError = unknown,
>(
  variables?: Types.AdminSitesTableStatsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSitesTableStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSitesTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSitesTableStatsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminSitesTableStatsSuspense"]
        : ["AdminSitesTableStatsSuspense", variables],
    queryFn: adminFetcher<
      Types.AdminSitesTableStatsQuery,
      Types.AdminSitesTableStatsQueryVariables
    >(AdminSitesTableStatsDocument, variables),
    ...options,
  })
}

useSuspenseAdminSitesTableStatsQuery.getKey = (
  variables?: Types.AdminSitesTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableStatsSuspense"]
    : ["AdminSitesTableStatsSuspense", variables]

useSuspenseAdminSitesTableStatsQuery.rootKey = "AdminSitesTableStats"

export const useInfiniteAdminSitesTableStatsQuery = <
  TData = InfiniteData<Types.AdminSitesTableStatsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSitesTableStatsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSitesTableStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSitesTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSitesTableStatsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminSitesTableStats.infinite"]
            : ["AdminSitesTableStats.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminSitesTableStatsQuery,
            Types.AdminSitesTableStatsQueryVariables
          >(AdminSitesTableStatsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSitesTableStatsQuery.getKey = (
  variables?: Types.AdminSitesTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableStats.infinite"]
    : ["AdminSitesTableStats.infinite", variables]

useInfiniteAdminSitesTableStatsQuery.rootKey = "AdminSitesTableStats.infinite"

export const useSuspenseInfiniteAdminSitesTableStatsQuery = <
  TData = InfiniteData<Types.AdminSitesTableStatsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSitesTableStatsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSitesTableStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSitesTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSitesTableStatsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminSitesTableStats.infiniteSuspense"]
            : ["AdminSitesTableStats.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminSitesTableStatsQuery,
            Types.AdminSitesTableStatsQueryVariables
          >(AdminSitesTableStatsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSitesTableStatsQuery.getKey = (
  variables?: Types.AdminSitesTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableStats.infiniteSuspense"]
    : ["AdminSitesTableStats.infiniteSuspense", variables]

useSuspenseInfiniteAdminSitesTableStatsQuery.rootKey = "AdminSitesTableStats.infinite"

useAdminSitesTableStatsQuery.fetcher = (
  variables?: Types.AdminSitesTableStatsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.AdminSitesTableStatsQuery,
    Types.AdminSitesTableStatsQueryVariables
  >(AdminSitesTableStatsDocument, variables, options)

export const AdminSitesTableRowsDocument = `
    query AdminSitesTableRows($keys: [Int!]!) {
  allSites {
    rows(keys: $keys) {
      id
      orgName
      orgPid
      orgSlug
      portfolioPid
      portfolioName
      sitePid
      createdAt
      status
      statusReason
      statusCustomReason
      name
      customerId
      streetAddress
      city
      state
      zipCode
      roofMaxPvKw
      utilityId
      utilityShortName
      utilityName
      buildingFootprintSqft
      parcelFootprintSqft
      freeSpaceSqft
      buildingType
    }
  }
}
    `

export const useAdminSitesTableRowsQuery = <
  TData = Types.AdminSitesTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminSitesTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSitesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminSitesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminSitesTableRowsQuery, TError, TData>({
    queryKey: ["AdminSitesTableRows", variables],
    queryFn: adminFetcher<
      Types.AdminSitesTableRowsQuery,
      Types.AdminSitesTableRowsQueryVariables
    >(AdminSitesTableRowsDocument, variables),
    ...options,
  })
}

useAdminSitesTableRowsQuery.getKey = (
  variables: Types.AdminSitesTableRowsQueryVariables,
) => ["AdminSitesTableRows", variables]

useAdminSitesTableRowsQuery.rootKey = "AdminSitesTableRows"

export const useSuspenseAdminSitesTableRowsQuery = <
  TData = Types.AdminSitesTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminSitesTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSitesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSitesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSitesTableRowsQuery, TError, TData>({
    queryKey: ["AdminSitesTableRowsSuspense", variables],
    queryFn: adminFetcher<
      Types.AdminSitesTableRowsQuery,
      Types.AdminSitesTableRowsQueryVariables
    >(AdminSitesTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminSitesTableRowsQuery.getKey = (
  variables: Types.AdminSitesTableRowsQueryVariables,
) => ["AdminSitesTableRowsSuspense", variables]

useSuspenseAdminSitesTableRowsQuery.rootKey = "AdminSitesTableRows"

export const useInfiniteAdminSitesTableRowsQuery = <
  TData = InfiniteData<Types.AdminSitesTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSitesTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSitesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSitesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSitesTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSitesTableRows.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminSitesTableRowsQuery,
            Types.AdminSitesTableRowsQueryVariables
          >(AdminSitesTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSitesTableRowsQuery.getKey = (
  variables: Types.AdminSitesTableRowsQueryVariables,
) => ["AdminSitesTableRows.infinite", variables]

useInfiniteAdminSitesTableRowsQuery.rootKey = "AdminSitesTableRows.infinite"

export const useSuspenseInfiniteAdminSitesTableRowsQuery = <
  TData = InfiniteData<Types.AdminSitesTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSitesTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSitesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSitesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSitesTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminSitesTableRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminSitesTableRowsQuery,
            Types.AdminSitesTableRowsQueryVariables
          >(AdminSitesTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSitesTableRowsQuery.getKey = (
  variables: Types.AdminSitesTableRowsQueryVariables,
) => ["AdminSitesTableRows.infiniteSuspense", variables]

useSuspenseInfiniteAdminSitesTableRowsQuery.rootKey = "AdminSitesTableRows.infinite"

useAdminSitesTableRowsQuery.fetcher = (
  variables: Types.AdminSitesTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AdminSitesTableRowsQuery, Types.AdminSitesTableRowsQueryVariables>(
    AdminSitesTableRowsDocument,
    variables,
    options,
  )

export const AdminBidsTableViewDocument = `
    query AdminBidsTableView($filterBy: AdminBidsTableFilter, $sortBy: [AdminBidsTableSort!]) {
  allBids {
    view(filterBy: $filterBy, sortBy: $sortBy) {
      count
      keys
      stats {
        buildingOwnerNames
        financierNames
      }
    }
  }
}
    `

export const useAdminBidsTableViewQuery = <
  TData = Types.AdminBidsTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBidsTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminBidsTableViewQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminBidsTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBidsTableView"]
        : ["AdminBidsTableView", variables],
    queryFn: adminFetcher<
      Types.AdminBidsTableViewQuery,
      Types.AdminBidsTableViewQueryVariables
    >(AdminBidsTableViewDocument, variables),
    ...options,
  })
}

useAdminBidsTableViewQuery.getKey = (
  variables?: Types.AdminBidsTableViewQueryVariables,
) =>
  variables === undefined ? ["AdminBidsTableView"] : ["AdminBidsTableView", variables]

useAdminBidsTableViewQuery.rootKey = "AdminBidsTableView"

export const useSuspenseAdminBidsTableViewQuery = <
  TData = Types.AdminBidsTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBidsTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBidsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBidsTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBidsTableViewSuspense"]
        : ["AdminBidsTableViewSuspense", variables],
    queryFn: adminFetcher<
      Types.AdminBidsTableViewQuery,
      Types.AdminBidsTableViewQueryVariables
    >(AdminBidsTableViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminBidsTableViewQuery.getKey = (
  variables?: Types.AdminBidsTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBidsTableViewSuspense"]
    : ["AdminBidsTableViewSuspense", variables]

useSuspenseAdminBidsTableViewQuery.rootKey = "AdminBidsTableView"

export const useInfiniteAdminBidsTableViewQuery = <
  TData = InfiniteData<Types.AdminBidsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBidsTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBidsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBidsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBidsTableView.infinite"]
            : ["AdminBidsTableView.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminBidsTableViewQuery,
            Types.AdminBidsTableViewQueryVariables
          >(AdminBidsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBidsTableViewQuery.getKey = (
  variables?: Types.AdminBidsTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBidsTableView.infinite"]
    : ["AdminBidsTableView.infinite", variables]

useInfiniteAdminBidsTableViewQuery.rootKey = "AdminBidsTableView.infinite"

export const useSuspenseInfiniteAdminBidsTableViewQuery = <
  TData = InfiniteData<Types.AdminBidsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBidsTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBidsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminBidsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBidsTableView.infiniteSuspense"]
            : ["AdminBidsTableView.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminBidsTableViewQuery,
            Types.AdminBidsTableViewQueryVariables
          >(AdminBidsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBidsTableViewQuery.getKey = (
  variables?: Types.AdminBidsTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBidsTableView.infiniteSuspense"]
    : ["AdminBidsTableView.infiniteSuspense", variables]

useSuspenseInfiniteAdminBidsTableViewQuery.rootKey = "AdminBidsTableView.infinite"

useAdminBidsTableViewQuery.fetcher = (
  variables?: Types.AdminBidsTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AdminBidsTableViewQuery, Types.AdminBidsTableViewQueryVariables>(
    AdminBidsTableViewDocument,
    variables,
    options,
  )

export const AdminBidsTableRowsDocument = `
    query AdminBidsTableRows($keys: [Int!]!) {
  allBids {
    rows(keys: $keys) {
      id
      bidPackagePid
      bundlePid
      bidPackageName
      name
      createdAt
      isAwarded
      isCustom
      packageRowStatus
      packageRowIsClosed
      packageRowClosedAt
      packageRowCloseReason
      packageRowCloseCustomReason
      estimatedCod
      estimatedNtp
      csEstimatedAwardDate
      pvSizeKwDc
      pvSizeKwAc
      pvTypes
      buildingOwnerPid
      buildingOwnerName
      financierPid
      financierName
      sitePid
      siteName
      streetAddress
      city
      state
      zipCode
      utilityId
      utilityName
      utilityShortName
      termYears
      roofRentY1Usd
      roofRentEscalator
      buildingOwnerExtraPaymentsUsd
      extraPaymentsNotes
      buildingOwnerNoiLifetimeUsd
      interconnectionCostUtilityMaxUsd
      interconnectionCostUtilityMinUsd
      interconnectionCostNonUtilityUsd
      interconnectionCostUsdPerWatt
      interconnectionNotes
      itcPercentageAssumption
      recOwner
      incentivesNotes
      otherNotes
    }
  }
}
    `

export const useAdminBidsTableRowsQuery = <
  TData = Types.AdminBidsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBidsTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminBidsTableRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminBidsTableRowsQuery, TError, TData>({
    queryKey: ["AdminBidsTableRows", variables],
    queryFn: adminFetcher<
      Types.AdminBidsTableRowsQuery,
      Types.AdminBidsTableRowsQueryVariables
    >(AdminBidsTableRowsDocument, variables),
    ...options,
  })
}

useAdminBidsTableRowsQuery.getKey = (
  variables: Types.AdminBidsTableRowsQueryVariables,
) => ["AdminBidsTableRows", variables]

useAdminBidsTableRowsQuery.rootKey = "AdminBidsTableRows"

export const useSuspenseAdminBidsTableRowsQuery = <
  TData = Types.AdminBidsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBidsTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBidsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBidsTableRowsQuery, TError, TData>({
    queryKey: ["AdminBidsTableRowsSuspense", variables],
    queryFn: adminFetcher<
      Types.AdminBidsTableRowsQuery,
      Types.AdminBidsTableRowsQueryVariables
    >(AdminBidsTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminBidsTableRowsQuery.getKey = (
  variables: Types.AdminBidsTableRowsQueryVariables,
) => ["AdminBidsTableRowsSuspense", variables]

useSuspenseAdminBidsTableRowsQuery.rootKey = "AdminBidsTableRows"

export const useInfiniteAdminBidsTableRowsQuery = <
  TData = InfiniteData<Types.AdminBidsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBidsTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBidsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBidsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminBidsTableRows.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminBidsTableRowsQuery,
            Types.AdminBidsTableRowsQueryVariables
          >(AdminBidsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBidsTableRowsQuery.getKey = (
  variables: Types.AdminBidsTableRowsQueryVariables,
) => ["AdminBidsTableRows.infinite", variables]

useInfiniteAdminBidsTableRowsQuery.rootKey = "AdminBidsTableRows.infinite"

export const useSuspenseInfiniteAdminBidsTableRowsQuery = <
  TData = InfiniteData<Types.AdminBidsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBidsTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBidsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminBidsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminBidsTableRows.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminBidsTableRowsQuery,
            Types.AdminBidsTableRowsQueryVariables
          >(AdminBidsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBidsTableRowsQuery.getKey = (
  variables: Types.AdminBidsTableRowsQueryVariables,
) => ["AdminBidsTableRows.infiniteSuspense", variables]

useSuspenseInfiniteAdminBidsTableRowsQuery.rootKey = "AdminBidsTableRows.infinite"

useAdminBidsTableRowsQuery.fetcher = (
  variables: Types.AdminBidsTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AdminBidsTableRowsQuery, Types.AdminBidsTableRowsQueryVariables>(
    AdminBidsTableRowsDocument,
    variables,
    options,
  )
